// TODO(brand): migrate to <Button>
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { GameState, Player, Team, StatRecord, ViewState, GameEvent, Lineup, AppSettings, GameSuggestion, MatchRecord, UserProfile, RotationalSubstitution, isGameType, AdvancedStatToggles, DEFAULT_ADVANCED_STAT_TOGGLES, LiberoAssignment } from '../types';

// ── Feature flags — set to true to re-enable these panels ───────────────────
const ENABLE_AI_COACH = false;
const ENABLE_OPPONENT_VISUALIZER = false;

import GameSuggestions from './GameSuggestions';
import { generateGameSuggestions, GameSuggestionsRequest, generateSetInsights, SetInsightsTip } from '../services/geminiService';
import { RotateCcw, Minus, Plus, RotateCw, Volleyball, AlertCircle, UserPlus, X, Check, ArrowRight, ArrowLeft, ArrowUp, ArrowDown, ChevronUp, ChevronDown, Trophy, Trash2, CalendarDays, Download, Save, ChevronDown as ChevronDownIcon, Users, Flag, Swords, FilePlus, Zap, XCircle, Shield, Skull, Hand, Target, Shovel, HelpingHand, Play, RefreshCw, Repeat, User, BarChart2, Filter, ArrowLeftRight, Lock, Upload, Pencil, ScanLine, Mic, MicOff, Loader2, Wand2, TrendingUp, Activity, WifiOff, CloudUpload } from 'lucide-react';
import { PieChart, Pie, Cell, Tooltip as RechartsTooltip, ResponsiveContainer } from 'recharts';
import { useVoiceLogger } from '../hooks/useVoiceLogger';
import { isDevUser } from '../lib/featureFlags';
import { formatMonthDay } from '../lib/dates';
import { VoiceParsedEvent } from '../types';
import LineupSheetScanner from './LineupSheetScanner';
import QuickLineupBuilder from './QuickLineupBuilder';
import { supabase, mapPlayerFromDB, mapTeamFromDB, mapEventFromDB, submitMatchRecord, getMatchRecords } from '../services/supabase';
import PlayerEditModal from './PlayerEditModal';

interface GameTimeProps {
  organizationId: string;
  currentUser?: UserProfile;
  players: Player[];
  teams: Team[];
  events: GameEvent[];
  lineups?: Lineup[];
  matchRecords?: MatchRecord[];
  setPlayers: React.Dispatch<React.SetStateAction<Player[]>>;
  setLineups?: React.Dispatch<React.SetStateAction<Lineup[]>>;
  setEvents?: React.Dispatch<React.SetStateAction<GameEvent[]>>;
  setTeams?: React.Dispatch<React.SetStateAction<Team[]>>;
  setMatchRecords?: React.Dispatch<React.SetStateAction<MatchRecord[]>>;
  changeView: (view: ViewState) => void;
  triggerTeamCreation: () => void;
  appSettings?: AppSettings;
  setAppSettings?: React.Dispatch<React.SetStateAction<AppSettings>>;
  initialMatchId?: string;
  initialActiveTeamId?: string;
  onActiveTeamChange?: (id: string) => void;
}

const initialGameState: GameState = {
  id: 'temp-game',
  opponent: 'Guest',
  ourScore: 0,
  theirScore: 0,
  ourSetsWon: 0,
  theirSetsWon: 0,
  set: 1,
  lineup: {
    p1: '', p2: '', p3: '', p4: '', p5: '', p6: ''
  },
  numerals: {
    p1: 'I', p2: 'II', p3: 'III', p4: 'IV', p5: 'V', p6: 'VI'
  },
  opponentLineup: {
    op1: '', op2: '', op3: '', op4: '', op5: '', op6: ''
  },
  opponentNumerals: {
    op1: 'I', op2: 'II', op3: 'III', op4: 'IV', op5: 'V', op6: 'VI'
  },
  stats: [],
  substitutionsUsed: { us: 0, them: 0 }
};

// Simplified opponent player structure for local state
interface SimplePlayer {
  id: string;
  name: string;
  number: string;
  position: string;
}

// Helper to convert internal Roman numerals to Rotation Number
const getRotationNumber = (roman: string) => {
  const map: Record<string, string> = { 'I': '1', 'II': '2', 'III': '3', 'IV': '4', 'V': '5', 'VI': '6' };
  return map[roman] || '-';
};

// Helper to get Position Abbreviation
const getPosAbbreviation = (pos: string) => {
  const map: Record<string, string> = {
    'Setter': 'S',
    'Outside Hitter': 'OH',
    'Right Side': 'Oppo',
    'Opposite': 'Oppo',
    'Opposite (Setter)': 'Opp(S)',
    'Setter (Opposite)': 'S(Opp)',
    'Middle Blocker': 'MB',
    'Libero': 'L',
    'DS': 'SD',
    'Defensive Specialist': 'SD',
    'Unknown': '?'
  };
  return map[pos] || pos?.substring(0, 2).toUpperCase() || '?';
};

// Fix: Define missing StatCategory type for stats filtering
type StatCategory = 'Summary' | 'Attacking' | 'Serving' | 'Blocking' | 'Digging' | 'Setting' | 'Serve Receiving';

// --- GAME STATS COMPONENT ---
const GS_ERR_ORDER = ['out', 'net', 'blocked', 'lift', 'double', 'under', 'other'] as const;
const GS_ERR_LABELS: Record<string, string> = { out: 'Out', net: 'Net Vio', blocked: 'Blocked', lift: 'Lift', double: 'Double', under: 'Under', other: 'Other' };
const GS_ERR_COLORS: Record<string, string> = { out: '#ef4444', net: '#2563eb', blocked: '#10b981', lift: '#f97316', double: '#7c3aed', under: '#78350f', other: '#94a3b8' };

const GameStats = ({ stats, players, currentSet, history }: { stats: StatRecord[], players: Player[], currentSet: number, history: GameEvent[] }) => {
  const [viewMode, setViewMode] = useState<'team' | 'player'>('team');
  const [filterSet, setFilterSet] = useState<string>('all');
  const [category, setCategory] = useState('Summary');
  const [activeTooltip, setActiveTooltip] = useState<string | null>(null);
  const [expandedPlayer, setExpandedPlayer] = useState<string | null>(null);

  // Helper to format decimals
  const fmt = (n: number) => n.toFixed(2);
  const fmtPct = (n: number) => (n * 100).toFixed(1) + '%';

  // Available Sets for Filter
  const setsPlayed = useMemo(() => {
    const s = new Set<number>();
    stats.forEach(r => { if (r.setNumber) s.add(r.setNumber); });
    s.add(currentSet);
    return Array.from(s).sort((a, b) => a - b);
  }, [stats, currentSet]);

  const aggregatedData = useMemo(() => {
    const data: Record<string, any> = {};
    players.forEach(p => {
      data[p.id] = {
        name: p.name,
        number: p.jerseyNumber || (p as any).number,
        position: p.position,
        gamesPlayed: 0,
        kills: 0, errors: 0, attempts: 0,
        aces: 0, serveErrors: 0, serveAttempts: 0,
        blocks: 0, blockSolos: 0, blockAssists: 0, blockErrors: 0,
        digs: 0, digErrors: 0,
        assists: 0,
        receptions: 0, receptionErrors: 0, receptionSum: 0,
        srPerfect: 0, srGood: 0, srPoor: 0,
        receiveKills: 0,
        servePhaseKills: 0,
        breakPoints: 0,
        attackErrorsByType: {} as Record<string, number>,
      };
    });
    const teamTotal = {
      name: 'Team Total', gamesPlayed: 0, kills: 0, errors: 0, attempts: 0, aces: 0, serveErrors: 0, serveAttempts: 0,
      blocks: 0, blockSolos: 0, blockAssists: 0, blockErrors: 0, digs: 0, digErrors: 0, assists: 0, receptions: 0, receptionErrors: 0, receptionSum: 0,
      srPerfect: 0, srGood: 0, srPoor: 0,
      receiveKills: 0, servePhaseKills: 0, breakPoints: 0,
      attackErrorsByType: {} as Record<string, number>,
      killHitTypeCounts: {} as Record<string, number>,
      killOutcomeCounts: {} as Record<string, number>,
    };
    let sourceData: { eventId: string, stats: StatRecord[] }[] = [];
    if (filterSet === 'season') {
       const historical = history.filter(e => isGameType(e.type) && e.stats).map(e => ({ eventId: e.id, stats: e.stats! }));
       sourceData = [...historical, { eventId: 'current', stats }];
    } else if (filterSet === 'all') {
       sourceData = [{ eventId: 'current', stats }];
    } else {
       const setNum = parseInt(filterSet);
       sourceData = [{ eventId: 'current', stats: stats.filter(s => s.setNumber === setNum) }];
    }
    sourceData.forEach(evt => {
        const playerSetsInMatch = new Map<string, Set<number>>();
        evt.stats.forEach(stat => {
            const p = data[stat.playerId];
            if (!p) return; 
            if (stat.setNumber) {
                if (!playerSetsInMatch.has(stat.playerId)) playerSetsInMatch.set(stat.playerId, new Set());
                playerSetsInMatch.get(stat.playerId)?.add(stat.setNumber);
            }
            switch(stat.statType) {
                case 'kill':
                  p.kills++; p.attempts++; teamTotal.kills++; teamTotal.attempts++;
                  if (stat.phase === 'receive') { p.receiveKills++; teamTotal.receiveKills++; }
                  if (stat.phase === 'serve')   { p.servePhaseKills++; teamTotal.servePhaseKills++; }
                  if ((stat as any).killHitType) { const ht = (stat as any).killHitType; teamTotal.killHitTypeCounts[ht] = (teamTotal.killHitTypeCounts[ht] ?? 0) + 1; }
                  if ((stat as any).killOutcome) { const oc = (stat as any).killOutcome; teamTotal.killOutcomeCounts[oc] = (teamTotal.killOutcomeCounts[oc] ?? 0) + 1; }
                  break;
                case 'attack_error': p.errors++; p.attempts++; teamTotal.errors++; teamTotal.attempts++; { const et = (stat as any).attackErrorType; if (et) { p.attackErrorsByType[et] = (p.attackErrorsByType[et] ?? 0) + 1; teamTotal.attackErrorsByType[et] = (teamTotal.attackErrorsByType[et] ?? 0) + 1; } } break;
                case 'attack_attempt': p.attempts++; teamTotal.attempts++; break;
                case 'ace': p.aces++; p.serveAttempts++; teamTotal.aces++; teamTotal.serveAttempts++; break;
                case 'serve_error': p.serveErrors++; p.serveAttempts++; teamTotal.serveErrors++; teamTotal.serveAttempts++; break;
                case 'serve_attempt': p.serveAttempts++; teamTotal.serveAttempts++; break;
                case 'block_solo': p.blockSolos++; p.blocks += 1; teamTotal.blockSolos++; teamTotal.blocks += 1; break;
                case 'block_assist': p.blockAssists++; p.blocks += 0.5; teamTotal.blockAssists++; teamTotal.blocks += 0.5; break;
                case 'block_error': p.blockErrors++; teamTotal.blockErrors++; break;
                case 'dig': p.digs++; teamTotal.digs++; break;
                case 'dig_error': p.digErrors++; teamTotal.digErrors++; break;
                case 'assist': p.assists++; teamTotal.assists++; break;
                case 'reception':
                p.receptions++; teamTotal.receptions++;
                if (stat.passRating !== undefined) {
                    p.receptionSum += stat.passRating;
                    teamTotal.receptionSum += stat.passRating;
                    if (stat.passRating === 3) { p.srPerfect++; teamTotal.srPerfect++; }
                    else if (stat.passRating === 2) { p.srGood++; teamTotal.srGood++; }
                    else if (stat.passRating >= 0 && stat.passRating < 2) { p.srPoor++; teamTotal.srPoor++; }
                }
                break;
                case 'reception_error': p.receptionErrors++; teamTotal.receptionErrors++; p.receptions++; teamTotal.receptions++; break;
                case 'break_point': p.breakPoints++; teamTotal.breakPoints++; break;
            }
        });
        playerSetsInMatch.forEach((sets, playerId) => { if (data[playerId]) data[playerId].gamesPlayed += sets.size; });
        let maxSets = 0; playerSetsInMatch.forEach(sets => { if (sets.size > maxSets) maxSets = sets.size; });
        teamTotal.gamesPlayed += maxSets;
    });
    return { players: Object.values(data), team: teamTotal };
  }, [filterSet, stats, players, history]);

  const columns = useMemo(() => {
    type ColDef = { label: string; val: (r: any) => React.ReactNode; w?: string; highlight?: boolean; };
    const cols: ColDef[] = [
      { label: 'Player', val: (r: any) => <div className="flex flex-col"><span className="font-bold text-slate-800">{r.number}</span><span className="text-[10px] text-slate-500 truncate max-w-[60px]">{r.name.split(' ')[0]}</span></div>, w: 'w-16' },
      { label: 'GP', val: (r: any) => r.gamesPlayed }
    ];
    switch(category) {
      case 'Attacking': return [...cols, { label: 'K', val: (r: any) => r.kills }, { label: 'K/G', val: (r: any) => r.gamesPlayed ? fmt(r.kills/r.gamesPlayed) : '0.00' }, { label: 'E', val: (r: any) => r.errors }, { label: 'Att', val: (r: any) => r.attempts }, { label: 'Hit%', val: (r: any) => r.attempts ? fmt((r.kills - r.errors) / r.attempts) : '.000', highlight: true }, { label: 'K%', val: (r: any) => r.attempts ? fmtPct(r.kills / r.attempts) : '0%' }];
      case 'Serving': return [...cols, { label: 'Ace', val: (r: any) => r.aces }, { label: 'A/G', val: (r: any) => r.gamesPlayed ? fmt(r.aces/r.gamesPlayed) : '0.00' }, { label: 'Err', val: (r: any) => r.serveErrors }, { label: 'Att', val: (r: any) => r.serveAttempts }, { label: 'Ace%', val: (r: any) => r.serveAttempts ? fmtPct(r.aces / r.serveAttempts) : '0%', highlight: true }];
      case 'Blocking': return [...cols, { label: 'BS', val: (r: any) => r.blockSolos || 0 }, { label: 'BA', val: (r: any) => r.blockAssists || 0 }, { label: 'TB', val: (r: any) => (r.blocks || 0).toFixed(1), highlight: true }, { label: 'TB/G', val: (r: any) => r.gamesPlayed ? fmt(r.blocks/r.gamesPlayed) : '0.00' }, { label: 'Err', val: (r: any) => r.blockErrors }];
      case 'Digging': return [...cols, { label: 'Dig', val: (r: any) => r.digs, highlight: true }, { label: 'D/G', val: (r: any) => r.gamesPlayed ? fmt(r.digs/r.gamesPlayed) : '0.00' }, { label: 'Err', val: (r: any) => r.digErrors }];
      case 'Serve Receiving': return [...cols, { label: 'R', val: (r: any) => r.receptions }, { label: 'R/G', val: (r: any) => r.gamesPlayed ? fmt(r.receptions/r.gamesPlayed) : '0.00' }, { label: 'Err', val: (r: any) => r.receptionErrors }, { label: 'Avg', val: (r: any) => r.receptions ? fmt(r.receptionSum / r.receptions) : '0.0', highlight: true }];
      case 'Setting': return [...cols, { label: 'Ast', val: (r: any) => r.assists, highlight: true }, { label: 'A/G', val: (r: any) => r.gamesPlayed ? fmt(r.assists/r.gamesPlayed) : '0.00' }];
      default: return [...cols,
        { label: 'K', val: (r: any) => r.kills },
        { label: 'Hit%', val: (r: any) => r.attempts ? fmt((r.kills - r.errors) / r.attempts) : '.000', highlight: true },
        { label: 'Srv%', val: (r: any) => r.serveAttempts ? ((r.serveAttempts - r.serveErrors) / r.serveAttempts * 100).toFixed(0) + '%' : '—' },
        { label: 'Avg', val: (r: any) => r.receptions ? fmt(r.receptionSum / r.receptions) : '—' },
        { label: 'SO%', val: (r: any) => r.receptions ? (r.receiveKills / r.receptions * 100).toFixed(0) + '%' : '—', highlight: true },
        { label: 'BP%', val: (r: any) => r.serveAttempts ? (r.breakPoints / r.serveAttempts * 100).toFixed(0) + '%' : '—' },
      ];
    }
  }, [category]);

  // ── Shared donut renderer ──────────────────────────────────────────────────
  const renderDonut = (
    title: string,
    pieData: { name: string; value: number; color: string }[],
    totalLabel: string,
    emptyMsg: string,
  ) => {
    const total = pieData.reduce((s, d) => s + d.value, 0);
    const isEmpty = total === 0;
    return (
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
        <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-3">{title}</div>
        {isEmpty ? (
          <div className="flex items-center gap-3">
            <div className="shrink-0 opacity-30" style={{ width: 80, height: 80 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart><Pie data={[{ name: '', value: 1 }]} cx="50%" cy="50%" innerRadius={20} outerRadius={36} dataKey="value"><Cell fill="#e2e8f0" /></Pie></PieChart>
              </ResponsiveContainer>
            </div>
            <p className="text-[11px] text-slate-400 italic leading-relaxed">{emptyMsg}</p>
          </div>
        ) : (
          <div className="flex items-center gap-4">
            <div className="shrink-0" style={{ width: 80, height: 80 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={pieData} cx="50%" cy="50%" innerRadius={20} outerRadius={36} paddingAngle={2} dataKey="value" startAngle={90} endAngle={-270}>
                    {pieData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                  </Pie>
                  <RechartsTooltip formatter={(v: any, name: any) => [`${v} (${((Number(v) / total) * 100).toFixed(0)}%)`, name]} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="flex-1 space-y-1">
              {pieData.map(d => (
                <div key={d.name} className="flex items-center gap-1.5">
                  <div className="w-2 h-2 rounded-full shrink-0" style={{ background: d.color }} />
                  <span className="flex-1 text-[11px] text-slate-600">{d.name}</span>
                  <span className="text-xs font-bold text-slate-800">{d.value}</span>
                  <span className="text-[10px] text-slate-400 w-7 text-right">{((d.value / total) * 100).toFixed(0)}%</span>
                </div>
              ))}
              <div className="pt-1 border-t border-slate-100 flex justify-between text-[10px] text-slate-500">
                <span>{totalLabel}</span><span className="font-bold text-slate-700">{total}</span>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden mt-4 mx-2 md:mx-4 mb-20 animate-fade-in-up">
      {/* ── Header ── */}
      <div className="p-3 border-b border-slate-100 flex flex-col gap-3">
        <div className="flex justify-between items-center">
          <h3 className="font-bold text-slate-800 flex items-center gap-2 text-[24px]"><BarChart2 className="w-6 h-6 text-blue-600" /> Match Stats</h3>
          <div className="flex bg-slate-100 rounded-lg p-0.5">
            <button onClick={() => setViewMode('team')} className={`px-3 py-1 text-xs font-bold rounded-md transition-all ${viewMode === 'team' ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-500'}`}>Team</button>
            <button onClick={() => setViewMode('player')} className={`px-3 py-1 text-xs font-bold rounded-md transition-all ${viewMode === 'player' ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-500'}`}>Player</button>
          </div>
        </div>
        <div className="flex gap-2 overflow-x-auto pb-1 items-center">
          <div className="relative shrink-0">
            <select className="appearance-none bg-slate-50 border border-slate-200 text-slate-700 text-xs font-bold py-1.5 pl-3 pr-8 rounded-lg outline-none focus:border-blue-400 cursor-pointer" value={filterSet} onChange={e => setFilterSet(e.target.value)}>
              <option value="all">Current Match</option>
              <option value="season">All Time (Season)</option>
              {setsPlayed.map(s => <option key={s} value={s.toString()}>Set {s}</option>)}
            </select>
            <Filter className="w-3 h-3 text-slate-400 absolute right-2.5 top-2 pointer-events-none" />
          </div>
          {/* Category tabs — only meaningful for the player table */}
          {viewMode === 'player' && (
            <div className="flex gap-1">
              {(['Summary', 'Attacking', 'Serving', 'Blocking', 'Digging', 'Setting', 'Serve Receiving'] as StatCategory[]).map(c => (
                <button key={c} onClick={() => setCategory(c)} className={`px-3 py-1.5 rounded-lg text-xs font-bold whitespace-nowrap transition-colors ${category === c ? 'bg-blue-50 text-blue-700 border border-blue-100' : 'text-slate-500 hover:bg-slate-50'}`}>{c}</button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="p-4">
        {viewMode === 'team' ? (() => {
          const t = aggregatedData.team;
          const srvPct  = t.serveAttempts ? (t.serveAttempts - t.serveErrors) / t.serveAttempts : null;
          const hitPct  = t.attempts ? (t.kills - t.errors) / t.attempts : null;
          const bpReceiveStats = (() => {
            if (filterSet === 'season') {
              const histStats = history.flatMap((e: any) => (e.stats || []) as StatRecord[]);
              return [...histStats, ...stats].filter(s => s.phase === 'receive' && s.statType === 'break_point');
            }
            const src = filterSet === 'all' ? stats : stats.filter(s => s.setNumber === parseInt(filterSet));
            return src.filter(s => s.phase === 'receive' && s.statType === 'break_point');
          })();
          const soAttempts = bpReceiveStats.length;
          const soWon = bpReceiveStats.filter(s => !s.flags?.includes('receive_failure')).length;
          const soFrac = soAttempts > 0
            ? soWon / soAttempts
            : (t.receptions > 0 ? t.receiveKills / t.receptions : null);
          const soSub = soAttempts > 0 ? `${soWon}/${soAttempts}` : `${t.receiveKills}/${t.receptions}`;
          const bp      = t.serveAttempts ? t.breakPoints / t.serveAttempts : null;
          const avgPass = t.receptions ? t.receptionSum / t.receptions : null;

          // ── Summary metric cards ──
          const card = (label: string, value: string, color: string, sub?: string, definition?: string, fullLabel?: string) => (
            <div className="bg-slate-50 p-3 rounded-xl border border-slate-200 flex flex-col items-center justify-center text-center cursor-default select-none" onClick={() => definition && setActiveTooltip(activeTooltip === label ? null : label)}>
              <div className="text-[9px] uppercase font-bold text-slate-400 mb-1 leading-tight"><span className="md:hidden">{label}</span><span className="hidden md:block">{fullLabel || label}</span></div>
              <div className={`text-xl md:stat-num sm ${color}`}>{value}</div>
              {sub && <div className="text-[9px] text-slate-400 mt-0.5">{sub}</div>}
              {definition && activeTooltip === label && <div className="md:hidden text-[9px] text-slate-600 font-medium mt-1 leading-tight bg-slate-200 rounded px-1 py-0.5">{definition}</div>}
              {definition && <div className="hidden md:block text-[9px] text-slate-500 mt-1 italic leading-tight">{definition}</div>}
            </div>
          );

          // ── Donut chart data ──
          const attackPie = t.attempts > 0 ? [
            { name: 'Kills',   value: t.kills,                                   color: '#22c55e' },
            { name: 'Errors',  value: t.errors,                                  color: '#ef4444' },
            { name: 'In Play', value: Math.max(0, t.attempts - t.kills - t.errors), color: '#94a3b8' },
          ].filter(d => d.value > 0) : [];

          const servePie = t.serveAttempts > 0 ? [
            { name: 'Aces',    value: t.aces,                                              color: '#22c55e' },
            { name: 'Errors',  value: t.serveErrors,                                       color: '#ef4444' },
            { name: 'In Play', value: Math.max(0, t.serveAttempts - t.aces - t.serveErrors), color: '#94a3b8' },
          ].filter(d => d.value > 0) : [];

          const srPie = (t.srPerfect + t.srGood + t.srPoor + t.receptionErrors) > 0 ? [
            { name: 'Perfect (3)', value: t.srPerfect,       color: '#22c55e' },
            { name: 'Good (2)',    value: t.srGood,           color: '#3b82f6' },
            { name: 'Poor (1)',    value: t.srPoor,           color: '#f59e0b' },
            { name: 'Error (0)',   value: t.receptionErrors,  color: '#ef4444' },
          ].filter(d => d.value > 0) : [];

          const hasErrDetail = GS_ERR_ORDER.filter(k => k !== 'other').some(k => (t.attackErrorsByType[k] ?? 0) > 0);
          const errPie = hasErrDetail
            ? GS_ERR_ORDER.map(k => ({ name: GS_ERR_LABELS[k], value: t.attackErrorsByType[k] ?? 0, color: GS_ERR_COLORS[k] })).filter(d => d.value > 0).sort((a, b) => b.value - a.value)
            : [];

          const GS_HIT_TYPE_COLORS: Record<string, string> = { driven: '#22c55e', shot: '#3b82f6', tip: '#f59e0b' };
          const GS_OUTCOME_COLORS: Record<string, string> = { tool: '#8b5cf6', floor: '#10b981', shank: '#ef4444' };
          const hitTypePie = (Object.entries(t.killHitTypeCounts) as [string, number][]).filter(([, v]) => v > 0).map(([k, v]) => ({ name: k.charAt(0).toUpperCase() + k.slice(1), value: v, color: GS_HIT_TYPE_COLORS[k] ?? '#94a3b8' }));
          const outcomePie = (Object.entries(t.killOutcomeCounts) as [string, number][]).filter(([, v]) => v > 0).map(([k, v]) => ({ name: k.charAt(0).toUpperCase() + k.slice(1), value: v, color: GS_OUTCOME_COLORS[k] ?? '#94a3b8' }));

          return (
            <div className="space-y-4">
              {/* Summary metrics */}
              <div className="grid grid-cols-3 md:grid-cols-5 gap-3">
                {card('Avg Pass', avgPass !== null ? avgPass.toFixed(2) : '—', 'text-emerald-600', '0–3 scale', 'Avg serve-receive pass rating', 'Avg Pass Rating')}
                {card('Srv %', srvPct !== null ? (srvPct * 100).toFixed(1) + '%' : '—', 'text-blue-600', `${t.serveAttempts - t.serveErrors}/${t.serveAttempts}`, 'Serves in-bounds ÷ attempts', 'Serving %')}
                {card('Hit %', hitPct !== null ? hitPct.toFixed(3) : '.000', 'text-slate-900', `${t.kills}K ${t.errors}E ${t.attempts}Att`, '(Kills − Errors) ÷ Attempts', 'Hitting %')}
                {card('SO %', soFrac !== null ? (soFrac * 100).toFixed(1) + '%' : '—', 'text-violet-600', soSub, 'Points scored while receiving', 'Sideout %')}
                {card('BP %', bp !== null ? (bp * 100).toFixed(1) + '%' : '—', 'text-orange-600', `${t.breakPoints}/${t.serveAttempts}`, 'Points scored while serving', 'Break Point %')}
              </div>
              {/* Donut charts */}
              <div className={`grid gap-3 ${errPie.length > 0 ? 'grid-cols-1 md:grid-cols-4' : 'grid-cols-1 md:grid-cols-3'}`}>
                {renderDonut('Attacking', attackPie, 'Total Attempts', 'No attack data yet')}
                {errPie.length > 0 && renderDonut('Error Breakdown', errPie, 'Typed Errors', '')}
                {renderDonut('Serving', servePie, 'Total Serves', 'No serve data yet')}
                {renderDonut('Serve Receive', srPie, 'Total Passes', 'No reception data yet')}
              </div>
              {/* Kill detail donuts — only when data exists */}
              {(hitTypePie.length > 0 || outcomePie.length > 0) && (
                <div className={`grid gap-3 ${hitTypePie.length > 0 && outcomePie.length > 0 ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1'}`}>
                  {hitTypePie.length > 0 && renderDonut('Kill Hit Type', hitTypePie, 'Tagged Kills', '')}
                  {outcomePie.length > 0 && renderDonut('Kill Outcome', outcomePie, 'Tagged Kills', '')}
                </div>
              )}
            </div>
          );
        })() : (
          // ── Player view ──
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="text-xs text-slate-500 bg-slate-50 uppercase font-bold">
                <tr>{columns.map((col, i) => <th key={i} className={`px-2 py-2 ${col.label === 'Player' ? 'sticky left-0 bg-slate-50 z-10' : 'text-center'}`}>{col.label}</th>)}</tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {aggregatedData.players.filter(p => (p.kills + p.attempts + p.digs + p.aces + p.receptions + p.assists + p.blocks) > 0).map((row: any) => {
                  const isExpanded = expandedPlayer === row.name;
                  const inPlay = Math.max(0, row.attempts - row.kills - row.errors);
                  const hittingPie = row.attempts > 0 ? [
                    { name: 'Kills', value: row.kills, color: '#22c55e' },
                    { name: 'Errors', value: row.errors, color: '#ef4444' },
                    { name: 'In Play', value: inPlay, color: '#94a3b8' },
                  ].filter(d => d.value > 0) : [];
                  const srPie = (row.srPerfect + row.srGood + row.srPoor + row.receptionErrors) > 0 ? [
                    { name: 'Perfect (3)', value: row.srPerfect,      color: '#22c55e' },
                    { name: 'Good (2)',    value: row.srGood,          color: '#3b82f6' },
                    { name: 'Poor (1)',    value: row.srPoor,          color: '#f59e0b' },
                    { name: 'Error (0)',   value: row.receptionErrors, color: '#ef4444' },
                  ].filter(d => d.value > 0) : [];
                  const hasErrDetail = GS_ERR_ORDER.filter(k => k !== 'other').some(k => (row.attackErrorsByType[k] ?? 0) > 0);
                  const errPie = hasErrDetail
                    ? GS_ERR_ORDER.map(k => ({ name: GS_ERR_LABELS[k], value: row.attackErrorsByType[k] ?? 0, color: GS_ERR_COLORS[k] })).filter(d => d.value > 0).sort((a, b) => b.value - a.value)
                    : [];
                  const hasCharts = hittingPie.length > 0 || srPie.length > 0 || errPie.length > 0;
                  return (
                    <React.Fragment key={row.name}>
                      <tr
                        className={`transition-colors cursor-pointer ${isExpanded ? 'bg-blue-50 ring-1 ring-inset ring-blue-200' : 'hover:bg-slate-50'} ${hasCharts ? '' : 'cursor-default'}`}
                        onClick={() => hasCharts && setExpandedPlayer(isExpanded ? null : row.name)}
                      >
                        {columns.map((col, cIdx) => (
                          <td key={cIdx} className={`px-2 py-2 ${col.label === 'Player' ? 'sticky left-0 z-10 border-r border-slate-100' : 'text-center text-slate-600'} ${isExpanded ? 'bg-blue-50' : 'bg-white'} ${col.highlight ? 'font-bold text-slate-900' : ''}`}>
                            {col.label === 'Player'
                              ? <div className="flex items-center gap-1.5">
                                  <div className="flex flex-col"><span className="font-bold text-slate-800">{row.number}</span><span className="text-[10px] text-slate-500 truncate max-w-[60px]">{row.name.split(' ')[0]}</span></div>
                                  {hasCharts && <ChevronDown className={`w-3 h-3 text-slate-400 shrink-0 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />}
                                </div>
                              : col.val(row)}
                          </td>
                        ))}
                      </tr>
                      {isExpanded && hasCharts && (
                        <tr>
                          <td colSpan={columns.length} className="px-3 py-3 bg-gradient-to-r from-blue-50/60 to-indigo-50/40">
                            <div className="rounded-xl border border-blue-200 bg-white shadow-sm p-4">
                              <div className="text-xs font-bold text-slate-500 mb-3">{row.name} · {row.position}</div>
                              <div className={`grid gap-4 ${[hittingPie, errPie, srPie].filter(p => p.length > 0).length === 1 ? 'grid-cols-1' : [hittingPie, errPie, srPie].filter(p => p.length > 0).length === 2 ? 'grid-cols-2' : 'grid-cols-1 md:grid-cols-3'}`}>
                                {hittingPie.length > 0 && (
                                  <div className="space-y-1">
                                    <div className="flex items-center gap-1.5 text-xs font-bold text-slate-600"><TrendingUp className="w-3.5 h-3.5 text-green-500" /> Hitting <span className="font-normal text-slate-400">(N={row.attempts})</span></div>
                                    <ResponsiveContainer width="100%" height={130}>
                                      <PieChart>
                                        <Pie data={hittingPie} cx="50%" cy="50%" outerRadius={55} dataKey="value" strokeWidth={2} stroke="white">
                                          {hittingPie.map((d, i) => <Cell key={i} fill={d.color} />)}
                                        </Pie>
                                        <RechartsTooltip formatter={(v: any, name: any) => [`${v} (${((Number(v) / row.attempts) * 100).toFixed(0)}%)`, name]} />
                                      </PieChart>
                                    </ResponsiveContainer>
                                    <div className="grid grid-cols-3 gap-1 text-center">
                                      {hittingPie.map(d => <div key={d.name}><div className="text-base font-black" style={{ color: d.color }}>{d.value}</div><div className="text-[9px] text-slate-500">{d.name}</div></div>)}
                                    </div>
                                  </div>
                                )}
                                {errPie.length > 0 && (
                                  <div className="space-y-1">
                                    <div className="flex items-center gap-1.5 text-xs font-bold text-slate-600"><X className="w-3.5 h-3.5 text-red-500" /> Error Types <span className="font-normal text-slate-400">(N={errPie.reduce((s, d) => s + d.value, 0)})</span></div>
                                    <ResponsiveContainer width="100%" height={130}>
                                      <PieChart>
                                        <Pie data={errPie} cx="50%" cy="50%" outerRadius={55} dataKey="value" strokeWidth={2} stroke="white">
                                          {errPie.map((d, i) => <Cell key={i} fill={d.color} />)}
                                        </Pie>
                                        <RechartsTooltip formatter={(v: any, name: any) => { const tot = errPie.reduce((s, d) => s + d.value, 0); return [`${v} (${((Number(v)/tot)*100).toFixed(0)}%)`, name]; }} />
                                      </PieChart>
                                    </ResponsiveContainer>
                                    <div className="grid grid-cols-2 gap-x-2 gap-y-0.5">
                                      {errPie.map(d => { const tot = errPie.reduce((s, x) => s + x.value, 0); return <div key={d.name} className="flex items-center gap-1 text-[10px]"><div className="w-2 h-2 rounded-sm shrink-0" style={{ background: d.color }} /><span className="text-slate-500">{d.name}</span><span className="ml-auto font-bold">{d.value}</span><span className="text-slate-400">{((d.value/tot)*100).toFixed(0)}%</span></div>; })}
                                    </div>
                                  </div>
                                )}
                                {srPie.length > 0 && (
                                  <div className="space-y-1">
                                    <div className="flex items-center gap-1.5 text-xs font-bold text-slate-600"><Activity className="w-3.5 h-3.5 text-blue-500" /> Serve Receive <span className="font-normal text-slate-400">(N={srPie.reduce((s, d) => s + d.value, 0)})</span></div>
                                    <ResponsiveContainer width="100%" height={130}>
                                      <PieChart>
                                        <Pie data={srPie} cx="50%" cy="50%" outerRadius={55} dataKey="value" strokeWidth={2} stroke="white">
                                          {srPie.map((d, i) => <Cell key={i} fill={d.color} />)}
                                        </Pie>
                                        <RechartsTooltip formatter={(v: any, name: any) => { const tot = srPie.reduce((s, d) => s + d.value, 0); return [`${v} (${((Number(v)/tot)*100).toFixed(0)}%)`, name]; }} />
                                      </PieChart>
                                    </ResponsiveContainer>
                                    <div className="grid grid-cols-2 gap-x-2 gap-y-0.5">
                                      {srPie.map(d => { const tot = srPie.reduce((s, x) => s + x.value, 0); return <div key={d.name} className="flex items-center gap-1 text-[10px]"><div className="w-2 h-2 rounded-sm shrink-0" style={{ background: d.color }} /><span className="text-slate-500">{d.name}</span><span className="ml-auto font-bold">{d.value}</span><span className="text-slate-400">{((d.value/tot)*100).toFixed(0)}%</span></div>; })}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

// Extracted PlayerCard Component
interface PlayerCardProps {
  side: 'us' | 'them';
  pos: string;
  label: string;
  playerId: string;
  actualPlayerId: string;
  isLiberoActive: boolean;
  originalPlayerId: string;
  availablePlayers: (Player | SimplePlayer)[];
  isHighlighted: boolean;
  onPlayerChange: (value: string) => void;
  isScrimmageMode?: boolean;
  isServer?: boolean;
  isRallyInProgress?: boolean;
  onServeInPlay?: (serveType: 'Jump' | 'Float' | null) => void;
  row: 'front' | 'back';
  onServeAction?: (action: 'Ace' | 'Err', type: 'Jump' | 'Float' | null) => void;
  onGameStat?: (type: string, detail?: string, passRating?: number) => void;
  suggestAssist?: boolean;
  onConfirmAssist?: () => void;
  onDismissAssist?: () => void;
  onSub?: () => void;
  onAddPlayer?: () => void;
  onRemovePlayer?: () => void;
  overlayType: 'kill' | 'error' | 'sr' | null;
  onRequestErrorOverlay?: () => void;
  onOverlayOptionSelect?: (option: string, type: 'kill' | 'error' | 'sr') => void;
  onCancelOverlay?: () => void;
  onDumpSelected?: (row: 'front' | 'back') => void;
  onEditPlayer?: (playerId: string) => void;
  onLiberoToggle?: () => void;
  isLiberoSandwiched?: boolean; // true when this player has an active libero sandwich
  onBlockAssistStart?: () => void;
  isLineupEditMode?: boolean;
  simpleStats?: boolean;
  isAssignmentMode?: boolean;
  onAssign?: () => void;
  isFlashing?: boolean;
  flashBgClass?: string;
  onSRDetailCapture?: (incomingServeStyle: 'topspin' | 'float' | null, contactType: 'hands' | 'platform' | null) => void;
  serveTypeProp?: 'Jump' | 'Float' | null;
  onServeTypeChange?: (type: 'Jump' | 'Float') => void;
  preselectedServeStyle?: 'topspin' | 'float' | null;
  sidelineMode?: boolean;
  inlineStats?: { label: string; value: number; color: string }[];
  editStats?: { label: string; value: string; rank?: number }[];
  killOverlayRows?: { hitType: boolean; outcome: boolean };
  isDualRoleSetter?: boolean;
  isBackRowDualSetter?: boolean;
}

const PlayerCard: React.FC<PlayerCardProps> = ({ 
  side, 
  pos, 
  label,
  playerId,
  actualPlayerId,
  isLiberoActive,
  originalPlayerId,
  availablePlayers, 
  isHighlighted, 
  onPlayerChange,
  isScrimmageMode,
  isServer,
  isRallyInProgress,
  onServeInPlay,
  row,
  onServeAction,
  onGameStat,
  suggestAssist,
  onConfirmAssist,
  onDismissAssist,
  onSub,
  onAddPlayer,
  onRemovePlayer,
  overlayType,
  onRequestErrorOverlay,
  onOverlayOptionSelect,
  onCancelOverlay,
  onDumpSelected,
  onEditPlayer,
  onLiberoToggle,
  isLiberoSandwiched = false,
  onBlockAssistStart,
  isLineupEditMode = false,
  simpleStats = false,
  isAssignmentMode = false,
  onAssign,
  isFlashing = false,
  flashBgClass = '',
  onSRDetailCapture,
  serveTypeProp,
  onServeTypeChange,
  preselectedServeStyle,
  sidelineMode = false,
  inlineStats,
  editStats,
  killOverlayRows = { hitType: false, outcome: false },
  isDualRoleSetter = false,
  isBackRowDualSetter = false,
}) => {
  const isUs = side === 'us';
  const serveType = serveTypeProp ?? null;
  const [showLiberoAssistFlag, setShowLiberoAssistFlag] = useState(false);
  const [showSROptions, setShowSROptions] = useState(false);
  const [showAttackOptions, setShowAttackOptions] = useState(false);
  const [showDumpOrSwing, setShowDumpOrSwing] = useState(false);
  const [showBlockOptions, setShowBlockOptions] = useState(false);
  const srTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastClickRef = useRef<number>(0);
  const [flashButton, setFlashButton] = useState<string | null>(null);
  const flashRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const triggerFlash = (name: string) => { setFlashButton(name); if (flashRef.current) clearTimeout(flashRef.current); flashRef.current = setTimeout(() => setFlashButton(null), 600); };
  // SR detail overlay — shown for ~5s after pass grade is selected
  const [srDetail, setSrDetail] = useState<{ serveStyle: 'topspin' | 'float' | null; contactType: 'hands' | 'platform' | null; fading: boolean } | null>(null);
  const [killHitTypeSel, setKillHitTypeSel] = useState<string | null>(null);
  const [killOutcomeSel, setKillOutcomeSel] = useState<string | null>(null);
  useEffect(() => { if (!overlayType) { setKillHitTypeSel(null); setKillOutcomeSel(null); } }, [overlayType]);
  const srDetailTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const srDetailFadeRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const showSRDetailOverlay = () => {
    setSrDetail({ serveStyle: preselectedServeStyle ?? null, contactType: null, fading: false });
    if (srDetailTimerRef.current) clearTimeout(srDetailTimerRef.current);
    if (srDetailFadeRef.current) clearTimeout(srDetailFadeRef.current);
    srDetailTimerRef.current = setTimeout(() => {
      setSrDetail(prev => prev ? { ...prev, fading: true } : null);
      srDetailFadeRef.current = setTimeout(() => setSrDetail(null), 400);
    }, 5000);
  };
  const dismissSRDetail = () => {
    if (srDetailTimerRef.current) clearTimeout(srDetailTimerRef.current);
    if (srDetailFadeRef.current) clearTimeout(srDetailFadeRef.current);
    setSrDetail(null);
  };
  let playerDisplay: { name: string, number: string, position: string, photoUrl?: string } | undefined;
  if (actualPlayerId) {
    const p = availablePlayers.find(p => p.id === actualPlayerId);
    if (p) {
      const number = ('jerseyNumber' in p) ? (p as Player).jerseyNumber : (p as SimplePlayer).number;
      playerDisplay = { name: p.name, number: number, position: p.position, photoUrl: ('photoUrl' in p) ? (p as Player).photoUrl : undefined };
    }
  }
  // Dual-role setter: acts as Opposite when front row, shows dual label when back row
  const isDualRoleFrontRow = isDualRoleSetter && row === 'front';
  const isSetter = playerDisplay?.position === 'Setter' && !isDualRoleFrontRow;
  const displayPosition = isDualRoleFrontRow
    ? 'Opposite (Setter)'
    : isBackRowDualSetter
    ? 'Setter (Opposite)'
    : (playerDisplay?.position ?? '');
  const posNum = parseInt(pos.replace(/\D/g, ''), 10);
  const posRoman = ['', 'I', 'II', 'III', 'IV', 'V', 'VI'][posNum] ?? '';
  let bgColorClass = isLiberoActive ? (isUs ? 'bg-slate-700 border-slate-600 text-white' : 'bg-red-900 border-red-800 text-white') : (isUs ? (isHighlighted ? 'bg-yellow-50 border-yellow-400' : 'bg-white border-slate-700') : (isHighlighted ? 'bg-red-100 border-red-400' : 'bg-red-50 border-red-800'));
  const textColorClass = isLiberoActive ? 'text-white' : (isUs || isScrimmageMode ? 'text-slate-900' : 'text-red-900');
  const subIconColor = isLiberoActive ? 'text-slate-300 hover:text-white' : 'text-slate-500 hover:text-blue-600';
  useEffect(() => { return () => { if (srTimeoutRef.current) clearTimeout(srTimeoutRef.current); if (flashRef.current) clearTimeout(flashRef.current); if (srDetailTimerRef.current) clearTimeout(srDetailTimerRef.current); if (srDetailFadeRef.current) clearTimeout(srDetailFadeRef.current); }; }, []);
  const handleStatClick = (type: string, detail?: string) => { const now = Date.now(); if (now - lastClickRef.current < 300) return; lastClickRef.current = now; onGameStat?.(type, detail); };
  const handleAssistClick = () => { const now = Date.now(); if (now - lastClickRef.current < 500) return; lastClickRef.current = now; onConfirmAssist?.(); };
  const openSROptions = () => { setShowSROptions(true); if (srTimeoutRef.current) clearTimeout(srTimeoutRef.current); srTimeoutRef.current = setTimeout(() => setShowSROptions(false), 4000); };
  const handleSRGrade = (grade: number, label: string) => { if (srTimeoutRef.current) clearTimeout(srTimeoutRef.current); setShowSROptions(false); onGameStat?.(grade === 0 ? 'Reception Error' : 'Reception', label, grade); showSRDetailOverlay(); };
  const renderTiles = () => {
    if (!actualPlayerId) return null;
    // Libero assist: show location prompt to flag 10-ft line violations
    const handleAssistTap = () => {
      triggerFlash('ast');
      if (isLiberoActive) { setShowLiberoAssistFlag(true); return; }
      handleStatClick('Assist');
    };
    return (
      <div className="grid grid-cols-2 grid-rows-2 gap-1 h-full w-full">
        {/* TOP-LEFT: SR for regular players & libero; Assist for setter */}
        {isLiberoActive || !isSetter ? (
          <button onClick={() => { triggerFlash('sr'); openSROptions(); }} className={`rounded-lg flex flex-col items-center justify-center transition-all shadow-sm border h-full ${flashButton === 'sr' ? 'bg-slate-200 border-slate-300 ring-2 ring-slate-400/60 scale-105 text-slate-900' : 'bg-slate-100 hover:bg-slate-200 text-slate-800 border-slate-200 active:scale-95'}`}>
            <Target className="w-6 h-6 mb-0.5" />
            <span className="text-[16px] font-black uppercase tracking-tight">SR</span>
          </button>
        ) : (
          <button onClick={handleAssistTap} className={`rounded-lg flex flex-col items-center justify-center transition-all shadow-sm border h-full ${flashButton === 'ast' ? 'bg-purple-200 border-purple-300 ring-2 ring-purple-400/60 scale-105 text-purple-900' : 'bg-purple-100 hover:bg-purple-200 text-purple-800 border-purple-200 active:scale-95'}`}>
            <HelpingHand className="w-6 h-6 mb-0.5" />
            <span className="text-[16px] font-black uppercase tracking-tight">AST</span>
          </button>
        )}

        {/* TOP-RIGHT: BLOCK (front row, non-libero) or DIG (back row, or any libero position) */}
        {row === 'front' && !isLiberoActive ? (
          <button
            onClick={() => { triggerFlash('blk'); setShowBlockOptions(true); }}
            className={`rounded-lg flex flex-col items-center justify-center transition-all shadow-sm border h-full ${
              flashButton === 'blk' ? 'bg-indigo-200 border-indigo-300 ring-2 ring-indigo-400/60 scale-105 text-indigo-900' :
              'bg-indigo-100 hover:bg-indigo-200 text-indigo-800 border-indigo-200 active:scale-95'
            }`}
          >
            <Shield className="w-6 h-6 mb-0.5" />
            <span className="text-[16px] font-black uppercase tracking-tight">BLK</span>
          </button>
        ) : (
          <button onClick={() => { triggerFlash('dig'); handleStatClick('Dig'); }} className={`rounded-lg flex flex-col items-center justify-center transition-all shadow-sm border h-full ${flashButton === 'dig' ? 'bg-amber-200 border-amber-300 ring-2 ring-amber-400/60 scale-105 text-amber-900' : 'bg-amber-100 hover:bg-amber-200 text-amber-800 border-amber-200 active:scale-95'}`}>
            <Shovel className="w-6 h-6 mb-0.5" />
            <span className="text-[16px] font-black uppercase tracking-tight">DIG</span>
          </button>
        )}

        {/* BOTTOM-LEFT: Assist for libero; Attack for everyone else (including setter) */}
        {isLiberoActive ? (
          <button onClick={handleAssistTap} className={`rounded-lg flex flex-col items-center justify-center transition-all shadow-sm border h-full ${flashButton === 'ast' ? 'bg-purple-200 border-purple-300 ring-2 ring-purple-400/60 scale-105 text-purple-900' : 'bg-purple-100 hover:bg-purple-200 text-purple-800 border-purple-200 active:scale-95'}`}>
            <HelpingHand className="w-6 h-6 mb-0.5" />
            <span className="text-[16px] font-black uppercase tracking-tight">AST</span>
          </button>
        ) : (
          <button
            onClick={() => { triggerFlash('atk'); if (isSetter) { setShowDumpOrSwing(true); } else { setShowAttackOptions(true); } }}
            className={`rounded-lg flex flex-col items-center justify-center transition-all shadow-sm border h-full ${
              flashButton === 'atk' ? 'bg-green-200 border-green-300 ring-2 ring-green-400/60 scale-105 text-green-900' :
              'bg-green-100 hover:bg-green-200 text-green-800 border-green-200 active:scale-95'
            }`}
          >
            <Swords className="w-6 h-6 mb-0.5" />
            <span className="text-[16px] font-black uppercase tracking-tight hidden md:block">Attack</span>
            <span className="text-[16px] font-black uppercase tracking-tight md:hidden">ATK</span>
          </button>
        )}

        <button onClick={() => { triggerFlash('err'); onRequestErrorOverlay?.(); }} className={`rounded-lg flex flex-col items-center justify-center transition-all shadow-sm border h-full ${flashButton === 'err' ? 'bg-red-200 border-red-300 ring-2 ring-red-400/60 scale-105 text-red-900' : 'bg-red-100 hover:bg-red-200 text-red-800 border-red-200 active:scale-95'}`}>
          <XCircle className="w-6 h-6 mb-0.5" />
          <span className="text-[16px] font-black uppercase tracking-tight">ERR</span>
        </button>
      </div>
    );
  };
  const renderServingView = () => ( <div className="flex flex-col flex-1 gap-1 p-1 overflow-hidden"> {!simpleStats && <div className="flex w-full shrink-0"> <div className="flex rounded-lg bg-slate-700 p-0.5 w-full h-8"> <button type="button" onClick={() => onServeTypeChange?.('Float')} className={`flex-1 text-[11px] font-black rounded-md transition-all ${serveType === 'Float' || !serveType ? 'bg-slate-500 text-white shadow-sm' : 'text-slate-400 hover:text-slate-200'}`}>Float</button> <button type="button" onClick={() => onServeTypeChange?.('Jump')} className={`flex-1 text-[11px] font-black rounded-md transition-all ${serveType === 'Jump' ? 'bg-slate-500 text-white shadow-sm' : 'text-slate-400 hover:text-slate-200'}`}>Topspin</button> </div> </div>} <div className="flex-1 grid grid-cols-2 gap-1.5 min-h-0"> <button type="button" onClick={() => { triggerFlash('ace'); onServeAction?.('Ace', serveType); }} className={`text-white rounded-lg flex flex-col items-center justify-center shadow-sm border transition-all duration-150 ${flashButton === 'ace' ? 'bg-lime-500 border-lime-300 ring-4 ring-lime-400/40 scale-105' : 'bg-green-600 hover:bg-green-500 border-green-500 active:scale-95'}`}> <Zap className={`w-6 h-6 mb-0.5 transition-transform duration-150 ${flashButton === 'ace' ? 'scale-125' : ''}`} /><span className="text-[16px] font-black uppercase">{flashButton === 'ace' ? '✓ Ace!' : 'Ace'}</span> </button> <button type="button" onClick={() => { triggerFlash('se'); onServeAction?.('Err', serveType); }} className={`text-white rounded-lg flex flex-col items-center justify-center shadow-sm border transition-all duration-150 ${flashButton === 'se' ? 'bg-red-500 border-red-300 ring-4 ring-red-400/40 scale-105' : 'bg-red-600 hover:bg-red-500 border-red-500 active:scale-95'}`}> <XCircle className={`w-6 h-6 mb-0.5 transition-transform duration-150 ${flashButton === 'se' ? 'scale-110' : ''}`} /><span className="text-[16px] font-black uppercase">{flashButton === 'se' ? '✗ SE!' : 'SE'}</span> </button> </div> <button type="button" onClick={() => onServeInPlay?.(serveType)} className={`${simpleStats ? 'h-10 md:h-16' : 'h-7 md:h-8'} bg-slate-500 hover:bg-slate-400 text-white rounded-lg flex items-center justify-center font-bold text-[10px] md:text-xs uppercase tracking-wider active:scale-95 transition-transform shadow-sm gap-1.5 shrink-0`}> <Play className="w-3.5 h-3.5" /> Serve In Play </button> </div> );

  return (
    <div className={`relative flex flex-col rounded-xl border-2 transition-all shadow-sm overflow-hidden ${bgColorClass} ${isHighlighted ? 'ring-2 md:ring-4 ring-yellow-400/20 z-10' : ''} ${suggestAssist && isSetter ? 'ring-4 ring-green-400 shadow-lg scale-[1.02] z-20' : ''} h-40 md:h-48`}>
      {suggestAssist && isSetter && (
        <div className="absolute top-10 inset-x-0 bottom-0 bg-green-800/95 z-50 flex flex-col items-center justify-center gap-2 p-2 backdrop-blur-sm animate-fade-in rounded-b-xl">
          <HelpingHand className="w-7 h-7 text-green-300 animate-bounce" />
          <div className="text-base font-black uppercase tracking-wider text-white">Assist?</div>
          <div className="flex gap-2 w-full">
            <button
              onClick={(e) => { e.stopPropagation(); onDismissAssist?.(); }}
              className="flex-1 py-2 bg-red-600 hover:bg-red-500 active:scale-95 text-white font-black rounded-lg text-sm flex items-center justify-center gap-1 transition-all"
            >
              <X className="w-4 h-4" /> No
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); handleAssistClick(); }}
              className="flex-1 py-2 bg-green-500 hover:bg-green-400 active:scale-95 text-white font-black rounded-lg text-sm flex items-center justify-center gap-1 transition-all"
            >
              <Check className="w-4 h-4" /> Yes
            </button>
          </div>
        </div>
      )}
      {showSROptions && ( <div className="absolute inset-0 bg-slate-900/95 z-30 flex flex-col animate-fade-in p-1.5"> <div className="flex justify-between items-center mb-1 px-1"> <span className="text-[10px] text-slate-400 font-bold uppercase">Pass Quality</span> <button onClick={() => setShowSROptions(false)}><X className="w-4 h-4 text-white" /></button> </div> <div className="grid grid-cols-2 gap-1.5 flex-1"> <button onClick={() => handleSRGrade(0, '0 - Ace')} className="bg-red-500 hover:bg-red-400 text-white rounded-lg flex flex-col items-center justify-center border border-red-400 active:scale-95 transition-transform"> <span className="stat-num sm">0</span> <span className="text-[9px] font-bold uppercase">Ace</span> </button> <button onClick={() => handleSRGrade(1, '1 Option')} className="bg-orange-500 hover:bg-orange-400 text-white rounded-lg flex flex-col items-center justify-center border border-orange-400 active:scale-95 transition-transform"> <span className="stat-num sm">1</span> <span className="text-[9px] font-bold uppercase">Poor</span> </button> <button onClick={() => handleSRGrade(2, '2 Options')} className="bg-yellow-500 hover:bg-yellow-400 text-white rounded-lg flex flex-col items-center justify-center border border-yellow-400 active:scale-95 transition-transform"> <span className="stat-num sm">2</span> <span className="text-[9px] font-bold uppercase">Good</span> </button> <button onClick={() => handleSRGrade(3, '3 Options')} className="bg-emerald-500 hover:bg-emerald-400 text-white rounded-lg flex flex-col items-center justify-center border border-emerald-400 active:scale-95 transition-transform"> <span className="stat-num sm">3</span> <span className="text-[9px] font-bold uppercase">Perf</span> </button> </div> </div> )}
      {/* Libero Assist: 10-foot line location flag */}
      {showLiberoAssistFlag && (
        <div className="absolute inset-0 bg-purple-950/95 z-30 flex flex-col animate-fade-in p-2">
          <div className="text-[10px] text-purple-300 font-bold uppercase mb-2 px-1">Libero Setting Location</div>
          <div className="flex flex-col gap-2 flex-1">
            <button
              onClick={() => { setShowLiberoAssistFlag(false); handleStatClick('Assist', 'behind_10ft'); }}
              className="flex-1 bg-teal-600 hover:bg-teal-500 text-white rounded-xl flex flex-col items-center justify-center active:scale-95 transition-all gap-1"
            >
              <HelpingHand className="w-6 h-6" />
              <span className="text-sm font-black">Behind 10-ft</span>
              <span className="text-[9px] opacity-75">Standard set</span>
            </button>
            <button
              onClick={() => { setShowLiberoAssistFlag(false); handleStatClick('Assist', 'front_10ft'); }}
              className="flex-1 bg-amber-500 hover:bg-amber-400 text-white rounded-xl flex flex-col items-center justify-center active:scale-95 transition-all gap-1"
            >
              <AlertCircle className="w-6 h-6" />
              <span className="text-sm font-black">Front of 10-ft</span>
              <span className="text-[9px] opacity-75">Attack must stay below net</span>
            </button>
          </div>
          <button onClick={() => setShowLiberoAssistFlag(false)} className="mt-1.5 py-1.5 bg-purple-900 text-purple-200 rounded-lg text-[10px] font-bold uppercase active:scale-95">Cancel</button>
        </div>
      )}
      {showDumpOrSwing && !overlayType && (
        <div className="absolute inset-0 bg-slate-900/95 z-30 flex flex-col animate-fade-in p-2">
          <div className="text-[10px] text-slate-400 font-bold uppercase mb-1.5 px-1 shrink-0">Setter Attack Type</div>
          <div className="grid grid-cols-2 gap-2 flex-[2]">
            <button
              onClick={() => { setShowDumpOrSwing(false); onDumpSelected?.(row); setShowAttackOptions(true); }}
              className="bg-yellow-600 hover:bg-yellow-500 active:bg-yellow-700 text-white rounded-xl flex flex-col items-center justify-center active:scale-95 transition-all shadow-md border border-yellow-500 gap-1"
            >
              <Hand className="w-7 h-7" />
              <span className="text-sm font-black uppercase tracking-tight">Dump</span>
              <span className="text-[9px] font-medium opacity-75">{row === 'front' ? 'Front Row' : 'Back Row'}</span>
            </button>
            <button
              onClick={() => { setShowDumpOrSwing(false); setShowAttackOptions(true); }}
              className="bg-green-600 hover:bg-green-500 active:bg-green-700 text-white rounded-xl flex flex-col items-center justify-center active:scale-95 transition-all shadow-md border border-green-500 gap-1"
            >
              <Swords className="w-7 h-7" />
              <span className="text-sm font-black uppercase tracking-tight">Swing</span>
              <span className="text-[9px] font-medium opacity-75">Regular Attack</span>
            </button>
          </div>
          <button
            onClick={() => setShowDumpOrSwing(false)}
            className="mt-1.5 py-1.5 bg-slate-700 hover:bg-slate-600 active:bg-slate-800 text-slate-300 rounded-lg text-[10px] font-bold uppercase tracking-widest active:scale-95 transition-all shrink-0"
          >
            Cancel
          </button>
        </div>
      )}
      {showAttackOptions && !overlayType && (
        <div className="absolute inset-0 bg-slate-900/95 z-30 flex flex-col animate-fade-in p-2">
          <div className="text-[10px] text-slate-400 font-bold uppercase mb-1.5 px-1 shrink-0">Attack Result</div>
          <div className="grid grid-cols-2 gap-2 flex-[2]">
            <button
              onClick={() => { setShowAttackOptions(false); handleStatClick('Kill'); }}
              className="bg-green-600 hover:bg-green-500 active:bg-green-700 text-white rounded-xl flex flex-col items-center justify-center active:scale-95 transition-all shadow-md border border-green-500 gap-1"
            >
              <Skull className="w-7 h-7" />
              <span className="text-sm font-black uppercase tracking-tight">Kill</span>
              <span className="text-[9px] font-medium opacity-75">Point scored</span>
            </button>
            <button
              onClick={() => { setShowAttackOptions(false); onRequestErrorOverlay?.(); }}
              className="bg-red-600 hover:bg-red-500 active:bg-red-700 text-white rounded-xl flex flex-col items-center justify-center active:scale-95 transition-all shadow-md border border-red-500 gap-1"
            >
              <XCircle className="w-7 h-7" />
              <span className="text-sm font-black uppercase tracking-tight">Error</span>
              <span className="text-[9px] font-medium opacity-75">Point lost</span>
            </button>
          </div>
          <button
            onClick={() => { setShowAttackOptions(false); handleStatClick('In Play'); }}
            className="mt-2 flex-1 bg-amber-500 hover:bg-amber-400 active:bg-amber-600 text-white rounded-xl flex items-center justify-center active:scale-95 transition-all shadow-md border border-amber-400 gap-2"
          >
            <Hand className="w-5 h-5" />
            <div className="flex flex-col items-start">
              <span className="text-sm font-black uppercase tracking-tight">In Play</span>
              <span className="text-[9px] font-medium opacity-75">Dug / Still Live</span>
            </div>
          </button>
          <button
            onClick={() => setShowAttackOptions(false)}
            className="mt-1.5 py-1.5 bg-slate-700 hover:bg-slate-600 active:bg-slate-800 text-slate-300 rounded-lg text-[10px] font-bold uppercase tracking-widest active:scale-95 transition-all shrink-0"
          >
            Cancel
          </button>
        </div>
      )}
      {showBlockOptions && !overlayType && (
        <div className="absolute inset-0 bg-slate-900/95 z-30 flex flex-col animate-fade-in p-2">
          <div className="text-[10px] text-slate-400 font-bold uppercase mb-2 px-1 shrink-0">Block Type</div>
          <div className="grid grid-cols-2 gap-2 flex-1">
            <button
              onClick={() => { setShowBlockOptions(false); handleStatClick('Block Solo'); }}
              className="bg-indigo-600 hover:bg-indigo-500 active:bg-indigo-700 text-white rounded-xl flex flex-col items-center justify-center active:scale-95 transition-all shadow-md border border-indigo-500 gap-1"
            >
              <Shield className="w-7 h-7" />
              <span className="text-sm font-black uppercase tracking-tight">Solo</span>
              <span className="text-[9px] font-medium opacity-75">1.0 block</span>
            </button>
            <button
              onClick={() => { setShowBlockOptions(false); onBlockAssistStart?.(); }}
              className="bg-purple-600 hover:bg-purple-500 active:bg-purple-700 text-white rounded-xl flex flex-col items-center justify-center active:scale-95 transition-all shadow-md border border-purple-500 gap-1"
            >
              <Users className="w-7 h-7" />
              <span className="text-sm font-black uppercase tracking-tight">Assist</span>
              <span className="text-[9px] font-medium opacity-75">0.5 each</span>
            </button>
          </div>
          <button
            onClick={() => setShowBlockOptions(false)}
            className="mt-2 py-2 bg-slate-700 hover:bg-slate-600 active:bg-slate-800 text-slate-300 rounded-lg text-[10px] font-bold uppercase tracking-widest active:scale-95 transition-all shrink-0"
          >
            Cancel
          </button>
        </div>
      )}
      {overlayType === 'kill' && (
        <div className="absolute inset-0 bg-slate-900/95 z-30 flex flex-col animate-fade-in p-1.5">
          <div className="text-[10px] text-slate-400 font-bold uppercase mb-1 px-1">Kill Detail</div>
          <div className="flex flex-col gap-1.5 flex-1">
            {killOverlayRows.hitType && (
              <div className="flex flex-col gap-1 flex-1">
                <div className="text-[9px] text-green-400 font-bold uppercase px-0.5 leading-none">Hit Type</div>
                <div className="grid grid-cols-3 gap-1 flex-1">
                  {(['Driven', 'Shot', 'Tip'] as const).map(ht => {
                    const key = ht.toLowerCase();
                    const sel = killHitTypeSel === key;
                    return (
                      <button key={ht} type="button" onClick={() => { setKillHitTypeSel(key); onOverlayOptionSelect?.(`hittype:${key}`, 'kill'); }} className={`rounded-lg font-bold text-xs uppercase active:scale-95 transition-all border-2 ${sel ? 'bg-green-500 border-green-400 text-white ring-2 ring-green-400/40 scale-105' : 'bg-green-800 hover:bg-green-700 border-green-700 text-white'}`}>{ht}</button>
                    );
                  })}
                </div>
              </div>
            )}
            {killOverlayRows.outcome && (
              <div className="flex flex-col gap-1 flex-1">
                <div className="text-[9px] text-indigo-400 font-bold uppercase px-0.5 leading-none">Outcome</div>
                <div className="grid grid-cols-3 gap-1 flex-1">
                  {(['Tool', 'Floor', 'Shank'] as const).map(oc => {
                    const key = oc.toLowerCase();
                    const sel = killOutcomeSel === key;
                    return (
                      <button key={oc} type="button" onClick={() => { setKillOutcomeSel(key); onOverlayOptionSelect?.(`outcome:${key}`, 'kill'); }} className={`rounded-lg font-bold text-xs uppercase active:scale-95 transition-all border-2 ${sel ? 'bg-indigo-500 border-indigo-400 text-white ring-2 ring-indigo-400/40 scale-105' : 'bg-indigo-800 hover:bg-indigo-700 border-indigo-700 text-white'}`}>{oc}</button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
          <button type="button" onClick={() => onCancelOverlay?.()} className="mt-1.5 py-1.5 bg-slate-700/60 hover:bg-slate-700/80 active:bg-slate-700 text-slate-400 rounded-lg text-[10px] font-bold uppercase tracking-widest active:scale-95 transition-all shrink-0">Skip</button>
        </div>
      )}
      {overlayType === 'error' && ( <div className="absolute inset-0 bg-red-900/95 z-30 flex flex-col animate-fade-in p-1.5"> <div className="text-[10px] text-red-200 font-bold uppercase mb-1 px-1">Error Type</div> <div className="grid grid-cols-3 gap-1.5 flex-1 overflow-y-auto"> <button onClick={() => onOverlayOptionSelect?.('Hit Out', 'error')} className="bg-red-700 hover:bg-red-600 text-white rounded-lg font-bold text-[10px] uppercase active:scale-95 transition-all">Out</button> <button onClick={() => onOverlayOptionSelect?.('Hit in Net', 'error')} className="bg-red-700 hover:bg-red-600 text-white rounded-lg font-bold text-[10px] uppercase active:scale-95 transition-all">Net Vio</button> <button onClick={() => onOverlayOptionSelect?.('Blocked', 'error')} className="bg-red-700 hover:bg-red-600 text-white rounded-lg font-bold text-[10px] uppercase active:scale-95 transition-all">Blocked</button> <button onClick={() => onOverlayOptionSelect?.('Lift', 'error')} className="bg-red-700 hover:bg-red-600 text-white rounded-lg font-bold text-[10px] uppercase active:scale-95 transition-all">Lift</button> <button onClick={() => onOverlayOptionSelect?.('Double', 'error')} className="bg-red-700 hover:bg-red-600 text-white rounded-lg font-bold text-[10px] uppercase active:scale-95 transition-all">Double</button> <button onClick={() => onOverlayOptionSelect?.('Under', 'error')} className="bg-red-700 hover:bg-red-600 text-white rounded-lg font-bold text-[10px] uppercase active:scale-95 transition-all">Under</button> <button onClick={() => onOverlayOptionSelect?.('Other', 'error')} className="bg-red-800 hover:bg-red-700 text-white rounded-lg font-bold text-[10px] uppercase active:scale-95 transition-all col-span-3">Other</button> </div> <button onClick={() => onCancelOverlay?.()} className="mt-1.5 py-1.5 bg-red-950/60 hover:bg-red-950/80 active:bg-red-950 text-red-200 rounded-lg text-[10px] font-bold uppercase tracking-widest active:scale-95 transition-all shrink-0">Cancel</button> </div> )}
      {srDetail !== null && (
        <div className={`absolute top-10 inset-x-0 bottom-0 bg-slate-900 z-30 flex flex-col transition-opacity duration-[400ms] ${srDetail.fading ? 'opacity-0 pointer-events-none' : 'opacity-100 animate-fade-in'}`}>
          <button onClick={dismissSRDetail} className="absolute top-1 right-1 z-10 text-slate-400 hover:text-white p-0.5 transition-colors"><X className="w-3 h-3" /></button>

          {/* ── Serve Type (top half) ── */}
          <div className="flex-1 flex flex-col justify-center gap-1 px-2 py-1.5 bg-slate-800 border-b-2 border-slate-700">
            <span className="text-[8px] font-black uppercase tracking-widest text-amber-400 leading-none">Serve Type</span>
            <div className="flex gap-1.5">
              <button
                onClick={() => { const ns: 'topspin' | null = srDetail.serveStyle === 'topspin' ? null : 'topspin'; setSrDetail(prev => prev ? { ...prev, serveStyle: ns } : null); onSRDetailCapture?.(ns, srDetail.contactType); if (ns && srDetail.contactType) dismissSRDetail(); }}
                className={`flex-1 flex items-center justify-center gap-1 py-1.5 rounded-lg border-2 font-black text-[10px] uppercase tracking-tight transition-all active:scale-95 ${srDetail.serveStyle === 'topspin' ? 'bg-amber-500 border-amber-400 text-white ring-2 ring-amber-400/40' : 'bg-slate-700 border-slate-600 text-slate-300 hover:border-amber-500/50 hover:text-amber-200'}`}
              >
                <RotateCw className="w-3 h-3 shrink-0" /> Topspin
              </button>
              <button
                onClick={() => { const ns: 'float' | null = srDetail.serveStyle === 'float' ? null : 'float'; setSrDetail(prev => prev ? { ...prev, serveStyle: ns } : null); onSRDetailCapture?.(ns, srDetail.contactType); if (ns && srDetail.contactType) dismissSRDetail(); }}
                className={`flex-1 flex items-center justify-center gap-1 py-1.5 rounded-lg border-2 font-black text-[10px] uppercase tracking-tight transition-all active:scale-95 ${srDetail.serveStyle === 'float' ? 'bg-amber-500 border-amber-400 text-white ring-2 ring-amber-400/40' : 'bg-slate-700 border-slate-600 text-slate-300 hover:border-amber-500/50 hover:text-amber-200'}`}
              >
                <Minus className="w-3 h-3 shrink-0" /> Float
              </button>
            </div>
          </div>

          {/* ── Contact Type (bottom half) ── */}
          <div className="flex-1 flex flex-col justify-center gap-1 px-2 py-1.5 bg-slate-900">
            <span className="text-[8px] font-black uppercase tracking-widest text-teal-400 leading-none">Contact</span>
            <div className="flex gap-1.5">
              <button
                onClick={() => { const nc: 'hands' | null = srDetail.contactType === 'hands' ? null : 'hands'; setSrDetail(prev => prev ? { ...prev, contactType: nc } : null); onSRDetailCapture?.(srDetail.serveStyle, nc); if (nc && srDetail.serveStyle) dismissSRDetail(); }}
                className={`flex-1 flex items-center justify-center gap-1 py-1.5 rounded-lg border-2 font-black text-[10px] uppercase tracking-tight transition-all active:scale-95 ${srDetail.contactType === 'hands' ? 'bg-teal-500 border-teal-400 text-white ring-2 ring-teal-400/40' : 'bg-slate-700 border-slate-600 text-slate-300 hover:border-teal-500/50 hover:text-teal-200'}`}
              >
                <Hand className="w-3 h-3 shrink-0" /> Hands
              </button>
              <button
                onClick={() => { const nc: 'platform' | null = srDetail.contactType === 'platform' ? null : 'platform'; setSrDetail(prev => prev ? { ...prev, contactType: nc } : null); onSRDetailCapture?.(srDetail.serveStyle, nc); if (nc && srDetail.serveStyle) dismissSRDetail(); }}
                className={`flex-1 flex items-center justify-center gap-1 py-1.5 rounded-lg border-2 font-black text-[10px] uppercase tracking-tight transition-all active:scale-95 ${srDetail.contactType === 'platform' ? 'bg-teal-500 border-teal-400 text-white ring-2 ring-teal-400/40' : 'bg-slate-700 border-slate-600 text-slate-300 hover:border-teal-500/50 hover:text-teal-200'}`}
              >
                <HelpingHand className="w-3 h-3 shrink-0" /> Platform
              </button>
            </div>
          </div>
        </div>
      )}
      <div className={`flex items-center px-1.5 py-1 border-b h-10 shrink-0 gap-1 ${isLiberoActive ? 'bg-black/10 border-white/10' : 'border-black/5 bg-black/5'}`}>
         {!sidelineMode && (
           <div className="flex items-center gap-1 shrink-0 cursor-pointer hover:opacity-80 transition-opacity" onClick={(e) => { if (playerId && onEditPlayer) { e.stopPropagation(); onEditPlayer(actualPlayerId); } }}>
              {playerDisplay?.photoUrl ? ( <div className="w-9 h-9 rounded-full border border-white shadow-sm overflow-hidden bg-slate-200"> <img src={playerDisplay.photoUrl} alt="Player" className="w-full h-full object-cover" /> </div> ) : ( playerDisplay && ( <div className={`w-9 h-9 rounded-full border border-white shadow-sm flex items-center justify-center text-sm font-bold ${isUs ? 'bg-white text-slate-700' : 'bg-red-100 text-red-800'}`}> {playerDisplay.number || playerDisplay.name.charAt(0)} </div> ) )}
              <span className={`text-lg font-black leading-none ${textColorClass} min-w-[1.5rem] text-center`}> {playerDisplay ? playerDisplay.number : ''} </span>
           </div>
         )}
         <div className="flex-1 min-w-0 flex items-center gap-1.5 overflow-hidden"> {playerDisplay ? ( <> <div className="flex items-baseline gap-1 min-w-0 overflow-hidden"> <span className={`text-[17px] font-bold leading-none shrink-0 ${textColorClass}`}>{playerDisplay.name}</span> <span className={`text-[11px] font-semibold opacity-50 truncate ${textColorClass}`}>{displayPosition}</span> </div> {inlineStats && inlineStats.filter(s => s.value > 0).map(s => ( <span key={s.label} className={`shrink-0 text-[10px] font-black px-1 py-0.5 rounded ${s.color}`}>{s.value}{s.label}</span> ))} </> ) : ( <span className="text-[11px] italic text-slate-400">Empty</span> )} </div>
         <div className="flex items-center gap-1 shrink-0">
            {onLiberoToggle && (
              <button
                onClick={(e) => { e.stopPropagation(); onLiberoToggle(); }}
                className={`px-3 py-1 rounded transition-colors ${
                  isLiberoActive
                    ? 'bg-purple-700 text-white hover:bg-purple-600'
                    : 'bg-purple-600 text-white hover:bg-purple-500'
                }`}
                title={isLiberoActive ? 'Sub libero out' : 'Sub libero in'}
              >
                <ArrowLeftRight className="w-3 h-3" />
              </button>
            )}
            {playerDisplay && onRemovePlayer && ( <button onClick={(e) => { e.stopPropagation(); onRemovePlayer(); }} className={`p-1 rounded transition-colors ${isLiberoActive ? 'bg-white/20 text-white hover:bg-red-400/60' : 'bg-slate-200 hover:bg-red-100 text-slate-400 hover:text-red-500'}`} title="Remove from position"> <X className="w-3 h-3" /> </button> )}
            {playerDisplay && onSub && !isLiberoActive && !onRemovePlayer && ( <button onClick={(e) => { e.stopPropagation(); onSub(); }} className={`p-1 rounded bg-slate-200 hover:bg-blue-100 transition-colors ${subIconColor}`} title="Substitute"> <Repeat className="w-3 h-3" /> </button> )}
            <span className={`text-[9px] font-black px-1.5 py-0.5 rounded text-white min-w-[1.5rem] text-center ${isLiberoActive ? (isUs ? 'bg-purple-500' : 'bg-purple-800') : (isDualRoleFrontRow || isBackRowDualSetter) ? 'bg-amber-600' : (isUs ? 'bg-slate-700' : 'bg-red-800')}`}> {playerDisplay ? (isLiberoActive ? 'L' : getPosAbbreviation(displayPosition)) : ''} </span>
         </div>
      </div>
      <div className="flex-1 flex flex-col relative overflow-hidden">
        {/* Quick stat assignment flash overlay */}
        {isFlashing && flashBgClass && (
          <div className={`absolute inset-0 ${flashBgClass} opacity-70 pointer-events-none z-20`} />
        )}
        {isLineupEditMode ? (
          <button
            onClick={() => onSub?.()}
            className={`absolute inset-0 w-full h-full flex items-center py-2 px-1 transition-opacity active:opacity-80 ${isLiberoActive ? 'hover:brightness-110' : 'hover:bg-slate-50'}`}
          >
            {/* Left col: Roman numeral */}
            <div className="flex-1 flex items-center justify-center">
              {posRoman && <span className={`text-4xl font-black select-none ${isLiberoActive ? 'text-white/40' : 'text-slate-300'}`}>{posRoman}</span>}
            </div>

            {/* Center: player circle or empty state */}
            {playerDisplay ? (
              playerDisplay.photoUrl ? (
                <div className="h-full aspect-square rounded-full overflow-hidden border-2 border-slate-200 shadow-md shrink-0">
                  <img src={playerDisplay.photoUrl} alt="" className="w-full h-full object-cover" />
                </div>
              ) : (
                <div className={`h-full aspect-square rounded-full flex items-center justify-center border-2 shadow-md shrink-0 ${isLiberoActive ? 'bg-white/20 text-white border-white/30' : 'bg-slate-100 text-slate-700 border-slate-200'}`}>
                  <span className="text-4xl font-black select-none leading-none">{playerDisplay.number || playerDisplay.name.charAt(0)}</span>
                </div>
              )
            ) : (
              <div className="flex flex-col items-center gap-1 opacity-40 shrink-0">
                <UserPlus className={`w-7 h-7 ${isUs ? 'text-slate-400' : 'text-red-300'}`} />
                <span className={`text-[10px] font-bold uppercase tracking-wide ${isUs ? 'text-slate-400' : 'text-red-300'}`}>+ Assign</span>
              </div>
            )}

            {/* Right col: season stats */}
            <div className="flex-1 flex flex-col items-center justify-center min-w-0 gap-0.5">
              {editStats && editStats.length > 0 && (
                <div className="w-full flex flex-col items-center">
                  {/* Column headers */}
                  <div className="flex items-center gap-1.5 mb-0.5">
                    <div className={`w-7 shrink-0 text-right text-[7px] font-black uppercase tracking-wider opacity-40 ${textColorClass}`}></div>
                    <div className={`w-8 shrink-0 text-right text-[7px] font-black uppercase tracking-wider opacity-40 ${textColorClass}`}>Stat</div>
                    <div className={`w-7 shrink-0 text-center text-[7px] font-black uppercase tracking-wider opacity-40 ${textColorClass}`}>Rk</div>
                  </div>
                  {/* Stat rows */}
                  {editStats.map(s => (
                    <div key={s.label} className="flex items-center gap-1.5">
                      <span className={`w-7 shrink-0 text-right text-[8px] font-semibold opacity-50 ${textColorClass}`}>{s.label}</span>
                      <span className={`w-8 shrink-0 text-right text-[13px] font-black leading-none ${s.value === '—' ? 'opacity-25' : ''} ${textColorClass}`}>{s.value}</span>
                      <div className={`w-7 shrink-0 text-center text-[13px] font-black leading-none ${s.rank === 1 ? 'text-amber-400' : `opacity-35 ${textColorClass}`}`}>
                        {s.rank != null ? `#${s.rank}` : <span className="opacity-25">—</span>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </button>
        ) : isAssignmentMode ? (
          /* Assignment mode: same visual as edit mode, tap to assign the pending stat */
          <button
            onClick={() => actualPlayerId ? onAssign?.() : undefined}
            disabled={!actualPlayerId}
            className={`absolute inset-0 w-full h-full flex items-center justify-center p-2 transition-opacity ${actualPlayerId ? 'active:opacity-80 hover:bg-blue-50' : 'cursor-default'}`}
          >
            {playerDisplay ? (
              playerDisplay.photoUrl ? (
                <div className="h-full aspect-square max-w-full rounded-full overflow-hidden border-2 border-blue-300 shadow-md shrink-0">
                  <img src={playerDisplay.photoUrl} alt="" className="w-full h-full object-cover" />
                </div>
              ) : (
                <div className={`h-full aspect-square max-w-full rounded-full flex items-center justify-center border-2 shadow-md shrink-0 ${isLiberoActive ? 'bg-white/20 text-white border-blue-400' : 'bg-slate-100 text-slate-700 border-blue-300'}`}>
                  <span className="text-4xl font-black select-none leading-none">{playerDisplay.number || playerDisplay.name.charAt(0)}</span>
                </div>
              )
            ) : (
              <div className="flex flex-col items-center gap-1 opacity-25">
                <UserPlus className="w-7 h-7 text-slate-300" />
                <span className="text-[10px] font-bold uppercase tracking-wide text-slate-300">empty</span>
              </div>
            )}
          </button>
        ) : (
          <div className="flex-1 p-1 flex flex-row relative gap-1">
            {sidelineMode && playerDisplay && (
              <div
                className="shrink-0 flex items-center justify-center self-stretch cursor-pointer hover:opacity-80 transition-opacity"
                style={{ width: '25%' }}
                onClick={(e) => { if (playerId && onEditPlayer) { e.stopPropagation(); onEditPlayer(actualPlayerId); } }}
              >
                {playerDisplay.photoUrl ? (
                  <div className="aspect-square max-h-full max-w-full rounded-full overflow-hidden border-2 border-white shadow-md">
                    <img src={playerDisplay.photoUrl} alt="Player" className="w-full h-full object-cover" />
                  </div>
                ) : (
                  <div className={`aspect-square max-h-full max-w-full rounded-full flex items-center justify-center border-2 shadow-md ${isLiberoActive ? 'bg-white/20 text-white border-white/40' : (isUs ? 'bg-white text-slate-700 border-slate-200' : 'bg-red-100 text-red-800 border-red-200')}`}>
                    <span className="stat-num sm">{playerDisplay.number || playerDisplay.name.charAt(0)}</span>
                  </div>
                )}
              </div>
            )}
            <div className="flex-1 flex flex-col relative min-w-0">
              {isServer && !isRallyInProgress && !suggestAssist ? ( renderServingView() ) : playerId ? ( renderTiles() ) : ( <button onClick={() => onAddPlayer?.()} className="absolute inset-0 w-full h-full z-10 cursor-pointer" /> )}
              {!playerId && ( <div className={`w-full h-full flex flex-col items-center justify-center pointer-events-none ${isUs ? 'text-slate-300' : 'text-red-200'}`}> <div className="text-4xl font-black mb-1 opacity-20">{label}</div> <UserPlus className="w-6 h-6 mb-1 opacity-40" /> <span className="text-[10px] font-bold uppercase tracking-wide opacity-40">+ Assign</span> </div> )}
            </div>
          </div>
        )}
      </div>
      {!playerId && ( <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-[0.03] z-0"> <span className="text-4xl font-black uppercase transform -rotate-12">{label}</span> </div> )}
    </div>
  );
};

const GAMETIME_STORAGE_KEY = 'sideoutiq_gametime_state_v2';
const OFFLINE_SAVE_KEY = 'sideoutiq_offline_save_queue';

// Maps each position to its mirror position exactly 3 rotations away.
// Back-row spots (1,5,6) mirror their front-row counterparts (4,2,3).
const OPPOSITE_POSITIONS: Record<string, string> = {
  'p1': 'p4', 'p4': 'p1',
  'p6': 'p3', 'p3': 'p6',
  'p5': 'p2', 'p2': 'p5',
  'op1': 'op4', 'op4': 'op1',
  'op6': 'op3', 'op3': 'op6',
  'op5': 'op2', 'op2': 'op5',
};

const GameTime: React.FC<GameTimeProps> = ({
  organizationId, currentUser, players, teams, events, lineups = [], matchRecords = [], setPlayers, setLineups, setEvents, setTeams, setMatchRecords, changeView, triggerTeamCreation,
  appSettings = { substitutionsPerSet: 12, measurementSystem: 'imperial' as const, allowTwoLiberos: false, simpleStats: false, advancedStatToggles: {}, defaultOpponentServesFloat: false }, setAppSettings, initialMatchId, initialActiveTeamId, onActiveTeamChange
}) => {
  // Compute effective advanced stat toggles — when simpleStats is on, all are off
  const advToggles: AdvancedStatToggles = appSettings.simpleStats
    ? { serveTypeTracking: false, killHitTypeRow: false, killOutcomeRow: false, errorDetailOverlay: false, serveReceiveDetail: false }
    : { ...DEFAULT_ADVANCED_STAT_TOGGLES, ...appSettings.advancedStatToggles };

  // Load persisted game state once on mount (lazy initializer — runs only on first render)
  const [_savedGame] = useState<any>(() => { try { const raw = localStorage.getItem(GAMETIME_STORAGE_KEY); return raw ? JSON.parse(raw) : null; } catch { return null; } });

  // Discard a stale saved session when it belongs to a different event or a different team.
  const _activeSavedGame = (_savedGame && (
    (initialMatchId && _savedGame.selectedMatchId !== initialMatchId) ||
    (initialActiveTeamId && _savedGame.activeTeamId && _savedGame.activeTeamId !== initialActiveTeamId)
  )) ? null : _savedGame;

  const [activeTeamId, setActiveTeamId] = useState<string>('');
  // Never skip init — always validate the saved team ID against the current teams list.
  // A saved ID can be stale (org switch, deleted team, etc.) and would silently produce an
  // empty lineup list if accepted without checking.
  const [hasInitializedTeam, setHasInitializedTeam] = useState(false);
  const [selectedMatchId, setSelectedMatchId] = useState<string>(_activeSavedGame?.selectedMatchId || 'unselected');
  const [showStickyScoreboard, setShowStickyScoreboard] = useState(false);
  const mainScoreboardRef = useRef<HTMLDivElement>(null);
  const assistPromptTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingKillSideRef = useRef<'us' | 'them' | null>(null);
  const killOverlayTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingKillTimestampRef = useRef<number | null>(null);
  const pendingKillSelectionsRef = useRef<{ hitType?: string; outcome?: string }>({});
  const pendingDumpRowRef = useRef<'front' | 'back' | null>(null);
  const [activeOverlay, setActiveOverlay] = useState<{ side: 'us' | 'them', playerId: string, type: 'kill' | 'error' | 'sr' } | null>(null);
  const [editingPlayerId, setEditingPlayerId] = useState<string | null>(null);
  const [lastSavedLineup, setLastSavedLineup] = useState(initialGameState.lineup);
  const [timeouts, setTimeouts] = useState(_activeSavedGame?.timeouts || { us: 2, them: 2 });
  // ── 2026 NFHS/NCAA Libero State ──────────────────────────────────────────────
  // Designated libero player IDs per side, up to 2 (L1 at index 0, L2 at index 1)
  const [liberoDesignations, setLiberoDesignations] = useState<{ us: string[], them: string[] }>(() => {
    if (_activeSavedGame?.liberoDesignations) return _activeSavedGame.liberoDesignations;
    // Migrate legacy format
    const legacy = _activeSavedGame;
    if (!legacy) return { us: [], them: [] };
    return {
      us: legacy.liberoId?.us ? [legacy.liberoId.us] : [],
      them: legacy.liberoId?.them ? [legacy.liberoId.them] : [],
    };
  });

  // Sandwich tracking: liberoPlayerId → [playerIdA, playerIdB] the two players they replace.
  // Both players are replaced automatically whenever they rotate to the back row.
  const [liberoSandwiches, setLiberoSandwiches] = useState<Record<string, string[]>>(() => {
    if (_activeSavedGame?.liberoSandwiches) {
      // Migrate: old format stored a single string; new format stores string[]
      const raw = _activeSavedGame.liberoSandwiches;
      const migrated: Record<string, string[]> = {};
      for (const [k, v] of Object.entries(raw)) {
        migrated[k] = Array.isArray(v) ? (v as string[]) : [v as string];
      }
      return migrated;
    }
    // Migrate legacy format (pre-v2)
    const legacy = _activeSavedGame;
    if (!legacy) return {};
    const out: Record<string, string[]> = {};
    if (legacy.liberoId?.us && legacy.liberoServingFor?.us)
      out[legacy.liberoId.us] = [legacy.liberoServingFor.us];
    if (legacy.liberoId?.them && legacy.liberoServingFor?.them)
      out[legacy.liberoId.them] = [legacy.liberoServingFor.them];
    return out;
  });

  // Serving lock: liberoPlayerId → player ID of the player they serve for.
  // If set, the libero ONLY enters the serving position when that specific player's ID is at p1.
  // Reset each set via handleClearGame.
  const [liberoServingLocks, setLiberoServingLocks] = useState<Record<string, string>>(
    _activeSavedGame?.liberoServingLocks || {}
  );

  // Manual swap state: liberoId → false means libero is manually benched (sandwich player stays on
  // court even in back row). Undefined/true = default auto-swap behavior. Reset each set.
  const [liberoOnCourt, setLiberoOnCourt] = useState<Record<string, boolean>>(
    _activeSavedGame?.liberoOnCourt || {}
  );

  // When two liberos are designated, this tracks which slot (0=L1, 1=L2) is the "active" libero
  // on court. Only one may be active at a time (mutual exclusivity rule). Reset on designation clear.
  const [activeLiberoSlot, setActiveLiberoSlot] = useState<0 | 1>(
    _activeSavedGame?.activeLiberoSlot ?? 0
  );

  // Libero config modal: opened after designating a libero (or tapping a libero chip) to choose
  // which 2 players they replace and which one (if any) they serve for.
  const [liberoConfigModal, setLiberoConfigModal] = useState<{
    side: 'us' | 'them';
    liberoId: string;
    player1Id: string;   // First designated MB/player
    player2Id: string;   // Second designated MB/player (opposite rotation)
    servesForPlayerId: string; // '' = doesn't serve, or player1Id / player2Id
  } | null>(null);

  // Libero designation picker (choose which player(s) are L1 / L2)
  const [liberoPickerModal, setLiberoPickerModal] = useState<{
    side: 'us' | 'them';
    slot: 0 | 1;  // 0 = L1, 1 = L2
  } | null>(null);
  const [isOpponentExpanded, setIsOpponentExpanded] = useState(_activeSavedGame?.isOpponentExpanded || false);
  const [activeLineupId, setActiveLineupId] = useState<string | null>(null);
  const [liberoMismatchModal, setLiberoMismatchModal] = useState<{
    lineupId: string;
    lineupCount: number; // how many liberos the lineup has
    gameAllowsTwo: boolean; // current app setting
  } | null>(null);

  // ── Court Orientation ─────────────────────────────────────────────────────
  // 'standard' = net on bottom, 'net-top' = net on top, 'net-right' / 'net-left' = sideline views
  type CourtOrientation = 'standard' | 'net-top' | 'net-right' | 'net-left';
  const [courtOrientation, setCourtOrientation] = useState<CourtOrientation>(() => {
    try { return (localStorage.getItem('sideoutiq_court_orientation') as CourtOrientation) || 'net-right'; } catch { return 'net-right'; }
  });
  const [netFlipConfirm, setNetFlipConfirm] = useState(false);
  const [showOrientationOnboarding, setShowOrientationOnboarding] = useState<boolean>(() => {
    try { return !localStorage.getItem('sideoutiq_gt_orient_onboarded'); } catch { return false; }
  });
  const [showConfigStatsModal, setShowConfigStatsModal] = useState(false);
  const handleCourtOrientationChange = useCallback((val: CourtOrientation) => {
    setCourtOrientation(val);
    try { localStorage.setItem('sideoutiq_court_orientation', val); } catch {}
  }, []);
  const handleOrientationOnboardingSelect = useCallback((val: CourtOrientation) => {
    setCourtOrientation(val);
    try {
      localStorage.setItem('sideoutiq_court_orientation', val);
      localStorage.setItem('sideoutiq_gt_orient_onboarded', '1');
    } catch {}
    setShowOrientationOnboarding(false);
  }, []);

  // ── Court orientation grid helpers ────────────────────────────────────────
  const COURT_POSITIONS: Record<CourtOrientation, string[]> = {
    // Standard: 3 cols × 2 rows — net on bottom
    //   p1(RB) p6(MB) p5(LB)   ← back row
    //   p2(RF) p3(MF) p4(LF)   ← front row (net side)
    standard:  ['p1', 'p6', 'p5', 'p2', 'p3', 'p4'],
    // Net-top: 3 cols × 2 rows — net on top
    //   p4(LF) p3(MF) p2(RF)   ← front row (net side)
    //   p5(LB) p6(MB) p1(RB)   ← back row
    'net-top': ['p4', 'p3', 'p2', 'p5', 'p6', 'p1'],
    // Net-right: 2 cols × 3 rows — net on right
    //   p5(LB) p4(LF)   ← left column=back, right=front(net)
    //   p6(MB) p3(MF)
    //   p1(RB) p2(RF)
    'net-right': ['p5', 'p4', 'p6', 'p3', 'p1', 'p2'],
    // Net-left: 2 cols × 3 rows — net on left
    //   p2(RF) p1(RB)   ← left=front(net), right=back
    //   p3(MF) p6(MB)
    //   p4(LF) p5(LB)
    'net-left':  ['p2', 'p1', 'p3', 'p6', 'p4', 'p5'],
  };
  const OPP_COURT_POSITIONS: Record<CourtOrientation, string[]> = {
    standard:    ['op4', 'op3', 'op2', 'op5', 'op6', 'op1'],
    'net-top':   ['op1', 'op6', 'op5', 'op2', 'op3', 'op4'],
    'net-right': ['op2', 'op1', 'op3', 'op6', 'op4', 'op5'],
    'net-left':  ['op5', 'op4', 'op6', 'op3', 'op1', 'op2'],
  };
  const isSideline = courtOrientation === 'net-left' || courtOrientation === 'net-right';
  const courtGridClass = isSideline
    ? 'grid grid-cols-2 grid-rows-3 gap-px bg-slate-200'
    : 'grid grid-cols-3 grid-rows-2 gap-px bg-slate-200';

  // ── AI Coaching Suggestions (Premium) ─────────────────────────────────────
  const [aiSuggestions, setAiSuggestions] = useState<GameSuggestion[]>([]);
  const [isLoadingAI, setIsLoadingAI] = useState(false);
  const [lastAIStatCount, setLastAIStatCount] = useState(0);

  // ── Set-End Tactical Insights (Dean-only) ──────────────────────────────────
  const canSeeSetInsights = isDevUser(currentUser?.email);
  const [setInsights, setSetInsights] = useState<SetInsightsTip[]>([]);
  const [isLoadingSetInsights, setIsLoadingSetInsights] = useState(false);
  const [showSetInsightsPanel, setShowSetInsightsPanel] = useState(false);
  const [activeOpponentLineupId, setActiveOpponentLineupId] = useState<string | null>(_activeSavedGame?.activeOpponentLineupId || null);

  useEffect(() => {
    if (!teams.length) return;
    // If the parent has a valid active team, always use it (handles late resolution of globalActiveTeamId)
    if (initialActiveTeamId && teams.some(t => t.id === initialActiveTeamId)) {
      setActiveTeamId(initialActiveTeamId);
      setHasInitializedTeam(true);
    } else if (!hasInitializedTeam) {
      // Fallback only runs once, before the parent's active team is known
      const clubTeams = teams.filter(t => t.type === 'club' || !t.type);
      setActiveTeamId(clubTeams.length > 0 ? clubTeams[0].id : teams[0].id);
      setHasInitializedTeam(true);
    }
  }, [teams, hasInitializedTeam, initialActiveTeamId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => setShowStickyScoreboard(!entry.isIntersecting && entry.boundingClientRect.top < 0), { threshold: 0 });
    if (mainScoreboardRef.current) observer.observe(mainScoreboardRef.current);
    return () => { if (mainScoreboardRef.current) observer.unobserve(mainScoreboardRef.current); };
  }, []);

  // Drain offline save queue when connection is restored
  useEffect(() => {
    const handleOnline = async () => {
      const raw = localStorage.getItem(OFFLINE_SAVE_KEY);
      if (!raw) return;
      try {
        const payload = JSON.parse(raw);
        await submitMatchRecord(payload.orgId, payload.eventId, payload.userId, payload.userName, payload.gameState, payload.teamId);
        localStorage.removeItem(OFFLINE_SAVE_KEY);
        setPendingOfflineSave(null);
        setSyncSuccessBanner(true);
        setTimeout(() => setSyncSuccessBanner(false), 5000);
      } catch (e) {
        console.error('[offline-sync] retry failed:', e);
      }
    };
    window.addEventListener('online', handleOnline);
    return () => window.removeEventListener('online', handleOnline);
  }, []);

  const [gameState, setGameState] = useState<GameState>(_activeSavedGame?.gameState ? { ...initialGameState, ..._activeSavedGame.gameState } : initialGameState);
  const [possession, setPossession] = useState<'serving' | 'receiving'>(_activeSavedGame?.possession || 'serving');
  // Preselected incoming serve type shown above the player grid during serve receive
  const [incomingServeType, setIncomingServeType] = useState<'topspin' | 'float' | null>(null);
  // Who served first in Set 1 — used to auto-flip possession each set
  const [serveFirstSelection, setServeFirstSelection] = useState<'us' | 'them' | null>(_activeSavedGame?.serveFirstSelection || null);
  const [showBackRotatePrompt, setShowBackRotatePrompt] = useState(false);
  // Persisted preference: auto-apply back-rotate when receiving
  const [backRotatePreference, setBackRotatePreference] = useState<boolean>(() => {
    try { return localStorage.getItem('siq_backrotate') === 'true'; } catch { return false; }
  });
  const [rallyInProgress, setRallyInProgress] = useState(false);
  const [showClearConfirm, setShowClearConfirm] = useState<null | 'choose' | 'set' | 'match'>(null);
  const [showNewSetConfirm, setShowNewSetConfirm] = useState(false);
  const [showEndMatchConfirm, setShowEndMatchConfirm] = useState(false);
  const [showNoLiberoWarning, setShowNoLiberoWarning] = useState(false);
  const [liberoRedesignAlert, setLiberoRedesignAlert] = useState<{
    liberoIds: string[];
    side: 'us' | 'them';
    suggestedSandwiches: Record<string, string[]>; // liberoId → suggested replacement players
  } | null>(null);
  const [showLineupScanner, setShowLineupScanner] = useState(false);
  // True at the start of each set (before any points) — replaced stat tiles with large player info / swap UI
  const [isLineupEditMode, setIsLineupEditMode] = useState<boolean>(() => {
    if (_activeSavedGame?.gameState) {
      const gs = _activeSavedGame.gameState;
      if (gs.ourScore > 0 || gs.theirScore > 0 || (gs.stats?.length || 0) > 0) return false;
    }
    return true;
  });
  const [showSubModal, setShowSubModal] = useState(false);
  // Rotational substitutions
  const [rotationalSubs, setRotationalSubs] = useState<RotationalSubstitution[]>(_activeSavedGame?.rotationalSubs || []);
  const [pendingSubConfirms, setPendingSubConfirms] = useState<RotationalSubstitution[]>([]);
  const [showRotSubManager, setShowRotSubManager] = useState(false);
  const [editingRotSub, setEditingRotSub] = useState<Partial<RotationalSubstitution> | null>(null);
  const [isSuggestedRotSub, setIsSuggestedRotSub] = useState(false);
  const lastServeTypeRef = useRef<'jump' | 'float' | null>(null);
  const [serveTypeByPlayer, setServeTypeByPlayer] = useState<Record<string, 'Jump' | 'Float'>>({});
  // Remembers incoming serve type per opponent rotation (keyed by op1 numeral) within a set
  const serveTypeByOpponentRotationRef = useRef<Record<string, 'topspin' | 'float'>>({});
  const lastReceptionTimestampRef = useRef<number>(0);
  const rotSubTriggerCountsRef = useRef<Record<string, number>>({});
  const rotationalSubsRef = useRef<RotationalSubstitution[]>([]);
  const prevLineupRef = useRef<typeof initialGameState.lineup | null>(null);
  const prevPossessionRef = useRef<'serving' | 'receiving'>('serving');
  const handleLiberoCardTapRef = useRef<((side: 'us' | 'them', liberoId: string) => void) | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [saveProgressFill, setSaveProgressFill] = useState(0);
  const [submitSuccessBanner, setSubmitSuccessBanner] = useState(false);
  const [submitErrorBanner, setSubmitErrorBanner] = useState(false);
  const [offlineSaveBanner, setOfflineSaveBanner] = useState(false);
  const [syncSuccessBanner, setSyncSuccessBanner] = useState(false);
  const [pendingOfflineSave, setPendingOfflineSave] = useState<{
    orgId: string; eventId: string; userId: string; userName: string;
    gameState: GameState; teamId?: string;
  } | null>(() => {
    try { const raw = localStorage.getItem(OFFLINE_SAVE_KEY); return raw ? JSON.parse(raw) : null; } catch { return null; }
  });
  const [showSavedGameWarning, setShowSavedGameWarning] = useState(false);
  const [upcomingGameForSwitch, setUpcomingGameForSwitch] = useState<GameEvent | null>(null);
  const hasCheckedSavedGameRef = useRef(false);
  const [subContext, setSubContext] = useState<{ side: 'us' | 'them', pos: string, currentPlayerId: string } | null>(null);
  const [assistPromptState, setAssistPromptState] = useState<{ side: 'us' | 'them', killerId: string, eligibleIds: string[] } | null>(null);
  const [blockAssistState, setBlockAssistState] = useState<{ side: 'us' | 'them', blockerId: string, selectedTeammateIds: string[] } | null>(null);
  const [opponentRoster, setOpponentRoster] = useState<(SimplePlayer | Player)[]>(_activeSavedGame?.opponentRoster || []);
  const [opponentTeamId, setOpponentTeamId] = useState<string | null>(_activeSavedGame?.opponentTeamId || null);
  const [isAddingOpponent, setIsAddingOpponent] = useState(false);
  const [newOpponentName, setNewOpponentName] = useState(''); // Added for inline creation
  const [isQuickCreating, setIsQuickCreating] = useState(false);
  const [quickCreateSide, setQuickCreateSide] = useState<'us' | 'them'>('us');
  const [quickCreateTargetPos, setQuickCreateTargetPos] = useState<string>('');
  const [newPlayerForm, setNewPlayerForm] = useState({ name: '', number: '', position: 'Unknown' });
  const [isSavingLineup, setIsSavingLineup] = useState(false);
  const [isSavingOpponentLineup, setIsSavingOpponentLineup] = useState(false);
  const [lineupName, setLineupName] = useState('');
  const [isCreatingOurLineup, setIsCreatingOurLineup] = useState(false);
  const [newOurLineupName, setNewOurLineupName] = useState('');
  // New lineup builder flow
  const [newLineupFlow, setNewLineupFlow] = useState<'chooser' | 'quick-build' | 'manual' | null>(null);
  const [newLineupDraft, setNewLineupDraft] = useState<{
    name: string;
    positions: Record<string, string>;
    liberoConfig: LiberoAssignment[];
    liberoCount: 1 | 2;
  } | null>(null);
  const [isSavingNewLineup, setIsSavingNewLineup] = useState(false);
  const [newLineupScanMode, setNewLineupScanMode] = useState(false);
  const [isSavingOurLineup, setIsSavingOurLineup] = useState(false);
  const [isSavingActiveLineup, setIsSavingActiveLineup] = useState(false);
  // No snapshot needed — dirty detection compares directly against saved lineup data
  // Quick Stat Bar state
  const [showStatBar, setShowStatBar] = useState(true);
  const [showStatModeInfo, setShowStatModeInfo] = useState(false);
  const [pendingStat, setPendingStat] = useState<{ statType: StatRecord['statType']; timestamp: number; flashBg: string; multiAssign?: boolean } | null>(null);
  const [statBarFlash, setStatBarFlash] = useState<string | null>(null);
  const [statBarSubTypePicker, setStatBarSubTypePicker] = useState<{ kind: 'block' | 'error' } | null>(null);
  const [statBarErrorPicker, setStatBarErrorPicker] = useState(false);
  const [statBarSRPicker, setStatBarSRPicker] = useState(false);
  const [multiAssignedIds, setMultiAssignedIds] = useState<string[]>([]);
  const [assignmentFlash, setAssignmentFlash] = useState<{ playerId: string; flashBg: string } | null>(null);
  const [undoStack, setUndoStack] = useState<Array<{ gameState: GameState; possession: 'serving' | 'receiving'; liberoOnCourt: Record<string, boolean>; activeLiberoSlot: 0 | 1 }>>([]);
  const [showUndoPanel, setShowUndoPanel] = useState(false);
  const [statBarSRDetail, setStatBarSRDetail] = useState<{ timestamp: number; serveStyle: 'topspin' | 'float' | null; contactType: 'hands' | 'platform' | null; fading: boolean } | null>(null);
  const statBarSRDetailTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const statBarSRDetailFadeRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Keep rotationalSubsRef in sync so trigger checks see latest subs without stale closures
  rotationalSubsRef.current = rotationalSubs;

  const teamPlayers = players.filter(p => p.teamId === activeTeamId);

  // ── Feature gates ────────────────────────────────────────────────────────────
  const canSeeVoiceLogging = isDevUser(currentUser?.email);
  const canSeeCourtOrientation = isDevUser(currentUser?.email);
  const {
    voiceState, pendingEvents, setPendingEvents,
    liveTranscript, recordingSeconds, error: voiceError, quotaWarning,
    isSupported: voiceSupported,
    startRecording, stopRecording, cancelRecording, clearPending,
  } = useVoiceLogger({ gameState, teamPlayers, opponentRoster, organizationId });
  const [quotaWarningDismissed, setQuotaWarningDismissed] = useState<string | null>(null);
  const [voiceEditIndex, setVoiceEditIndex] = useState<number | null>(null);
  const [voiceEditField, setVoiceEditField] = useState<'player' | 'statType' | 'passQuality' | 'serveType' | 'serveTypeReceived' | 'setLocation' | 'attackLocation' | 'attackType' | null>(null);
  const [voicePlayerSearch, setVoicePlayerSearch] = useState('');
  const updatePendingEvent = useCallback((idx: number, patch: Record<string, unknown>) => {
    setPendingEvents(prev => prev.map((e, i) => i === idx ? { ...e, ...patch } as VoiceParsedEvent : e));
  }, [setPendingEvents]);
  const closeVoiceEdit = useCallback(() => { setVoiceEditIndex(null); setVoiceEditField(null); setVoicePlayerSearch(''); }, []);
  // Auto-reset voice state when all pending events are individually dismissed
  useEffect(() => {
    if (voiceState === 'pending' && pendingEvents.length === 0) clearPending();
  }, [pendingEvents.length, voiceState, clearPending]);
  const teamLineups = lineups?.filter(l => l.teamId === activeTeamId) || [];
  const opponentLineups = lineups?.filter(l => l.teamId === (opponentTeamId || 'guest-team')) || [];
  const activeTeam = teams.find(t => t.id === activeTeamId);
  const availableMatches = useMemo(() => events.filter(e => isGameType(e.type)).sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()), [events]);

  // Detect unsaved changes to the active lineup by comparing directly against the
  // saved lineup in teamLineups. No snapshot needed — this is always accurate.
  // Comparison is rotation-normalized (by numeral→player map) so that the serve-receive
  // back-rotate button (which only shifts p-slots, not the underlying rotation order)
  // does NOT trigger the dirty flag.
  const isActiveLineupDirty = useMemo(() => {
    if (!activeLineupId) return false;
    const saved = teamLineups.find(l => l.id === activeLineupId);
    if (!saved) return false;
    const allNumerals = ['I','II','III','IV','V','VI'];
    // Build numeral→player map for current state
    const currentByNumeral: Record<string, string> = {};
    for (let i = 1; i <= 6; i++) {
      const pos = `p${i}`;
      const numeral = (gameState.numerals as any)[pos] as string;
      if (numeral) currentByNumeral[numeral] = (gameState.lineup as any)[pos] || '';
    }
    // Saved positions are always stored in rotation-I order (p1=I, p2=II, …)
    const savedByNumeral: Record<string, string> = {};
    for (let i = 1; i <= 6; i++) {
      savedByNumeral[allNumerals[i - 1]] = (saved.positions as any)[`p${i}`] || '';
    }
    if (allNumerals.some(n => (currentByNumeral[n] || '') !== (savedByNumeral[n] || ''))) return true;
    if (JSON.stringify(rotationalSubs) !== JSON.stringify(saved.rotationalSubstitutions || [])) return true;
    const savedDesigs = (saved.liberoAssignments || []).map(la => la.liberoId);
    if (JSON.stringify(liberoDesignations.us) !== JSON.stringify(savedDesigs)) return true;
    for (const lid of liberoDesignations.us) {
      const savedAssign = saved.liberoAssignments?.find(la => la.liberoId === lid);
      if (JSON.stringify(liberoSandwiches[lid] || []) !== JSON.stringify(savedAssign?.replacesPlayerIds || [])) return true;
      if ((liberoServingLocks[lid] || '') !== (savedAssign?.servesForPlayerId || '')) return true;
    }
    return false;
  }, [activeLineupId, teamLineups, gameState.lineup, gameState.numerals, rotationalSubs, liberoDesignations.us, liberoSandwiches, liberoServingLocks]);

// Helper to safely parse dates for upcoming game detection
  const getEventDate = (dateStr: string) => {
    if (!dateStr) return null;
    const parts = dateStr.split('-');
    if (parts.length === 3) return new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
    const d = new Date(dateStr);
    return isNaN(d.getTime()) ? null : d;
  };

  const formatMatchDate = (dateStr: string) => {
    const d = getEventDate(dateStr);
    if (!d) return '';
    return formatMonthDay(d);
  };

  // Helper: today's date as a YYYY-MM-DD string in LOCAL time — timezone-safe, no DST issues
  const getTodayStr = () => {
    const t = new Date();
    return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
  };

  // Schedule games that have a named opponent — source of truth for the opponent dropdown.
  // Sorted: today first, then other upcoming (ascending), then past (most recent first).
  const scheduleOpponents = useMemo(() => {
    const todayStr = getTodayStr();
    return availableMatches
      .filter(e => !!e.opponent)
      .sort((a, b) => {
        const aDate = (a.date || '').slice(0, 10);
        const bDate = (b.date || '').slice(0, 10);
        const aUpcoming = aDate >= todayStr;
        const bUpcoming = bDate >= todayStr;
        if (aUpcoming && !bUpcoming) return -1; // upcoming before past
        if (!aUpcoming && bUpcoming) return 1;
        if (aUpcoming && bUpcoming) return aDate.localeCompare(bDate); // nearest upcoming first
        return bDate.localeCompare(aDate); // most recent past first
      });
  }, [availableMatches]);

  // Registered opponent teams not already covered by a schedule entry (avoids duplication)
  const registeredOnlyTeams = useMemo(() =>
    teams.filter(t =>
      t.id !== activeTeamId &&
      t.type === 'opponent' &&
      !scheduleOpponents.some(e => e.opponent?.toLowerCase() === t.name.toLowerCase())
    ),
    [teams, activeTeamId, scheduleOpponents]
  );

  useEffect(() => {
    if (selectedMatchId !== 'unselected') return; // Only auto-load once

    // Use a local-time date string for comparison so today's game never expires before midnight
    const todayStr = getTodayStr();

    // Prefer the match explicitly requested from the schedule, otherwise auto-select next upcoming game
    let targetGame = initialMatchId
      ? availableMatches.find(e => e.id === initialMatchId)
      : undefined;

    if (!targetGame) {
      // Find closest upcoming game that hasn't already been submitted
      const upcomingGames = availableMatches
        .filter(e => {
          if ((e.date || '').slice(0, 10) < todayStr) return false;
          // Skip games that already have an official submitted record
          return !matchRecords.some(r => r.eventId === e.id && r.isRecord);
        })
        .sort((a, b) => {
            const da = getEventDate(a.date)!;
            const db = getEventDate(b.date)!;
            return da.getTime() - db.getTime();
        });
      targetGame = upcomingGames[0];
    }

    const nextGame = targetGame;
    if (nextGame) {
      setSelectedMatchId(nextGame.id);
      if (nextGame.opponent) {
        // Try to automatically load the actual opponent team roster if it exists
        const matchingTeam = teams.find(t => t.type === 'opponent' && t.name.toLowerCase() === nextGame.opponent!.toLowerCase());

        if (matchingTeam) {
            setGameState(prev => ({ ...prev, opponent: matchingTeam.name }));
            setOpponentTeamId(matchingTeam.id);
            setOpponentRoster(players.filter(p => p.teamId === matchingTeam.id));

            const oppLineups = lineups?.filter(l => l.teamId === matchingTeam.id) || [];
            const defaultLineup = oppLineups.find(l => l.isDefault) || oppLineups[0];
            if (defaultLineup) {
                // Manually load lineup to avoid dependency issues in useEffect
                setGameState(prev => ({
                   ...prev,
                   opponentLineup: { op1: defaultLineup.positions.p1, op2: defaultLineup.positions.p2, op3: defaultLineup.positions.p3, op4: defaultLineup.positions.p4, op5: defaultLineup.positions.p5, op6: defaultLineup.positions.p6 },
                   opponentNumerals: { op1: 'I', op6: 'VI', op5: 'V', op4: 'IV', op3: 'III', op2: 'II' }
                }));
                setActiveOpponentLineupId(defaultLineup.id);
            }
        } else {
            // No matching team found in DB, just show the string
            setGameState(prev => ({ ...prev, opponent: nextGame.opponent! }));
        }
      }
    }
  }, [availableMatches, teams, players, lineups, selectedMatchId, initialMatchId, matchRecords]);

  // Detect when a saved in-progress game belongs to a different event than today's next upcoming game
  useEffect(() => {
    if (hasCheckedSavedGameRef.current) return;
    if (!_savedGame) return;
    const savedStats: any[] = _savedGame.gameState?.stats ?? [];
    const savedOurScore: number = _savedGame.gameState?.ourScore ?? 0;
    const savedTheirScore: number = _savedGame.gameState?.theirScore ?? 0;
    const hasProgress = savedStats.length > 0 || savedOurScore > 0 || savedTheirScore > 0;
    if (!hasProgress) return;
    if (availableMatches.length === 0) return;

    hasCheckedSavedGameRef.current = true;

    const todayStr = getTodayStr();
    const nextGame = availableMatches
      .filter(e => (e.date || '').slice(0, 10) >= todayStr)
      .sort((a, b) => {
        const da = getEventDate(a.date)?.getTime() ?? 0;
        const db = getEventDate(b.date)?.getTime() ?? 0;
        return da - db;
      })[0];

    if (nextGame && nextGame.id !== _savedGame.selectedMatchId) {
      setUpcomingGameForSwitch(nextGame);
      setShowSavedGameWarning(true);
    }
  }, [availableMatches]);

  const isScrimmage = useMemo(() => {
    const oppTeam = teams.find(t => t.id === opponentTeamId);
    return oppTeam?.type === 'club' || (opponentTeamId && !oppTeam?.type);
  }, [opponentTeamId, teams]);

  useEffect(() => {
    if (!activeTeamId || teamLineups.length === 0) { setActiveLineupId(null); return; }
    let target = teamLineups.find(l => l.isFavorite) || teamLineups.find(l => l.isDefault) || [...teamLineups].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];
    if (target) { const isActiveValid = activeLineupId && teamLineups.some(l => l.id === activeLineupId); if (!isActiveValid) handleLoadLineup(target.id, 'us'); }
  }, [activeTeamId, teamLineups]);

  // Auto-dismiss the kill assist prompt after 8 seconds so it doesn't get stuck
  useEffect(() => {
    if (assistPromptTimeoutRef.current) clearTimeout(assistPromptTimeoutRef.current);
    if (assistPromptState) {
      assistPromptTimeoutRef.current = setTimeout(() => setAssistPromptState(null), 8000);
    }
    return () => { if (assistPromptTimeoutRef.current) clearTimeout(assistPromptTimeoutRef.current); };
  }, [assistPromptState]);

  // ── Rotational substitution trigger checker ────────────────────────────────
  useEffect(() => {
    // Don't check triggers during lineup edit mode
    if (isLineupEditMode) {
      prevLineupRef.current = gameState.lineup;
      prevPossessionRef.current = possession;
      return;
    }
    const newLineup = gameState.lineup;
    const prevLineup = prevLineupRef.current;
    const newPossession = possession;
    const prevPossession = prevPossessionRef.current;

    // First render: just record initial state, no triggers
    if (!prevLineup) {
      prevLineupRef.current = newLineup;
      prevPossessionRef.current = newPossession;
      return;
    }

    const frontRowPositions = ['p2', 'p3', 'p4'];
    const backRowPositions = ['p1', 'p5', 'p6'];

    const getSetterRotation = (lineup: typeof newLineup): number => {
      for (const [key, id] of Object.entries(lineup)) {
        const p = teamPlayers.find(r => r.id === id);
        if (p?.position === 'Setter') return parseInt(key.replace('p', ''));
      }
      return 1;
    };
    const currentRotNum = getSetterRotation(newLineup);
    const prevRotNum = getSetterRotation(prevLineup);

    const toConfirm: RotationalSubstitution[] = [];

    // after_serving: fire when we LOSE possession (side-out — other team wins the rally)
    if (prevPossession === 'serving' && newPossession === 'receiving') {
      const prevServerId = (prevLineup as any).p1;
      if (prevServerId) {
        rotationalSubsRef.current
          .filter(sub => sub.triggerType === 'after_serving' && sub.playerOffId === prevServerId)
          .forEach(sub => {
            const count = (rotSubTriggerCountsRef.current[sub.id] || 0) + 1;
            rotSubTriggerCountsRef.current[sub.id] = count;
            if (!(count === 1 && sub.ignoreFirstTime)) {
              toConfirm.push(sub);
            }
          });
      }
    }

    rotationalSubsRef.current.forEach(sub => {
      const playerOffNewPos = Object.entries(newLineup).find(([, id]) => id === sub.playerOffId)?.[0];
      if (!playerOffNewPos) return; // player not currently on court
      const playerOffPrevPos = Object.entries(prevLineup).find(([, id]) => id === sub.playerOffId)?.[0];

      let triggered = false;
      switch (sub.triggerType) {
        case 'front_row':
          // Only trigger when the player rotates into p4 from p5 (not on manual sub-in)
          triggered = playerOffNewPos === 'p4' && playerOffPrevPos === 'p5';
          break;
        case 'back_row':
          // Only trigger when the player rotates into p1 from p2 (not on manual sub-in)
          triggered = playerOffNewPos === 'p1' && playerOffPrevPos === 'p2';
          break;
        case 'rotation_number':
          triggered = currentRotNum === sub.triggerRotation && prevRotNum !== sub.triggerRotation;
          break;
        case 'serve_receive_rotation':
          triggered = newPossession === 'receiving' && prevPossession === 'serving' &&
                     currentRotNum === sub.triggerRotation;
          break;
        case 'after_serving':
          break; // handled above via possession change
      }

      if (triggered) {
        const count = (rotSubTriggerCountsRef.current[sub.id] || 0) + 1;
        rotSubTriggerCountsRef.current[sub.id] = count;
        if (!(count === 1 && sub.ignoreFirstTime)) {
          toConfirm.push(sub);
        }
      }
    });

    if (toConfirm.length > 0) {
      setPendingSubConfirms(prev => {
        // Avoid duplicate entries
        const existingIds = new Set(prev.map(s => s.id));
        return [...prev, ...toConfirm.filter(s => !existingIds.has(s.id))];
      });
    }

    prevLineupRef.current = newLineup;
    prevPossessionRef.current = newPossession;
  }, [gameState.lineup, possession, isLineupEditMode]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-apply back-rotate preference whenever edit mode opens or possession changes while in edit mode
  useEffect(() => {
    if (isLineupEditMode && possession === 'receiving' && backRotatePreference) {
      handleSetStartingRotation('VI');
    }
  }, [isLineupEditMode, possession]); // eslint-disable-line react-hooks/exhaustive-deps

  // Manage incoming serve type preselection on possession change
  useEffect(() => {
    if (possession === 'receiving') {
      // Restore the last serve type used for this opponent rotation, or apply default
      const oppRot = (gameState.opponentNumerals as any).op1 as string;
      const remembered = serveTypeByOpponentRotationRef.current[oppRot];
      if (remembered) {
        setIncomingServeType(remembered);
      } else if (appSettings.defaultOpponentServesFloat) {
        setIncomingServeType('float');
      } else {
        setIncomingServeType(null);
      }
    } else {
      setIncomingServeType(null);
    }
  }, [possession]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleTimeout = (team: 'us' | 'them') => setTimeouts(prev => ({ ...prev, [team]: prev[team] === 0 ? 2 : prev[team] - 1 }));

  // Sets incoming serve type and persists it to the current opponent rotation for this set
  const handleSetIncomingServeType = (type: 'topspin' | 'float') => {
    const next: 'topspin' | 'float' | null = incomingServeType === type ? null : type;
    setIncomingServeType(next);
    const oppRot = (gameState.opponentNumerals as any).op1 as string;
    if (next !== null) {
      serveTypeByOpponentRotationRef.current[oppRot] = next;
    }
  };


  const handleTeamSwitch = (newTeamId: string) => {
    setActiveTeamId(newTeamId);
    setHasInitializedTeam(true);
    onActiveTeamChange?.(newTeamId);
    // Clear our lineup so the new team's roster starts fresh
    setGameState(prev => ({
      ...prev,
      lineup: { p1: '', p2: '', p3: '', p4: '', p5: '', p6: '' },
      numerals: { p1: 'I', p2: 'II', p3: 'III', p4: 'IV', p5: 'V', p6: 'VI' },
    }));
    setActiveLineupId(null);
    // Clear our-side libero state
    setLiberoDesignations(prev => ({ ...prev, us: [] }));
    setLiberoSandwiches({});
    setLiberoServingLocks({});
    setLiberoOnCourt({});
    setActiveLiberoSlot(0);
  };

  // ── Libero Designation ────────────────────────────────────────────────────────
  const handleLiberoDesignate = (side: 'us' | 'them', playerId: string, slot: 0 | 1) => {
    setLiberoDesignations(prev => {
      const current = [...prev[side]];
      const max = appSettings.allowTwoLiberos ? 2 : 1;
      // Remove from any other slot first (prevent duplicates)
      const filtered = current.filter(id => id !== playerId);
      filtered.splice(slot, 1, playerId);
      return { ...prev, [side]: filtered.slice(0, max) };
    });
    setLiberoPickerModal(null);

    // Immediately open config modal to choose which pair this libero replaces.
    // Default to III & VI pair (p3/p6 or op3/op6) when no existing config.
    const lineup = side === 'us' ? gameState.lineup : gameState.opponentLineup;
    const posPrefix = side === 'us' ? 'p' : 'op';
    const existing = liberoSandwiches[playerId] || [];
    const defaultP1 = (lineup as any)[`${posPrefix}3`] || '';
    const defaultP2 = (lineup as any)[`${posPrefix}6`] || '';
    setLiberoConfigModal({
      side,
      liberoId: playerId,
      player1Id: existing[0] || defaultP1,
      player2Id: existing[1] || defaultP2,
      servesForPlayerId: liberoServingLocks[playerId] || '',
    });
  };

  const handleLiberoRemove = (side: 'us' | 'them', libId: string) => {
    if (side === 'us') {
      // If the removed libero was active or was the last one, reset to slot 0
      const removedSlot = liberoDesignations.us.indexOf(libId);
      if (removedSlot === activeLiberoSlot || liberoDesignations.us.length - 1 <= activeLiberoSlot) {
        setActiveLiberoSlot(0);
      }
    }
    setLiberoDesignations(prev => ({ ...prev, [side]: prev[side].filter(id => id !== libId) }));
    setLiberoSandwiches(prev => { const n = { ...prev }; delete n[libId]; return n; });
    setLiberoServingLocks(prev => { const n = { ...prev }; delete n[libId]; return n; });
  };

  // ── Libero Config (pair + serving) ───────────────────────────────────────
  /** Open config modal for an already-designated libero (tap their chip or a designated card). */
  const handleLiberoCardTap = (side: 'us' | 'them', liberoId: string) => {
    const lineup = side === 'us' ? gameState.lineup : gameState.opponentLineup;
    const posPrefix = side === 'us' ? 'p' : 'op';
    const roster = side === 'us' ? teamPlayers : opponentRoster;
    const existing = liberoSandwiches[liberoId] || [];
    // Default to the pair with the most middle blockers; fall back to III & VI (p3/p6)
    let defaultP1 = (lineup as any)[`${posPrefix}3`] || '';
    let defaultP2 = (lineup as any)[`${posPrefix}6`] || '';
    if (!existing.length) {
      const pairNums: [number, number][] = [[1, 4], [2, 5], [3, 6]];
      let bestScore = -1;
      for (const [a, b] of pairNums) {
        const idA = (lineup as any)[`${posPrefix}${a}`] || '';
        const idB = (lineup as any)[`${posPrefix}${b}`] || '';
        const pA = roster.find(p => p.id === idA);
        const pB = roster.find(p => p.id === idB);
        const score =
          (('position' in (pA ?? {})) && (pA as Player).position === 'Middle Blocker' ? 1 : 0) +
          (('position' in (pB ?? {})) && (pB as Player).position === 'Middle Blocker' ? 1 : 0);
        if (score > bestScore) { bestScore = score; defaultP1 = idA; defaultP2 = idB; }
      }
    }
    setLiberoConfigModal({
      side,
      liberoId,
      player1Id: existing[0] || defaultP1,
      player2Id: existing[1] || defaultP2,
      servesForPlayerId: liberoServingLocks[liberoId] || '',
    });
  };

  // Keep ref in sync so setTimeout callbacks always call the latest version (avoids stale closure).
  handleLiberoCardTapRef.current = handleLiberoCardTap;

  /** Toggle the libero in or out of the game manually without changing their designation. */
  const handleLiberoSwap = (liberoId: string) => {
    setLiberoOnCourt(prev => {
      const current = prev[liberoId] ?? true;
      return { ...prev, [liberoId]: !current };
    });
  };

  /**
   * Swap the active libero (L1 ⇄ L2). The incoming libero inherits the outgoing libero's
   * serving slot lock so the per-match serving position is preserved across the swap.
   */
  const handleLiberoActiveSwap = () => {
    if (liberoDesignations.us.length < 2) return;
    const outSlot = activeLiberoSlot;
    const newSlot: 0 | 1 = outSlot === 0 ? 1 : 0;
    const outId = liberoDesignations.us[outSlot];
    const inId = liberoDesignations.us[newSlot];
    // Transfer serving lock: incoming libero inherits the serving slot of the outgoing one
    const outLock = liberoServingLocks[outId];
    setLiberoServingLocks(prev => {
      const next = { ...prev };
      if (outLock) next[inId] = outLock;
      else delete next[inId];
      return next;
    });
    // Clear manual bench overrides so the new active libero follows sandwich logic immediately
    setLiberoOnCourt(prev => {
      const next = { ...prev };
      delete next[outId];
      delete next[inId];
      return next;
    });
    setActiveLiberoSlot(newSlot);
  };

  /** Save the libero's configuration (which 2 players they replace, and who they serve for). */
  const handleConfirmLiberoConfig = () => {
    if (!liberoConfigModal) return;
    const { liberoId, player1Id, player2Id, servesForPlayerId } = liberoConfigModal;
    const players = [player1Id, player2Id].filter(Boolean);
    setLiberoSandwiches(prev => ({ ...prev, [liberoId]: players }));
    if (servesForPlayerId) {
      setLiberoServingLocks(prev => ({ ...prev, [liberoId]: servesForPlayerId }));
    } else {
      setLiberoServingLocks(prev => { const n = { ...prev }; delete n[liberoId]; return n; });
      // Clear any manual on-court override so "no serve" config is respected at p1
      setLiberoOnCourt(prev => { const n = { ...prev }; delete n[liberoId]; return n; });
    }
    setLiberoConfigModal(null);
  };

  /**
   * After any lineup change for a given side, check that every sandwich player
   * is still present in the updated lineup.
   *
   * When a sandwich player was replaced by exactly one new player (clean 1-for-1
   * swap), the sandwich is updated silently — the libero just tracks the new
   * player automatically, exactly as the user expects.
   *
   * Only falls back to the alert when the replacement is ambiguous (no clear
   * sub-in player, or multiple sandwich players left the lineup at once).
   */
  const checkLiberoSandwichValidity = (
    side: 'us' | 'them',
    updatedPositions: Record<string, string>,
    changeInfo?: { removedPlayerId: string; addedPlayerId: string },
  ) => {
    const desigs = side === 'us' ? liberoDesignations.us : liberoDesignations.them;
    if (!desigs.length) return;
    const lineupIds = new Set(Object.values(updatedPositions).filter(Boolean));

    const silentIds: string[] = [];
    const silentSandwiches: Record<string, string[]> = {};
    const invalidIds: string[] = [];
    const suggestedSandwiches: Record<string, string[]> = {};

    desigs.forEach(liberoId => {
      const sandwich = liberoSandwiches[liberoId] || [];
      if (sandwich.length === 0 || sandwich.every(pid => lineupIds.has(pid))) return; // still valid

      const missingCount = sandwich.filter(pid => !lineupIds.has(pid)).length;
      const suggested = sandwich.map(pid => {
        if (lineupIds.has(pid)) return pid;
        if (changeInfo?.addedPlayerId && changeInfo.addedPlayerId !== liberoId) return changeInfo.addedPlayerId;
        return '';
      }).filter(Boolean);

      // Clean 1-for-1: exactly one sandwich player left and the new player fills the gap
      if (
        missingCount === 1 &&
        changeInfo?.addedPlayerId &&
        changeInfo.addedPlayerId !== liberoId &&
        suggested.length === sandwich.length
      ) {
        silentIds.push(liberoId);
        silentSandwiches[liberoId] = suggested;
      } else {
        invalidIds.push(liberoId);
        suggestedSandwiches[liberoId] = suggested;
      }
    });

    // Silent auto-reassign — no popup, libero just follows the new player
    if (silentIds.length > 0) {
      setLiberoSandwiches(prev => {
        const next = { ...prev };
        silentIds.forEach(lid => { next[lid] = silentSandwiches[lid]; });
        return next;
      });
      silentIds.forEach(lid => {
        if (changeInfo?.removedPlayerId && liberoServingLocks[lid] === changeInfo.removedPlayerId) {
          setLiberoServingLocks(prev => ({ ...prev, [lid]: changeInfo!.addedPlayerId }));
        }
      });
    }

    // Ambiguous cases still need the alert
    if (invalidIds.length > 0) {
      setLiberoSandwiches(prev => {
        const next = { ...prev };
        invalidIds.forEach(lid => delete next[lid]);
        return next;
      });
      setLiberoServingLocks(prev => {
        const next = { ...prev };
        invalidIds.forEach(lid => delete next[lid]);
        return next;
      });
      setLiberoRedesignAlert({ liberoIds: invalidIds, side, suggestedSandwiches });
    }
  };

  /**
   * 2026 NFHS/NCAA Libero Resolution
   *
   * Rules enforced:
   * 1. Libero plays in back-row positions only (p1/5/6 for us, op1/5/6 for them).
   * 2. The libero replaces only the MB player stored in liberoSandwiches[liberoId].
   * 3. Serving slot (p1/op1): if the libero has a serving lock, they serve ONLY in
   *    the locked rotation numeral. If no lock yet, they may serve freely.
   * 4. If a designated libero's ID appears directly in the lineup, mark it active.
   */
  const getEffectivePlayer = (side: 'us' | 'them', pos: string, currentRotationId: string, _unused: boolean) => {
    const libDesigs = side === 'us' ? liberoDesignations.us : liberoDesignations.them;

    if (!libDesigs.length || !currentRotationId) {
      return { effectiveId: currentRotationId, isLiberoActive: false };
    }

    // If the position itself holds a designated libero player (e.g. manually assigned)
    if (libDesigs.includes(currentRotationId)) {
      return { effectiveId: currentRotationId, isLiberoActive: true };
    }

    // Find whichever libero has this player in their sandwich.
    // With two liberos, both can be active — switching is automatic based on whose
    // sandwich players are in back row. The one-libero-on-court rule below prevents
    // both from appearing simultaneously.
    const sandwichLibId = libDesigs.find(lid => (liberoSandwiches[lid] || []).includes(currentRotationId));
    if (!sandwichLibId) {
      return { effectiveId: currentRotationId, isLiberoActive: false };
    }

    // Manual bench override: libero was explicitly swapped out — sandwich player stays on court
    if (liberoOnCourt[sandwichLibId] === false) {
      return { effectiveId: currentRotationId, isLiberoActive: false };
    }

    // With two designated liberos on our side, only the active libero (activeLiberoSlot) auto-swaps.
    // The inactive libero's sandwich players stay on court regardless of row position.
    if (side === 'us' && libDesigs.length >= 2) {
      const activeLibId = libDesigs[activeLiberoSlot];
      if (sandwichLibId !== activeLibId) {
        return { effectiveId: currentRotationId, isLiberoActive: false };
      }
    }

    // Libero only plays in back-row positions
    const backRowPos = side === 'us' ? ['p1', 'p5', 'p6'] : ['op1', 'op5', 'op6'];
    if (!backRowPos.includes(pos)) {
      return { effectiveId: currentRotationId, isLiberoActive: false };
    }

    // Serving slot: only restrict the libero from p1/op1 when this team is ACTIVELY SERVING.
    // When receiving, p1 is just right-back (a normal back-row position) and the libero
    // should sub straight back in for their sandwich player automatically.
    const servingSlot = side === 'us' ? 'p1' : 'op1';
    const thisTeamIsServing = (side === 'us' && possession === 'serving') ||
                              (side === 'them' && possession === 'receiving');
    if (pos === servingSlot) {
      if (thisTeamIsServing) {
        // Always enforce the serving lock regardless of liberoOnCourt state.
        // liberoOnCourt=true only bypasses the back-row auto-swap; at p1 while serving,
        // the lock is the sole authority. This prevents the libero from illegally serving
        // for a second sandwich player after already serving for the first.
        const servesForId = liberoServingLocks[sandwichLibId];
        if (!servesForId || servesForId !== currentRotationId) {
          return { effectiveId: currentRotationId, isLiberoActive: false };
        }
      }
    }

    // ── One-libero-on-court rule ──────────────────────────────────────────────
    // Only one libero may be visible on court at a time. Scan back-row positions
    // with higher priority (earlier in backRowPos order) to see if any libero
    // is already active there. This covers two failure modes:
    //  (a) Same libero has two sandwich players both in back row simultaneously
    //  (b) Two different designated liberos both have sandwich players in back row
    //
    // Helper: would a libero be active for the player in `checkPos`?
    const wouldLiberoBeActiveAt = (checkPos: string): boolean => {
      const rawInCheck = (side === 'us' ? gameState.lineup : gameState.opponentLineup as any)[checkPos] as string | undefined;
      if (!rawInCheck) return false;
      return libDesigs.some(lid => {
        if (liberoOnCourt[lid] === false) return false;
        if (!(liberoSandwiches[lid] || []).includes(rawInCheck)) return false;
        // With two liberos on our side, only the active one can be on court
        if (side === 'us' && libDesigs.length >= 2 && lid !== libDesigs[activeLiberoSlot]) return false;
        // Replicate the serving-slot restriction for the check position
        if (checkPos === servingSlot && thisTeamIsServing) {
          const servesForId = liberoServingLocks[lid];
          if (!servesForId || servesForId !== rawInCheck) return false;
        }
        return true;
      });
    };
    const posPriority = backRowPos.indexOf(pos); // 0 = highest priority
    for (let i = 0; i < posPriority; i++) {
      if (wouldLiberoBeActiveAt(backRowPos[i])) {
        // A libero is already active in a higher-priority back-row slot — keep
        // this slot as the rotation player to prevent two liberos on court.
        return { effectiveId: currentRotationId, isLiberoActive: false };
      }
    }

    return { effectiveId: sandwichLibId, isLiberoActive: true };
  };

  const performRotation = (side: 'us' | 'them', direction: 'cw' | 'ccw') => {
    setRallyInProgress(false);
    setGameState(prev => {
      const isUs = side === 'us';
      const lineupKey = (isUs ? 'lineup' : 'opponentLineup') as 'lineup' | 'opponentLineup';
      const numeralsKey = (isUs ? 'numerals' : 'opponentNumerals') as 'numerals' | 'opponentNumerals';
      const currentLineup = prev[lineupKey] as any;
      const currentNumerals = prev[numeralsKey] as any;
      const nextLineup = { ...currentLineup };
      const nextNumerals = { ...currentNumerals };
      const prefix = isUs ? 'p' : 'op';
      // Volleyball rotation: 1 moves to 6, 6 moves to 5, 5 moves to 4, 4 moves to 3, 3 moves to 2, 2 moves to 1
      if (direction === 'cw') {
        nextLineup[`${prefix}1`] = currentLineup[`${prefix}2`]; nextNumerals[`${prefix}1`] = currentNumerals[`${prefix}2`];
        nextLineup[`${prefix}6`] = currentLineup[`${prefix}1`]; nextNumerals[`${prefix}6`] = currentNumerals[`${prefix}1`];
        nextLineup[`${prefix}5`] = currentLineup[`${prefix}6`]; nextNumerals[`${prefix}5`] = currentNumerals[`${prefix}6`];
        nextLineup[`${prefix}4`] = currentLineup[`${prefix}5`]; nextNumerals[`${prefix}4`] = currentNumerals[`${prefix}5`];
        nextLineup[`${prefix}3`] = currentLineup[`${prefix}4`]; nextNumerals[`${prefix}3`] = currentNumerals[`${prefix}4`];
        nextLineup[`${prefix}2`] = currentLineup[`${prefix}3`]; nextNumerals[`${prefix}2`] = currentNumerals[`${prefix}3`];
      } else {
        nextLineup[`${prefix}2`] = currentLineup[`${prefix}1`]; nextNumerals[`${prefix}2`] = currentNumerals[`${prefix}1`];
        nextLineup[`${prefix}1`] = currentLineup[`${prefix}6`]; nextNumerals[`${prefix}1`] = currentNumerals[`${prefix}6`];
        nextLineup[`${prefix}6`] = currentLineup[`${prefix}5`]; nextNumerals[`${prefix}6`] = currentNumerals[`${prefix}5`];
        nextLineup[`${prefix}5`] = currentLineup[`${prefix}4`]; nextNumerals[`${prefix}5`] = currentNumerals[`${prefix}4`];
        nextLineup[`${prefix}4`] = currentLineup[`${prefix}3`]; nextNumerals[`${prefix}4`] = currentNumerals[`${prefix}3`];
        nextLineup[`${prefix}3`] = currentLineup[`${prefix}2`]; nextNumerals[`${prefix}3`] = currentNumerals[`${prefix}2`];
      }
      // In pregame edit mode for our side: move players but keep rotation number fixed
      if (isUs && isLineupEditMode) {
        return { ...prev, [lineupKey]: nextLineup };
      }
      return { ...prev, [lineupKey]: nextLineup, [numeralsKey]: nextNumerals };
    });
  };

  const updateScore = (team: 'us' | 'them', delta: number) => {
    setRallyInProgress(false);
    let shouldRotate = false;
    let nextPossession = possession;
    if (delta > 0) {
       if (team === 'us' && possession === 'receiving') { shouldRotate = true; nextPossession = 'serving'; }
       else if (team === 'them' && possession === 'serving') { shouldRotate = true; nextPossession = 'receiving'; }
    }
    if (shouldRotate) { setPossession(nextPossession); performRotation(team, 'cw'); }
    setGameState(prev => ({
      ...prev,
      ourScore: team === 'us' ? Math.max(0, prev.ourScore + delta) : prev.ourScore,
      theirScore: team === 'them' ? Math.max(0, prev.theirScore + delta) : prev.theirScore
    }));
  };

  const handleLoadLineup = (lineupId: string, side: 'us' | 'them') => {
     const pool = side === 'us' ? teamLineups : lineups?.filter(l => l.teamId === (opponentTeamId || 'guest-team')) || [];
     const selected = pool.find(l => l.id === lineupId);
     if (selected) {
        let positionsToUse: any = side === 'them' ? { op1: selected.positions.p1, op2: selected.positions.p2, op3: selected.positions.p3, op4: selected.positions.p4, op5: selected.positions.p5, op6: selected.positions.p6 } : { ...selected.positions };
        setGameState(prev => ({
           ...prev,
           [side === 'us' ? 'lineup' : 'opponentLineup']: positionsToUse,
           [side === 'us' ? 'numerals' : 'opponentNumerals']: side === 'us' ? { p1: 'I', p6: 'VI', p5: 'V', p4: 'IV', p3: 'III', p2: 'II' } : { op1: 'I', op6: 'VI', op5: 'V', op4: 'IV', op3: 'III', op2: 'II' }
        }));
        if (selected.liberoAssignments && selected.liberoAssignments.length > 0) {
            const newDesigs: string[] = [];
            const newSandwiches: Record<string, string[]> = {};
            const newServingFor: Record<string, string> = {};
            selected.liberoAssignments.forEach(la => {
                newDesigs.push(la.liberoId);
                if (la.replacesPlayerIds?.length) newSandwiches[la.liberoId] = la.replacesPlayerIds;
                if (la.servesForPlayerId) newServingFor[la.liberoId] = la.servesForPlayerId;
            });
            setLiberoDesignations(prev => ({ ...prev, [side]: newDesigs }));
            setLiberoSandwiches(prev => ({ ...prev, ...newSandwiches }));
            if (Object.keys(newServingFor).length) setLiberoServingLocks(prev => ({ ...prev, ...newServingFor }));
        } else {
            setLiberoDesignations(prev => ({ ...prev, [side]: [] }));
        }
        // Serving locks reset each set (line 2152) but are loaded fresh from lineup on initial load
        if (side === 'us') setActiveLineupId(lineupId); else setActiveOpponentLineupId(lineupId);
        // Load rotational substitutions for our team
        if (side === 'us') {
          setRotationalSubs(selected.rotationalSubstitutions || []);
          rotSubTriggerCountsRef.current = {};
          // Reset prev refs so triggers don't fire on the initial load
          prevLineupRef.current = null;
          // Dirty detection compares against teamLineups directly — no snapshot needed
        }
     }
  };

  // Intercept 'us' lineup loads to warn about libero count mismatch
  const tryLoadLineup = (lineupId: string) => {
    const pool = teamLineups;
    const selected = pool.find(l => l.id === lineupId);
    if (!selected) { handleLoadLineup(lineupId, 'us'); return; }
    const lineupCount = selected.liberoAssignments?.length ?? 0;
    const gameAllowsTwo = appSettings.allowTwoLiberos;
    const mismatch =
      (lineupCount >= 2 && !gameAllowsTwo) ||
      (lineupCount === 1 && gameAllowsTwo);
    if (mismatch) {
      setLiberoMismatchModal({ lineupId, lineupCount, gameAllowsTwo });
    } else {
      handleLoadLineup(lineupId, 'us');
    }
  };

  const updateLineupGeneric = (side: 'us' | 'them', pos: string, playerId: string) => {
    const lineupKey = (side === 'us' ? 'lineup' : 'opponentLineup') as 'lineup' | 'opponentLineup';
    const currentLineup = { ...(gameState[lineupKey] as any) };
    const removedPlayerId = (currentLineup as any)[pos] || '';

    // Guard: libero designees must not be placed directly into lineup slots.
    // They enter/exit court exclusively through the sandwich toggle mechanism.
    if (playerId !== '') {
      const desigs = side === 'us' ? liberoDesignations.us : liberoDesignations.them;
      if (desigs.includes(playerId)) {
        console.warn('Blocked attempt to place designated libero into a lineup slot.');
        return;
      }
    }

    // Duplicate Prevention: If player is already in another slot, clear it
    if (playerId !== '') {
      const existingPos = Object.keys(currentLineup).find(k => (currentLineup as any)[k] === playerId);
      if (existingPos) (currentLineup as any)[existingPos] = '';
    }

    (currentLineup as any)[pos] = playerId;

    // Update Local State
    setGameState(prev => ({ ...prev, [lineupKey]: currentLineup }));

    // Auto-reassign libero sandwich when a sandwiched player is swapped for another player.
    // Also handle the case where an incoming court player's old slot (cleared by duplicate
    // prevention) held a sandwiched player — treat the incoming player as the replacement.
    // If no clear replacement, fall back to checkLiberoSandwichValidity which now also
    // does a silent auto-reassign for clean 1-for-1 swaps.
    if (side === 'us') {
      const desigs = liberoDesignations.us;
      let autoReassigned = false;
      if (removedPlayerId && playerId) {
        desigs.forEach(liberoId => {
          const sandwich = liberoSandwiches[liberoId] || [];
          if (sandwich.includes(removedPlayerId)) {
            autoReassigned = true;
            const newSandwich = sandwich.map((pid: string) => pid === removedPlayerId ? playerId : pid);
            setLiberoSandwiches(prev => ({ ...prev, [liberoId]: newSandwich }));
            if (liberoServingLocks[liberoId] === removedPlayerId) {
              setLiberoServingLocks(prev => ({ ...prev, [liberoId]: playerId }));
            }
          }
        });
      }
      // Also check if duplicate prevention cleared a slot that held a sandwich player
      // (e.g., an on-court player was moved to replace the subbed-out player,
      // vacating their old position which was a sandwich slot).
      if (!autoReassigned) {
        checkLiberoSandwichValidity('us', currentLineup, { removedPlayerId, addedPlayerId: playerId });
      }
    }

  };

  const saveQuickPlayer = async () => {
    const hasName = !!newPlayerForm.name.trim(); 
    const hasNumber = !!newPlayerForm.number.trim();
    
    // Validation: Require Name OR Number for Opponent
    if (quickCreateSide === 'them') {
        if (!hasName && !hasNumber) return;
    } else {
        if (!hasName) return;
    }

    const displayName = hasName ? newPlayerForm.name : `Opponent #${newPlayerForm.number}`;
    const targetTeam = quickCreateSide === 'us' ? activeTeamId : (opponentTeamId || 'guest-team');

    if (organizationId === 'dev-org-id' || targetTeam === 'guest-team') {
        const newPlayer: Player = { 
            id: `dev-${Date.now()}`, 
            organizationId, 
            teamId: targetTeam, 
            name: displayName, 
            jerseyNumber: newPlayerForm.number, 
            position: newPlayerForm.position, 
            rank: 999 
        };
        setPlayers(prev => [...prev, newPlayer]);
        if (quickCreateSide === 'them') setOpponentRoster(prev => [...prev, newPlayer]);
        if (quickCreateTargetPos === '__libero__') {
            setLiberoDesignations(prev => {
              const current = [...prev[quickCreateSide]];
              if (!current.includes(newPlayer.id)) current.push(newPlayer.id);
              return { ...prev, [quickCreateSide]: current.slice(0, appSettings.allowTwoLiberos ? 2 : 1) };
            });
        } else {
            updateLineupGeneric(quickCreateSide, quickCreateTargetPos, newPlayer.id);
        }
        setIsQuickCreating(false);
        return;
    }

    try {
        const { data, error } = await supabase.from('players').insert([{
            name: displayName,
            organization_id: organizationId,
            team_id: targetTeam,
            jersey_number: newPlayerForm.number,
            position: newPlayerForm.position,
            rank: 999
        }]).select();

        if (data && !error) {
            const newP = mapPlayerFromDB(data[0]);
            setPlayers(prev => [...prev, newP]);
            if (quickCreateSide === 'them') setOpponentRoster(prev => [...prev, newP]);
            if (quickCreateTargetPos === '__libero__') {
                setLiberoDesignations(prev => {
                  const current = [...prev[quickCreateSide]];
                  if (!current.includes(newP.id)) current.push(newP.id);
                  return { ...prev, [quickCreateSide]: current.slice(0, appSettings.allowTwoLiberos ? 2 : 1) };
                });
            } else {
                updateLineupGeneric(quickCreateSide, quickCreateTargetPos, newP.id);
            }
        }
    } catch (error) { 
        console.error(error); 
    }
    setIsQuickCreating(false);
  };

  const handleCreateOpponentTeam = async () => {
      if (!newOpponentName.trim()) return;
      if (!setTeams) return;

      const tempId = organizationId === 'dev-org-id' ? `dev-team-${Date.now()}` : `temp-team-${Date.now()}`;
      const newTeam: Team = { 
          id: tempId, 
          organizationId, 
          name: newOpponentName, 
          isDefault: false, 
          isFavorite: false,
          type: 'opponent',
          coachNotes: []
      };
      
      setTeams(prev => [...prev, newTeam]); 
      setOpponentTeamId(newTeam.id);
      setGameState(prev => ({...prev, opponent: newTeam.name}));
      
      // Migrate any current guest roster to this new team
      if (opponentRoster.length > 0 && organizationId !== 'dev-org-id') {
          // In a real app, you'd batch update these players. For now, we just update local state logic.
          // Since we are in the "creating" flow, we assume the roster might be empty or we just accept they are still guest players visually until refreshed.
      }

      setIsAddingOpponent(false);
      setNewOpponentName('');

      if (organizationId !== 'dev-org-id') {
          try {
              const { data, error } = await supabase
                  .from('teams')
                  .insert([{ name: newTeam.name, organization_id: organizationId, is_default: false, type: 'opponent' }])
                  .select()
                  .single();
              
              if (data && !error) {
                  const realTeam = mapTeamFromDB(data);
                  setTeams(prev => prev.map(t => t.id === tempId ? realTeam : t));
                  setOpponentTeamId(realTeam.id);
              }
          } catch (e) {
              console.error(e);
          }
      }
  };

 // Persist game state to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem(GAMETIME_STORAGE_KEY, JSON.stringify({
        gameState, possession, serveFirstSelection, timeouts,
        liberoDesignations, liberoSandwiches, liberoServingLocks, liberoOnCourt, activeLiberoSlot,
        activeTeamId, selectedMatchId,
        opponentRoster, opponentTeamId, activeLineupId, activeOpponentLineupId,
        isOpponentExpanded, rotationalSubs,
      }));
    } catch (e) { console.error('Failed to save game state', e); }
  }, [gameState, possession, serveFirstSelection, timeouts,
      liberoDesignations, liberoSandwiches, liberoServingLocks, activeLiberoSlot,
      activeTeamId, selectedMatchId,
      opponentRoster, opponentTeamId, activeLineupId, activeOpponentLineupId,
      isOpponentExpanded, rotationalSubs]);

  const handleClearGame = () => {
    localStorage.removeItem(GAMETIME_STORAGE_KEY);
    // Clear events.stats for the selected match so season views don't retain cleared stats
    if (setEvents && selectedMatchId && selectedMatchId !== 'unselected') {
      setEvents(prev => prev.map(evt => evt.id === selectedMatchId ? { ...evt, stats: [] } : evt));
    }
    // Reset scores, stats, and rotations back to starting position (Rotation I)
    setGameState(prev => {
      // Helper: rebuild lineup so Rotation I player is at p1, II at p2, etc.
      const resetSideLineup = (
        lineup: Record<string, string>,
        numerals: Record<string, string>,
        prefix: 'p' | 'op'
      ) => {
        const numeralToPlayer: Record<string, string> = {};
        for (let i = 1; i <= 6; i++) {
          const pos = `${prefix}${i}`;
          const numeral = numerals[pos];
          if (numeral) numeralToPlayer[numeral] = lineup[pos];
        }
        const startNumerals = ['I', 'II', 'III', 'IV', 'V', 'VI'];
        const resetLineup: Record<string, string> = {};
        const resetNumerals: Record<string, string> = {};
        for (let i = 1; i <= 6; i++) {
          const pos = `${prefix}${i}`;
          resetLineup[pos] = numeralToPlayer[startNumerals[i - 1]] || '';
          resetNumerals[pos] = startNumerals[i - 1];
        }
        return { resetLineup, resetNumerals };
      };

      const { resetLineup: usLineup, resetNumerals: usNumerals } = resetSideLineup(
        prev.lineup as unknown as Record<string, string>,
        prev.numerals as unknown as Record<string, string>,
        'p'
      );
      const { resetLineup: themLineup, resetNumerals: themNumerals } = resetSideLineup(
        prev.opponentLineup as unknown as Record<string, string>,
        prev.opponentNumerals as unknown as Record<string, string>,
        'op'
      );

      return {
        ...prev,
        ourScore: 0,
        theirScore: 0,
        ourSetsWon: 0,
        theirSetsWon: 0,
        set: 1,
        stats: [],
        substitutionsUsed: { us: 0, them: 0 },
        lineup: usLineup as typeof prev.lineup,
        numerals: usNumerals as typeof prev.numerals,
        opponentLineup: themLineup as typeof prev.opponentLineup,
        opponentNumerals: themNumerals as typeof prev.opponentNumerals,
      };
    });
    setPossession('serving');
    setRallyInProgress(false);
    setTimeouts({ us: 2, them: 2 });
    // Serving locks and libero on-court state reset; designations + sandwiches persist
    setLiberoServingLocks({});
    setLiberoOnCourt({});
    setAssistPromptState(null);
    setBlockAssistState(null);
    setActiveOverlay(null);
    setPendingSubConfirms([]);
    rotSubTriggerCountsRef.current = {};
    prevLineupRef.current = null;
    setShowClearConfirm(null);
  };

  const handleClearSet = () => {
    // Remove stats for the current set only from the event record
    if (setEvents && selectedMatchId && selectedMatchId !== 'unselected') {
      setEvents(prev => prev.map(evt =>
        evt.id === selectedMatchId
          ? { ...evt, stats: (evt.stats ?? []).filter(s => s.setNumber !== gameState.set) }
          : evt
      ));
    }
    setGameState(prev => {
      // Rebuild lineup so Rotation I player is at p1, etc.
      const resetSideLineup = (
        lineup: Record<string, string>,
        numerals: Record<string, string>,
        prefix: 'p' | 'op'
      ) => {
        const numeralToPlayer: Record<string, string> = {};
        for (let i = 1; i <= 6; i++) {
          const pos = `${prefix}${i}`;
          const numeral = numerals[pos];
          if (numeral) numeralToPlayer[numeral] = lineup[pos];
        }
        const startNumerals = ['I', 'II', 'III', 'IV', 'V', 'VI'];
        const resetLineup: Record<string, string> = {};
        const resetNumerals: Record<string, string> = {};
        for (let i = 1; i <= 6; i++) {
          const pos = `${prefix}${i}`;
          resetLineup[pos] = numeralToPlayer[startNumerals[i - 1]] || '';
          resetNumerals[pos] = startNumerals[i - 1];
        }
        return { resetLineup, resetNumerals };
      };
      const { resetLineup: usLineup, resetNumerals: usNumerals } = resetSideLineup(
        prev.lineup as unknown as Record<string, string>,
        prev.numerals as unknown as Record<string, string>,
        'p'
      );
      const { resetLineup: themLineup, resetNumerals: themNumerals } = resetSideLineup(
        prev.opponentLineup as unknown as Record<string, string>,
        prev.opponentNumerals as unknown as Record<string, string>,
        'op'
      );
      return {
        ...prev,
        ourScore: 0,
        theirScore: 0,
        stats: prev.stats.filter(s => s.setNumber !== prev.set),
        substitutionsUsed: { us: 0, them: 0 },
        lineup: usLineup as typeof prev.lineup,
        numerals: usNumerals as typeof prev.numerals,
        opponentLineup: themLineup as typeof prev.opponentLineup,
        opponentNumerals: themNumerals as typeof prev.opponentNumerals,
      };
    });
    setPossession('serving');
    setRallyInProgress(false);
    setTimeouts({ us: 2, them: 2 });
    setLiberoServingLocks({});
    setLiberoOnCourt({});
    setAssistPromptState(null);
    setBlockAssistState(null);
    setActiveOverlay(null);
    setPendingSubConfirms([]);
    rotSubTriggerCountsRef.current = {};
    prevLineupRef.current = null;
    setShowClearConfirm(null);
  };

  // Load a new scheduled game, resetting scores/stats while preserving our lineup
  const switchToGame = useCallback((game: GameEvent) => {
    const matchingTeam = teams.find(t => t.type === 'opponent' && t.name.toLowerCase() === (game.opponent ?? '').toLowerCase());
    const newOpponent = matchingTeam ? matchingTeam.name : (game.opponent || '');
    setSelectedMatchId(game.id);
    setGameState(prev => ({
      ...initialGameState,
      lineup: prev.lineup,
      numerals: prev.numerals,
      opponent: newOpponent,
    }));
    setPossession('serving');
    setRallyInProgress(false);
    setTimeouts({ us: 2, them: 2 });
    setLiberoDesignations({ us: [], them: [] });
    setLiberoSandwiches({});
    setLiberoServingLocks({});
    setLiberoOnCourt({});
    setAssistPromptState(null);
    setBlockAssistState(null);
    setActiveOverlay(null);
    setIsLineupEditMode(true);
    if (matchingTeam) {
      setOpponentTeamId(matchingTeam.id);
      setOpponentRoster(players.filter(p => p.teamId === matchingTeam.id));
      const oppLineups = lineups?.filter(l => l.teamId === matchingTeam.id) || [];
      const defaultLineup = oppLineups.find(l => l.isDefault) || oppLineups[0];
      if (defaultLineup) {
        setGameState(prev => ({
          ...prev,
          opponentLineup: { op1: defaultLineup.positions.p1, op2: defaultLineup.positions.p2, op3: defaultLineup.positions.p3, op4: defaultLineup.positions.p4, op5: defaultLineup.positions.p5, op6: defaultLineup.positions.p6 },
          opponentNumerals: { op1: 'I', op2: 'II', op3: 'III', op4: 'IV', op5: 'V', op6: 'VI' },
        }));
        setActiveOpponentLineupId(defaultLineup.id);
      }
    } else {
      setOpponentTeamId(null);
      setOpponentRoster([]);
    }
    localStorage.removeItem(GAMETIME_STORAGE_KEY);
  }, [teams, players, lineups]);

  const handleSaveAndSwitch = async () => {
    if (!upcomingGameForSwitch || !currentUser) return;
    setIsSubmitting(true);
    try {
      if (selectedMatchId && selectedMatchId !== 'unselected') {
        const record = await submitMatchRecord(
          organizationId, selectedMatchId, currentUser.id, currentUser.fullName,
          { ...gameState, originalSource: gameState.originalSource ?? 'live' },
          activeTeamId || undefined,
        );
        if (setMatchRecords) {
          setMatchRecords(prev => {
            const idx = prev.findIndex(r => r.id === record.id);
            return idx >= 0 ? prev.map(r => r.id === record.id ? record : r) : [...prev, record];
          });
        }
      }
    } catch (e) {
      console.error('Save before switch failed:', e);
    } finally {
      setIsSubmitting(false);
    }
    switchToGame(upcomingGameForSwitch);
    setShowSavedGameWarning(false);
  };

  const handleDiscardAndSwitch = () => {
    switchToGame(upcomingGameForSwitch!);
    setShowSavedGameWarning(false);
  };

  // Resets a side's lineup so Rotation I player is at p1, II→p2, etc.
  const resetToRotationI = (
    lineup: Record<string, string>,
    numerals: Record<string, string>,
    prefix: 'p' | 'op'
  ) => {
    const numeralToPlayer: Record<string, string> = {};
    for (let i = 1; i <= 6; i++) {
      const pos = `${prefix}${i}`;
      const numeral = numerals[pos];
      if (numeral) numeralToPlayer[numeral] = lineup[pos];
    }
    const startNumerals = ['I', 'II', 'III', 'IV', 'V', 'VI'];
    const outLineup: Record<string, string> = {};
    const outNumerals: Record<string, string> = {};
    for (let i = 1; i <= 6; i++) {
      const pos = `${prefix}${i}`;
      outLineup[pos] = numeralToPlayer[startNumerals[i - 1]] || '';
      outNumerals[pos] = startNumerals[i - 1];
    }
    return { lineup: outLineup, numerals: outNumerals };
  };

  // In pregame edit mode: shift lineup so the chosen rotation numeral is at p1
  const handleSetStartingRotation = (targetNumeral: string) => {
    const allNumerals = ['I', 'II', 'III', 'IV', 'V', 'VI'];
    const targetIdx = allNumerals.indexOf(targetNumeral);
    if (targetIdx === -1) return;
    setGameState(prev => {
      const lineup = prev.lineup as unknown as Record<string, string>;
      const numerals = prev.numerals as unknown as Record<string, string>;
      const numeralToPlayer: Record<string, string> = {};
      for (let i = 1; i <= 6; i++) {
        const pos = `p${i}`;
        const numeral = numerals[pos];
        if (numeral) numeralToPlayer[numeral] = lineup[pos];
      }
      const rotated = [...allNumerals.slice(targetIdx), ...allNumerals.slice(0, targetIdx)];
      const newLineup: Record<string, string> = {};
      const newNumerals: Record<string, string> = {};
      for (let i = 1; i <= 6; i++) {
        const pos = `p${i}`;
        newNumerals[pos] = rotated[i - 1];
        newLineup[pos] = numeralToPlayer[rotated[i - 1]] || '';
      }
      return {
        ...prev,
        lineup: newLineup as typeof prev.lineup,
        numerals: newNumerals as typeof prev.numerals,
      };
    });
  };

  const handleSetStartingRotationThem = (targetNumeral: string) => {
    const allNumerals = ['I', 'II', 'III', 'IV', 'V', 'VI'];
    const targetIdx = allNumerals.indexOf(targetNumeral);
    if (targetIdx === -1) return;
    setGameState(prev => {
      const lineup = prev.opponentLineup as unknown as Record<string, string>;
      const numerals = prev.opponentNumerals as unknown as Record<string, string>;
      const numeralToPlayer: Record<string, string> = {};
      for (let i = 1; i <= 6; i++) {
        const pos = `op${i}`;
        const numeral = numerals[pos];
        if (numeral) numeralToPlayer[numeral] = lineup[pos];
      }
      const rotated = [...allNumerals.slice(targetIdx), ...allNumerals.slice(0, targetIdx)];
      const newLineup: Record<string, string> = {};
      const newNumerals: Record<string, string> = {};
      for (let i = 1; i <= 6; i++) {
        const pos = `op${i}`;
        newNumerals[pos] = rotated[i - 1];
        newLineup[pos] = numeralToPlayer[rotated[i - 1]] || "";
      }
      return {
        ...prev,
        opponentLineup: newLineup as typeof prev.opponentLineup,
        opponentNumerals: newNumerals as typeof prev.opponentNumerals,
      };
    });
  };

  const triggerSetInsights = async () => {
    if (!canSeeSetInsights) return;
    const setNum = gameState.set;
    const setStats = gameState.stats.filter(s => s.setNumber === setNum);
    if (setStats.length < 5) return;

    const playerMap = new Map<string, Player>(teamPlayers.map(p => [p.id, p]));

    const rotSO: Record<string, { soAttempts: number; soWon: number; bpAttempts: number; bpWon: number }> = {
      "1": { soAttempts: 0, soWon: 0, bpAttempts: 0, bpWon: 0 },
      "2": { soAttempts: 0, soWon: 0, bpAttempts: 0, bpWon: 0 },
      "3": { soAttempts: 0, soWon: 0, bpAttempts: 0, bpWon: 0 },
      "4": { soAttempts: 0, soWon: 0, bpAttempts: 0, bpWon: 0 },
      "5": { soAttempts: 0, soWon: 0, bpAttempts: 0, bpWon: 0 },
      "6": { soAttempts: 0, soWon: 0, bpAttempts: 0, bpWon: 0 },
    };
    const passMap: Record<string, { hSum: number; hN: number; pSum: number; pN: number; fSum: number; fN: number; tSum: number; tN: number }> = {};
    const serveMap: Record<string, { jump: number; float: number; jumpFloat: number; aces: number; errors: number; total: number }> = {};
    const hitMap: Record<string, { kills: number; errors: number; attempts: number }> = {};

    for (const s of setStats) {
      const rot = s.rotation;
      if (rot && rotSO[rot] && s.statType === 'break_point') {
        if (s.phase === 'receive') {
          rotSO[rot].soAttempts++;
          if (!s.flags?.includes('receive_failure')) rotSO[rot].soWon++;
        } else {
          rotSO[rot].bpAttempts++;
          rotSO[rot].bpWon++;
        }
      }
      if (s.statType === 'reception' || s.statType === 'reception_error') {
        const pid = s.playerId;
        if (!passMap[pid]) passMap[pid] = { hSum: 0, hN: 0, pSum: 0, pN: 0, fSum: 0, fN: 0, tSum: 0, tN: 0 };
        const rating = s.statType === 'reception' ? (s.passRating ?? 0) : 0;
        if ((s as any).contactType === 'hands') { passMap[pid].hSum += rating; passMap[pid].hN++; }
        else if ((s as any).contactType === 'platform') { passMap[pid].pSum += rating; passMap[pid].pN++; }
        if ((s as any).incomingServeStyle === 'float') { passMap[pid].fSum += rating; passMap[pid].fN++; }
        else if ((s as any).incomingServeStyle === 'topspin') { passMap[pid].tSum += rating; passMap[pid].tN++; }
      }
      if (s.statType === 'ace' || s.statType === 'serve_error' || s.statType === 'serve_attempt') {
        const pid = s.playerId;
        if (!serveMap[pid]) serveMap[pid] = { jump: 0, float: 0, jumpFloat: 0, aces: 0, errors: 0, total: 0 };
        serveMap[pid].total++;
        if (s.statType === 'ace') serveMap[pid].aces++;
        if (s.statType === 'serve_error') serveMap[pid].errors++;
        if (s.serveType === 'jump') serveMap[pid].jump++;
        else if (s.serveType === 'float') serveMap[pid].float++;
        else if (s.serveType === 'jump-float') serveMap[pid].jumpFloat++;
      }
      if (s.statType === 'kill' || s.statType === 'attack_error' || s.statType === 'attack_attempt') {
        const pid = s.playerId;
        if (!hitMap[pid]) hitMap[pid] = { kills: 0, errors: 0, attempts: 0 };
        hitMap[pid].attempts++;
        if (s.statType === 'kill') hitMap[pid].kills++;
        if (s.statType === 'attack_error') hitMap[pid].errors++;
      }
    }

    const currentLineup = (['p1','p2','p3','p4','p5','p6'] as const).map(slot => {
      const pid = (gameState.lineup as Record<string, string>)[slot];
      const player = playerMap.get(pid);
      return { slot, playerName: player?.name ?? 'Unknown', playerPosition: player?.position ?? 'Unknown' };
    });

    const params = {
      completedSet: setNum,
      setScore: { our: gameState.ourScore, their: gameState.theirScore },
      rotationSideout: Object.entries(rotSO).map(([rotation, d]) => ({ rotation, ...d })),
      passerData: Object.entries(passMap).map(([pid, d]) => {
        const p = playerMap.get(pid);
        return { name: p?.name ?? pid, position: p?.position ?? '', handCount: d.hN, handAvg: d.hN > 0 ? d.hSum / d.hN : 0, platformCount: d.pN, platformAvg: d.pN > 0 ? d.pSum / d.pN : 0, floatCount: d.fN, floatAvg: d.fN > 0 ? d.fSum / d.fN : 0, topspinCount: d.tN, topspinAvg: d.tN > 0 ? d.tSum / d.tN : 0 };
      }),
      serverData: Object.entries(serveMap).map(([pid, d]) => {
        const p = playerMap.get(pid);
        return { name: p?.name ?? pid, position: p?.position ?? '', attempts: d.total, aces: d.aces, errors: d.errors, jumpAttempts: d.jump, floatAttempts: d.float, jumpFloatAttempts: d.jumpFloat };
      }),
      hitterData: Object.entries(hitMap).map(([pid, d]) => {
        const p = playerMap.get(pid);
        return { name: p?.name ?? pid, position: p?.position ?? '', kills: d.kills, errors: d.errors, attempts: d.attempts };
      }),
      currentLineup,
    };

    setIsLoadingSetInsights(true);
    setSetInsights([]);
    setShowSetInsightsPanel(true);
    try {
      const tips = await generateSetInsights(params);
      setSetInsights(tips);
    } catch {
      setSetInsights([]);
    } finally {
      setIsLoadingSetInsights(false);
    }
  };

  const handleNewSet = () => {
    const nextSet = gameState.set + 1;
    // Auto-flip possession based on who served first in Set 1
    if (serveFirstSelection) {
      const usServedFirst = serveFirstSelection === 'us';
      const usServesNextSet = usServedFirst ? (nextSet % 2 === 1) : (nextSet % 2 === 0);
      const newPossession: 'serving' | 'receiving' = usServesNextSet ? 'serving' : 'receiving';
      setPossession(newPossession);
      if (newPossession === 'receiving') setShowBackRotatePrompt(true);
    }

    // Reload the original saved starting lineup so subs/rotations from the last set don't carry over
    const savedUsLineup = activeLineupId ? teamLineups.find(l => l.id === activeLineupId) : null;
    const savedThemLineup = activeOpponentLineupId ? lineups?.find(l => l.id === activeOpponentLineupId) : null;

    setGameState(prev => {
      let usLineup: typeof prev.lineup;
      let usNumerals: typeof prev.numerals;
      if (savedUsLineup) {
        usLineup = { ...savedUsLineup.positions } as typeof prev.lineup;
        usNumerals = { p1: 'I', p2: 'II', p3: 'III', p4: 'IV', p5: 'V', p6: 'VI' } as typeof prev.numerals;
      } else {
        const usReset = resetToRotationI(prev.lineup as unknown as Record<string, string>, prev.numerals as unknown as Record<string, string>, 'p');
        usLineup = usReset.lineup as typeof prev.lineup;
        usNumerals = usReset.numerals as typeof prev.numerals;
      }

      let themLineup: typeof prev.opponentLineup;
      let themNumerals: typeof prev.opponentNumerals;
      if (savedThemLineup) {
        themLineup = { op1: savedThemLineup.positions.p1, op2: savedThemLineup.positions.p2, op3: savedThemLineup.positions.p3, op4: savedThemLineup.positions.p4, op5: savedThemLineup.positions.p5, op6: savedThemLineup.positions.p6 } as typeof prev.opponentLineup;
        themNumerals = { op1: 'I', op2: 'II', op3: 'III', op4: 'IV', op5: 'V', op6: 'VI' } as typeof prev.opponentNumerals;
      } else {
        const themReset = resetToRotationI(prev.opponentLineup as unknown as Record<string, string>, prev.opponentNumerals as unknown as Record<string, string>, 'op');
        themLineup = themReset.lineup as typeof prev.opponentLineup;
        themNumerals = themReset.numerals as typeof prev.opponentNumerals;
      }

      return {
        ...prev,
        ourScore: 0,
        theirScore: 0,
        ourSetsWon: prev.ourScore > prev.theirScore ? prev.ourSetsWon + 1 : prev.ourSetsWon,
        theirSetsWon: prev.theirScore > prev.ourScore ? prev.theirSetsWon + 1 : prev.theirSetsWon,
        setHistory: [...(prev.setHistory ?? []), { our: prev.ourScore, their: prev.theirScore }],
        set: prev.set + 1,
        substitutionsUsed: { us: 0, them: 0 },
        lineup: usLineup,
        numerals: usNumerals,
        opponentLineup: themLineup,
        opponentNumerals: themNumerals,
      };
    });
    setTimeouts({ us: 2, them: 2 });
    // liberoSandwiches and liberoServingLocks intentionally preserved — both carry over for the full match
    setLiberoOnCourt({});
    setRallyInProgress(false);
    setAssistPromptState(null);
    setBlockAssistState(null);
    setActiveOverlay(null);
    setIsLineupEditMode(true);
    setShowNewSetConfirm(false);
    setUndoStack([]);
    serveTypeByOpponentRotationRef.current = {};
    rotSubTriggerCountsRef.current = {};
    prevLineupRef.current = null;
    setIncomingServeType(null);
  };

  const handleEndMatch = async () => {
    if (!currentUser) return;

    // Award the final set win. If "End Set" was tapped before "End Match", scores are already
    // reset to 0–0 so we must NOT add another set win here — only add one if scores are live.
    const finalSetScoreLive = gameState.ourScore > 0 || gameState.theirScore > 0;
    const finalGameState = {
      ...gameState,
      ourSetsWon: gameState.ourScore > gameState.theirScore ? gameState.ourSetsWon + 1 : gameState.ourSetsWon,
      theirSetsWon: gameState.theirScore > gameState.ourScore ? gameState.theirSetsWon + 1 : gameState.theirSetsWon,
      setHistory: finalSetScoreLive
        ? [...(gameState.setHistory ?? []), { our: gameState.ourScore, their: gameState.theirScore }]
        : (gameState.setHistory ?? []),
    };

    setSaveProgressFill(0);
    setIsSubmitting(true);
    // Kick off animated fill after the bar renders at 0%
    setTimeout(() => setSaveProgressFill(90), 50);
    let eventId = selectedMatchId;
    try {

      // No schedule event linked — auto-create a game entry (same as ↑Submit does)
      if (!eventId || eventId === 'unselected') {
        const today = new Date();
        const dateStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
        const autoTitle = `vs ${gameState.opponent || 'Opponent'}`;

        if (organizationId === 'dev-org-id') {
          eventId = `dev-event-${Date.now()}`;
          const newEvt: GameEvent = {
            id: eventId, organizationId, type: 'game', title: autoTitle,
            date: dateStr, time: 'TBD', location: 'TBD',
            opponent: gameState.opponent, teamId: activeTeamId || undefined,
          };
          if (setEvents) setEvents(prev => [...prev, newEvt]);
        } else {
          const { data: evtData, error: evtError } = await supabase
            .from('events')
            .insert([{
              organization_id: organizationId,
              type: 'game',
              title: autoTitle,
              date: dateStr,
              time: 'TBD',
              location: 'TBD',
              opponent: gameState.opponent || null,
              team_id: activeTeamId || null,
            }])
            .select()
            .single();
          if (evtError) throw evtError;
          eventId = evtData.id;
          if (setEvents) setEvents(prev => [...prev, mapEventFromDB(evtData)]);
        }
        setSelectedMatchId(eventId);
      }

      const record = await submitMatchRecord(
        organizationId,
        eventId,
        currentUser.id,
        currentUser.fullName,
        { ...finalGameState, originalSource: finalGameState.originalSource ?? 'live' },
        activeTeamId || undefined,
      );
      if (setMatchRecords) {
        setMatchRecords(prev => {
          const idx = prev.findIndex(r => r.id === record.id);
          return idx >= 0 ? prev.map(r => r.id === record.id ? record : r) : [...prev, record];
        });
        // Re-fetch so any other devices see a consistent state immediately
        getMatchRecords(organizationId).then(fresh => setMatchRecords(fresh)).catch(() => {});
      }
      // Only clear game state on successful save
      localStorage.removeItem(GAMETIME_STORAGE_KEY);
      setGameState(prev => ({
        ...prev,
        ourScore: 0,
        theirScore: 0,
        ourSetsWon: 0,
        theirSetsWon: 0,
        set: 1,
        stats: [],
        substitutionsUsed: { us: 0, them: 0 },
        setHistory: [],
      }));
      setPossession('serving');
      setRallyInProgress(false);
      setTimeouts({ us: 2, them: 2 });
      setLiberoServingLocks({});
      setAssistPromptState(null);
      setBlockAssistState(null);
      setActiveOverlay(null);
      setShowEndMatchConfirm(false);
      setShowNewSetConfirm(false);
      setSubmitSuccessBanner(true);
      setTimeout(() => setSubmitSuccessBanner(false), 4000);
    } catch (e: any) {
      console.error('Failed to save match on end:', e);
      const isNetworkErr = !navigator.onLine || (e instanceof TypeError && /fetch|network/i.test(e.message));
      if (isNetworkErr && eventId && eventId !== 'unselected') {
        const payload = {
          orgId: organizationId,
          eventId,
          userId: currentUser.id,
          userName: currentUser.fullName,
          gameState: { ...finalGameState, originalSource: finalGameState.originalSource ?? 'live' },
          teamId: activeTeamId || undefined,
        };
        try { localStorage.setItem(OFFLINE_SAVE_KEY, JSON.stringify(payload)); } catch {}
        setPendingOfflineSave(payload);
        setOfflineSaveBanner(true);
        setTimeout(() => setOfflineSaveBanner(false), 8000);
      } else {
        setSubmitErrorBanner(true);
        setTimeout(() => setSubmitErrorBanner(false), 5000);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

 // Commit a deferred kill rotation — called when the kill detail overlay resolves
  // (option selected, Skip tapped, or 6-second auto-timeout).
  const commitPendingKill = () => {
    if (killOverlayTimeoutRef.current) { clearTimeout(killOverlayTimeoutRef.current); killOverlayTimeoutRef.current = null; }
    if (pendingKillSideRef.current) { updateScore(pendingKillSideRef.current, 1); pendingKillSideRef.current = null; }
    pendingKillTimestampRef.current = null;
    pendingKillSelectionsRef.current = {};
    setActiveOverlay(null);
  };

  // Auto-dismiss kill detail overlay after 6 s and apply the deferred rotation
  useEffect(() => {
    if (activeOverlay?.type === 'kill') {
      if (killOverlayTimeoutRef.current) clearTimeout(killOverlayTimeoutRef.current);
      killOverlayTimeoutRef.current = setTimeout(() => { commitPendingKill(); }, 6000);
    } else {
      if (killOverlayTimeoutRef.current) { clearTimeout(killOverlayTimeoutRef.current); killOverlayTimeoutRef.current = null; }
    }
    return () => { if (killOverlayTimeoutRef.current) clearTimeout(killOverlayTimeoutRef.current); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeOverlay?.type]);

 const handleGameStat = (playerId: string, side: 'us' | 'them', type: string, detail?: string, passRating?: number) => {
    if (type !== 'Assist') pushUndoSnapshot();
    if (activeOverlay) setActiveOverlay(null);
    if (type !== 'Assist') {
        setRallyInProgress(true);
    }
    const opponentSide = side === 'us' ? 'them' : 'us';
    if (type === 'Ace') { const ts = Date.now(); const sType = (detail === 'Jump' ? 'jump' : detail === 'Float' ? 'float' : null) ?? (advToggles.serveTypeTracking ? lastServeTypeRef.current : null); recordStat('ace', playerId, undefined, undefined, undefined, ts); if (sType && advToggles.serveTypeTracking) { lastServeTypeRef.current = sType; setGameState(prev => ({ ...prev, stats: prev.stats.map(s => s.timestamp === ts ? { ...s, serveType: sType } : s) })); } updateScore(side, 1); }
    else if (type === 'Serve Error') { const ts = Date.now(); const sType = (detail === 'Jump' ? 'jump' : detail === 'Float' ? 'float' : null) ?? (advToggles.serveTypeTracking ? lastServeTypeRef.current : null); recordStat('serve_error', playerId, undefined, undefined, undefined, ts); if (sType && advToggles.serveTypeTracking) { lastServeTypeRef.current = sType; setGameState(prev => ({ ...prev, stats: prev.stats.map(s => s.timestamp === ts ? { ...s, serveType: sType } : s) })); } updateScore(opponentSide, 1); }
    else if (type === 'Kill') { const dumpFlags = pendingDumpRowRef.current ? ['dump_' + pendingDumpRowRef.current] : undefined; pendingDumpRowRef.current = null; const killTs = recordStat('kill', playerId, detail, undefined, dumpFlags); const showKillOverlay = !detail && (advToggles.killHitTypeRow || advToggles.killOutcomeRow); if (showKillOverlay) { pendingKillSideRef.current = side; pendingKillTimestampRef.current = killTs; pendingKillSelectionsRef.current = {}; } else { updateScore(side, 1); } const killLineup = side === 'us' ? gameState.lineup : gameState.opponentLineup; const killPrefix = side === 'us' ? 'p' : 'op'; const killEligibleIds = ['1','2','3','4','5','6'].map(n => { const raw = (killLineup as any)[`${killPrefix}${n}`] || ''; const { effectiveId: eid } = getEffectivePlayer(side, `${killPrefix}${n}`, raw, false); return eid; }).filter(id => id && id !== playerId); setAssistPromptState({ side, killerId: playerId, eligibleIds: killEligibleIds }); if (showKillOverlay) setActiveOverlay({ side, playerId: playerId, type: 'kill' }); }
    else if (type === 'Block' || type === 'Block Solo') { recordStat('block_solo', playerId); updateScore(side, 1); }
    else if (type === 'Attack Error') { const ts = Date.now(); const dumpFlags = pendingDumpRowRef.current ? ['dump_' + pendingDumpRowRef.current] : undefined; pendingDumpRowRef.current = null; recordStat('attack_error', playerId, undefined, undefined, dumpFlags, ts); updateScore(opponentSide, 1); if (detail) { const errTypeMap: Record<string, StatRecord['attackErrorType']> = { 'Hit Out': 'out', 'Hit in Net': 'net', 'Blocked': 'blocked', 'Lift': 'lift', 'Double': 'double', 'Under': 'under', 'Other': 'other' }; const attackErrorType = errTypeMap[detail]; if (attackErrorType) setGameState(prev => ({ ...prev, stats: prev.stats.map(s => s.timestamp === ts ? { ...s, attackErrorType } : s) })); } }
    else if (type === 'Dig') recordStat('dig', playerId);
    else if (type === 'Phase' || type === 'Reception') { const ts = recordStat('reception', playerId, detail, passRating); lastReceptionTimestampRef.current = ts; if (incomingServeType) { setGameState(prev => ({ ...prev, stats: prev.stats.map(s => s.timestamp === ts ? { ...s, incomingServeStyle: incomingServeType } : s) })); } }
    else if (type === 'Reception Error') { recordStat('reception_error', playerId, detail, passRating); updateScore(opponentSide, 1); }
    else if (type === 'Assist') {
      const flags = detail === 'front_10ft' ? ['libero_front_10ft'] : undefined;
      recordStat('assist', playerId, undefined, undefined, flags);
      setAssistPromptState(null);
    }
    else if (type === 'In Play') { const dumpFlags = pendingDumpRowRef.current ? ['dump_' + pendingDumpRowRef.current] : undefined; pendingDumpRowRef.current = null; recordStat('attack_attempt', playerId, undefined, undefined, dumpFlags); }

    // Record a break_point whenever the serving team wins a point via a rally.
    // Aces are excluded: the 'ace' stat type already counts as a serve-phase point in Stats,
    // so recording a break_point for aces would double-count them in BP%.
    const servingSide = possession === 'serving' ? 'us' : 'them';
    const scoringSide = (type === 'Attack Error' || type === 'Reception Error') ? opponentSide : side;
    if (['Kill', 'Block', 'Block Solo', 'Attack Error', 'Reception Error'].includes(type) && scoringSide === servingSide) {
      // Resolve the current server — respects libero sandwich + serving lock
      const serverSide = possession === 'serving' ? 'us' : 'them';
      const serverPos = possession === 'serving' ? 'p1' : 'op1';
      const serverRaw = possession === 'serving' ? (gameState.lineup as any).p1 : (gameState.opponentLineup as any).op1;
      const { effectiveId: serverId } = getEffectivePlayer(serverSide, serverPos, serverRaw, false);

      // Auto-record serve_attempt for the server when "Serve in Play" was not clicked.
      // rallyInProgress is still the pre-update value here (React batches state updates),
      // so false means the button was never pressed this rally.
      // 'ace' already increments serveAttempts via its own stat type, so skip it.
      if (!rallyInProgress && type !== 'Ace') {
        const ts = Date.now();
        recordStat('serve_attempt', serverId, undefined, undefined, undefined, ts);
        if (advToggles.serveTypeTracking && lastServeTypeRef.current) setGameState(prev => ({ ...prev, stats: prev.stats.map(s => s.timestamp === ts ? { ...s, serveType: lastServeTypeRef.current! } : s) }));
      }

      // Credit the player who scored for kills/blocks; credit the server for opponent errors.
      const bpPlayerId = (type === 'Attack Error' || type === 'Reception Error') ? serverId : playerId;
      recordStat('break_point', bpPlayerId);
      // Always credit the actual server for PS% (separate from who scored the point).
      recordStat('server_point', serverId);
    }
  };

  const recordStat = (statType: StatRecord['statType'], playerId: string = 'team', detail?: string, passRating?: number, flags?: string[], ts?: number): number => {
    const timestamp = ts ?? Date.now();
    const newStat: StatRecord = { playerId, statType, timestamp, setNumber: gameState.set, phase: possession === 'serving' ? 'serve' : 'receive', rotation: calculateRotation('us') };
    if (passRating !== undefined) newStat.passRating = passRating;
    if (flags?.length) newStat.flags = flags;
    setGameState(prev => ({ ...prev, stats: [...prev.stats, newStat] }));
    if (setEvents && selectedMatchId !== 'unselected') setEvents(prev => prev.map(evt => evt.id === selectedMatchId ? { ...evt, stats: [...(evt.stats || []), newStat] } : evt));
    return timestamp;
  };

  // Called by the score stepper "+" buttons — records a phase+rotation-tagged break_point so
  // the point counts toward BP% (when serving) or Sideout% (when receiving) by rotation.
  // Also records serve_attempt for the denominator so BP% and serve% are correct even in
  // stepper-only games (no individual stat taps). When the opponent scores, records the
  // appropriate denominator stat so SO% and BP% aren't inflated to 100%.
  const handleStepperPoint = (team: 'us' | 'them') => {
    pushUndoSnapshot();
    if (possession === 'serving') {
      // Resolve server for both win and loss paths while we're serving.
      const serverRaw = gameState.lineup.p1;
      const { effectiveId: serverId } = getEffectivePlayer('us', 'p1', serverRaw, false);
      if (team === 'us') {
        // We scored while serving — break point + serve attempt (denominator) + PS%
        recordStat('serve_attempt', serverId);
        recordStat('break_point', serverId);
        recordStat('server_point', serverId);
      } else {
        // They scored while we were serving — just the serve attempt for denominator
        recordStat('serve_attempt', serverId);
      }
    } else {
      // possession === 'receiving'
      if (team === 'us') {
        // We scored while receiving — sideout success
        recordStat('break_point', 'team');
      } else {
        // They scored while we were receiving — failed sideout; counts denominator only
        recordStat('break_point', 'team', undefined, undefined, ['receive_failure']);
      }
    }
    updateScore(team, 1);
  };

  const handleStepperMinus = (team: 'us' | 'them') => {
    pushUndoSnapshot();
    if (team === 'us') {
      // Remove the most recent break_point + server_point pair for this rotation
      setGameState(prev => {
        const currentRotation = calculateRotation('us');
        let stats = [...prev.stats];
        // Remove last break_point in this rotation
        const bpIdx = stats.map((s, i) => ({ s, i }))
          .reverse()
          .find(({ s }) => s.statType === 'break_point' && s.rotation === currentRotation)?.i;
        if (bpIdx !== undefined) stats.splice(bpIdx, 1);
        // Remove last server_point in this rotation
        const spIdx = stats.map((s, i) => ({ s, i }))
          .reverse()
          .find(({ s }) => s.statType === 'server_point' && s.rotation === currentRotation)?.i;
        if (spIdx !== undefined) stats.splice(spIdx, 1);
        return { ...prev, stats, ourScore: Math.max(0, prev.ourScore - 1) };
      });
    } else {
      updateScore(team, -1);
    }
  };

  const pushUndoSnapshot = () => {
    setUndoStack(prev => [
      { gameState: { ...gameState, stats: [...gameState.stats], lineup: { ...gameState.lineup }, opponentLineup: { ...gameState.opponentLineup } }, possession, liberoOnCourt: { ...liberoOnCourt }, activeLiberoSlot },
      ...prev
    ].slice(0, 10));
  };

  const confirmVoiceEvents = (events: VoiceParsedEvent[]) => {
    pushUndoSnapshot();
    events.forEach((event, i) => {
      if (event.eventType === 'substitution') {
        if (!event.playerInId || !event.playerOutId) return;
        const lineup = event.team === 'us' ? gameState.lineup : gameState.opponentLineup;
        const slot = Object.entries(lineup).find(([, id]) => id === event.playerOutId)?.[0];
        if (slot) updateLineupGeneric(event.team as 'us' | 'them', slot, event.playerInId);
      } else if (event.eventType === 'score') {
        const scoringTeam = event.team === 'us' ? 'us' : 'them';
        const pts = Math.max(1, Math.round(event.points ?? 1));
        for (let j = 0; j < pts; j++) updateScore(scoringTeam, 1);
      } else if (event.eventType === 'rotation') {
        const rotTeam = event.team === 'them' ? 'them' : 'us';
        const times = Math.max(1, Math.min(6, Math.round(event.count ?? 1)));
        for (let j = 0; j < times; j++) performRotation(rotTeam, event.direction);
      } else if (event.eventType === 'libero_toggle') {
        if (!event.liberoId) return; // couldn't resolve libero
        setLiberoOnCourt(prev => ({ ...prev, [event.liberoId]: event.targetState === 'on_court' }));
      } else if (event.eventType === 'possession_flip') {
        setPossession(event.possession);
      } else if (event.eventType === 'timeout') {
        const side = event.team === 'us' ? 'us' : 'them';
        setTimeouts(prev => ({ ...prev, [side]: Math.max(0, prev[side] - 1) }));
      } else if (event.eventType === 'stat' && event.resolvedPlayerId) {
        const passRating = event.passQuality === 'perfect' ? 3 : event.passQuality === 'good' ? 2 : event.passQuality === 'poor' ? 1 : undefined;
        const ts = Date.now() + i; // offset ensures unique timestamps within batch
        recordStat(event.statType, event.resolvedPlayerId, undefined, passRating, undefined, ts);
        // Auto-score for terminal stats (more reliable than asking Gemini to emit a paired score event)
        const t = event.team === 'us' ? 'us' : 'them';
        const opp = t === 'us' ? 'them' : 'us';
        if (['kill', 'ace', 'block_solo', 'block_assist'].includes(event.statType)) updateScore(t, 1);
        else if (['serve_error', 'attack_error', 'reception_error'].includes(event.statType)) updateScore(opp, 1);
        // Patch voice-only metadata onto the stat
        const hasMeta = event.setLocation || event.attackLocation || event.attackType ||
          event.serveType || event.serveTypeReceived || event.contactType || event.serveZone;
        if (hasMeta) {
          setGameState(prev => ({
            ...prev,
            stats: prev.stats.map(s => s.timestamp !== ts ? s : {
              ...s,
              source: 'voice' as const,
              ...(event.setLocation       && { setLocation: event.setLocation }),
              ...(event.attackLocation    && { attackLocation: event.attackLocation }),
              ...(event.attackType        && { attackType: event.attackType }),
              ...(event.serveType         && { serveType: event.serveType }),
              ...(event.serveTypeReceived && { incomingServeStyle: event.serveTypeReceived }),
              ...(event.contactType       && { contactType: event.contactType }),
              ...(event.serveZone         && { serveZone: event.serveZone }),
            }),
          }));
        }
      }
    });
    clearPending();

    // If a kill (our team) was confirmed but no assist in the same batch, prompt for setter assist
    const hasOurKill = events.some(e => e.eventType === 'stat' && e.statType === 'kill' && e.team === 'us' && e.resolvedPlayerId);
    const hasAssist  = events.some(e => e.eventType === 'stat' && e.statType === 'assist' && e.team === 'us');
    if (hasOurKill && !hasAssist) {
      const killEvent = events.find((e: any) => e.eventType === 'stat' && e.statType === 'kill' && e.team === 'us' && e.resolvedPlayerId);
      const setter = teamPlayers.find(p => p.position === 'Setter' && Object.values(gameState.lineup).includes(p.id));
      if (setter && (killEvent as any)?.resolvedPlayerId) {
        setAssistPromptState({ side: 'us', killerId: (killEvent as any).resolvedPlayerId, eligibleIds: [setter.id] });
      }
    }
  };

  const handleUndo = () => {
    if (undoStack.length === 0) return;
    const [snapshot, ...rest] = undoStack;
    setGameState(snapshot.gameState);
    setPossession(snapshot.possession);
    setLiberoOnCourt(snapshot.liberoOnCourt);
    setActiveLiberoSlot(snapshot.activeLiberoSlot ?? 0);
    setUndoStack(rest);
    setShowUndoPanel(false);
    setPendingStat(null);
  };

  const handleDeleteIndividualStat = (timestamp: number) => {
    setGameState(prev => ({ ...prev, stats: prev.stats.filter(s => s.timestamp !== timestamp) }));
  };

  const handleUndoToIndex = (index: number) => {
    const snapshot = undoStack[index];
    setGameState(snapshot.gameState);
    setPossession(snapshot.possession);
    setLiberoOnCourt(snapshot.liberoOnCourt);
    setActiveLiberoSlot(snapshot.activeLiberoSlot ?? 0);
    setUndoStack(undoStack.slice(index + 1));
    setShowUndoPanel(false);
    setPendingStat(null);
  };

  // ── Quick Stat Bar ─────────────────────────────────────────────────────────
  const QUICK_STATS = [
    { id: 'srv_att',    label: 'SRV ATT', statType: 'serve_attempt' as StatRecord['statType'], Icon: Play,        bgClass: 'bg-slate-500',  textClass: 'text-white',      activeBg: 'bg-slate-400',  ring: 'ring-slate-400/60'  },
    { id: 'ace',        label: 'Ace',     statType: 'ace' as StatRecord['statType'],        Icon: Zap,         bgClass: 'bg-green-600',  textClass: 'text-white',      activeBg: 'bg-green-500',  ring: 'ring-green-400/60'  },
    { id: 'sr',         label: 'SR',      statType: 'reception' as StatRecord['statType'],  Icon: ArrowLeft,   bgClass: 'bg-sky-700',    textClass: 'text-white',      activeBg: 'bg-sky-500',    ring: 'ring-sky-400/60'    },
    { id: 'dig',        label: 'Dig',     statType: 'dig' as StatRecord['statType'],        Icon: Shovel,      bgClass: 'bg-amber-100',  textClass: 'text-amber-800',  activeBg: 'bg-amber-200',  ring: 'ring-amber-400/60'  },
    { id: 'block_solo', label: 'Block',   statType: 'block_solo' as StatRecord['statType'], Icon: Shield,      bgClass: 'bg-indigo-100', textClass: 'text-indigo-800', activeBg: 'bg-indigo-200', ring: 'ring-indigo-400/60' },
    { id: 'kill',       label: 'Kill',    statType: 'kill' as StatRecord['statType'],       Icon: Swords,      bgClass: 'bg-green-100',  textClass: 'text-green-800',  activeBg: 'bg-green-200',  ring: 'ring-green-400/60'  },
    { id: 'assist',     label: 'Assist',  statType: 'assist' as StatRecord['statType'],     Icon: HelpingHand, bgClass: 'bg-purple-100', textClass: 'text-purple-800', activeBg: 'bg-purple-200', ring: 'ring-purple-400/60' },
    { id: 'error',      label: 'Error',   statType: 'error' as StatRecord['statType'],      Icon: XCircle,     bgClass: 'bg-red-100',    textClass: 'text-red-800',    activeBg: 'bg-red-200',    ring: 'ring-red-400/60'    },
  ] as const;

  const STAT_TYPE_LABELS: Partial<Record<StatRecord['statType'], string>> = {
    kill: 'Kill', attack_error: 'Attack Error', ace: 'Ace', serve_error: 'Serve Error',
    serve_attempt: 'Serve Attempt', reception: 'Pass', reception_error: 'Pass Error',
    dig: 'Dig', block_solo: 'Block Solo', block_assist: 'Block Assist', assist: 'Assist',
    error: 'Error', break_point: 'Break Point', attack_attempt: 'Attack Attempt',
  };

  const STAT_TYPE_COLORS: Partial<Record<StatRecord['statType'], { bg: string; text: string; border: string }>> = {
    kill:            { bg: 'bg-green-100',   text: 'text-green-800',   border: 'border-green-300' },
    attack_attempt:  { bg: 'bg-green-50',    text: 'text-green-700',   border: 'border-green-200' },
    attack_error:    { bg: 'bg-red-100',     text: 'text-red-800',     border: 'border-red-300' },
    ace:             { bg: 'bg-green-600',   text: 'text-white',       border: 'border-green-500' },
    serve_attempt:   { bg: 'bg-slate-200',   text: 'text-slate-700',   border: 'border-slate-300' },
    serve_error:     { bg: 'bg-red-100',     text: 'text-red-800',     border: 'border-red-300' },
    reception:       { bg: 'bg-sky-100',     text: 'text-sky-800',     border: 'border-sky-300' },
    reception_error: { bg: 'bg-red-100',     text: 'text-red-700',     border: 'border-red-200' },
    dig:             { bg: 'bg-amber-100',   text: 'text-amber-800',   border: 'border-amber-300' },
    dig_error:       { bg: 'bg-red-100',     text: 'text-red-700',     border: 'border-red-200' },
    block_solo:      { bg: 'bg-indigo-100',  text: 'text-indigo-800',  border: 'border-indigo-300' },
    block_assist:    { bg: 'bg-indigo-100',  text: 'text-indigo-800',  border: 'border-indigo-300' },
    block_error:     { bg: 'bg-indigo-50',   text: 'text-indigo-600',  border: 'border-indigo-200' },
    assist:          { bg: 'bg-purple-100',  text: 'text-purple-800',  border: 'border-purple-300' },
    error:           { bg: 'bg-red-100',     text: 'text-red-800',     border: 'border-red-300' },
    break_point:     { bg: 'bg-blue-100',    text: 'text-blue-800',    border: 'border-blue-200' },
    server_point:    { bg: 'bg-blue-100',    text: 'text-blue-700',    border: 'border-blue-200' },
    rotation_point:  { bg: 'bg-slate-100',   text: 'text-slate-600',   border: 'border-slate-200' },
    set_played:      { bg: 'bg-slate-100',   text: 'text-slate-600',   border: 'border-slate-200' },
  };

  const handleStatBarTap = (statConfig: typeof QUICK_STATS[number]) => {
    // Flash the bar button briefly
    setStatBarFlash(statConfig.id);
    setTimeout(() => setStatBarFlash(null), 400);

    // SR opens the inline 0–3 grade picker
    if (statConfig.id === 'sr') {
      setStatBarSRPicker(true);
      return;
    }

    // Block and Error require sub-type selection before assignment
    if (statConfig.id === 'block_solo') {
      setStatBarSubTypePicker({ kind: 'block' });
      return;
    }
    if (statConfig.id === 'error') {
      setStatBarErrorPicker(true);
      return;
    }

    // All other stats: record immediately and enter single-player assignment
    pushUndoSnapshot();
    const ts = recordStat(statConfig.statType, 'team');
    setPendingStat({ statType: statConfig.statType, timestamp: ts, flashBg: statConfig.activeBg });
    switch (statConfig.statType) {
      case 'ace':
        updateScore('us', 1);
        // ace already increments totalPointsOnServe via its stat type — no break_point needed
        // Tag with last known serve type so serve breakdown stays accurate
        if (advToggles.serveTypeTracking && lastServeTypeRef.current) {
          setGameState(prev => ({ ...prev, stats: prev.stats.map(s => s.timestamp === ts ? { ...s, serveType: lastServeTypeRef.current! } : s) }));
        }
        break;
      case 'kill':
        updateScore('us', 1);
        if (possession === 'serving') {
          // Mirror handleGameStat: auto-record serve_attempt + break_point for serve-phase kill
          const killServerRaw = (gameState.lineup as any).p1;
          const { effectiveId: killServerId } = getEffectivePlayer('us', 'p1', killServerRaw, false);
          if (!rallyInProgress) recordStat('serve_attempt', killServerId || 'team');
          recordStat('break_point', 'team');
          recordStat('server_point', killServerId || 'team');
        }
        break;
      case 'serve_attempt':
        // Tag with last known serve type (mirrors what stepper-based serve_attempts do)
        if (advToggles.serveTypeTracking && lastServeTypeRef.current) {
          setGameState(prev => ({ ...prev, stats: prev.stats.map(s => s.timestamp === ts ? { ...s, serveType: lastServeTypeRef.current! } : s) }));
        }
        break;
      // dig, assist — no point scored
    }
  };

  const handleStatBarSubType = (choice: string) => {
    setStatBarSubTypePicker(null);
    setStatBarErrorPicker(false);
    pushUndoSnapshot();
    switch (choice) {
      case 'block_solo': {
        const ts = recordStat('block_solo', 'team');
        setPendingStat({ statType: 'block_solo', timestamp: ts, flashBg: 'bg-indigo-200' });
        updateScore('us', 1);
        if (possession === 'serving') {
          const bsServerRaw = (gameState.lineup as any).p1;
          const { effectiveId: bsServerId } = getEffectivePlayer('us', 'p1', bsServerRaw, false);
          if (!rallyInProgress) recordStat('serve_attempt', bsServerId || 'team');
          recordStat('break_point', 'team');
          recordStat('server_point', bsServerId || 'team');
        }
        break;
      }
      case 'block_assist': {
        // Score immediately; record per-player stats as each blocker is tapped
        updateScore('us', 1);
        if (possession === 'serving') {
          const baServerRaw = (gameState.lineup as any).p1;
          const { effectiveId: baServerId } = getEffectivePlayer('us', 'p1', baServerRaw, false);
          if (!rallyInProgress) recordStat('serve_attempt', baServerId || 'team');
          recordStat('break_point', 'team');
          recordStat('server_point', baServerId || 'team');
        }
        setMultiAssignedIds([]);
        setPendingStat({ statType: 'block_assist', timestamp: Date.now(), flashBg: 'bg-purple-200', multiAssign: true });
        break;
      }
      case 'serve_error': {
        const ts = recordStat('serve_error', 'team');
        setPendingStat({ statType: 'serve_error', timestamp: ts, flashBg: 'bg-red-200' });
        updateScore('them', 1);
        break;
      }
      case 'attack_error': {
        const ts = recordStat('attack_error', 'team');
        setPendingStat({ statType: 'attack_error', timestamp: ts, flashBg: 'bg-red-200' });
        updateScore('them', 1);
        break;
      }
      case 'reception_error': {
        const ts = recordStat('reception_error', 'team');
        setPendingStat({ statType: 'reception_error', timestamp: ts, flashBg: 'bg-red-200' });
        updateScore('them', 1);
        break;
      }
      case 'error': {
        const ts = recordStat('error', 'team');
        setPendingStat({ statType: 'error', timestamp: ts, flashBg: 'bg-red-200' });
        updateScore('them', 1);
        break;
      }
    }
  };

  const SR_GRADES = [
    { grade: 0, label: 'Error', bg: 'bg-red-500',     hover: 'hover:bg-red-400',     border: 'border-red-400',     ring: 'ring-red-400/50'     },
    { grade: 1, label: 'Poor',  bg: 'bg-orange-500',  hover: 'hover:bg-orange-400',  border: 'border-orange-400',  ring: 'ring-orange-400/50'  },
    { grade: 2, label: 'Good',  bg: 'bg-yellow-500',  hover: 'hover:bg-yellow-400',  border: 'border-yellow-400',  ring: 'ring-yellow-400/50'  },
    { grade: 3, label: 'Perf',  bg: 'bg-emerald-500', hover: 'hover:bg-emerald-400', border: 'border-emerald-400', ring: 'ring-emerald-400/50' },
  ] as const;

  const handleStatBarSRGrade = (grade: number) => {
    setStatBarSRPicker(false);
    pushUndoSnapshot();
    const isError = grade === 0;
    const statType: StatRecord['statType'] = isError ? 'reception_error' : 'reception';
    const ts = recordStat(statType, 'team', undefined, isError ? undefined : grade);
    const flashColors = ['bg-red-200', 'bg-orange-200', 'bg-yellow-200', 'bg-emerald-200'];
    setPendingStat({ statType, timestamp: ts, flashBg: flashColors[grade] });
    if (isError) updateScore('them', 1);
    // Always tag the stat with the current incoming serve type — applies even without
    // the serveReceiveDetail overlay toggle (e.g. defaultOpponentServesFloat setting).
    if (incomingServeType) {
      setGameState(prev => ({ ...prev, stats: prev.stats.map(s => s.timestamp === ts ? { ...s, incomingServeStyle: incomingServeType } : s) }));
    }
    if (advToggles.serveReceiveDetail) {
      if (statBarSRDetailTimerRef.current) clearTimeout(statBarSRDetailTimerRef.current);
      if (statBarSRDetailFadeRef.current) clearTimeout(statBarSRDetailFadeRef.current);
      setStatBarSRDetail({ timestamp: ts, serveStyle: incomingServeType, contactType: null, fading: false });
      statBarSRDetailTimerRef.current = setTimeout(() => {
        setStatBarSRDetail(prev => prev ? { ...prev, fading: true } : null);
        statBarSRDetailFadeRef.current = setTimeout(() => setStatBarSRDetail(null), 400);
      }, 5000);
    }
  };

  const handleAssignStat = (playerId: string) => {
    if (!pendingStat || !playerId || assignmentFlash) return;
    const { timestamp, flashBg, multiAssign } = pendingStat;

    if (multiAssign) {
      // Block assist: record a fresh stat for each tapped player, stay in assignment mode
      recordStat('block_assist', playerId);
      setMultiAssignedIds(prev => [...prev, playerId]);
      setAssignmentFlash({ playerId, flashBg });
      setTimeout(() => setAssignmentFlash(null), 400);
      return;
    }

    // Re-tag the stat from 'team' to the tapped player
    setGameState(prev => ({
      ...prev,
      stats: prev.stats.map(s => s.timestamp === timestamp ? { ...s, playerId } : s),
    }));
    if (setEvents && selectedMatchId !== 'unselected') {
      setEvents(prev => prev.map(evt => evt.id === selectedMatchId
        ? { ...evt, stats: (evt.stats || []).map((s: StatRecord) => s.timestamp === timestamp ? { ...s, playerId } : s) }
        : evt));
    }
    // Color-flash the player card; keep assignment mode (name/photo) visible until flash ends
    setAssignmentFlash({ playerId, flashBg });
    setTimeout(() => {
      setAssignmentFlash(null);
      setPendingStat(null);
    }, 600);
  };
  // ───────────────────────────────────────────────────────────────────────────

  // Aggregate season stats per player from all historical events.
  // Mirrors Stats.tsx: prefer match-of-record stats over inline evt.stats when available.
  const seasonPlayerStats = useMemo(() => {
    const m: Record<string, { kills: number; aErr: number; aAtt: number; aces: number; sErr: number; sAtt: number; rcv: number; rcvSum: number; ast: number; blocks: number; digs: number; setsPlayed: number; serverPoints: number; rotationPoints: number }> = {};
    const init = (id: string) => { if (!m[id]) m[id] = { kills:0, aErr:0, aAtt:0, aces:0, sErr:0, sAtt:0, rcv:0, rcvSum:0, ast:0, blocks:0, digs:0, setsPlayed:0, serverPoints:0, rotationPoints:0 }; };
    for (const evt of events) {
      const record = matchRecords.find(r => r.eventId === evt.id && r.isRecord);
      const statsToUse = record?.gameState.stats?.length ? record.gameState.stats : (evt.stats || []);
      for (const s of statsToUse) {
        init(s.playerId);
        if (s.statType === 'kill') m[s.playerId].kills++;
        else if (s.statType === 'attack_error') m[s.playerId].aErr++;
        else if (s.statType === 'attack_attempt') m[s.playerId].aAtt++;
        else if (s.statType === 'ace') { m[s.playerId].aces++; m[s.playerId].sAtt++; }
        else if (s.statType === 'serve_error') { m[s.playerId].sErr++; m[s.playerId].sAtt++; }
        else if (s.statType === 'serve_attempt') m[s.playerId].sAtt++;
        else if (s.statType === 'reception') { m[s.playerId].rcv++; m[s.playerId].rcvSum += (s.passRating || 0); }
        else if (s.statType === 'assist') m[s.playerId].ast++;
        else if (s.statType === 'block_solo' || s.statType === 'block_assist') m[s.playerId].blocks++;
        else if (s.statType === 'dig') m[s.playerId].digs++;
        else if (s.statType === 'server_point') m[s.playerId].serverPoints++;
        else if (s.statType === 'rotation_point') m[s.playerId].rotationPoints++;
      }
      // Derive sets played from match record: ourSetsWon + theirSetsWon = completed sets
      if (record) {
        const completedSets = Math.max((record.gameState.ourSetsWon || 0) + (record.gameState.theirSetsWon || 0), 1);
        const matchPlayerIds = new Set(statsToUse.map(s => s.playerId));
        for (const pid of matchPlayerIds) {
          if (m[pid]) m[pid].setsPlayed += completedSets;
        }
      }
    }
    return m;
  }, [events, matchRecords]);

  // Float vs jump serve breakdown per player (season)
  const seasonServeByType = useMemo(() => {
    const m: Record<string, { float: { att: number; err: number }; jump: { att: number; err: number } }> = {};
    const init = (id: string) => { if (!m[id]) m[id] = { float: { att: 0, err: 0 }, jump: { att: 0, err: 0 } }; };
    for (const evt of events) {
      const recordStats = matchRecords.find(r => r.eventId === evt.id && r.isRecord)?.gameState.stats;
      const statsToUse = recordStats?.length ? recordStats : (evt.stats || []);
      for (const s of statsToUse) {
        if (!s.serveType) continue;
        init(s.playerId);
        const isJump = s.serveType === 'jump' || s.serveType === 'jump-float';
        const bucket = isJump ? m[s.playerId].jump : m[s.playerId].float;
        if (['ace', 'serve_error', 'serve_attempt'].includes(s.statType)) bucket.att++;
        if (s.statType === 'serve_error') bucket.err++;
      }
    }
    return m;
  }, [events, matchRecords]);

  const buildSuggestionsContext = useCallback((): GameSuggestionsRequest => {
    const gs = gameState.stats;
    // Per-player game stats
    const gm: Record<string, { kills:number; aErr:number; aAtt:number; aces:number; sErr:number; sAtt:number; rcv:number; rcvSum:number; ast:number }> = {};
    const initG = (id: string) => { if (!gm[id]) gm[id] = { kills:0, aErr:0, aAtt:0, aces:0, sErr:0, sAtt:0, rcv:0, rcvSum:0, ast:0 }; };
    for (const s of gs) {
      initG(s.playerId);
      if (s.statType === 'kill') gm[s.playerId].kills++;
      else if (s.statType === 'attack_error') gm[s.playerId].aErr++;
      else if (s.statType === 'attack_attempt') gm[s.playerId].aAtt++;
      else if (s.statType === 'ace') { gm[s.playerId].aces++; gm[s.playerId].sAtt++; }
      else if (s.statType === 'serve_error') { gm[s.playerId].sErr++; gm[s.playerId].sAtt++; }
      else if (s.statType === 'serve_attempt') gm[s.playerId].sAtt++;
      else if (s.statType === 'reception') { gm[s.playerId].rcv++; gm[s.playerId].rcvSum += (s.passRating || 0); }
      else if (s.statType === 'assist') gm[s.playerId].ast++;
    }
    // Current server
    const serverRawId = (gameState.lineup as any).p1;
    const { effectiveId: serverEffId } = getEffectivePlayer('us', 'p1', serverRawId, false);
    const serverPlayerId = serverEffId || serverRawId || '';
    const serverPlayer = teamPlayers.find(p => p.id === serverPlayerId);
    const sgame = gm[serverPlayerId];
    const sseason = seasonPlayerStats[serverPlayerId];
    const serverServes = gs.filter(s => s.playerId === serverPlayerId && ['ace','serve_error','serve_attempt'].includes(s.statType));
    const typeBreak = serverServes.filter(s => s.serveType).reduce((a,s) => { a[s.serveType!]=(a[s.serveType!]||0)+1; return a; }, {} as Record<string,number>);
    const zoneBreak = serverServes.filter(s => s.serveZone).reduce((a,s) => { a[s.serveZone!]=(a[s.serveZone!]||0)+1; return a; }, {} as Record<number,number>);
    // Front row (p2/p3/p4)
    const frontRow = (['p2','p3','p4'] as const).map(pos => {
      const rawId = (gameState.lineup as any)[pos];
      const { effectiveId } = getEffectivePlayer('us', pos, rawId, false);
      const pid = effectiveId || rawId || '';
      if (!pid) return null;
      const pl = teamPlayers.find(p => p.id === pid);
      const g = gm[pid]; const ss = seasonPlayerStats[pid];
      return { name: pl?.name || pid, position: pl?.position || 'Unknown', gameKills: g?.kills||0, gameErrors: g?.aErr||0, gameAttempts: g?.aAtt||0, seasonKills: ss?.kills||0, seasonErrors: ss?.aErr||0, seasonAttempts: ss?.aAtt||0 };
    }).filter((p): p is NonNullable<typeof p> => p !== null && p.name !== p.name.length.toString());
    // Setter
    const setterPlayer = teamPlayers.find(p => p.position === 'Setter');
    const setterPos = setterPlayer ? Object.entries(gameState.lineup).find(([,v]) => v === setterPlayer.id)?.[0] : null;
    const setter = setterPlayer ? { name: setterPlayer.name, inFrontRow: ['p2','p3','p4'].includes(setterPos||''), gameAssists: gm[setterPlayer.id]?.ast||0, seasonAssists: seasonPlayerStats[setterPlayer.id]?.ast||0 } : null;
    // Team metrics
    const kills = gs.filter(s=>s.statType==='kill').length;
    const aErrs = gs.filter(s=>s.statType==='attack_error').length;
    const aAtts = gs.filter(s=>s.statType==='attack_attempt').length;
    const totalAtt = kills+aErrs+aAtts;
    const aces = gs.filter(s=>s.statType==='ace').length;
    const sErrs = gs.filter(s=>s.statType==='serve_error').length;
    const sAtts = gs.filter(s=>['ace','serve_error','serve_attempt'].includes(s.statType)).length;
    const rcvs = gs.filter(s=>s.statType==='reception'); const rcvErr = gs.filter(s=>s.statType==='reception_error').length;
    const rcvSum = rcvs.reduce((a,s)=>a+(s.passRating||0),0);
    const receivePts = gs.filter(s=>s.phase==='receive'&&['kill','block_solo','block_assist'].includes(s.statType)).length;
    const receiveOpp = rcvs.length + rcvErr;
    // Opponent data
    const opData = opponentRoster && opponentRoster.length > 0 ? {
      roster: opponentRoster.map(p => ({ name: p.name, position: 'position' in p ? (p as Player).position : (p as SimplePlayer).position, jerseyNumber: 'jerseyNumber' in p ? (p as Player).jerseyNumber : (p as SimplePlayer).number })),
      passStats: (() => {
        const acc: Record<string,{sum:number;cnt:number}> = {};
        for (const s of gs.filter(s=>s.statType==='reception'&&opponentRoster.some(op=>op.id===s.playerId))) {
          const op = opponentRoster.find(p=>p.id===s.playerId); if (!op) continue;
          if (!acc[op.name]) acc[op.name]={sum:0,cnt:0};
          acc[op.name].sum+=(s.passRating||0); acc[op.name].cnt++;
        }
        return Object.entries(acc).map(([name,{sum,cnt}])=>({ name, avgPassRating: sum/cnt }));
      })(),
    } : null;
    return {
      possession,
      ourScore: gameState.ourScore, theirScore: gameState.theirScore,
      set: gameState.set, currentRotation: parseInt(calculateRotation('us'))||1,
      server: { name: serverPlayer?.name||'Server', position: serverPlayer?.position||'Unknown', gameAces: sgame?.aces||0, gameErrors: sgame?.sErr||0, gameAttempts: sgame?.sAtt||0, seasonAces: sseason?.aces||0, seasonErrors: sseason?.sErr||0, seasonAttempts: sseason?.sAtt||0, serveTypeBreakdown: Object.entries(typeBreak).map(([t,c])=>`${t}: ${c}`).join(', ')||'No data', zoneBreakdown: Object.entries(zoneBreak).map(([z,c])=>`Zone ${z}: ${c}`).join(', ')||'No data' },
      frontRow,
      setter,
      teamMetrics: { hitEfficiency: totalAtt>0?(kills-aErrs)/totalAtt:0, serveAceRate: sAtts>0?aces/sAtts:0, serveErrorRate: sAtts>0?sErrs/sAtts:0, sideoutPct: receiveOpp>0?receivePts/receiveOpp:0, receptionAvg: rcvs.length>0?rcvSum/rcvs.length:0, totalStats: gs.length },
      opponentData: opData,
    };
  }, [gameState, possession, teamPlayers, opponentRoster, seasonPlayerStats]);

  const hasEnoughData = useMemo(() => {
    if (gameState.stats.length >= 10) return true;
    // Also OK if season history exists for players in current lineup
    const lineupIds = Object.values(gameState.lineup).filter(Boolean);
    return lineupIds.some(id => seasonPlayerStats[id] && (seasonPlayerStats[id].kills + seasonPlayerStats[id].aces + seasonPlayerStats[id].sAtt) > 0);
  }, [gameState.stats.length, gameState.lineup, seasonPlayerStats]);

  const isAISuggestionStale = useMemo(() =>
    aiSuggestions.length > 0 && gameState.stats.length >= lastAIStatCount + 5,
    [aiSuggestions.length, gameState.stats.length, lastAIStatCount]
  );

  const handleGenerateSuggestions = async () => {
    if (!hasEnoughData || isLoadingAI) return;
    setIsLoadingAI(true);
    try {
      const context = buildSuggestionsContext();
      const suggestions = await generateGameSuggestions(context);
      setAiSuggestions(suggestions);
      setLastAIStatCount(gameState.stats.length);
    } catch (e) {
      console.error('AI suggestions error:', e);
    } finally {
      setIsLoadingAI(false);
    }
  };

  const findPositionForPlayer = (playerId: string, side: 'us' | 'them'): string => {
    const lineup = side === 'us' ? gameState.lineup : gameState.opponentLineup;
    const entry = Object.entries(lineup).find(([k, v]) => v === playerId);
    return entry ? entry[0] : '';
  };

  const calculateRotation = (side: 'us' | 'them'): string => {
    const lineup = side === 'us' ? gameState.lineup : gameState.opponentLineup;
    const roster = side === 'us' ? teamPlayers : opponentRoster;
    for (const [key, id] of Object.entries(lineup)) { if (!id) continue; const p = roster.find(r => r.id === id); if (p?.position === 'Setter') return key.replace('p', '').replace('op', ''); }
    return "1";
  };

  const handleServeAction = (action: 'Ace' | 'Err', serveType: 'Jump' | 'Float' | null) => {
    if (serveType) lastServeTypeRef.current = serveType === 'Jump' ? 'jump' : 'float';
    setAssistPromptState(null);
    const servingSide = possession === 'serving' ? 'us' : 'them';
    const rawId = servingSide === 'us' ? (gameState.lineup as any).p1 : (gameState.opponentLineup as any).op1;
    const { effectiveId } = getEffectivePlayer(servingSide, servingSide === 'us' ? 'p1' : 'op1', rawId, false);
    const serverId = effectiveId || rawId;
    if (action === 'Ace') handleGameStat(serverId, servingSide, 'Ace', serveType); else handleGameStat(serverId, servingSide, 'Serve Error', serveType);
    setRallyInProgress(false);
  };

  const getBlockAssistTeammates = (side: 'us' | 'them', blockerId: string) => {
    const frontRowPositions = side === 'us' ? ['p2', 'p3', 'p4'] : ['op2', 'op3', 'op4'];
    const lineup = side === 'us' ? gameState.lineup : gameState.opponentLineup;
    const roster = side === 'us' ? teamPlayers : opponentRoster;
    return frontRowPositions.map(pos => {
      const rawId = (lineup as any)[pos];
      const { effectiveId } = getEffectivePlayer(side, pos, rawId, false);
      if (!effectiveId || effectiveId === blockerId) return null;
      const player = roster.find(p => p.id === effectiveId);
      if (!player) return null;
      const number = ('jerseyNumber' in player) ? (player as Player).jerseyNumber : (player as SimplePlayer).number;
      return { id: effectiveId, name: player.name, number };
    }).filter(Boolean) as { id: string, name: string, number: string }[];
  };

  const handleConfirmBlockAssist = (overrideParticipants?: string[]) => {
    if (!blockAssistState) return;
    const { side, blockerId, selectedTeammateIds } = blockAssistState;
    const allParticipants = overrideParticipants || [blockerId, ...selectedTeammateIds];
    allParticipants.forEach(pid => recordStat('block_assist', pid));
    updateScore(side, 1);
    // Record one break_point for the serving team when they win via block assist.
    const servingSide = possession === 'serving' ? 'us' : 'them';
    if (side === servingSide) {
      const baSide = possession === 'serving' ? 'us' : 'them';
      const baPos = possession === 'serving' ? 'p1' : 'op1';
      const baRaw = possession === 'serving' ? (gameState.lineup as any).p1 : (gameState.opponentLineup as any).op1;
      const { effectiveId: baServerId } = getEffectivePlayer(baSide, baPos, baRaw, false);
      if (!rallyInProgress) {
        recordStat('serve_attempt', baServerId);
      }
      recordStat('break_point', blockerId);
      recordStat('server_point', baServerId);
    }
    setBlockAssistState(null);
  };

  const handleOpponentSelection = (val: string) => {
    if (val === 'NEW') {
      setIsAddingOpponent(true);
    } else if (val === 'Guest') {
      // Clear / reset to no opponent
      setIsAddingOpponent(false);
      const guestUsReset = resetToRotationI(gameState.lineup as unknown as Record<string, string>, gameState.numerals as unknown as Record<string, string>, 'p');
      const guestThemReset = resetToRotationI(gameState.opponentLineup as unknown as Record<string, string>, gameState.opponentNumerals as unknown as Record<string, string>, 'op');
      setGameState(prev => ({ ...prev, opponent: 'Guest', stats: [], ourScore: 0, theirScore: 0, ourSetsWon: 0, theirSetsWon: 0, set: 1, substitutionsUsed: { us: 0, them: 0 }, lineup: guestUsReset.lineup as typeof prev.lineup, numerals: guestUsReset.numerals as typeof prev.numerals, opponentLineup: guestThemReset.lineup as typeof prev.opponentLineup, opponentNumerals: guestThemReset.numerals as typeof prev.opponentNumerals }));
      setOpponentTeamId(null);
      setOpponentRoster([]);
      setActiveOpponentLineupId(null);
      setSelectedMatchId('unselected');
    } else if (val.startsWith('schedule:')) {
      // A schedule game was selected — carries match context for stat recording
      const eventId = val.slice('schedule:'.length);
      const event = availableMatches.find(e => e.id === eventId);
      if (!event) return;
      setIsAddingOpponent(false);
      setSelectedMatchId(event.id);
      const opponentName = event.opponent || 'Guest';
      const schedUsReset = resetToRotationI(gameState.lineup as unknown as Record<string, string>, gameState.numerals as unknown as Record<string, string>, 'p');
      const schedThemReset = resetToRotationI(gameState.opponentLineup as unknown as Record<string, string>, gameState.opponentNumerals as unknown as Record<string, string>, 'op');
      setGameState(prev => ({ ...prev, opponent: opponentName, stats: [], ourScore: 0, theirScore: 0, ourSetsWon: 0, theirSetsWon: 0, set: 1, substitutionsUsed: { us: 0, them: 0 }, lineup: schedUsReset.lineup as typeof prev.lineup, numerals: schedUsReset.numerals as typeof prev.numerals, opponentLineup: schedThemReset.lineup as typeof prev.opponentLineup, opponentNumerals: schedThemReset.numerals as typeof prev.opponentNumerals }));
      // Try to link a registered team for roster/lineup access
      const matchingTeam = teams.find(t =>
        t.type === 'opponent' &&
        t.name.toLowerCase() === opponentName.toLowerCase()
      );
      if (matchingTeam) {
        setOpponentTeamId(matchingTeam.id);
        setOpponentRoster(players.filter(p => p.teamId === matchingTeam.id));
        const oppLineups = lineups?.filter(l => l.teamId === matchingTeam.id) || [];
        const defaultLineup = oppLineups.find(l => l.isDefault) || oppLineups[0];
        if (defaultLineup) {
          handleLoadLineup(defaultLineup.id, 'them');
        } else {
          setActiveOpponentLineupId(null);
        }
      } else {
        setOpponentTeamId(null);
        setOpponentRoster([]);
        setActiveOpponentLineupId(null);
      }
    } else {
      // A registered opponent team selected outside of schedule context
      const team = teams.find(t => t.id === val);
      if (team) {
        setIsAddingOpponent(false);
        setSelectedMatchId('unselected');
        const teamUsReset = resetToRotationI(gameState.lineup as unknown as Record<string, string>, gameState.numerals as unknown as Record<string, string>, 'p');
        const teamThemReset = resetToRotationI(gameState.opponentLineup as unknown as Record<string, string>, gameState.opponentNumerals as unknown as Record<string, string>, 'op');
        setGameState(prev => ({ ...prev, opponent: team.name, stats: [], ourScore: 0, theirScore: 0, ourSetsWon: 0, theirSetsWon: 0, set: 1, substitutionsUsed: { us: 0, them: 0 }, lineup: teamUsReset.lineup as typeof prev.lineup, numerals: teamUsReset.numerals as typeof prev.numerals, opponentLineup: teamThemReset.lineup as typeof prev.opponentLineup, opponentNumerals: teamThemReset.numerals as typeof prev.opponentNumerals }));
        setOpponentTeamId(team.id);
        setOpponentRoster(players.filter(p => p.teamId === team.id));
        const oppLineups = lineups?.filter(l => l.teamId === team.id) || [];
        const defaultLineup = oppLineups.find(l => l.isDefault) || oppLineups[0];
        if (defaultLineup) {
          handleLoadLineup(defaultLineup.id, 'them');
        } else {
          setActiveOpponentLineupId(null);
        }
      }
    }
  };

  const handleLineupChange = (side: 'us' | 'them', pos: string, value: string) => {
    if (value === 'NEW_PLAYER') { 
        setQuickCreateSide(side); 
        setQuickCreateTargetPos(pos); 
        setNewPlayerForm({ name: '', number: '', position: 'Unknown' }); 
        setIsQuickCreating(true); 
    } else { 
        updateLineupGeneric(side, pos, value); 
    }
  };

  const handleSaveOpponentLineup = async () => {
      // If we are in Guest mode, we must create a team first
      if (!opponentTeamId) {
          setIsAddingOpponent(true);
          return;
      }

      const name = window.prompt("Enter a name for this opponent lineup:");
      if (!name) return;

      setIsSavingOpponentLineup(true);
      try {
          const payload = {
              organization_id: organizationId,
              team_id: opponentTeamId,
              name: name,
              positions: gameState.opponentLineup,
              is_default: false
          };
          
          if (organizationId !== 'dev-org-id') {
              const { data, error } = await supabase.from('lineups').insert([payload]).select();
              if (error) {
                  console.error('Opponent lineup save error:', error.message, error.code);
                  alert(`Failed to save opponent lineup: ${error.message}`);
              } else if (data && setLineups) {
                  const saved = {
                      id: data[0].id,
                      organizationId,
                      teamId: opponentTeamId,
                      name,
                      positions: gameState.opponentLineup as any,
                      isDefault: false,
                      isFavorite: false,
                      createdAt: new Date().toISOString()
                  };
                  setLineups(prev => [...prev, saved]);
                  setActiveOpponentLineupId(saved.id);
              }
          }
      } catch (e: any) {
          console.error('handleSaveOpponentLineup error:', e);
          alert(`Failed to save opponent lineup: ${e?.message || 'Unknown error'}`);
      } finally {
          setIsSavingOpponentLineup(false);
      }
  };

  const handleUpdateActiveLineup = async () => {
    if (!activeLineupId || !activeTeamId) return;
    setIsSavingActiveLineup(true);
    const liberoAssignmentsPayload = liberoDesignations.us.map(liberoId => ({
      liberoId,
      replacesPlayerIds: liberoSandwiches[liberoId] || [],
      servesForPlayerId: liberoServingLocks[liberoId] || null,
    }));
    // Normalize positions to rotation I before saving so serve-receive back-rotate
    // doesn't persist into the saved lineup.
    const allNumerals = ['I','II','III','IV','V','VI'];
    const numeralToPlayer: Record<string, string> = {};
    for (let i = 1; i <= 6; i++) {
      const pos = `p${i}`;
      const numeral = (gameState.numerals as any)[pos] as string;
      if (numeral) numeralToPlayer[numeral] = (gameState.lineup as any)[pos] || '';
    }
    const normalizedLineup: Record<string, string> = {};
    for (let i = 1; i <= 6; i++) {
      normalizedLineup[`p${i}`] = numeralToPlayer[allNumerals[i - 1]] || '';
    }
    const updatedPositions = normalizedLineup as typeof gameState.lineup;
    const updatedRotSubs = rotationalSubs.length > 0 ? rotationalSubs : null;
    try {
      if (organizationId === 'dev-org-id' || activeTeamId.startsWith('dev-') || activeTeamId.startsWith('temp-')) {
        setLineups?.(prev => prev.map(l => l.id === activeLineupId ? {
          ...l,
          positions: updatedPositions as any,
          liberoAssignments: liberoAssignmentsPayload,
          rotationalSubstitutions: updatedRotSubs ?? undefined,
        } : l));
      } else {
        const { error } = await supabase.from('lineups').update({
          positions: updatedPositions,
          libero_assignments: liberoAssignmentsPayload,
          rotational_substitutions: updatedRotSubs,
        }).eq('id', activeLineupId);
        if (error) {
          console.error('Lineup update error:', error.message);
          alert(`Failed to save lineup: ${error.message}`);
          return;
        }
        setLineups?.(prev => prev.map(l => l.id === activeLineupId ? {
          ...l,
          positions: updatedPositions as any,
          liberoAssignments: liberoAssignmentsPayload,
          rotationalSubstitutions: updatedRotSubs ?? undefined,
        } : l));
      }
      // Dirty indicator resets automatically — isActiveLineupDirty compares against
      // teamLineups which was just updated by setLineups above
    } catch (e: any) {
      console.error('handleUpdateActiveLineup error:', e);
      alert(`Failed to save lineup: ${e?.message || 'Unknown error'}`);
    } finally {
      setIsSavingActiveLineup(false);
    }
  };

  const handleSaveOurLineup = async () => {
    const name = newOurLineupName.trim();
    if (!name || !activeTeamId) return;
    setIsSavingOurLineup(true);
    const liberoAssignmentsPayload = liberoDesignations.us.map(liberoId => ({
      liberoId,
      replacesPlayerIds: liberoSandwiches[liberoId] || [],
      servesForPlayerId: liberoServingLocks[liberoId] || null,
    }));
    try {
      const payload: any = {
        organization_id: organizationId,
        team_id: activeTeamId,
        name,
        positions: gameState.lineup,
        libero_assignments: liberoAssignmentsPayload,
        rotational_substitutions: rotationalSubs.length > 0 ? rotationalSubs : null,
        is_default: false,
        is_favorite: false,
      };
      if (organizationId === 'dev-org-id' || activeTeamId.startsWith('dev-') || activeTeamId.startsWith('temp-')) {
        const saved: Lineup = {
          id: `dev-${Date.now()}`,
          organizationId,
          teamId: activeTeamId,
          name,
          positions: gameState.lineup as any,
          liberoAssignments: liberoAssignmentsPayload,
          rotationalSubstitutions: rotationalSubs.length > 0 ? rotationalSubs : undefined,
          isDefault: false,
          isFavorite: false,
          createdAt: new Date().toISOString(),
        };
        setLineups?.(prev => [...prev, saved]);
        setActiveLineupId(saved.id);
      } else {
        const { data, error } = await supabase.from('lineups').insert([payload]).select();
        if (error) {
          console.error('Lineup save error:', error.message, error.code);
          alert(`Failed to save lineup: ${error.message}`);
          return;
        }
        if (data && setLineups) {
          const saved: Lineup = {
            id: data[0].id,
            organizationId,
            teamId: activeTeamId,
            name,
            positions: gameState.lineup as any,
            liberoAssignments: liberoAssignmentsPayload,
            rotationalSubstitutions: rotationalSubs.length > 0 ? rotationalSubs : undefined,
            isDefault: false,
            isFavorite: false,
            createdAt: new Date().toISOString(),
          };
          setLineups(prev => [...prev, saved]);
          setActiveLineupId(saved.id);
        }
      }
    } catch (e: any) {
      console.error('handleSaveOurLineup error:', e);
      alert(`Failed to save lineup: ${e?.message || 'Unknown error'}`);
    } finally {
      setIsSavingOurLineup(false);
      setIsCreatingOurLineup(false);
      setNewOurLineupName('');
    }
  };

  const handleSaveNewLineupDraft = async () => {
    if (!newLineupDraft || !newLineupDraft.name.trim() || !activeTeamId) return;
    setIsSavingNewLineup(true);
    const { name, positions, liberoConfig: libCfg } = newLineupDraft;
    const liberoAssignmentsPayload = libCfg.map(la => ({
      liberoId: la.liberoId,
      replacesPlayerIds: la.replacesPlayerIds || [],
      servesForPlayerId: la.servesForPlayerId || null,
    }));
    const finalize = (saved: Lineup) => {
      setLineups?.(prev => [...prev, saved]);
      handleLoadLineup(saved.id, 'us');
      setNewLineupFlow(null);
      setNewLineupDraft(null);
      setIsSavingNewLineup(false);
    };
    try {
      if (organizationId === 'dev-org-id' || activeTeamId.startsWith('dev-') || activeTeamId.startsWith('temp-')) {
        finalize({ id: `dev-${Date.now()}`, organizationId, teamId: activeTeamId, name: name.trim(), positions: positions as any, liberoAssignments: liberoAssignmentsPayload, isDefault: false, isFavorite: false, createdAt: new Date().toISOString() });
        return;
      }
      const { data, error } = await supabase.from('lineups').insert([{
        organization_id: organizationId, team_id: activeTeamId, name: name.trim(),
        positions, libero_assignments: liberoAssignmentsPayload, is_default: false, is_favorite: false,
      }]).select();
      if (error) throw error;
      if (data?.[0]) {
        const { mapLineupFromDB } = await import('../services/supabase');
        finalize(mapLineupFromDB(data[0]));
      }
    } catch (e: any) {
      console.error('handleSaveNewLineupDraft error:', e);
      alert(`Failed to save lineup: ${e?.message || 'Unknown error'}`);
      setIsSavingNewLineup(false);
    }
  };

  const isPreGame = gameState.ourScore === 0 && gameState.theirScore === 0 && gameState.stats.length === 0 && !rallyInProgress;

  const computeSeasonStats = (playerId: string, position: string): { label: string; value: string; rank?: number }[] => {
    const s = seasonPlayerStats[playerId];
    if (!s) return [];
    const pos = position?.toLowerCase() ?? '';

    // Derive computed values
    const aAtt = s.aAtt + s.kills + s.aErr;
    const hitPct   = aAtt   >= 10 ? (s.kills - s.aErr) / aAtt : null;
    const srvPct   = s.sAtt >= 10 ? (s.sAtt - s.sErr) / s.sAtt : null;
    const passAvg  = s.rcv  >= 10 ? s.rcvSum / s.rcv : null;
    const sp       = Math.max(s.setsPlayed, 1);
    const bpg      = s.blocks >= 10 ? s.blocks / sp : null;
    const apg      = s.ast   >= 10 ? s.ast   / sp : null;
    const dpg      = s.digs  >= 10 ? s.digs  / sp : null;
    // PS%: same formula as Stats page — prefer server_point data, fall back to rotation_point
    const psPct    = s.sAtt >= 10 ? (s.serverPoints > 0 ? (s.aces + s.serverPoints) / s.sAtt : (s.rotationPoints > 0 ? (s.aces + s.rotationPoints) / s.sAtt : (s.aces / s.sAtt))) : null;

    // Rank among all team players (only those with enough data to have a real value)
    type RawFn = (ps: typeof s) => number | null;
    const getRank = (fn: RawFn, higherIsBetter = true): number | undefined => {
      const vals = teamPlayers
        .map(p => { const ps = seasonPlayerStats[p.id]; return ps ? { id: p.id, v: fn(ps) } : null; })
        .filter((x): x is { id: string; v: number } => x !== null && x.v !== null)
        .sort((a, b) => higherIsBetter ? b.v - a.v : a.v - b.v);
      const idx = vals.findIndex(x => x.id === playerId);
      return idx >= 0 && vals.length > 1 ? idx + 1 : undefined;
    };

    const fmtHit  = (v: number | null) => v !== null ? `${v < 0 ? '-' : ''}${Math.abs(v * 100).toFixed(0)}%` : '—';
    const fmtPct  = (v: number | null) => v !== null ? `${(v * 100).toFixed(0)}%` : '—';
    const fmtPass = (v: number | null) => v !== null ? v.toFixed(2) : '—';
    const fmtPer  = (v: number | null) => v !== null ? v.toFixed(1) : '—';

    const hitFn:  RawFn = ps => { const a = ps.aAtt+ps.kills+ps.aErr; return a >= 10 ? (ps.kills-ps.aErr)/a : null; };
    const srvFn:  RawFn = ps => ps.sAtt >= 10 ? (ps.sAtt-ps.sErr)/ps.sAtt : null;
    const passFn: RawFn = ps => ps.rcv >= 10 ? ps.rcvSum/ps.rcv : null;
    const blkFn:  RawFn = ps => ps.blocks >= 10 ? ps.blocks/Math.max(ps.setsPlayed,1) : null;
    const astFn:  RawFn = ps => ps.ast   >= 10 ? ps.ast  /Math.max(ps.setsPlayed,1) : null;
    const digFn:  RawFn = ps => ps.digs  >= 10 ? ps.digs /Math.max(ps.setsPlayed,1) : null;
    const ptsFn:  RawFn = ps => ps.sAtt >= 10 ? (ps.serverPoints > 0 ? (ps.aces + ps.serverPoints) / ps.sAtt : (ps.rotationPoints > 0 ? (ps.aces + ps.rotationPoints) / ps.sAtt : ps.aces / ps.sAtt)) : null;

    if (pos === 'middle blocker') return [
      { label: 'Hit%',  value: fmtHit(hitPct), rank: hitPct  !== null ? getRank(hitFn)  : undefined },
      { label: 'Blk/S', value: fmtPer(bpg),    rank: bpg     !== null ? getRank(blkFn)  : undefined },
      { label: 'PS%', value: fmtPct(psPct), rank: psPct !== null ? getRank(ptsFn) : undefined },
    ];
    if (pos === 'outside hitter' || pos === 'right side' || pos === 'opposite') return [
      { label: 'Pass',  value: fmtPass(passAvg), rank: passAvg !== null ? getRank(passFn) : undefined },
      { label: 'Hit%',  value: fmtHit(hitPct),   rank: hitPct  !== null ? getRank(hitFn)  : undefined },
      { label: 'PS%', value: fmtPct(psPct), rank: psPct !== null ? getRank(ptsFn) : undefined },
    ];
    if (pos === 'setter') return [
      { label: 'Ast/S', value: fmtPer(apg),    rank: apg    !== null ? getRank(astFn)  : undefined },
      { label: 'Srv%',  value: fmtPct(srvPct), rank: srvPct !== null ? getRank(srvFn)  : undefined },
      { label: 'PS%', value: fmtPct(psPct), rank: psPct !== null ? getRank(ptsFn) : undefined },
    ];
    if (pos === 'libero' || pos === 'ds') return [
      { label: 'Pass',  value: fmtPass(passAvg), rank: passAvg !== null ? getRank(passFn) : undefined },
      { label: 'Dig/S', value: fmtPer(dpg),      rank: dpg     !== null ? getRank(digFn)  : undefined },
      { label: 'PS%', value: fmtPct(psPct), rank: psPct !== null ? getRank(ptsFn) : undefined },
    ];
    return [
      { label: 'Hit%',  value: fmtHit(hitPct), rank: hitPct  !== null ? getRank(hitFn)  : undefined },
      { label: 'PS%', value: fmtPct(psPct), rank: psPct !== null ? getRank(ptsFn) : undefined },
    ];
  };

  const computeInlineStats = (playerId: string): { label: string; value: number; color: string }[] => {
    if (!playerId) return [];
    const playerStats = gameState.stats.filter(s => s.playerId === playerId);
    const count = (type: StatRecord['statType']) => playerStats.filter(s => s.statType === type).length;
    const kills   = count('kill');
    const aces    = count('ace');
    const errors  = count('attack_error') + count('serve_error') + count('error');
    const assists = count('assist');
    const digs    = count('dig');
    const blocks  = count('block_solo') + count('block_assist');
    return [
      ...(aces    > 0 ? [{ label: 'A',   value: aces,    color: 'bg-green-600 text-white' }]   : []),
      ...(kills   > 0 ? [{ label: 'K',   value: kills,   color: 'bg-green-100 text-green-800' }] : []),
      ...(assists > 0 ? [{ label: 'AST', value: assists,  color: 'bg-purple-100 text-purple-800' }] : []),
      ...(blocks  > 0 ? [{ label: 'B',   value: blocks,  color: 'bg-indigo-100 text-indigo-800' }] : []),
      ...(digs    > 0 ? [{ label: 'D',   value: digs,    color: 'bg-amber-100 text-amber-800' }]  : []),
      ...(errors  > 0 ? [{ label: 'E',   value: errors,  color: 'bg-red-100 text-red-700' }]    : []),
    ];
  };

  const renderCard = (side: 'us' | 'them', pos: string) => {
    const isUs = side === 'us';
    const lineup = isUs ? gameState.lineup : gameState.opponentLineup;
    const numerals = isUs ? gameState.numerals : gameState.opponentNumerals;
    const rawId = (lineup as any)[pos];
    const label = (numerals as any)[pos];
    const { effectiveId, isLiberoActive } = getEffectivePlayer(side, pos, rawId, false);
    const overlay = (activeOverlay?.side === side && activeOverlay?.playerId === effectiveId) ? activeOverlay.type : null;

    // Suppress setter AST tile when front-row and 2 setters are on court (dual-role 6-2 or any situation
    // where both setters are in the lineup). Back-row setter always gets AST; front-row setter gets SR/Oppo.
    const activeLineupForDual = isUs ? teamLineups.find(l => l.id === activeLineupId) : null;
    const dualRoleSetterIds = activeLineupForDual?.dualRoleSetterIds || [];
    const courtSetterCount = isUs
      ? (['p1','p2','p3','p4','p5','p6'] as const)
          .filter(slot => teamPlayers.find(p => p.id === (gameState.lineup as any)[slot])?.position === 'Setter')
          .length
      : 0;
    const isCurrentPlayerSetter = isUs && teamPlayers.find(p => p.id === effectiveId)?.position === 'Setter';
    const isFrontRowForUs = isUs && ['p2','p3','p4'].includes(pos);
    // Fires when this is a front-row setter and either explicitly dual-role or 2 setters are on court
    const isDualRoleSetter = isCurrentPlayerSetter && isFrontRowForUs && (
      dualRoleSetterIds.includes(effectiveId) || courtSetterCount >= 2
    );
    // Back-row setter in a 6-2 situation — badge shows "S(RS)" but stat tiles unchanged
    const isBackRowSetterIn62 = isCurrentPlayerSetter && !isFrontRowForUs && courtSetterCount >= 2;

    // Libero toggle appears on: (a) back-row cards whose occupant is a sandwich player, OR
    // (b) any card where the libero is currently active (isLiberoActive=true).
    const backRowUs = ['p1', 'p5', 'p6'];
    const designatedPlayerIds = liberoDesignations.us.flatMap(lid => liberoSandwiches[lid] || []);
    // Block the card toggle at p1 while this team is serving when the libero is NOT already
    // active at that slot. Serving must be configured via the libero config modal — not via
    // an accidental toggle tap which would silently create a permanent serving lock.
    const servingSlotBlocksToggle = isUs && !isLiberoActive && pos === 'p1' && possession === 'serving';
    const showLiberoToggle = isUs && !servingSlotBlocksToggle && (isLiberoActive || (backRowUs.includes(pos) && rawId && designatedPlayerIds.includes(rawId)));

    const card = (
      <PlayerCard
        side={side}
        pos={pos}
        label={label}
        playerId={rawId}
        actualPlayerId={effectiveId}
        isLiberoActive={isLiberoActive}
        originalPlayerId={rawId}
        availablePlayers={isUs ? teamPlayers : opponentRoster}
        isHighlighted={false}
        onPlayerChange={(val) => handleLineupChange(side, pos, val)}
        isScrimmageMode={isScrimmage}
        isServer={possession === (isUs ? 'serving' : 'receiving') && (pos === (isUs ? 'p1' : 'op1')) && !!effectiveId}
        isRallyInProgress={rallyInProgress}
        onServeInPlay={(sType) => { const mapped = sType === 'Jump' ? 'jump' : sType === 'Float' ? 'float' : null; if (mapped) lastServeTypeRef.current = mapped; setAssistPromptState(null); if (effectiveId) { const ts = Date.now(); recordStat('serve_attempt', effectiveId, undefined, undefined, undefined, ts); if (advToggles.serveTypeTracking && mapped) { setGameState(prev => ({ ...prev, stats: prev.stats.map(s => s.timestamp === ts ? { ...s, serveType: mapped } : s) })); } } setRallyInProgress(true); }}
        row={isUs ? (['p2', 'p3', 'p4'].includes(pos) ? 'front' : 'back') : (['op2', 'op3', 'op4'].includes(pos) ? 'front' : 'back')}
        onServeAction={(action, type) => handleServeAction(action, type)}
        suggestAssist={assistPromptState?.side === side && !!assistPromptState?.eligibleIds?.includes(effectiveId)}
        onConfirmAssist={() => handleGameStat(effectiveId, side, 'Assist')}
        onDismissAssist={() => setAssistPromptState(null)}
        onSub={() => {
          if (isUs && isLineupEditMode && isLiberoActive) { handleLiberoCardTap('us', effectiveId); return; }
          if (!isUs) {
            const avail = opponentRoster.filter(p => !Object.values(gameState.opponentLineup).includes(p.id));
            if (avail.length === 0) { setQuickCreateSide('them'); setQuickCreateTargetPos(pos); setNewPlayerForm({ name: '', number: '', position: 'Unknown' }); setIsQuickCreating(true); return; }
          }
          setSubContext({ side, pos, currentPlayerId: rawId }); setShowSubModal(true);
        }}
        onAddPlayer={() => {
          if (!isUs) {
            const avail = opponentRoster.filter(p => !Object.values(gameState.opponentLineup).includes(p.id));
            if (avail.length === 0) { setQuickCreateSide('them'); setQuickCreateTargetPos(pos); setNewPlayerForm({ name: '', number: '', position: 'Unknown' }); setIsQuickCreating(true); return; }
          }
          setSubContext({ side, pos, currentPlayerId: rawId }); setShowSubModal(true);
        }}
        onRemovePlayer={isUs && isPreGame && !!rawId ? () => {
          if (isLiberoActive) {
            // Libero is currently showing — remove their designation, restoring the original player
            const libId = liberoDesignations.us.find(lid => lid === effectiveId);
            if (libId) handleLiberoRemove('us', libId);
          } else {
            updateLineupGeneric('us', pos, '');
          }
        } : undefined}
        overlayType={overlay}
        onRequestErrorOverlay={() => !advToggles.errorDetailOverlay ? handleGameStat(effectiveId, side, 'Attack Error') : setActiveOverlay({ side, playerId: effectiveId, type: 'error' })}
        onOverlayOptionSelect={(option, type) => {
          if (type === 'kill') {
            if (option.startsWith('hittype:')) {
              const val = option.slice(8) as 'driven' | 'shot' | 'tip';
              pendingKillSelectionsRef.current = { ...pendingKillSelectionsRef.current, hitType: val };
              if (pendingKillTimestampRef.current) {
                const ts = pendingKillTimestampRef.current;
                setGameState(prev => ({ ...prev, stats: prev.stats.map(s => s.timestamp === ts ? { ...s, killHitType: val } : s) }));
              }
            } else if (option.startsWith('outcome:')) {
              const val = option.slice(8) as 'tool' | 'floor' | 'touch';
              pendingKillSelectionsRef.current = { ...pendingKillSelectionsRef.current, outcome: val };
              if (pendingKillTimestampRef.current) {
                const ts = pendingKillTimestampRef.current;
                setGameState(prev => ({ ...prev, stats: prev.stats.map(s => s.timestamp === ts ? { ...s, killOutcome: val } : s) }));
              }
            }
            const { hitType, outcome } = pendingKillSelectionsRef.current;
            const allDone = (!advToggles.killHitTypeRow || !!hitType) && (!advToggles.killOutcomeRow || !!outcome);
            if (allDone) commitPendingKill();
          } else {
            handleGameStat(effectiveId, side, 'Attack Error', option);
          }
        }}
        onCancelOverlay={() => {
          if (activeOverlay?.type === 'kill') commitPendingKill();
          else setActiveOverlay(null);
        }}
        onDumpSelected={(dumpRow) => { pendingDumpRowRef.current = dumpRow; }}
        onEditPlayer={(pid) => setEditingPlayerId(pid)}
        onLiberoToggle={showLiberoToggle ? () => {
          const libId = isLiberoActive
            ? liberoDesignations.us.find(lid => lid === effectiveId)
            : liberoDesignations.us.find(lid => (liberoSandwiches[lid] || []).includes(rawId));
          if (!libId) return;
          if (pos === 'p1' && possession === 'serving' && isLiberoActive) {
            // Bench the libero out of the serve slot without clearing their lock
            setLiberoOnCourt(prev => ({ ...prev, [libId]: false }));
          } else {
            handleLiberoSwap(libId);
          }
        } : undefined}
        isLiberoSandwiched={isUs ? !!liberoDesignations.us.find(lid => (liberoSandwiches[lid] || []).includes(rawId)) : false}
        onBlockAssistStart={() => setBlockAssistState({ side, blockerId: effectiveId, selectedTeammateIds: [] })}
        onGameStat={(type, detail, passRating) => handleGameStat(effectiveId, side, type, detail, passRating)}
        isLineupEditMode={isUs && isLineupEditMode}
        simpleStats={!advToggles.serveTypeTracking}
        isAssignmentMode={isUs && !!pendingStat}
        onAssign={isUs ? () => handleAssignStat(effectiveId) : undefined}
        isFlashing={isUs && !!assignmentFlash && assignmentFlash.playerId === effectiveId}
        flashBgClass={assignmentFlash?.flashBg || ''}
        serveTypeProp={advToggles.serveTypeTracking ? (serveTypeByPlayer[effectiveId] ?? (teamPlayers.find(p => p.id === effectiveId)?.primaryServeType === 'Topspin' ? 'Jump' : 'Float')) : undefined}
        onServeTypeChange={advToggles.serveTypeTracking ? (type) => { lastServeTypeRef.current = type === 'Jump' ? 'jump' : 'float'; setServeTypeByPlayer(prev => ({ ...prev, [effectiveId]: type })); } : undefined}
        preselectedServeStyle={isUs && possession === 'receiving' ? incomingServeType : undefined}
        onSRDetailCapture={isUs && advToggles.serveReceiveDetail ? (serveStyle, contactType) => {
          const ts = lastReceptionTimestampRef.current;
          if (!ts) return;
          setGameState(prev => ({
            ...prev,
            stats: prev.stats.map(s => s.timestamp === ts
              ? { ...s, ...(serveStyle !== null ? { incomingServeStyle: serveStyle } : {}), ...(contactType !== null ? { contactType } : {}) }
              : s
            )
          }));
        } : undefined}
        sidelineMode={isSideline}
        inlineStats={computeInlineStats(effectiveId)}
        editStats={isUs && isLineupEditMode && effectiveId ? computeSeasonStats(effectiveId, teamPlayers.find(p => p.id === effectiveId)?.position ?? '') : undefined}
        killOverlayRows={{ hitType: advToggles.killHitTypeRow, outcome: advToggles.killOutcomeRow }}
        isDualRoleSetter={isDualRoleSetter}
        isBackRowDualSetter={isBackRowSetterIn62}
      />
    );

    // Block assist: show an overlay on the other front-row cards (not the blocker) so the user
    // can tap them to add as co-blockers without a full-screen modal.
    const frontRowPositions = isUs ? ['p2', 'p3', 'p4'] : ['op2', 'op3', 'op4'];
    const isBlockAssistTarget = !!blockAssistState &&
      blockAssistState.side === side &&
      frontRowPositions.includes(pos) &&
      !!effectiveId &&
      effectiveId !== blockAssistState.blockerId;
    const isBlockSelected = isBlockAssistTarget && blockAssistState!.selectedTeammateIds.includes(effectiveId!);

    if (isBlockAssistTarget) {
      return (
        <div className="relative">
          {card}
          <div
            onClick={(e) => {
              e.stopPropagation();
              if (!blockAssistState || !effectiveId) return;
              handleConfirmBlockAssist([blockAssistState.blockerId, effectiveId]);
            }}
            className="absolute inset-0 z-20 flex flex-col items-center justify-center cursor-pointer rounded-xl border-4 transition-all select-none bg-purple-950/80 border-purple-500/60 hover:bg-purple-700/85 active:bg-purple-600/90"
          >
            <Users className="w-6 h-6 text-white mb-1 pointer-events-none" />
            <span className="text-sm font-black text-white uppercase pointer-events-none">Co-Block?</span>
          </div>
        </div>
      );
    }

    // When we're serving and the rally hasn't started yet, fade non-server cards
    // to nudge the user to tap the server card first.
    // Exception: don't dim cards with an active assist prompt or kill detail overlay —
    // those need to be resolved first, and dimming would hide/block the interaction.
    const isNonServerDuringServe = isUs && possession === 'serving' && !rallyInProgress && !isLineupEditMode && pos !== 'p1';
    const hasActivePrompt =
      (assistPromptState?.side === side && !!assistPromptState?.eligibleIds?.includes(effectiveId)) ||
      !!overlay;
    if (isNonServerDuringServe && !hasActivePrompt) {
      return <div className="opacity-40 transition-opacity duration-200">{card}</div>;
    }

    return card;
  };

  const getTeamRotationLabel = (side: 'us' | 'them') => {
      const numeralAtServePos = side === 'us' ? (gameState.numerals as any).p1 : (gameState.opponentNumerals as any).op1;
      return getRotationNumber(numeralAtServePos);
  };

  return (
    <div className="space-y-4 relative pb-20">
      {editingPlayerId && players.find(p => p.id === editingPlayerId) && ( <PlayerEditModal player={players.find(p => p.id === editingPlayerId)!} isOpen={true} onClose={() => setEditingPlayerId(null)} onUpdate={(p) => setPlayers(prev => prev.map(x => x.id === p.id ? p : x))} organizationId={organizationId} /> )}

      {/* ── AI usage quota warning banner ──────────────────────────────────────── */}
      {quotaWarning && quotaWarning !== quotaWarningDismissed && (() => {
        const cfg = {
          moderate: { bg: 'bg-amber-50 border-amber-200',  text: 'text-amber-800', icon: '⚡', msg: 'You\'re halfway through your monthly AI features. Voice logging will stop working when the limit is reached.' },
          high:     { bg: 'bg-orange-50 border-orange-200', text: 'text-orange-800', icon: '⚠️', msg: 'Your AI feature usage is running low this month. Consider reducing voice logging frequency.' },
          critical: { bg: 'bg-red-50 border-red-300',       text: 'text-red-800',    icon: '🚨', msg: 'AI feature limit almost reached. Voice logging may stop working soon this month.' },
        }[quotaWarning];
        return (
          <div className={`mx-0 flex items-start gap-3 px-4 py-3 rounded-xl border ${cfg.bg} ${cfg.text}`}>
            <span className="text-base shrink-0 mt-0.5">{cfg.icon}</span>
            <p className="flex-1 text-xs font-medium leading-snug">{cfg.msg}</p>
            <button
              type="button"
              onClick={() => setQuotaWarningDismissed(quotaWarning)}
              className="shrink-0 opacity-50 hover:opacity-100 transition-opacity mt-0.5"
              aria-label="Dismiss"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        );
      })()}

      {/* ── Libero Count Mismatch Warning ─────────────────────────────────────── */}
      {liberoMismatchModal && (() => {
        const { lineupId, lineupCount, gameAllowsTwo } = liberoMismatchModal;
        const lineupLabel = lineupCount >= 2 ? '2-libero' : '1-libero';
        const gameLabel = gameAllowsTwo ? '2-libero' : '1-libero';
        const switchLabel = gameAllowsTwo ? '1-libero' : '2-libero';
        return (
          <div className="fixed inset-0 z-[95] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm">
            <div className="bg-white rounded-2xl shadow-2xl p-5 w-full max-w-sm animate-fade-in-up">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center shrink-0">
                  <AlertCircle className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <h3 className="font-bold text-lg text-slate-900">Libero mismatch</h3>
                  <p className="text-xs text-slate-500">This lineup is configured for {lineupLabel} but your game is set to {gameLabel}.</p>
                </div>
              </div>
              <div className="flex flex-col gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setAppSettings?.(prev => ({ ...prev, allowTwoLiberos: !gameAllowsTwo }));
                    handleLoadLineup(lineupId, 'us');
                    setLiberoMismatchModal(null);
                  }}
                  className="w-full py-3 bg-purple-600 hover:bg-purple-700 text-white font-bold rounded-xl text-sm transition-colors"
                >
                  Switch to {switchLabel} and load
                </button>
                <button
                  type="button"
                  onClick={() => {
                    handleLoadLineup(lineupId, 'us');
                    setLiberoMismatchModal(null);
                  }}
                  className="w-full py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-xl text-sm transition-colors"
                >
                  Load anyway (keep {gameLabel} setting)
                </button>
                <button
                  type="button"
                  onClick={() => setLiberoMismatchModal(null)}
                  className="w-full py-2 text-slate-400 hover:text-slate-600 font-bold text-sm transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Libero Config Modal ──────────────────────────────────────────────────── */}
      {liberoConfigModal && (() => {
        const libRoster = liberoConfigModal.side === 'us' ? teamPlayers : opponentRoster;
        const libPlayer = libRoster.find(p => p.id === liberoConfigModal.liberoId);
        const libNum = libPlayer ? (('jerseyNumber' in libPlayer) ? (libPlayer as Player).jerseyNumber : (libPlayer as SimplePlayer).number) : null;
        const libName = libPlayer ? `${libNum ? `#${libNum} ` : ''}${libPlayer.name}` : 'Libero';
        const lineup = liberoConfigModal.side === 'us' ? gameState.lineup : gameState.opponentLineup;
        const posPrefix = liberoConfigModal.side === 'us' ? 'p' : 'op';
        const getOptLabel = (pid: string) => {
          if (!pid) return 'None';
          const p = libRoster.find(r => r.id === pid);
          if (!p) return 'Unknown';
          const num = ('jerseyNumber' in p) ? (p as Player).jerseyNumber : (p as SimplePlayer).number;
          return `${num ? `#${num} ` : ''}${p.name}`;
        };
        const currentIds = [liberoConfigModal.player1Id, liberoConfigModal.player2Id].filter(Boolean);
        const p1Label = getOptLabel(liberoConfigModal.player1Id);
        const p2Label = getOptLabel(liberoConfigModal.player2Id);
        const sideDesigs = liberoConfigModal.side === 'us' ? liberoDesignations.us : liberoDesignations.them;
        const isTwoLiberoMode = sideDesigs.length >= 2;
        return (
          <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm">
            <div className="bg-white rounded-2xl shadow-2xl p-5 w-full max-w-sm animate-fade-in-up">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center shrink-0">
                  <ArrowLeftRight className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <h3 className="font-bold text-lg text-slate-900">{libName} — Libero</h3>
                  <p className="text-xs text-slate-500">{isTwoLiberoMode ? 'Select which player(s) this libero covers' : 'Choose the rotation pair this libero replaces'}</p>
                </div>
              </div>

              {/* Player / Pair selector */}
              <div className="mb-4">
                {isTwoLiberoMode ? (
                  /* Two liberos: single player radio select */
                  <>
                    <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wide mb-2 block">Replaces player (select one)</label>
                    <div className="flex flex-col gap-1.5">
                      {[1,2,3,4,5,6].map(num => {
                        const pid = (lineup as any)[`${posPrefix}${num}`] as string | undefined;
                        if (!pid) return null;
                        const player = libRoster.find(r => r.id === pid);
                        if (!player) return null;
                        const isLibero = ('position' in player) && (player as Player).position === 'Libero';
                        if (isLibero) return null;
                        // Radio: only player1Id matters for two-libero (one player per libero)
                        const isSelected = liberoConfigModal.player1Id === pid;
                        const slotLabel = ['I','II','III','IV','V','VI'][num - 1];
                        const isMB = ('position' in player) && (player as Player).position === 'Middle Blocker';
                        return (
                          <button
                            key={pid}
                            type="button"
                            onClick={() => setLiberoConfigModal(prev => {
                              if (!prev) return prev;
                              // Radio: select exclusively, or deselect if tapping the active one
                              const p1 = isSelected ? '' : pid;
                              const keepServes = p1 === prev.servesForPlayerId;
                              return { ...prev, player1Id: p1, player2Id: '', servesForPlayerId: keepServes ? prev.servesForPlayerId : '' };
                            })}
                            className={`w-full flex items-center gap-2 p-2.5 rounded-xl border-2 text-left transition-all ${
                              isSelected ? 'border-purple-400 bg-purple-50 text-purple-900' : 'border-slate-200 bg-slate-50 text-slate-700 hover:border-slate-300'
                            }`}
                          >
                            <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 ${isSelected ? 'border-purple-500' : 'border-slate-300'}`}>
                              {isSelected && <div className="w-2 h-2 rounded-full bg-purple-500" />}
                            </div>
                            <span className="text-sm font-bold flex-1">{getOptLabel(pid)}</span>
                            {isMB && <span className="text-[9px] font-black text-purple-600 bg-purple-100 px-1.5 py-0.5 rounded-full shrink-0">MB</span>}
                            <span className="text-xs text-slate-400">Rot. {slotLabel}</span>
                          </button>
                        );
                      })}
                    </div>
                  </>
                ) : (
                  /* Single libero: rotation pair buttons (original behavior) */
                  <>
                    <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wide mb-2 block">Replaces pair</label>
                    <div className="flex flex-col gap-1.5">
                      {([
                        { label: 'I & IV', nums: [1, 4] as const },
                        { label: 'II & V', nums: [2, 5] as const },
                        { label: 'III & VI', nums: [3, 6] as const },
                      ]).map(pair => {
                        const idA = (lineup as any)[`${posPrefix}${pair.nums[0]}`] as string | undefined;
                        const idB = (lineup as any)[`${posPrefix}${pair.nums[1]}`] as string | undefined;
                        const pairIds = [idA, idB].filter(Boolean) as string[];
                        const isActive = pairIds.length > 0 && pairIds.length === currentIds.length && pairIds.every(id => currentIds.includes(id));
                        const labelParts = pair.label.split(' & ');
                        const displayA = idA ? getOptLabel(idA) : labelParts[0];
                        const displayB = idB ? getOptLabel(idB) : labelParts[1];
                        const mbCount = (
                          ('position' in (libRoster.find(r => r.id === idA) ?? {})) && (libRoster.find(r => r.id === idA) as Player)?.position === 'Middle Blocker' ? 1 : 0
                        ) + (
                          ('position' in (libRoster.find(r => r.id === idB) ?? {})) && (libRoster.find(r => r.id === idB) as Player)?.position === 'Middle Blocker' ? 1 : 0
                        );
                        return (
                          <button
                            key={pair.label}
                            type="button"
                            onClick={() => setLiberoConfigModal(prev => prev ? {
                              ...prev, player1Id: idA || '', player2Id: idB || '',
                              servesForPlayerId: (prev.servesForPlayerId && [idA, idB].includes(prev.servesForPlayerId)) ? prev.servesForPlayerId : ''
                            } : prev)}
                            className={`w-full flex items-center gap-2 p-2.5 rounded-xl border-2 text-left transition-all ${
                              isActive ? 'border-purple-400 bg-purple-50 text-purple-900' : 'border-slate-200 bg-slate-50 text-slate-700 hover:border-slate-300'
                            }`}
                          >
                            <ArrowLeftRight className={`w-4 h-4 shrink-0 ${isActive ? 'text-purple-500' : 'text-slate-400'}`} />
                            <span className="text-sm font-bold flex-1">{displayA} & {displayB}</span>
                            {mbCount > 0 && (
                              <span className="text-[9px] font-black text-purple-600 bg-purple-100 px-1.5 py-0.5 rounded-full shrink-0">
                                {mbCount === 2 ? 'MB + MB' : 'MB'}
                              </span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>

              {/* Serving assignment */}
              <div className="border-t border-slate-100 pt-3 mb-4">
                <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wide mb-2 block">Libero serves for (optional)</label>
                <div className="flex flex-col gap-1.5">
                  {[
                    { id: '', label: 'Does not serve' },
                    ...(liberoConfigModal.player1Id ? [{ id: liberoConfigModal.player1Id, label: p1Label }] : []),
                    ...(liberoConfigModal.player2Id ? [{ id: liberoConfigModal.player2Id, label: p2Label }] : []),
                  ].map(opt => (
                    <button
                      key={opt.id}
                      onClick={() => setLiberoConfigModal(prev => prev ? { ...prev, servesForPlayerId: opt.id } : prev)}
                      className={`w-full flex items-center gap-2 p-2.5 rounded-xl border-2 text-left transition-all ${
                        liberoConfigModal.servesForPlayerId === opt.id
                          ? 'border-amber-400 bg-amber-50 text-amber-900'
                          : 'border-slate-200 bg-slate-50 text-slate-700 hover:border-slate-300'
                      }`}
                    >
                      {opt.id === '' ? <X className="w-4 h-4 text-slate-400 shrink-0" /> : <Lock className="w-4 h-4 text-amber-500 shrink-0" />}
                      <span className="text-sm font-bold">{opt.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex gap-2">
                <button onClick={() => setLiberoConfigModal(null)} className="flex-1 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-600 font-bold rounded-xl text-sm transition-colors">Cancel</button>
                <button onClick={handleConfirmLiberoConfig} className="flex-1 py-2.5 bg-purple-600 hover:bg-purple-700 text-white font-bold rounded-xl text-sm transition-colors">Save</button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Libero Designation Picker Modal ────────────────────────────────────── */}
      {liberoPickerModal && (
        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl p-5 w-full max-w-sm animate-fade-in-up">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-bold text-lg text-slate-900">
                  Designate {liberoPickerModal.slot === 0 ? 'Libero 1' : 'Libero 2'}
                </h3>
                <p className="text-xs text-slate-500">Select the player who wears the libero jersey</p>
              </div>
              <button onClick={() => setLiberoPickerModal(null)}><X className="w-5 h-5 text-slate-400" /></button>
            </div>

            {/* Current designations (for context) */}
            {liberoDesignations.us.length > 0 && (
              <div className="mb-3 flex flex-wrap gap-1">
                {liberoDesignations.us.map((lid, idx) => {
                  const lp = teamPlayers.find(p => p.id === lid);
                  return lp ? (
                    <div key={lid} className="flex items-center gap-1 bg-purple-100 text-purple-800 text-xs font-bold px-2 py-1 rounded-full">
                      <span>L{idx + 1}: #{lp.jerseyNumber} {lp.name.split(' ')[0]}</span>
                      <button onClick={() => handleLiberoRemove('us', lid)} className="ml-1 text-purple-400 hover:text-red-500"><X className="w-3 h-3" /></button>
                    </div>
                  ) : null;
                })}
              </div>
            )}

            <div className="grid grid-cols-2 gap-2 max-h-64 overflow-y-auto">
              {liberoDesignations.us[liberoPickerModal.slot] && (
                <button
                  onClick={() => { handleLiberoRemove('us', liberoDesignations.us[liberoPickerModal.slot]); setLiberoPickerModal(null); }}
                  className="flex items-center justify-center gap-2 p-3 border-2 border-dashed border-slate-300 rounded-xl hover:border-red-400 hover:bg-red-50 text-slate-400 hover:text-red-500 transition-all active:scale-95 col-span-2 text-sm font-bold"
                >
                  <span>Remove Libero (None)</span>
                </button>
              )}
              {[...teamPlayers].sort((a, b) => {
                const aL = a.position?.toLowerCase() === 'libero' ? 0 : 1;
                const bL = b.position?.toLowerCase() === 'libero' ? 0 : 1;
                return aL - bL;
              }).map(p => {
                const isCurrentSlot = liberoDesignations.us[liberoPickerModal.slot] === p.id;
                const isOtherSlot = !isCurrentSlot && liberoDesignations.us.includes(p.id);
                return (
                  <button
                    key={p.id}
                    onClick={() => {
                      if (isCurrentSlot) { setLiberoPickerModal(null); handleLiberoCardTap('us', p.id); }
                      else { handleLiberoDesignate('us', p.id, liberoPickerModal.slot); }
                    }}
                    disabled={isOtherSlot}
                    className={`flex flex-col items-center p-3 border-2 rounded-xl transition-all active:scale-95 ${
                      isCurrentSlot ? 'border-purple-500 bg-purple-100 ring-2 ring-purple-400/60' :
                      isOtherSlot ? 'border-purple-400 bg-purple-50 opacity-60 cursor-default' :
                      'border-slate-200 hover:border-purple-400 hover:bg-purple-50'
                    }`}
                  >
                    <span className="font-black text-xl text-slate-800">{p.jerseyNumber || ''}</span>
                    <span className="text-[10px] font-bold text-slate-600 truncate w-full text-center">{p.name.split(' ')[0]}</span>
                    <span className="text-[9px] text-slate-400">{p.position}</span>
                  </button>
                );
              })}
            </div>
            <button onClick={() => setLiberoPickerModal(null)} className="mt-3 w-full py-2 bg-slate-100 text-slate-500 font-bold rounded-xl text-sm hover:bg-slate-200 transition-colors">Cancel</button>
          </div>
        </div>
      )}

      {/* Quick Add Player Modal */}
      {isQuickCreating && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-sm animate-fade-in-up">
            <h3 className="font-bold text-xl text-slate-900 mb-2">New Player</h3>
            <p className="text-xs text-slate-500 mb-6">Adding to {quickCreateSide === 'us' ? 'My Team' : 'Opponent'}</p>
            <div className="space-y-4">
              <input autoFocus className="w-full bg-slate-100 border-transparent focus:border-blue-500 rounded-xl p-3 outline-none" placeholder={quickCreateSide === 'them' ? "Name (Optional if # used)" : "Name"} value={newPlayerForm.name} onChange={e => setNewPlayerForm({...newPlayerForm, name: e.target.value})} />
              <input className="w-full bg-slate-100 border-transparent focus:border-blue-500 rounded-xl p-3 outline-none" placeholder="Jersey #" value={newPlayerForm.number} onChange={e => setNewPlayerForm({...newPlayerForm, number: e.target.value})} />
              <select className="w-full bg-slate-100 border-transparent focus:border-blue-500 rounded-xl p-3 outline-none" value={newPlayerForm.position} onChange={e => setNewPlayerForm({...newPlayerForm, position: e.target.value})}>
                  <option value="Unknown">Unknown Position</option>
                  <option value="Outside Hitter">Outside Hitter</option>
                  <option value="Opposite">Opposite</option>
                  <option value="Middle Blocker">Middle Blocker</option>
                  <option value="Setter">Setter</option>
                  <option value="Libero">Libero</option>
              </select>
              <div className="flex gap-2">
                  <button onClick={() => setIsQuickCreating(false)} className="flex-1 py-3 text-slate-500 font-bold hover:bg-slate-50 rounded-xl">Cancel</button>
                  <button onClick={saveQuickPlayer} className="flex-1 bg-blue-600 text-white font-bold py-3 rounded-xl hover:bg-blue-700 transition-all">Add & Assign</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Our Lineup Modal */}
      {/* ── New Lineup: Method Chooser ────────────────────────────────────────── */}
      {newLineupFlow === 'chooser' && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-sm animate-fade-in-up">
            <h3 className="font-bold text-xl text-slate-900 mb-1">New Lineup</h3>
            <p className="text-xs text-slate-500 mb-5">How do you want to build it?</p>
            <div className="flex flex-col gap-2.5">
              <button
                type="button"
                onClick={() => setNewLineupFlow('quick-build')}
                className="flex items-center gap-3 p-4 rounded-xl border-2 border-purple-200 bg-purple-50 hover:bg-purple-100 text-left transition-colors"
              >
                <div className="w-9 h-9 bg-purple-600 rounded-xl flex items-center justify-center shrink-0">
                  <Wand2 className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="font-bold text-slate-800 text-sm">Quick Build</p>
                  <p className="text-xs text-slate-500">Auto-generate from depth chart</p>
                </div>
              </button>
              <button
                type="button"
                onClick={() => { setNewLineupScanMode(true); setShowLineupScanner(true); setNewLineupFlow(null); }}
                className="flex items-center gap-3 p-4 rounded-xl border-2 border-blue-200 bg-blue-50 hover:bg-blue-100 text-left transition-colors"
              >
                <div className="w-9 h-9 bg-blue-600 rounded-xl flex items-center justify-center shrink-0">
                  <ScanLine className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="font-bold text-slate-800 text-sm">Scan Sheet</p>
                  <p className="text-xs text-slate-500">Scan a printed lineup sheet</p>
                </div>
              </button>
              <button
                type="button"
                onClick={() => {
                  setNewLineupDraft({ name: `Lineup ${teamLineups.length + 1}`, positions: { p1: '', p2: '', p3: '', p4: '', p5: '', p6: '' }, liberoConfig: [], liberoCount: 1 });
                  setNewLineupFlow('manual');
                }}
                className="flex items-center gap-3 p-4 rounded-xl border-2 border-slate-200 bg-slate-50 hover:bg-slate-100 text-left transition-colors"
              >
                <div className="w-9 h-9 bg-slate-600 rounded-xl flex items-center justify-center shrink-0">
                  <Users className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="font-bold text-slate-800 text-sm">Manual</p>
                  <p className="text-xs text-slate-500">Assign players slot by slot</p>
                </div>
              </button>
            </div>
            <button
              type="button"
              onClick={() => setNewLineupFlow(null)}
              className="w-full mt-4 py-2.5 text-slate-400 hover:text-slate-600 font-bold text-sm transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* ── New Lineup: Quick Build ───────────────────────────────────────────── */}
      <QuickLineupBuilder
        isOpen={newLineupFlow === 'quick-build'}
        onClose={() => setNewLineupFlow('chooser')}
        teamPlayers={teamPlayers}
        organizationId={organizationId}
        teamId={activeTeamId || ''}
        setLineups={setLineups || (() => {})}
        allowTwoLiberos={appSettings.allowTwoLiberos}
        onSave={(saved) => { handleLoadLineup(saved.id, 'us'); setNewLineupFlow(null); }}
      />

      {/* ── New Lineup: Manual Editor ─────────────────────────────────────────── */}
      {newLineupFlow === 'manual' && newLineupDraft && (() => {
        const parseNum = (n?: string) => parseInt((n || '').replace(/\D/g, '') || '0') || 0;
        const sortedPlayers = [...teamPlayers].sort((a, b) => parseNum(a.jerseyNumber) - parseNum(b.jerseyNumber));
        const slots = ['p1','p2','p3','p4','p5','p6'] as const;
        const numerals: Record<string, string> = { p1: 'I', p2: 'II', p3: 'III', p4: 'IV', p5: 'V', p6: 'VI' };
        const occupiedIds = new Set(Object.values(newLineupDraft.positions).filter(Boolean));
        const liberoPlayers = sortedPlayers.filter(p => p.position === 'Libero');
        const nonLiberoPlayers = sortedPlayers.filter(p => p.position !== 'Libero');
        const filledCount = slots.filter(s => newLineupDraft.positions[s]).length;

        return (
          <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 p-3 backdrop-blur-sm">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm flex flex-col max-h-[90vh] animate-fade-in-up">
              {/* Header */}
              <div className="px-5 pt-5 pb-3 border-b border-slate-100 shrink-0">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-bold text-lg text-slate-900">New Lineup</h3>
                  <button type="button" onClick={() => setNewLineupFlow('chooser')} className="text-slate-400 hover:text-slate-600 p-1">
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <input
                  className="w-full bg-slate-100 rounded-xl px-3 py-2.5 outline-none focus:ring-2 focus:ring-blue-500 text-slate-900 font-bold text-sm"
                  placeholder="Lineup name"
                  value={newLineupDraft.name}
                  onChange={e => setNewLineupDraft(d => d ? { ...d, name: e.target.value } : d)}
                />
              </div>

              {/* Court slots */}
              <div className="px-5 py-4 overflow-y-auto flex-1 space-y-2">
                <div className="flex items-center justify-between mb-1">
                  <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Rotation positions</p>
                  <span className="text-[10px] text-slate-400">{filledCount}/6 filled</span>
                </div>
                {/* Back row (serving order) */}
                <div className="grid grid-cols-3 gap-1.5">
                  {(['p1','p6','p5'] as const).map(slot => (
                    <div key={slot}>
                      <label className="block text-[9px] font-bold text-slate-400 uppercase text-center mb-0.5">Rot. {numerals[slot]}</label>
                      <select
                        value={newLineupDraft.positions[slot] || ''}
                        onChange={e => setNewLineupDraft(d => {
                          if (!d) return d;
                          const pid = e.target.value;
                          const next = { ...d.positions };
                          // Clear duplicate
                          Object.keys(next).forEach(k => { if (next[k] === pid && k !== slot) next[k] = ''; });
                          next[slot] = pid;
                          return { ...d, positions: next };
                        })}
                        className="w-full p-1.5 border border-slate-200 rounded-lg text-[11px] font-bold outline-none bg-white text-slate-700 cursor-pointer"
                      >
                        <option value="">—</option>
                        {nonLiberoPlayers.map(p => (
                          <option key={p.id} value={p.id} disabled={occupiedIds.has(p.id) && newLineupDraft.positions[slot] !== p.id}>
                            #{p.jerseyNumber} {p.name.split(' ')[0]}
                          </option>
                        ))}
                      </select>
                    </div>
                  ))}
                </div>
                <div className="h-px bg-slate-200 mx-0" />
                <div className="grid grid-cols-3 gap-1.5">
                  {(['p2','p3','p4'] as const).map(slot => (
                    <div key={slot}>
                      <label className="block text-[9px] font-bold text-slate-400 uppercase text-center mb-0.5">Rot. {numerals[slot]}</label>
                      <select
                        value={newLineupDraft.positions[slot] || ''}
                        onChange={e => setNewLineupDraft(d => {
                          if (!d) return d;
                          const pid = e.target.value;
                          const next = { ...d.positions };
                          Object.keys(next).forEach(k => { if (next[k] === pid && k !== slot) next[k] = ''; });
                          next[slot] = pid;
                          return { ...d, positions: next };
                        })}
                        className="w-full p-1.5 border border-slate-200 rounded-lg text-[11px] font-bold outline-none bg-white text-slate-700 cursor-pointer"
                      >
                        <option value="">—</option>
                        {nonLiberoPlayers.map(p => (
                          <option key={p.id} value={p.id} disabled={occupiedIds.has(p.id) && newLineupDraft.positions[slot] !== p.id}>
                            #{p.jerseyNumber} {p.name.split(' ')[0]}
                          </option>
                        ))}
                      </select>
                    </div>
                  ))}
                </div>

                {/* Libero */}
                {liberoPlayers.length > 0 && (
                  <div className="pt-1">
                    <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1.5">Libero</p>
                    <select
                      value={newLineupDraft.liberoConfig[0]?.liberoId || ''}
                      onChange={e => {
                        const pid = e.target.value;
                        if (!pid) { setNewLineupDraft(d => d ? { ...d, liberoConfig: [] } : d); return; }
                        const pos = newLineupDraft.positions;
                        const defaultReplaces = [pos.p3, pos.p6].filter(Boolean) as string[];
                        setNewLineupDraft(d => d ? { ...d, liberoConfig: [{ liberoId: pid, replacesPlayerIds: defaultReplaces }] } : d);
                      }}
                      className="w-full p-2 border border-purple-200 rounded-lg text-sm font-bold outline-none bg-purple-50 text-purple-800 cursor-pointer"
                    >
                      <option value="">— None —</option>
                      {liberoPlayers.map(p => (
                        <option key={p.id} value={p.id}>#{p.jerseyNumber} {p.name} (L)</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="px-5 pb-5 pt-3 border-t border-slate-100 shrink-0 flex gap-2">
                <button type="button" onClick={() => setNewLineupFlow('chooser')} className="flex-1 py-2.5 text-slate-500 font-bold hover:bg-slate-50 rounded-xl text-sm">
                  Back
                </button>
                <button
                  type="button"
                  onClick={handleSaveNewLineupDraft}
                  disabled={!newLineupDraft.name.trim() || isSavingNewLineup}
                  className="flex-1 bg-blue-600 text-white font-bold py-2.5 rounded-xl hover:bg-blue-700 disabled:opacity-40 transition-all text-sm"
                >
                  {isSavingNewLineup ? '…' : 'Save & Load'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Legacy: save current court as lineup (still used if triggered directly) ── */}
      {isCreatingOurLineup && (() => {
        return (
          <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
            <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-sm animate-fade-in-up">
              <h3 className="font-bold text-xl text-slate-900 mb-1">Save Lineup</h3>
              <p className="text-xs text-slate-500 mb-4">Saves the current court positions as a new lineup.</p>
              <input
                autoFocus
                className="w-full bg-slate-100 rounded-xl p-3 outline-none focus:ring-2 focus:ring-blue-500 mb-4 text-slate-900"
                placeholder="Lineup name"
                value={newOurLineupName}
                onChange={e => setNewOurLineupName(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleSaveOurLineup(); }}
              />
              <div className="flex gap-2">
                <button onClick={() => { setIsCreatingOurLineup(false); setNewOurLineupName(''); }} className="flex-1 py-3 text-slate-500 font-bold hover:bg-slate-50 rounded-xl">Cancel</button>
                <button onClick={handleSaveOurLineup} disabled={!newOurLineupName.trim() || isSavingOurLineup} className="flex-1 bg-blue-600 text-white font-bold py-3 rounded-xl hover:bg-blue-700 disabled:opacity-40 transition-all">
                  {isSavingOurLineup ? '…' : 'Create & Load'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Block assist is now handled via card overlays + a confirmation bar above the court grid */}

      {/* Add Opponent Team Modal */}
      {isAddingOpponent && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-sm animate-fade-in-up">
            <h3 className="font-bold text-xl text-slate-900 mb-2">Create Opponent</h3>
            <p className="text-xs text-slate-500 mb-6">Create a team profile to save this lineup.</p>
            <div className="space-y-4">
              <input autoFocus className="w-full bg-slate-100 border-transparent focus:border-blue-500 rounded-xl p-3 outline-none" placeholder="Opponent Team Name" value={newOpponentName} onChange={e => setNewOpponentName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleCreateOpponentTeam()} />
              <div className="flex gap-2">
                  <button onClick={() => setIsAddingOpponent(false)} className="flex-1 py-3 text-slate-500 font-bold hover:bg-slate-50 rounded-xl">Cancel</button>
                  <button onClick={handleCreateOpponentTeam} disabled={!newOpponentName.trim()} className="flex-1 bg-blue-600 text-white font-bold py-3 rounded-xl hover:bg-blue-700 transition-all disabled:opacity-50">Create Team</button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className={`fixed top-0 right-0 left-0 lg:left-64 z-50 bg-slate-900/95 backdrop-blur text-white shadow-xl transform transition-transform duration-300 ${showStickyScoreboard ? 'translate-y-0' : '-translate-y-full'}`}>
        <div className="flex items-center justify-between px-3 py-1.5 max-w-3xl mx-auto">
            <div className="flex items-center gap-2"><button onClick={() => handleStepperMinus('us')} className="w-6 h-6 rounded-full bg-slate-800 text-slate-400 hover:bg-slate-700 flex items-center justify-center"><Minus className="w-3 h-3"/></button><span className="text-xl font-bold font-mono text-blue-400 min-w-[1.5em] text-center">{gameState.ourScore}</span><button onClick={() => handleStepperPoint('us')} className="w-6 h-6 rounded-full bg-blue-600 text-white hover:bg-blue-500 flex items-center justify-center"><Plus className="w-3 h-3"/></button></div>
            <div className="flex flex-col items-center"><span className="text-[9px] font-bold text-slate-500 uppercase">Set {gameState.set}</span><span className="text-[10px] font-bold text-slate-300">{gameState.ourSetsWon} - {gameState.theirSetsWon}</span></div>
            <div className="flex items-center gap-2"><button onClick={() => handleStepperMinus('them')} className="w-6 h-6 rounded-full bg-slate-800 text-slate-400 hover:bg-slate-700 flex items-center justify-center"><Minus className="w-3 h-3"/></button><span className="text-xl font-bold font-mono text-red-400 min-w-[1.5em] text-center">{gameState.theirScore}</span><button onClick={() => handleStepperPoint('them')} className="w-6 h-6 rounded-full bg-red-600 text-white hover:bg-red-500 flex items-center justify-center"><Plus className="w-4 h-4"/></button></div>
        </div>
      </div>
      {/* Tournament Badge — shown when the current game is part of a tournament */}
      {(() => {
        const currentEvent = events.find(e => e.id === selectedMatchId);
        if (!currentEvent?.tournamentId || !currentEvent?.tournamentName) return null;
        const tournamentGames = events.filter(e => e.tournamentId === currentEvent.tournamentId && e.id !== currentEvent.id);
        return (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 space-y-2">
            <div className="flex items-center gap-2">
              <Trophy className="w-4 h-4 text-amber-600" />
              <span className="font-bold text-amber-800 text-sm">{currentEvent.tournamentName}</span>
              <span className="text-xs text-amber-500 ml-auto">{tournamentGames.length + 1} game{tournamentGames.length > 0 ? 's' : ''}</span>
            </div>
            {tournamentGames.length > 0 && (
              <div className="space-y-1">
                {tournamentGames.map(g => {
                  const rec = matchRecords?.find(r => r.eventId === g.id && r.isRecord);
                  const score = rec ? `${rec.gameState.ourSetsWon}–${rec.gameState.theirSetsWon}` : null;
                  return (
                    <div key={g.id} className="flex items-center gap-2 text-xs text-amber-700">
                      <span className="font-medium">vs {g.opponent || 'TBD'}</span>
                      {score && <span className={`font-bold ${rec!.gameState.ourSetsWon > rec!.gameState.theirSetsWon ? 'text-green-600' : 'text-red-600'}`}>{score}</span>}
                      {!score && <span className="text-amber-400">—</span>}
                      <span className="text-amber-400">{g.time}</span>
                    </div>
                  );
                })}
              </div>
            )}
            {setEvents && (
              <button
                onClick={() => {
                  const newId = `temp-${Date.now()}`;
                  const newGame: GameEvent = {
                    id: newId,
                    organizationId,
                    type: currentEvent.type,
                    title: `${currentEvent.tournamentName} Game`,
                    date: currentEvent.date,
                    time: 'TBD',
                    location: currentEvent.location,
                    opponent: '',
                    teamId: currentEvent.teamId,
                    tournamentId: currentEvent.tournamentId,
                    tournamentName: currentEvent.tournamentName,
                  };
                  setEvents(prev => [...prev, newGame]);
                  // Also persist to DB
                  if (organizationId !== 'dev-org-id') {
                    supabase.from('events').insert([{
                      organization_id: organizationId,
                      type: currentEvent.type,
                      title: newGame.title,
                      date: newGame.date,
                      time: newGame.time,
                      location: newGame.location,
                      opponent: null,
                      team_id: newGame.teamId || null,
                      tournament_id: newGame.tournamentId || null,
                      tournament_name: newGame.tournamentName || null,
                    }]).then(({ error }) => {
                      if (error) console.error('Failed to add tournament game:', error);
                    });
                  }
                }}
                className="w-full flex items-center justify-center gap-1.5 text-xs font-bold text-amber-600 hover:bg-amber-100 py-1.5 rounded-lg transition-colors"
              >
                <Plus className="w-3.5 h-3.5" /> Add Game to Tournament
              </button>
            )}
          </div>
        );
      })()}

      {/* Simple Stats + Stat Bar toggles — above scoreboard */}
      <div className="flex items-center gap-4">
        <div className="relative flex items-center gap-1">
          <div className="flex items-center bg-slate-100 rounded-lg p-0.5 border border-slate-200">
            <button
              onClick={() => setAppSettings?.(prev => ({ ...prev, simpleStats: true }))}
              className={`px-2.5 py-1 rounded-md text-[11px] font-bold transition-all ${appSettings.simpleStats ? 'bg-blue-500 text-white shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
            >Simple</button>
            <button
              onClick={() => setAppSettings?.(prev => ({ ...prev, simpleStats: false }))}
              className={`px-2.5 py-1 rounded-md text-[11px] font-bold transition-all ${!appSettings.simpleStats ? 'bg-orange-500 text-white shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
            >Advanced</button>
          </div>
          <button
            type="button"
            onClick={() => setShowConfigStatsModal(true)}
            title="Configure Stats"
            className="w-6 h-6 rounded-full bg-slate-200 hover:bg-slate-300 text-slate-500 flex items-center justify-center transition-colors"
          >
            <BarChart2 className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={() => setShowStatModeInfo(prev => !prev)}
            className="w-6 h-6 rounded-full bg-slate-200 hover:bg-slate-300 text-slate-500 flex items-center justify-center text-[10px] font-black leading-none transition-colors"
          >?</button>
          {showStatModeInfo && (
            <div className="absolute top-full left-0 mt-1.5 w-64 bg-white border border-slate-200 rounded-xl shadow-lg p-3 z-50 text-left">
              <div className="flex justify-between items-center mb-2">
                <span className="text-xs font-bold text-slate-700">Stats Mode</span>
                <button onClick={() => setShowStatModeInfo(false)} className="text-slate-400 hover:text-slate-600 text-xs font-bold">✕</button>
              </div>
              <div className="space-y-2 text-[11px] text-slate-600 leading-relaxed">
                <div><span className="font-bold text-blue-600">Simple</span> — fastest entry. Kills and errors record in one tap, no detail prompts. Serve type tracking is hidden. Best for fast-paced games.</div>
                <div><span className="font-bold text-orange-600">Advanced</span> — captures richer data. Records serve type (Jump/Float), pass contact (hands/platform), and incoming serve style. Enables serve and reception breakdown charts in the Stats view.</div>
              </div>
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={() => setShowOrientationOnboarding(true)}
          className="flex items-center gap-1.5 text-[11px] font-bold bg-slate-100 border border-slate-200 rounded-lg px-2 py-1 text-slate-700 hover:bg-slate-200 active:scale-95 transition-all cursor-pointer"
        >
          <ArrowLeftRight className="w-3 h-3 shrink-0" />
          <span className="hidden sm:inline">Net: </span>{{ standard: 'Bottom', 'net-top': 'Top', 'net-right': 'Right', 'net-left': 'Left' }[courtOrientation]}
        </button>
        <div className="flex items-center gap-2">
          <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Stat Bar</span>
          <button
            onClick={() => { setShowStatBar(prev => !prev); if (pendingStat) setPendingStat(null); }}
            className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors ${showStatBar ? 'bg-blue-500' : 'bg-slate-300'}`}
            title={showStatBar ? 'Stat bar on' : 'Stat bar off'}
          >
            <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${showStatBar ? 'translate-x-[1.25rem]' : 'translate-x-0.5'}`} />
          </button>
        </div>
        <button
          onClick={() => setShowUndoPanel(prev => !prev)}
          disabled={undoStack.length === 0}
          className={`ml-auto flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-bold transition-colors ${
            undoStack.length > 0
              ? 'text-amber-700 bg-amber-100 hover:bg-amber-200 active:scale-95'
              : 'text-slate-300 cursor-not-allowed'
          }`}
          title="Undo last stat"
        >
          <RotateCcw className="w-3.5 h-3.5" />
          Undo
        </button>
      </div>

      {/* Undo panel */}
      {showUndoPanel && undoStack.length > 0 && (() => {
        const actions = undoStack.map((snapshot, idx) => {
          const prevStats = idx === 0 ? gameState.stats : undoStack[idx - 1].gameState.stats;
          const prevOurScore = idx === 0 ? gameState.ourScore : undoStack[idx - 1].gameState.ourScore;
          const prevTheirScore = idx === 0 ? gameState.theirScore : undoStack[idx - 1].gameState.theirScore;
          const addedStats = prevStats.filter(s => !snapshot.gameState.stats.some(u => u.timestamp === s.timestamp));
          const scoreChanged = prevOurScore !== snapshot.gameState.ourScore || prevTheirScore !== snapshot.gameState.theirScore;
          return { snapshot, addedStats, scoreChanged };
        });
        return (
          <div className="bg-slate-900 border border-slate-700 rounded-xl p-3 animate-fade-in-down">
            <div className="flex items-center justify-between mb-2.5">
              <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Action History</span>
              <button type="button" onClick={() => setShowUndoPanel(false)} className="text-slate-500 hover:text-slate-300 p-0.5 transition-colors"><X className="w-3.5 h-3.5" /></button>
            </div>
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {actions.map(({ snapshot, addedStats, scoreChanged }, idx) => (
                <div key={idx} className={`rounded-lg border p-2.5 ${idx === 0 ? 'border-slate-600 bg-slate-800' : 'border-slate-700/50 bg-slate-800/40'}`}>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                      {idx === 0 ? 'Most Recent' : `${idx + 1} actions ago`}
                    </span>
                    <button
                      type="button"
                      onClick={() => handleUndoToIndex(idx)}
                      className="flex items-center gap-1 px-2 py-0.5 rounded-md bg-amber-600 hover:bg-amber-500 text-white text-[10px] font-bold transition-colors active:scale-95"
                    >
                      <RotateCcw className="w-2.5 h-2.5" />
                      Undo to here
                    </button>
                  </div>
                  {addedStats.length === 0 ? (
                    <p className="text-[11px] text-slate-600">No stats recorded</p>
                  ) : (
                    <ul className="space-y-1">
                      {addedStats.map((s) => {
                        const player = players.find(p => p.id === s.playerId);
                        const label = STAT_TYPE_LABELS[s.statType] ?? s.statType;
                        const colors = STAT_TYPE_COLORS[s.statType] ?? { bg: 'bg-slate-700', text: 'text-slate-300', border: 'border-slate-600' };
                        return (
                          <li key={s.timestamp} className="flex items-center gap-2">
                            <div className={`flex-1 flex items-center gap-2 px-2.5 py-1 rounded-lg border ${colors.bg} ${colors.border}`}>
                              <span className={`text-xs font-bold ${colors.text}`}>{label}</span>
                              {player
                                ? <span className={`text-xs ${colors.text} opacity-70`}>#{player.jerseyNumber} {player.name}</span>
                                : s.playerId === 'team' ? <span className={`text-xs ${colors.text} opacity-50`}>Team</span> : null
                              }
                            </div>
                            {idx === 0 && (
                              <button
                                type="button"
                                onClick={() => handleDeleteIndividualStat(s.timestamp)}
                                className="shrink-0 w-7 h-7 flex items-center justify-center rounded-lg bg-slate-700 text-slate-400 hover:bg-red-900/60 hover:text-red-400 active:scale-95 transition-all"
                                title="Remove this stat only (score unchanged)"
                              >
                                <X className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  )}
                  {scoreChanged && (
                    <p className="text-[11px] text-amber-400 mt-1.5">
                      Reverts score back to {snapshot.gameState.ourScore} – {snapshot.gameState.theirScore}
                    </p>
                  )}
                </div>
              ))}
            </div>
            <button
              type="button"
              onClick={() => setShowUndoPanel(false)}
              className="mt-3 w-full py-2 bg-emerald-700 hover:bg-emerald-600 active:bg-emerald-800 text-white text-xs font-bold rounded-lg uppercase tracking-wider active:scale-95 transition-all flex items-center justify-center gap-1.5"
            >
              <Check className="w-3.5 h-3.5" /> Done
            </button>
          </div>
        );
      })()}

      <div ref={mainScoreboardRef} className="bg-slate-900 rounded-xl px-4 py-2 text-white shadow-lg">
          <div className="grid grid-cols-[1fr_auto_1fr] gap-2 items-center mb-2">
              <div className="flex flex-col items-start min-w-0">{(() => { const clubTeams = teams.filter(t => t.type === 'club' || !t.type); return clubTeams.length > 1 ? (<select value={activeTeamId} onChange={(e) => handleTeamSwitch(e.target.value)} className="appearance-none bg-transparent text-2xl font-bold outline-none cursor-pointer w-full truncate">{clubTeams.map(t => <option key={t.id} value={t.id} className="text-slate-900">{t.name}</option>)}</select>) : (<div className="text-2xl font-bold truncate w-full">{activeTeam?.name || 'Us'}</div>); })()}<div className="flex items-center gap-1 mt-2">{[...Array(2)].map((_,i) => { const used = i >= timeouts.us; return used ? <div key={i} className="w-3 h-3 rounded-full bg-slate-600 border border-slate-500" title="Timeout used" /> : <button key={i} onClick={() => setTimeouts(prev => ({ ...prev, us: Math.max(0, prev.us - 1) }))} className="w-3 h-3 rounded-full bg-yellow-400 hover:bg-yellow-300 hover:scale-110 active:scale-95 transition-all cursor-pointer" title="Click to use timeout" />; })}<span className="text-[10px] text-slate-500 ml-1 font-bold">TO</span><span className="text-slate-700 mx-1.5 font-bold text-[10px]">·</span>{(() => { const r = appSettings.substitutionsPerSet - gameState.substitutionsUsed.us; const c = r <= 0 ? 'text-red-400' : r <= 1 ? 'text-orange-400' : r <= 3 ? 'text-amber-400' : 'text-slate-500'; return <span className={`text-[10px] font-bold ${c}`} title={`${r} substitution${r === 1 ? '' : 's'} remaining`}>{r} SUB</span>; })()}</div></div>
              <div className="flex items-center gap-2 px-2">
                 <div className="flex items-center gap-1.5">
                    <button onClick={() => handleStepperMinus('us')} className="w-7 h-7 md:w-8 md:h-8 rounded-full bg-slate-700 text-slate-300 hover:bg-slate-600 flex items-center justify-center active:scale-95 transition-all shrink-0"><Minus className="w-3.5 h-3.5"/></button>
                    <div className="text-5xl font-black font-mono leading-none min-w-[1.5ch] text-center">{gameState.ourScore}</div>
                    <button onClick={() => handleStepperPoint('us')} className="w-7 h-7 md:w-8 md:h-8 rounded-full bg-blue-600 text-white hover:bg-blue-500 flex items-center justify-center active:scale-95 transition-all shrink-0"><Plus className="w-3.5 h-3.5"/></button>
                 </div>

                 <div className="flex flex-col items-center gap-2 min-w-[4.5rem]">
                    <button
                        onClick={() => setPossession(prev => prev === 'serving' ? 'receiving' : 'serving')}
                        className={`w-full flex items-center justify-center gap-1 px-2 py-1.5 rounded-lg font-black text-[10px] uppercase tracking-wider text-white shadow-md transition-all active:scale-95 ${possession === 'serving' ? 'bg-blue-600 hover:bg-blue-500' : 'bg-red-600 hover:bg-red-500'}`}
                        title="Toggle Server"
                    >
                        {possession === 'serving' && <ArrowLeft className="w-3 h-3 shrink-0 hidden md:block" />}
                        <span>Serving</span>
                        {possession !== 'serving' && <ArrowRight className="w-3 h-3 shrink-0 hidden md:block" />}
                    </button>
                    <div className="bg-slate-800 px-2 py-0.5 rounded text-[9px] font-bold text-slate-400 uppercase tracking-wider">Set {gameState.set}</div>
                    <div className="text-[10px] font-bold text-slate-500">{gameState.ourSetsWon} - {gameState.theirSetsWon}</div>
                 </div>

                 <div className="flex items-center gap-1.5">
                    <button onClick={() => handleStepperMinus('them')} className="w-7 h-7 md:w-8 md:h-8 rounded-full bg-slate-700 text-slate-300 hover:bg-slate-600 flex items-center justify-center active:scale-95 transition-all shrink-0"><Minus className="w-3.5 h-3.5"/></button>
                    <div className="text-5xl font-black font-mono leading-none min-w-[1.5ch] text-center">{gameState.theirScore}</div>
                    <button onClick={() => handleStepperPoint('them')} className="w-7 h-7 md:w-8 md:h-8 rounded-full bg-red-600 text-white hover:bg-red-500 flex items-center justify-center active:scale-95 transition-all shrink-0"><Plus className="w-3.5 h-3.5"/></button>
                 </div>
              </div>
              <div className="flex flex-col items-end min-w-0"> 
                  <select
                    className="appearance-none bg-transparent text-2xl font-bold text-right outline-none cursor-pointer w-full"
                    value="current"
                    onChange={(e) => handleOpponentSelection(e.target.value)}
                  >
                    {/* Always-visible display option — shows the active opponent name */}
                    <option value="current" className="text-slate-900">{gameState.opponent}</option>
                    {scheduleOpponents.length > 0 && (() => {
                      const todayStr = getTodayStr();
                      const upcoming = scheduleOpponents.filter(e => (e.date || '').slice(0, 10) >= todayStr);
                      const past = scheduleOpponents.filter(e => (e.date || '').slice(0, 10) < todayStr);
                      return (<>
                        {upcoming.length > 0 && (
                          <optgroup label="Upcoming Games">
                            {upcoming.map(e => (
                              <option key={e.id} value={`schedule:${e.id}`} className="text-slate-900">
                                vs. {e.opponent} · {formatMatchDate(e.date)}
                              </option>
                            ))}
                          </optgroup>
                        )}
                        {past.length > 0 && (
                          <optgroup label="Past Games (Score Later)">
                            {past.map(e => (
                              <option key={e.id} value={`schedule:${e.id}`} className="text-slate-900">
                                vs. {e.opponent} · {formatMatchDate(e.date)} (past)
                              </option>
                            ))}
                          </optgroup>
                        )}
                      </>);
                    })()}
                    {registeredOnlyTeams.length > 0 && (
                      <optgroup label="Registered Opponents">
                        {registeredOnlyTeams.map(t => (
                          <option key={t.id} value={t.id} className="text-slate-900">{t.name}</option>
                        ))}
                      </optgroup>
                    )}
                    <option value="Guest" className="text-slate-500">Clear</option>
                    <option value="NEW" className="text-blue-400">+ New Opponent</option>
                  </select>
                  <div className="flex items-center gap-1 mt-2 justify-end"><span className="text-[10px] text-slate-500 mr-1 font-bold">TO</span>{[...Array(2)].map((_,i) => { const used = i >= timeouts.them; return used ? <div key={i} className="w-3 h-3 rounded-full bg-slate-600 border border-slate-500" title="Timeout used" /> : <button key={i} onClick={() => setTimeouts(prev => ({ ...prev, them: Math.max(0, prev.them - 1) }))} className="w-3 h-3 rounded-full bg-yellow-400 hover:bg-yellow-300 hover:scale-110 active:scale-95 transition-all cursor-pointer" title="Click to use timeout" />; })}</div> 
              </div>
          </div>

          <div className="flex gap-2 mt-2">
            <button
              onClick={() => setShowNewSetConfirm(true)}
              className="flex-1 py-1.5 flex items-center justify-center gap-1.5 text-blue-400 hover:text-white hover:bg-slate-800 rounded-lg text-[10px] font-bold uppercase tracking-widest transition-colors active:scale-95"
            >
              <Flag className="w-3 h-3" /> End Set
            </button>
            <button
              onClick={() => setShowClearConfirm('choose')}
              className="flex-1 py-1.5 flex items-center justify-center gap-1.5 text-slate-500 hover:text-red-400 hover:bg-slate-800 rounded-lg text-[10px] font-bold uppercase tracking-widest transition-colors active:scale-95"
            >
              <Trash2 className="w-3 h-3" /> Clear All
            </button>
          </div>
      </div>
      {/* ── Voice logging bar ─────────────────────────────────────────────── */}
      {canSeeVoiceLogging && (
        <button
          type="button"
          onPointerDown={e => { e.preventDefault(); voiceState === 'idle' ? startRecording() : undefined; }}
          disabled={voiceState === 'processing' || voiceState === 'pending' || voiceState === 'recording'}
          className={[
            'w-full flex items-center justify-center gap-2 py-2.5 rounded-xl font-bold text-base transition-all select-none',
            voiceState === 'idle'       && 'bg-white hover:bg-red-50 active:scale-95 text-red-600 border-2 border-slate-800 shadow-sm',
            voiceState === 'recording'  && 'bg-red-600 text-white opacity-50 cursor-default',
            voiceState === 'processing' && 'bg-slate-700 text-slate-400 cursor-not-allowed',
            voiceState === 'pending' && pendingEvents.length > 0 && 'bg-green-700 text-white cursor-default',
          ].filter(Boolean).join(' ')}
          aria-label="Start voice logging"
        >
          {voiceState === 'processing' ? (
            <><Loader2 className="w-5 h-5 animate-spin" /> Processing…</>
          ) : voiceState === 'recording' ? (
            <><Mic className="w-5 h-5 animate-pulse" /> Recording…</>
          ) : voiceState === 'pending' ? (
            <><Check className="w-5 h-5" /> {pendingEvents.length} event{pendingEvents.length !== 1 ? 's' : ''} — tap below to confirm</>
          ) : (
            <><Mic className="w-5 h-5" /> Voice Log</>
          )}
        </button>
      )}

      {/* ── Voice recording fullscreen modal ──────────────────────────────── */}
      {canSeeVoiceLogging && (voiceState === 'recording' || voiceState === 'processing') && (
        <div className="fixed inset-0 z-[90] flex flex-col items-center justify-center bg-slate-950/95 backdrop-blur-sm px-6">
          {voiceState === 'recording' ? (
            <>
              {/* Pulsing mic */}
              <div className="relative mb-8">
                <div className="absolute inset-0 rounded-full bg-red-500/20 animate-ping" />
                <div className="absolute inset-[-8px] rounded-full bg-red-500/10 animate-ping" style={{ animationDelay: '150ms' }} />
                <div className="w-24 h-24 rounded-full bg-red-600 flex items-center justify-center shadow-2xl shadow-red-900/50">
                  <Mic className="w-10 h-10 text-white" />
                </div>
              </div>

              {/* Recording timer — the clip is transcribed by the model after you stop */}
              <div className="w-full max-w-sm min-h-[72px] flex flex-col items-center justify-center mb-8 gap-1">
                <p className="text-white text-3xl font-bold tabular-nums">
                  {Math.floor(recordingSeconds / 60)}:{String(recordingSeconds % 60).padStart(2, '0')}
                </p>
                <p className="text-slate-400 text-sm font-medium text-center">Listening… say the play, then tap stop</p>
              </div>

              {/* Stop button */}
              <button
                type="button"
                onPointerDown={e => { e.preventDefault(); stopRecording(); }}
                className="w-full max-w-sm py-4 rounded-2xl bg-red-600 hover:bg-red-700 active:scale-95 text-white font-bold text-base transition-all shadow-lg shadow-red-900/40 select-none"
              >
                <div className="flex items-center justify-center gap-2">
                  <MicOff className="w-5 h-5" />
                  Stop &amp; Parse
                </div>
              </button>

              {/* Cancel button */}
              <button
                type="button"
                onPointerDown={e => { e.preventDefault(); cancelRecording(); }}
                className="mt-3 w-full max-w-sm py-3 rounded-2xl text-slate-400 hover:text-slate-300 font-medium text-sm transition-colors select-none"
              >
                Cancel
              </button>
            </>
          ) : (
            <>
              {/* Processing state */}
              <div className="w-24 h-24 rounded-full bg-slate-800 flex items-center justify-center mb-8 shadow-2xl">
                <Loader2 className="w-10 h-10 text-blue-400 animate-spin" />
              </div>
              <p className="text-slate-400 text-sm font-medium">Transcribing &amp; parsing…</p>
            </>
          )}
        </div>
      )}

      <div className="flex flex-col gap-4">
         <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
             <div className="bg-slate-50 border-b border-slate-200 px-3 py-1.5 flex items-center gap-2 flex-wrap">
               {/* Our Court label */}
               <span className="font-black text-slate-700 text-[24px] shrink-0">Our Court</span>

               {/* Rotation grouped control */}
               <div className="flex items-center rounded-lg border border-blue-200 bg-blue-50 overflow-hidden shrink-0">
                 <button onClick={() => performRotation('us', 'ccw')} className="px-1.5 py-1 bg-blue-600 text-white hover:bg-blue-500 active:scale-95 transition-all" title="Back rotate"><RotateCcw className="w-3.5 h-3.5" /></button>
                 <select
                   value={(gameState.numerals as any).p1}
                   onChange={e => handleSetStartingRotation(e.target.value)}
                   className="text-[13px] bg-transparent text-blue-700 px-0.5 uppercase font-bold border-0 outline-none cursor-pointer"
                 >
                   {['I','II','III','IV','V','VI'].map((r, i) => (
                     <option key={r} value={r}>Rot {i + 1}</option>
                   ))}
                 </select>
                 <button onClick={() => performRotation('us', 'cw')} className="px-1.5 py-1 bg-blue-600 text-white hover:bg-blue-500 active:scale-95 transition-all" title="Rotate forward"><RotateCw className="w-3.5 h-3.5" /></button>
               </div>

               {/* Scan lineup sheet button — only in edit mode */}
               {isLineupEditMode && (
                 <button
                   type="button"
                   onClick={() => setShowLineupScanner(true)}
                   className="flex items-center gap-1 px-2 py-1 text-[13px] font-medium text-violet-700 border border-violet-300 bg-violet-50 hover:bg-violet-100 rounded-lg transition-colors whitespace-nowrap"
                   title="Scan lineup sheet"
                 >
                   <ScanLine className="w-3.5 h-3.5" />
                   <span className="hidden sm:inline">Scan</span>
                 </button>
               )}

               {/* Lineup selector — only in edit mode */}
               {isLineupEditMode && (
                 <select
                   value={activeLineupId || '__NONE__'}
                   onChange={(e) => {
                     if (e.target.value === '__NEW__') {
                       setNewLineupFlow('chooser');
                     } else if (e.target.value !== '__NONE__') {
                       tryLoadLineup(e.target.value);
                     }
                   }}
                   className="text-[15px] font-bold border border-slate-200 rounded-lg px-2 py-1 bg-white text-slate-700 outline-none cursor-pointer"
                 >
                   {!activeLineupId && <option value="__NONE__">— No lineup —</option>}
                   {teamLineups.map(l => (
                     <option key={l.id} value={l.id}>{l.isFavorite ? '★ ' : ''}{l.name}</option>
                   ))}
                   <option value="__NEW__">+ New Lineup</option>
                 </select>
               )}

               {/* Save lineup changes button (edit mode) */}
               {isLineupEditMode && activeLineupId && isActiveLineupDirty && (
                 <button
                   type="button"
                   onClick={handleUpdateActiveLineup}
                   disabled={isSavingActiveLineup}
                   className="flex items-center gap-1 px-2.5 py-1 bg-emerald-500 hover:bg-emerald-600 active:scale-95 disabled:opacity-60 text-white text-[13px] font-bold rounded-lg transition-all whitespace-nowrap"
                 >
                   <Save className="w-3.5 h-3.5" />
                   {isSavingActiveLineup ? 'Saving…' : 'Save'}
                 </button>
               )}

               {/* Libero chips */}
               <div className="flex items-center gap-1">
                 {liberoDesignations.us.length === 0 ? (
                   !isLineupEditMode && (
                     <button
                       onClick={() => setLiberoPickerModal({ side: 'us', slot: 0 })}
                       className="text-[15px] font-bold text-purple-500 border border-dashed border-purple-300 px-2 py-1 rounded-xl hover:bg-purple-50 transition-colors whitespace-nowrap"
                     >
                       + <span className="hidden sm:inline">Designate </span>Libero
                     </button>
                   )
                 ) : (
                   <>
                     {liberoDesignations.us.map((lid, idx) => {
                       const lp = teamPlayers.find(p => p.id === lid);
                       const servesForId = liberoServingLocks[lid];
                       const firstName = lp?.name?.split(' ')[0] ?? '';
                       const isTwoLib = liberoDesignations.us.length === 2;
                       const isActive = !isTwoLib || idx === activeLiberoSlot;
                       return (
                         <div
                           key={lid}
                           onClick={() => {
                             if (isTwoLib && !isActive) {
                               handleLiberoActiveSwap();
                             } else {
                               handleLiberoCardTap('us', lid);
                             }
                           }}
                           className={`flex items-center gap-1 text-white text-[17px] font-bold px-2.5 py-1 rounded-xl cursor-pointer transition-all ${
                             isActive
                               ? 'bg-purple-700 hover:bg-purple-600'
                               : 'bg-purple-900/70 opacity-60 hover:opacity-90 hover:bg-purple-800'
                           }`}
                           title={isTwoLib && !isActive ? 'Tap to activate this libero (L1 ⇄ L2 swap)' : 'Tap to reconfigure'}
                         >
                           {isTwoLib && (
                             <span className={`text-[10px] font-black px-1 py-0.5 rounded ${isActive ? 'bg-white/20' : 'bg-white/10'}`}>L{idx + 1}</span>
                           )}
                           <span className="sm:hidden">{lp?.jerseyNumber ? `#${lp.jerseyNumber}` : (isTwoLib ? '' : 'L')}</span>
                           <span className="hidden sm:inline">{isTwoLib ? '' : 'Libero'}{lp?.jerseyNumber ? ` · #${lp.jerseyNumber}` : ''}{firstName ? ` ${firstName}` : ''}</span>
                           {servesForId && isActive && <Lock className="w-2.5 h-2.5 text-amber-300 ml-0.5" />}
                           <button onClick={(e) => { e.stopPropagation(); handleLiberoRemove('us', lid); }} className="ml-1 p-1 rounded-full text-purple-300 hover:text-red-300 hover:bg-white/20 transition-colors"><X className="w-4 h-4" /></button>
                         </div>
                       );
                     })}
                     {liberoDesignations.us.length === 2 && (
                       <button
                         type="button"
                         onClick={handleLiberoActiveSwap}
                         className="flex items-center gap-1 px-2 py-1 bg-purple-900/80 text-purple-200 hover:bg-purple-800 text-[13px] font-bold rounded-lg border border-purple-600/50 active:scale-95 transition-all"
                         title="Swap active libero (L1 ⇄ L2)"
                       >
                         <ArrowLeftRight className="w-3.5 h-3.5" />
                         <span className="hidden sm:inline">L{activeLiberoSlot + 1} On</span>
                       </button>
                     )}
                     {appSettings.allowTwoLiberos && liberoDesignations.us.length < 2 && (
                       <button
                         onClick={() => setLiberoPickerModal({ side: 'us', slot: 1 })}
                         className="text-[15px] font-bold text-purple-500 border border-dashed border-purple-300 px-2 py-1 rounded-xl hover:bg-purple-50 transition-colors"
                       >
                         +L2
                       </button>
                     )}
                   </>
                 )}
               </div>

               {/* Scheduled subs + save lineup (non-edit mode) */}
               {!isLineupEditMode && (
                 <div className="ml-auto flex items-center gap-2">
                   {activeLineupId && isActiveLineupDirty && (
                     <button
                       type="button"
                       onClick={handleUpdateActiveLineup}
                       disabled={isSavingActiveLineup}
                       className="flex items-center gap-1 px-2.5 py-0.5 bg-emerald-500 hover:bg-emerald-600 active:scale-95 disabled:opacity-60 text-white text-[13px] font-bold rounded-lg transition-all whitespace-nowrap"
                     >
                       <Save className="w-3.5 h-3.5" />
                       {isSavingActiveLineup ? 'Saving…' : 'Save lineup'}
                     </button>
                   )}
                   <button
                     onClick={() => { setShowRotSubManager(true); setEditingRotSub(null); }}
                     className="text-[15px] font-bold text-indigo-500 border border-dashed border-indigo-300 px-2 py-0.5 rounded-lg hover:bg-indigo-50 transition-colors flex items-center gap-1 whitespace-nowrap"
                   >
                     <Repeat className="w-4 h-4" /><span className="hidden sm:inline">{rotationalSubs.length > 0 ? ` Sched Subs (${rotationalSubs.length})` : ' Sched Subs'}</span><span className="sm:hidden">{rotationalSubs.length > 0 ? ` Subs (${rotationalSubs.length})` : ' Subs'}</span>
                   </button>
                 </div>
               )}
             </div>
             {/* Marching-ants keyframes — injected only during edit mode */}
             {isLineupEditMode && (
               <style>{`
                 @keyframes marchingAnts {
                   0%   { background-position: 0 0,         100% 0,              100% 100%,        0 100%; }
                   100% { background-position: 16px 0,      100% 16px,           calc(100% - 16px) 100%, 0 calc(100% - 16px); }
                 }
                 .edit-mode-marching {
                   background:
                     linear-gradient(90deg, #93c5fd 50%, transparent 50%) top    / 16px 2px repeat-x,
                     linear-gradient(0deg,  #93c5fd 50%, transparent 50%) right  / 2px 16px repeat-y,
                     linear-gradient(90deg, #93c5fd 50%, transparent 50%) bottom / 16px 2px repeat-x,
                     linear-gradient(0deg,  #93c5fd 50%, transparent 50%) left   / 2px 16px repeat-y;
                   animation: marchingAnts 0.35s linear infinite;
                 }
               `}</style>
             )}
             {/* Wrapper: marching ants overlay encircles edit buttons + player grid together */}
             <div className="relative">
             {isLineupEditMode && (
               <div className="absolute inset-0 pointer-events-none z-10 edit-mode-marching" />
             )}
             {/* Lineup Edit Mode bar — shown before any points are scored */}
             {isLineupEditMode ? (
               <div className="flex flex-col gap-2 px-4 py-3">
                 {/* Serve-first picker + net orientation */}
                 <div className="flex gap-2">
                   {/* Segmented serve toggle */}
                   <div className="flex flex-1 rounded-xl border-2 border-slate-200 overflow-hidden">
                     <button
                       type="button"
                       onClick={() => {
                         setPossession('serving');
                         if (gameState.set === 1) setServeFirstSelection('us');
                         handleSetStartingRotation('I');
                       }}
                       className={`flex-1 flex items-center justify-center gap-1.5 text-sm font-black py-2.5 transition-all ${
                         possession === 'serving'
                           ? 'bg-blue-600 text-white shadow-inner'
                           : 'bg-white text-slate-500 hover:bg-blue-50 hover:text-blue-600'
                       }`}
                     >
                       <ArrowUp className="w-4 h-4 shrink-0" /> We Serve
                     </button>
                     <div className="w-0.5 bg-slate-200 shrink-0" />
                     <button
                       type="button"
                       onClick={() => {
                         setPossession('receiving');
                         if (gameState.set === 1) setServeFirstSelection('them');
                       }}
                       className={`flex-1 flex items-center justify-center gap-1.5 text-sm font-black py-2.5 transition-all ${
                         possession === 'receiving'
                           ? 'bg-red-600 text-white shadow-inner'
                           : 'bg-white text-slate-500 hover:bg-red-50 hover:text-red-600'
                       }`}
                     >
                       <ArrowDown className="w-4 h-4 shrink-0" /> Opp Serves
                     </button>
                   </div>
                   {/* Net orientation button */}
                   <button
                     type="button"
                     onClick={() => setShowOrientationOnboarding(true)}
                     title="Court Orientation"
                     className="flex-1 flex items-center justify-center gap-1.5 bg-white border-2 border-slate-200 rounded-xl px-3 py-2.5 text-slate-600 hover:border-slate-300 hover:bg-slate-50 active:scale-95 transition-all"
                   >
                     <span className="text-sm font-black">Net Orientation</span>
                     <img src="/images/volleyball_net_icon.webp" alt="net" className="w-5 h-5 object-contain shrink-0" />
                     <img src="/images/4 way arrow.png" alt="orientation" className="w-5 h-5 object-contain shrink-0" />
                   </button>
                 </div>

                 {/* Back-rotate button — only when receiving */}
                 {possession === 'receiving' && (
                   <button
                     onClick={() => {
                       const applying = (gameState.numerals as any).p1 !== 'VI';
                       handleSetStartingRotation(applying ? 'VI' : 'I');
                       setBackRotatePreference(applying);
                       try { localStorage.setItem('siq_backrotate', String(applying)); } catch {}
                     }}
                     className={`w-full flex items-center justify-center gap-2 text-sm font-black py-2.5 rounded-xl active:scale-95 transition-all border-2 ${
                       (gameState.numerals as any).p1 === 'VI'
                         ? 'bg-amber-500 border-amber-500 text-white shadow-md shadow-amber-900/20'
                         : 'bg-white border-amber-300 text-amber-600 hover:bg-amber-50'
                     }`}
                   >
                     <RotateCcw className="w-4 h-4 shrink-0" />
                     {(gameState.numerals as any).p1 === 'VI'
                       ? 'Back-rotated ✓ (Rotation VI)'
                       : 'Back-rotate to Rotation VI (Serve Receive)'}
                   </button>
                 )}

                 <div className="flex gap-2">
                   {appSettings.allowTwoLiberos && liberoDesignations.us.length >= 2 ? (
                     // Two-libero mode: show separate L1 / L2 buttons
                     <>
                       {[0, 1].map(slot => {
                         const lib = liberoDesignations.us[slot] ? teamPlayers.find(p => p.id === liberoDesignations.us[slot]) : null;
                         return (
                           <button
                             key={slot}
                             onClick={() => setLiberoPickerModal({ side: 'us', slot: slot as 0 | 1 })}
                             className="flex-1 flex flex-col items-center justify-center bg-purple-600 hover:bg-purple-700 text-white text-xs font-black py-2.5 rounded-xl active:scale-95 transition-all shadow-md shadow-purple-900/30"
                           >
                             <span className="text-[9px] font-bold text-purple-300 uppercase tracking-wider leading-none mb-0.5">L{slot + 1}</span>
                             {lib ? (
                               <span>#{lib.jerseyNumber || '?'} {lib.name.split(' ')[0]}</span>
                             ) : (
                               <span className="opacity-70">+ Libero {slot + 1}</span>
                             )}
                           </button>
                         );
                       })}
                     </>
                   ) : (
                     // Single libero button
                     <button
                       onClick={() => setLiberoPickerModal({ side: 'us', slot: 0 })}
                       className="flex-1 flex items-center justify-center gap-2 bg-purple-600 hover:bg-purple-700 text-white text-sm font-black py-3 rounded-xl active:scale-95 transition-all shadow-md shadow-purple-900/30"
                     >
                       {(() => {
                         const l0 = liberoDesignations.us[0] ? teamPlayers.find(p => p.id === liberoDesignations.us[0]) : null;
                         return l0 ? (
                           <>
                             <span className="sm:hidden">Lib #{l0.jerseyNumber || '?'}</span>
                             <span className="hidden sm:inline">Libero #{l0.jerseyNumber || '?'} {l0.name.split(' ')[0]}</span>
                           </>
                         ) : '+ Libero';
                       })()}
                     </button>
                   )}
                   <button
                     onClick={() => { setShowRotSubManager(true); setEditingRotSub(null); }}
                     className="flex-1 flex items-center justify-center gap-2 bg-slate-500 hover:bg-slate-600 text-white text-sm font-black py-3 rounded-xl active:scale-95 transition-all shadow-md shadow-slate-900/30"
                   >
                     <Repeat className="w-4 h-4" /><span className="hidden sm:inline"> Schedule Subs</span><span className="sm:hidden"> Sched Subs</span>{rotationalSubs.length > 0 ? ` (${rotationalSubs.length})` : ''}
                   </button>
                 </div>
                 <button
                   onClick={() => {
                     if (liberoDesignations.us.length === 0) {
                       setShowNoLiberoWarning(true);
                     } else {
                       setIsLineupEditMode(false);
                     }
                   }}
                   className="w-full flex items-center justify-center gap-2 bg-green-600 hover:bg-green-500 text-white text-lg font-black py-4 rounded-xl active:scale-95 transition-all shadow-lg shadow-green-900/30"
                 >
                   <Lock className="w-5 h-5" /> Lock Lineup (Rotation {(gameState.numerals as any).p1})
                 </button>
                 <div className="flex items-center justify-center gap-1.5">
                   <Pencil className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                   <span className="text-xs text-slate-500 font-bold">Tap any player to swap</span>
                 </div>
               </div>
             ) : (
               <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-200 bg-slate-50">
                 {isPreGame && (
                   <button
                     onClick={() => setIsLineupEditMode(true)}
                     className="w-full flex items-center justify-center gap-1.5 bg-green-600 hover:bg-green-500 text-white text-xs font-bold px-3 py-2 rounded-lg shadow-md transition-all active:scale-95"
                   >
                     <Pencil className="w-3.5 h-3.5" /> Edit Lineup
                   </button>
                 )}
               </div>
             )}
             {/* Block Assist Confirmation Bar — shown above our court when a block assist is in progress */}
             {blockAssistState?.side === 'us' && (
               <div className="flex items-center justify-between bg-purple-800 px-3 py-1.5 gap-2">
                 <div className="flex items-center gap-1.5">
                   <Shield className="w-3.5 h-3.5 text-purple-300 shrink-0" />
                   <span className="text-[11px] text-purple-200 font-bold">Tap a co-blocker to confirm (+1 pt)</span>
                 </div>
                 <button onClick={() => setBlockAssistState(null)} className="text-[10px] font-bold text-purple-300 px-2 py-1 rounded hover:text-white transition-colors shrink-0">Cancel</button>
               </div>
             )}
             {possession === 'receiving' && (
               <div className="flex items-center gap-1.5 mx-1 mt-1 shrink-0">
                 <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider shrink-0">Opp Serve</span>
                 <div className="flex flex-1 bg-slate-100 rounded-lg p-0.5 h-7">
                   <button
                     type="button"
                     onClick={() => handleSetIncomingServeType('float')}
                     className={`flex-1 text-[10px] font-bold rounded-md transition-colors ${incomingServeType === 'float' ? 'bg-sky-500 text-white shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}
                   >Float</button>
                   <button
                     type="button"
                     onClick={() => handleSetIncomingServeType('topspin')}
                     className={`flex-1 text-[10px] font-bold rounded-md transition-colors ${incomingServeType === 'topspin' ? 'bg-amber-500 text-white shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}
                   >Topspin</button>
                 </div>
               </div>
             )}
             <div className={isSideline ? 'flex gap-[5px]' : ''}>
               {courtOrientation === 'net-top' && (
                 <button type="button" title="Flip net side" onClick={() => setNetFlipConfirm(true)} className="w-full h-7 shrink-0 flex flex-row cursor-pointer hover:opacity-70 transition-opacity">
                   <div className="w-2.5 bg-black shrink-0" />
                   <div className="flex-1" style={{ backgroundImage: "repeating-linear-gradient(0deg, transparent 0px, transparent 5px, #111 5px, #111 6px), repeating-linear-gradient(90deg, transparent 0px, transparent 5px, #111 5px, #111 6px)", backgroundColor: '#d1d5db' }} />
                   <div className="w-2.5 bg-black shrink-0" />
                 </button>
               )}
               {courtOrientation === 'net-left' && (
                 <button type="button" title="Flip net side" onClick={() => setNetFlipConfirm(true)} className="w-7 shrink-0 flex flex-col cursor-pointer hover:opacity-70 transition-opacity">
                   <div className="h-2.5 bg-black shrink-0" />
                   <div className="flex-1" style={{ backgroundImage: "repeating-linear-gradient(0deg, transparent 0px, transparent 5px, #111 5px, #111 6px), repeating-linear-gradient(90deg, transparent 0px, transparent 5px, #111 5px, #111 6px)", backgroundColor: '#d1d5db' }} />
                   <div className="h-2.5 bg-black shrink-0" />
                 </button>
               )}
               <div className={`${courtGridClass} flex-1 border-[8px] border-blue-400 p-[5px] ${courtOrientation === 'net-left' ? 'border-l-0' : courtOrientation === 'net-right' ? 'border-r-0' : courtOrientation === 'net-top' ? 'border-t-0' : 'border-b-0'}`} style={isSideline ? { columnGap: '30px', rowGap: '5px' } : undefined}>
                 {COURT_POSITIONS[courtOrientation].map(pos => (
                   <div key={pos} className="bg-white">{renderCard('us', pos)}</div>
                 ))}
               </div>
               {courtOrientation === 'net-right' && (
                 <button type="button" title="Flip net side" onClick={() => setNetFlipConfirm(true)} className="w-7 shrink-0 flex flex-col cursor-pointer hover:opacity-70 transition-opacity">
                   <div className="h-2.5 bg-black shrink-0" />
                   <div className="flex-1" style={{ backgroundImage: "repeating-linear-gradient(0deg, transparent 0px, transparent 5px, #111 5px, #111 6px), repeating-linear-gradient(90deg, transparent 0px, transparent 5px, #111 5px, #111 6px)", backgroundColor: '#d1d5db' }} />
                   <div className="h-2.5 bg-black shrink-0" />
                 </button>
               )}
               {courtOrientation === 'standard' && (
                 <button type="button" title="Flip net side" onClick={() => setNetFlipConfirm(true)} className="w-full h-7 shrink-0 flex flex-row cursor-pointer hover:opacity-70 transition-opacity">
                   <div className="w-2.5 bg-black shrink-0" />
                   <div className="flex-1" style={{ backgroundImage: "repeating-linear-gradient(0deg, transparent 0px, transparent 5px, #111 5px, #111 6px), repeating-linear-gradient(90deg, transparent 0px, transparent 5px, #111 5px, #111 6px)", backgroundColor: '#d1d5db' }} />
                   <div className="w-2.5 bg-black shrink-0" />
                 </button>
               )}
             </div>
             </div>{/* end marching-ants wrapper */}
             {/* ── Quick Stat Bar ── */}
             {showStatBar && (
               <div className="bg-slate-900 mt-1 px-2 py-2 mb-5 rounded-xl">
                 {statBarErrorPicker ? (
                   <div className="animate-fade-in">
                     <div className="flex items-center justify-between mb-1.5 px-0.5">
                       <span className="text-[10px] text-red-400 font-bold uppercase tracking-wider">Error Type</span>
                       <button onClick={() => setStatBarErrorPicker(false)} className="text-slate-400 hover:text-white transition-colors p-0.5"><X className="w-3.5 h-3.5" /></button>
                     </div>
                     <div className="grid grid-cols-4 gap-1.5">
                       <button onClick={() => handleStatBarSubType('serve_error')} className="bg-red-700 hover:bg-red-600 text-white rounded-lg flex flex-col items-center justify-center py-3 border border-red-600 active:scale-95 transition-all"><span className="text-sm font-black uppercase">Serve</span></button>
                       <button onClick={() => handleStatBarSubType('attack_error')} className="bg-red-700 hover:bg-red-600 text-white rounded-lg flex flex-col items-center justify-center py-3 border border-red-600 active:scale-95 transition-all"><span className="text-sm font-black uppercase">Hit</span></button>
                       <button onClick={() => handleStatBarSubType('reception_error')} className="bg-red-700 hover:bg-red-600 text-white rounded-lg flex flex-col items-center justify-center py-3 border border-red-600 active:scale-95 transition-all"><span className="text-sm font-black uppercase">Pass</span></button>
                       <button onClick={() => handleStatBarSubType('error')} className="bg-red-900 hover:bg-red-800 text-white rounded-lg flex flex-col items-center justify-center py-3 border border-red-800 active:scale-95 transition-all"><span className="text-sm font-black uppercase">Other</span></button>
                     </div>
                   </div>
                 ) : statBarSRDetail !== null ? (
                   <div className={`transition-opacity duration-[400ms] ${statBarSRDetail.fading ? 'opacity-0 pointer-events-none' : 'opacity-100 animate-fade-in'}`}>
                     <div className="flex items-center justify-between mb-1.5 px-0.5">
                       <span className="text-[10px] text-sky-400 font-bold uppercase tracking-wider">Pass Detail — optional</span>
                       <button onClick={() => { if (statBarSRDetailTimerRef.current) clearTimeout(statBarSRDetailTimerRef.current); if (statBarSRDetailFadeRef.current) clearTimeout(statBarSRDetailFadeRef.current); setStatBarSRDetail(null); }} className="text-slate-400 hover:text-white transition-colors p-0.5"><X className="w-3.5 h-3.5" /></button>
                     </div>
                     <div className="grid grid-cols-4 gap-1.5">
                       {(() => { const ts = statBarSRDetail.timestamp; const isA = statBarSRDetail.serveStyle === 'topspin'; return (
                         <button key="topspin" onClick={() => { const ns = isA ? null : 'topspin' as const; setStatBarSRDetail(prev => prev ? { ...prev, serveStyle: ns } : null); setGameState(prev => ({ ...prev, stats: prev.stats.map(s => s.timestamp === ts ? { ...s, ...(ns ? { incomingServeStyle: ns } : { incomingServeStyle: undefined }) } : s) })); if (ns && statBarSRDetail.contactType) { if (statBarSRDetailTimerRef.current) clearTimeout(statBarSRDetailTimerRef.current); if (statBarSRDetailFadeRef.current) clearTimeout(statBarSRDetailFadeRef.current); setStatBarSRDetail(null); } }} className={`rounded-lg flex flex-col items-center justify-center py-2 border transition-all active:scale-95 ${isA ? 'bg-sky-500 border-sky-400 ring-2 ring-sky-400/60 text-white' : 'bg-slate-700 border-slate-600 text-slate-200 hover:bg-slate-600'}`}>
                           <span className="text-[clamp(10px,3.5vw,22px)] font-bold uppercase">Top</span>
                         </button>
                       ); })()}
                       {(() => { const ts = statBarSRDetail.timestamp; const isA = statBarSRDetail.serveStyle === 'float'; return (
                         <button key="float" onClick={() => { const ns = isA ? null : 'float' as const; setStatBarSRDetail(prev => prev ? { ...prev, serveStyle: ns } : null); setGameState(prev => ({ ...prev, stats: prev.stats.map(s => s.timestamp === ts ? { ...s, ...(ns ? { incomingServeStyle: ns } : { incomingServeStyle: undefined }) } : s) })); if (ns && statBarSRDetail.contactType) { if (statBarSRDetailTimerRef.current) clearTimeout(statBarSRDetailTimerRef.current); if (statBarSRDetailFadeRef.current) clearTimeout(statBarSRDetailFadeRef.current); setStatBarSRDetail(null); } }} className={`rounded-lg flex flex-col items-center justify-center py-2 border transition-all active:scale-95 ${isA ? 'bg-sky-500 border-sky-400 ring-2 ring-sky-400/60 text-white' : 'bg-slate-700 border-slate-600 text-slate-200 hover:bg-slate-600'}`}>
                           <span className="text-[clamp(10px,3.5vw,22px)] font-bold uppercase">Float</span>
                         </button>
                       ); })()}
                       {(() => { const ts = statBarSRDetail.timestamp; const isA = statBarSRDetail.contactType === 'hands'; return (
                         <button key="hands" onClick={() => { const nc = isA ? null : 'hands' as const; setStatBarSRDetail(prev => prev ? { ...prev, contactType: nc } : null); setGameState(prev => ({ ...prev, stats: prev.stats.map(s => s.timestamp === ts ? { ...s, ...(nc ? { contactType: nc } : { contactType: undefined }) } : s) })); if (nc && statBarSRDetail.serveStyle) { if (statBarSRDetailTimerRef.current) clearTimeout(statBarSRDetailTimerRef.current); if (statBarSRDetailFadeRef.current) clearTimeout(statBarSRDetailFadeRef.current); setStatBarSRDetail(null); } }} className={`rounded-lg flex flex-col items-center justify-center py-2 border transition-all active:scale-95 ${isA ? 'bg-cyan-500 border-cyan-400 ring-2 ring-cyan-400/60 text-white' : 'bg-slate-700 border-slate-600 text-slate-200 hover:bg-slate-600'}`}>
                           <span className="text-[clamp(10px,3.5vw,22px)] font-bold uppercase">Hands</span>
                         </button>
                       ); })()}
                       {(() => { const ts = statBarSRDetail.timestamp; const isA = statBarSRDetail.contactType === 'platform'; return (
                         <button key="platform" onClick={() => { const nc = isA ? null : 'platform' as const; setStatBarSRDetail(prev => prev ? { ...prev, contactType: nc } : null); setGameState(prev => ({ ...prev, stats: prev.stats.map(s => s.timestamp === ts ? { ...s, ...(nc ? { contactType: nc } : { contactType: undefined }) } : s) })); if (nc && statBarSRDetail.serveStyle) { if (statBarSRDetailTimerRef.current) clearTimeout(statBarSRDetailTimerRef.current); if (statBarSRDetailFadeRef.current) clearTimeout(statBarSRDetailFadeRef.current); setStatBarSRDetail(null); } }} className={`rounded-lg flex flex-col items-center justify-center py-2 border transition-all active:scale-95 ${isA ? 'bg-cyan-500 border-cyan-400 ring-2 ring-cyan-400/60 text-white' : 'bg-slate-700 border-slate-600 text-slate-200 hover:bg-slate-600'}`}>
                           <span className="text-[clamp(10px,3.5vw,22px)] font-bold uppercase">Platform</span>
                         </button>
                       ); })()}
                     </div>
                   </div>
                 ) : statBarSRPicker ? (
                   <div className="animate-fade-in">
                     <div className="flex items-center justify-between mb-1.5 px-0.5">
                       <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Pass Quality — tap a grade</span>
                       <button onClick={() => setStatBarSRPicker(false)} className="text-slate-400 hover:text-white transition-colors p-0.5">
                         <X className="w-3.5 h-3.5" />
                       </button>
                     </div>
                     <div className="grid grid-cols-4 gap-1.5">
                       {SR_GRADES.map(({ grade, label, bg, hover, border, ring }) => (
                         <button
                           key={grade}
                           onClick={() => handleStatBarSRGrade(grade)}
                           className={`${bg} ${hover} text-white rounded-lg flex flex-col items-center justify-center py-3 border ${border} active:scale-95 transition-all ring-0 hover:ring-2 ${ring}`}
                         >
                           <span className="stat-num sm leading-none">{grade}</span>
                           <span className="text-[10px] font-bold uppercase mt-0.5">{label}</span>
                         </button>
                       ))}
                     </div>
                   </div>
                 ) : (
                   <div className="grid grid-cols-8 gap-1.5">
                     {QUICK_STATS.map(stat => {
                       const { Icon } = stat;
                       const isActive = statBarFlash === stat.id || pendingStat?.statType === stat.statType;
                       return (
                         <button
                           key={stat.id}
                           onClick={() => handleStatBarTap(stat)}
                           className={`flex flex-col items-center justify-center py-2.5 rounded-lg transition-all active:scale-95 ${
                             isActive
                               ? `${stat.activeBg} ${stat.textClass} ring-2 inset-ring ${stat.ring} scale-105 shadow-sm z-10`
                               : `${stat.bgClass} ${stat.textClass} hover:opacity-80`
                           }`}
                         >
                           <Icon className="w-5 h-5 mb-0.5" />
                           <span className="text-[13px] font-black uppercase tracking-tight leading-tight">{stat.label}</span>
                         </button>
                       );
                     })}
                   </div>
                 )}
               </div>
             )}
             {/* Assignment Mode Banner */}
             {pendingStat && (
               <div className="flex items-center justify-between px-3 py-2 bg-blue-600 text-white">
                 {pendingStat.multiAssign ? (
                   <>
                     <span className="text-[12px] font-bold">
                       Tap blockers to assign ↑{multiAssignedIds.length > 0 ? ` (${multiAssignedIds.length} selected)` : ''}
                     </span>
                     <div className="flex items-center gap-2">
                       <button
                         onClick={() => { setPendingStat(null); setMultiAssignedIds([]); }}
                         className="text-[11px] font-bold text-blue-200 hover:text-white px-2 py-0.5 rounded hover:bg-blue-500 transition-colors"
                       >
                         Cancel
                       </button>
                       {multiAssignedIds.length > 0 && (
                         <button
                           onClick={() => { setPendingStat(null); setMultiAssignedIds([]); }}
                           className="text-[11px] font-bold text-white bg-blue-500 hover:bg-blue-400 px-3 py-0.5 rounded transition-colors"
                         >
                           Done
                         </button>
                       )}
                     </div>
                   </>
                 ) : (
                   <>
                     <span className="text-[12px] font-bold">Tap a player to assign ↑</span>
                     <button
                       onClick={() => setPendingStat(null)}
                       className="text-[11px] font-bold text-blue-200 hover:text-white px-2 py-0.5 rounded hover:bg-blue-500 transition-colors"
                     >
                       Cancel
                     </button>
                   </>
                 )}
               </div>
             )}
             {ENABLE_OPPONENT_VISUALIZER && (<>
             <div className="h-3 bg-slate-100 border-y border-slate-200" />
             <div className="border-t border-slate-200">
                {/* Single opponent row — name, rotation, lineup pills, libero, save — all in one red bar */}
                <div className="flex items-stretch bg-red-600 border-b border-red-700">
                  {/* Expand toggle with opponent name + rotation */}
                  <button
                    onClick={() => setIsOpponentExpanded(!isOpponentExpanded)}
                    className="flex items-center gap-1.5 px-3 py-2 hover:bg-red-700 transition-colors shrink-0 border-r border-red-700"
                  >
                    {isOpponentExpanded ? <ChevronUp className="w-3 h-3 text-red-200" /> : <ChevronDown className="w-3 h-3 text-red-200" />}
                    <span className="text-[11px] font-black text-white">{gameState.opponent}</span>
                  </button>

                  {/* Lineup selector dropdown */}
                  <div className="flex-1 flex items-center px-2 min-w-0">
                    {opponentTeamId && opponentLineups.length > 0 ? (
                      <select
                        value={activeOpponentLineupId || ''}
                        onChange={(e) => { if (e.target.value) handleLoadLineup(e.target.value, 'them'); }}
                        className="w-full text-[10px] font-bold bg-red-700/50 text-white border border-red-500/50 rounded-lg px-2 py-1 outline-none cursor-pointer"
                      >
                        {!activeOpponentLineupId && <option value="" className="text-slate-900 bg-white">— select —</option>}
                        {opponentLineups.map(l => (
                          <option key={l.id} value={l.id} className="text-slate-900 bg-white">{l.name}</option>
                        ))}
                      </select>
                    ) : (
                      <span className="text-[9px] text-red-200 italic">
                        {!opponentTeamId ? (gameState.opponent === 'Guest' ? 'Select opponent' : 'No profile') : 'No lineups'}
                      </span>
                    )}
                  </div>

                  {/* Opponent Libero selector (simplified — designation only, no sandwich required) */}
                  {opponentRoster.length > 0 && (
                    <select
                      className="text-[10px] border-l border-red-700 px-2 outline-none font-bold shrink-0 cursor-pointer text-white"
                      style={{ background: 'rgba(153, 27, 27, 0.85)' }}
                      value={liberoDesignations.them[0] || ''}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === '__ADD_PLAYER__') {
                          setQuickCreateSide('them');
                          setQuickCreateTargetPos('__libero__');
                          setNewPlayerForm({ name: '', number: '', position: 'Libero' });
                          setIsQuickCreating(true);
                        } else {
                          setLiberoDesignations(prev => ({ ...prev, them: val ? [val] : [] }));
                        }
                      }}
                    >
                      <option value="" className="text-slate-900 bg-white">No Libero</option>
                      {opponentRoster.map(p => (
                        <option key={p.id} value={p.id} className="text-slate-900 bg-white">
                          #{('jerseyNumber' in p) ? (p as Player).jerseyNumber : (p as SimplePlayer).number} - {p.name} {p.position === 'Libero' ? '(L)' : ''}
                        </option>
                      ))}
                      <option value="__ADD_PLAYER__" className="text-blue-600 bg-white font-bold">+ Add Player</option>
                    </select>
                  )}

                  {/* Opponent rotation grouped control */}
                  <div className="flex items-center border-l border-red-700 overflow-hidden shrink-0">
                    <button onClick={() => performRotation('them', 'ccw')} className="px-1.5 py-1.5 bg-red-800 text-white hover:bg-red-900 active:scale-95 transition-all" title="Back rotate"><RotateCcw className="w-3.5 h-3.5" /></button>
                    <select
                      value={(gameState.opponentNumerals as any).op1}
                      onChange={e => handleSetStartingRotationThem(e.target.value)}
                      className="text-[11px] bg-transparent text-red-100 px-0.5 uppercase font-bold border-0 outline-none cursor-pointer"
                    >
                      {['I','II','III','IV','V','VI'].map((r, i) => (
                        <option key={r} value={r} className="text-slate-900 bg-white">Rot {i + 1}</option>
                      ))}
                    </select>
                    <button onClick={() => performRotation('them', 'cw')} className="px-1.5 py-1.5 bg-red-800 text-white hover:bg-red-900 active:scale-95 transition-all" title="Rotate forward"><RotateCw className="w-3.5 h-3.5" /></button>
                  </div>

                  {/* Save lineup shortcut */}
                  <button
                    onClick={handleSaveOpponentLineup}
                    disabled={isSavingOpponentLineup}
                    className="shrink-0 px-3 py-2 text-[9px] font-bold text-white hover:bg-red-700 transition-colors border-l border-red-700 disabled:opacity-40 uppercase tracking-wide"
                  >
                    {isSavingOpponentLineup ? '…' : '+ Save'}
                  </button>
                </div>

                {isOpponentExpanded && (
                  <div className="animate-fade-in-down">
                    {/* Block Assist Confirmation Bar — opponent side */}
                    {blockAssistState?.side === 'them' && (
                      <div className="flex items-center justify-between bg-purple-800 px-3 py-1.5 gap-2">
                        <div className="flex items-center gap-1.5">
                          <Shield className="w-3.5 h-3.5 text-purple-300 shrink-0" />
                          <span className="text-[11px] text-purple-200 font-bold">Tap a co-blocker to confirm (+1 pt)</span>
                        </div>
                        <button onClick={() => setBlockAssistState(null)} className="text-[10px] font-bold text-purple-300 px-2 py-1 rounded hover:text-white transition-colors shrink-0">Cancel</button>
                      </div>
                    )}
                    <div className={isSideline ? 'flex gap-[5px]' : ''}>
                      {courtOrientation === 'standard' && (
                        <div className="w-full h-7 shrink-0 flex flex-row" title="Net">
                          <div className="w-2.5 bg-black shrink-0" />
                          <div className="flex-1" style={{ backgroundImage: "repeating-linear-gradient(0deg, transparent 0px, transparent 5px, #111 5px, #111 6px), repeating-linear-gradient(90deg, transparent 0px, transparent 5px, #111 5px, #111 6px)", backgroundColor: '#d1d5db' }} />
                          <div className="w-2.5 bg-black shrink-0" />
                        </div>
                      )}
                      {courtOrientation === 'net-left' && (
                        <div className="w-7 shrink-0 flex flex-col" title="Net">
                          <div className="h-2.5 bg-black shrink-0" />
                          <div className="flex-1" style={{ backgroundImage: "repeating-linear-gradient(0deg, transparent 0px, transparent 5px, #111 5px, #111 6px), repeating-linear-gradient(90deg, transparent 0px, transparent 5px, #111 5px, #111 6px)", backgroundColor: '#d1d5db' }} />
                          <div className="h-2.5 bg-black shrink-0" />
                        </div>
                      )}
                      <div className={`${courtGridClass} flex-1`} style={isSideline ? { columnGap: '30px', rowGap: '5px' } : undefined}>
                        {OPP_COURT_POSITIONS[courtOrientation].map(pos => (
                          <div key={pos} className="bg-white">{renderCard('them', pos)}</div>
                        ))}
                      </div>
                      {courtOrientation === 'net-right' && (
                        <div className="w-7 shrink-0 flex flex-col" title="Net">
                          <div className="h-2.5 bg-black shrink-0" />
                          <div className="flex-1" style={{ backgroundImage: "repeating-linear-gradient(0deg, transparent 0px, transparent 5px, #111 5px, #111 6px), repeating-linear-gradient(90deg, transparent 0px, transparent 5px, #111 5px, #111 6px)", backgroundColor: '#d1d5db' }} />
                          <div className="h-2.5 bg-black shrink-0" />
                        </div>
                      )}
                      {courtOrientation === 'net-top' && (
                        <div className="w-full h-7 shrink-0 flex flex-row" title="Net">
                          <div className="w-2.5 bg-black shrink-0" />
                          <div className="flex-1" style={{ backgroundImage: "repeating-linear-gradient(0deg, transparent 0px, transparent 5px, #111 5px, #111 6px), repeating-linear-gradient(90deg, transparent 0px, transparent 5px, #111 5px, #111 6px)", backgroundColor: '#d1d5db' }} />
                          <div className="w-2.5 bg-black shrink-0" />
                        </div>
                      )}
                    </div>
                  </div>
                )}
             </div>
             </>)}
         </div>
         {ENABLE_AI_COACH && (
           <GameSuggestions
             suggestions={aiSuggestions}
             isLoading={isLoadingAI}
             isStale={isAISuggestionStale}
             hasEnoughData={hasEnoughData}
             onRefresh={handleGenerateSuggestions}
           />
         )}
         <GameStats stats={gameState.stats} players={players} currentSet={gameState.set} history={events} />
      </div>

      {showNewSetConfirm && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-sm animate-fade-in-up">
            <div className="flex items-center gap-3 mb-1">
              <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center shrink-0">
                <Flag className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h3 className="font-bold text-lg text-slate-900">Start Set {gameState.set + 1}?</h3>
                <p className="text-xs text-slate-500">Scores and timeouts reset. Stats carry over. The set win goes to whoever is leading ({gameState.ourScore > gameState.theirScore ? (activeTeam?.name || 'Us') : gameState.theirScore > gameState.ourScore ? gameState.opponent : 'tied — no winner awarded'}).</p>
              </div>
            </div>
            <div className="flex gap-2 mt-5">
              <button onClick={() => setShowNewSetConfirm(false)} className="flex-1 py-3 text-slate-500 font-bold hover:bg-slate-50 rounded-xl transition-colors">Cancel</button>
              <button onClick={() => { triggerSetInsights(); handleNewSet(); }} className="flex-1 bg-blue-600 text-white font-bold py-3 rounded-xl hover:bg-blue-700 transition-all active:scale-95">Start Set {gameState.set + 1}</button>
            </div>
            <button
              onClick={() => { setShowNewSetConfirm(false); setShowEndMatchConfirm(true); }}
              className="w-full mt-2 py-2.5 flex items-center justify-center gap-2 text-orange-600 font-bold text-sm hover:bg-orange-50 rounded-xl transition-colors"
            >
              <Trophy className="w-4 h-4" /> End Match
            </button>
          </div>
        </div>
      )}
      {showEndMatchConfirm && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-sm animate-fade-in-up">
            {isSubmitting ? (
              <div className="flex flex-col items-center gap-4 py-2">
                <div className="w-12 h-12 bg-orange-100 rounded-2xl flex items-center justify-center">
                  <Trophy className="w-6 h-6 text-orange-500" />
                </div>
                <div className="text-center">
                  <p className="font-bold text-slate-800 text-base mb-0.5">Saving Match…</p>
                  <p className="text-xs text-slate-400">Uploading stats and finalizing your record</p>
                </div>
                {/* Animated progress bar */}
                <div className="w-full bg-slate-100 rounded-full h-2 overflow-hidden">
                  <div
                    className="h-2 bg-orange-500 rounded-full"
                    style={{ width: `${saveProgressFill}%`, transition: 'width 6s cubic-bezier(0.1, 0.4, 0.4, 1)' }}
                  />
                </div>
                <p className="text-[11px] text-slate-400">Please don't close the app</p>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-3 mb-1">
                  <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center shrink-0">
                    <Trophy className="w-5 h-5 text-orange-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg text-slate-900">End Match?</h3>
                    <p className="text-xs text-slate-500">
                      {selectedMatchId
                        ? 'Stats will be saved to history and the tracker will be cleared. This cannot be undone.'
                        : 'The tracker will be cleared. No match is selected, so stats will not be saved to history.'}
                    </p>
                  </div>
                </div>
                <div className="flex gap-2 mt-5">
                  <button onClick={() => setShowEndMatchConfirm(false)} className="flex-1 py-3 text-slate-500 font-bold hover:bg-slate-50 rounded-xl transition-colors">Cancel</button>
                  <button
                    onClick={handleEndMatch}
                    className="flex-1 bg-orange-500 text-white font-bold py-3 rounded-xl hover:bg-orange-600 transition-all active:scale-95"
                  >
                    End Match
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
      {showNoLiberoWarning && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-sm animate-fade-in-up">
            <div className="flex items-center gap-3 mb-1">
              <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center shrink-0">
                <AlertCircle className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <h3 className="font-bold text-lg text-slate-900">No Libero Designated</h3>
                <p className="text-xs text-slate-500">Are you sure you want to lock in without designating a libero?</p>
              </div>
            </div>
            <div className="flex gap-2 mt-5">
              <button
                onClick={() => { setShowNoLiberoWarning(false); setIsLineupEditMode(false); }}
                className="flex-1 py-3 text-slate-500 font-bold hover:bg-slate-50 rounded-xl transition-colors text-sm"
              >
                Lock In Anyway
              </button>
              <button
                onClick={() => { setShowNoLiberoWarning(false); setLiberoPickerModal({ side: 'us', slot: 0 }); }}
                className="flex-1 bg-purple-600 text-white font-bold py-3 rounded-xl hover:bg-purple-700 transition-all active:scale-95 text-sm"
              >
                Designate Libero
              </button>
            </div>
          </div>
        </div>
      )}
      {liberoRedesignAlert && (() => {
        const roster = liberoRedesignAlert.side === 'us' ? teamPlayers : opponentRoster;
        const playerLabel = (pid: string) => {
          const p = roster.find(pl => pl.id === pid);
          return p ? `${p.jerseyNumber ? `#${p.jerseyNumber} ` : ''}${p.name.split(' ')[0]}` : null;
        };
        return (
          <div className="fixed inset-0 z-[85] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
            <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-sm animate-fade-in-up">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center shrink-0">
                  <AlertCircle className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-bold text-lg text-slate-900">Confirm Libero</h3>
                  <p className="text-xs text-slate-500">Lineup changed — review the updated libero assignment below.</p>
                </div>
              </div>
              <div className="flex flex-col gap-3">
                {liberoRedesignAlert.liberoIds.map(lid => {
                  const liberoPlayer = roster.find(pl => pl.id === lid);
                  const liberoName = liberoPlayer ? `${liberoPlayer.jerseyNumber ? `#${liberoPlayer.jerseyNumber} ` : ''}${liberoPlayer.name.split(' ')[0]}` : 'Libero';
                  const suggested = liberoRedesignAlert.suggestedSandwiches[lid] || [];
                  const hasSuggestion = suggested.length > 0;
                  return (
                    <div key={lid} className="border border-slate-200 rounded-xl overflow-hidden">
                      <div className="bg-purple-700 text-white px-3 py-2 text-xs font-bold">{liberoName}</div>
                      <div className="px-3 py-3">
                        {hasSuggestion ? (
                          <div className="flex flex-wrap gap-1.5 mb-3">
                            {suggested.map(pid => {
                              const label = playerLabel(pid);
                              return label ? (
                                <span key={pid} className="bg-blue-100 text-blue-800 text-xs font-bold px-2.5 py-1 rounded-full">{label}</span>
                              ) : null;
                            })}
                          </div>
                        ) : (
                          <p className="text-xs text-slate-400 italic mb-3">No replacement in lineup — please assign manually.</p>
                        )}
                        <button
                          onClick={() => {
                            setLiberoRedesignAlert(null);
                            setLiberoConfigModal({
                              side: liberoRedesignAlert.side,
                              liberoId: lid,
                              player1Id: suggested[0] || '',
                              player2Id: suggested[1] || '',
                              servesForPlayerId: '',
                            });
                          }}
                          className="w-full py-2 bg-blue-600 text-white text-sm font-bold rounded-lg hover:bg-blue-700 active:scale-95 transition-all"
                        >
                          {hasSuggestion ? 'Confirm ↗' : 'Edit Assignment'}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
              <button onClick={() => setLiberoRedesignAlert(null)} className="w-full mt-4 py-2.5 text-slate-400 font-bold hover:bg-slate-50 rounded-xl transition-colors text-sm">Dismiss</button>
            </div>
          </div>
        );
      })()}
      {/* Configure Stats Modal */}
      {showConfigStatsModal && setAppSettings && (() => {
        const toggles: AdvancedStatToggles = { ...DEFAULT_ADVANCED_STAT_TOGGLES, ...appSettings.advancedStatToggles };
        const handleToggle = (key: keyof AdvancedStatToggles) => {
          setAppSettings(prev => ({
            ...prev,
            advancedStatToggles: { ...DEFAULT_ADVANCED_STAT_TOGGLES, ...prev.advancedStatToggles, [key]: !toggles[key] },
          }));
        };
        const items: { key: keyof AdvancedStatToggles; label: string; description: string }[] = [
          { key: 'serveTypeTracking', label: 'Serve Type Tracking', description: 'Track Float vs Topspin on serves.' },
          { key: 'killHitTypeRow', label: 'Kill Hit Type', description: 'Show Driven / Shot / Tip row after a kill.' },
          { key: 'killOutcomeRow', label: 'Kill Outcome', description: 'Show Tool / Floor / Shank row after a kill.' },
          { key: 'errorDetailOverlay', label: 'Error Detail Overlay', description: 'Show error type picker on attack errors.' },
          { key: 'serveReceiveDetail', label: 'Serve-Receive Detail', description: 'Capture serve style and contact type on receptions.' },
        ];
        return (
          <div className="fixed inset-0 z-[95] flex items-end sm:items-center justify-center bg-black/60 p-4 backdrop-blur-sm" onClick={() => setShowConfigStatsModal(false)}>
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm animate-fade-in-up" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-slate-100">
                <h3 className="font-black text-slate-800 text-lg flex items-center gap-2">
                  <BarChart2 className="w-5 h-5 text-blue-600" /> Stat Tracking
                </h3>
                <button type="button" onClick={() => setShowConfigStatsModal(false)} className="text-slate-400 hover:text-slate-600 transition-colors">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-5 space-y-4">
                {/* Simple / Advanced toggle */}
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="font-semibold text-slate-700 text-sm">Stats Mode</div>
                    <p className="text-xs text-slate-500 mt-0.5">
                      {appSettings.simpleStats
                        ? 'Simple: fewer prompts, faster tracking.'
                        : 'Advanced: captures extra detail per action.'}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setAppSettings(prev => ({ ...prev, simpleStats: !prev.simpleStats }))}
                    className={`shrink-0 px-4 py-2 rounded-lg border text-sm font-bold transition-colors ${appSettings.simpleStats ? 'bg-blue-50 border-blue-300 text-blue-700' : 'bg-orange-50 border-orange-300 text-orange-700'}`}
                  >
                    {appSettings.simpleStats ? 'Simple Stats' : 'Advanced Stats'}
                  </button>
                </div>
                {/* Advanced toggles */}
                {!appSettings.simpleStats && (
                  <div className="border-l-2 border-orange-200 pl-4 space-y-3">
                    {items.map(({ key, label, description }) => (
                      <div key={key} className="flex items-start justify-between gap-3">
                        <div>
                          <div className="text-sm font-semibold text-slate-700">{label}</div>
                          <p className="text-xs text-slate-500">{description}</p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer shrink-0 mt-0.5">
                          <input type="checkbox" className="sr-only peer" checked={toggles[key]} onChange={() => handleToggle(key)} />
                          <div className="w-9 h-5 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-orange-500"></div>
                        </label>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="px-5 pb-5">
                <button
                  type="button"
                  onClick={() => setShowConfigStatsModal(false)}
                  className="w-full py-2.5 bg-slate-800 hover:bg-slate-700 text-white font-bold rounded-xl transition-colors active:scale-95"
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        );
      })()}
      {/* Net flip confirm */}
      {showOrientationOnboarding && (
        <div className="fixed inset-0 z-[95] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl p-5 w-full max-w-sm animate-fade-in-up">
            <div className="text-center mb-4">
              <h3 className="font-black text-slate-800 text-lg mb-1">Court Orientation</h3>
              <p className="text-sm text-slate-500 leading-relaxed">
                Which way is the net on your screen? Pick the layout that matches how you're watching the court.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3 mb-3">
              {([
                {
                  value: 'standard' as CourtOrientation,
                  label: 'Net Bottom',
                  diagram: (
                    <div className="flex flex-col items-stretch" style={{ width: 48 }}>
                      <div className="grid bg-slate-200 gap-px p-0.5 rounded-t-sm" style={{ gridTemplateColumns: 'repeat(3,1fr)', height: 30 }}>
                        {[...Array(6)].map((_, i) => <div key={i} className="bg-slate-50 rounded-[1px]" />)}
                      </div>
                      <div className="bg-blue-500 rounded-b" style={{ height: 6 }} />
                    </div>
                  ),
                },
                {
                  value: 'net-top' as CourtOrientation,
                  label: 'Net Top',
                  diagram: (
                    <div className="flex flex-col items-stretch" style={{ width: 48 }}>
                      <div className="bg-blue-500 rounded-t" style={{ height: 6 }} />
                      <div className="grid bg-slate-200 gap-px p-0.5 rounded-b-sm" style={{ gridTemplateColumns: 'repeat(3,1fr)', height: 30 }}>
                        {[...Array(6)].map((_, i) => <div key={i} className="bg-slate-50 rounded-[1px]" />)}
                      </div>
                    </div>
                  ),
                },
                {
                  value: 'net-right' as CourtOrientation,
                  label: 'Net Right',
                  diagram: (
                    <div className="flex flex-row items-stretch" style={{ height: 36 }}>
                      <div className="grid bg-slate-200 gap-px p-0.5 rounded-l-sm" style={{ gridTemplateColumns: 'repeat(3,1fr)', width: 42 }}>
                        {[...Array(6)].map((_, i) => <div key={i} className="bg-slate-50 rounded-[1px]" />)}
                      </div>
                      <div className="bg-blue-500 rounded-r" style={{ width: 6 }} />
                    </div>
                  ),
                },
                {
                  value: 'net-left' as CourtOrientation,
                  label: 'Net Left',
                  diagram: (
                    <div className="flex flex-row items-stretch" style={{ height: 36 }}>
                      <div className="bg-blue-500 rounded-l" style={{ width: 6 }} />
                      <div className="grid bg-slate-200 gap-px p-0.5 rounded-r-sm" style={{ gridTemplateColumns: 'repeat(3,1fr)', width: 42 }}>
                        {[...Array(6)].map((_, i) => <div key={i} className="bg-slate-50 rounded-[1px]" />)}
                      </div>
                    </div>
                  ),
                },
              ]).map(({ value, label, diagram }) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => handleOrientationOnboardingSelect(value)}
                  className={`flex flex-col items-center gap-2.5 p-3 rounded-xl border-2 transition-all active:scale-95 ${
                    courtOrientation === value
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-slate-200 hover:border-blue-300 hover:bg-slate-50'
                  }`}
                >
                  {diagram}
                  <span className="text-xs font-bold text-slate-700">{label}</span>
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={() => handleOrientationOnboardingSelect(courtOrientation)}
              className="w-full text-xs text-slate-400 hover:text-slate-600 py-1.5 transition-colors"
            >
              Keep current &amp; continue
            </button>
          </div>
        </div>
      )}

      {netFlipConfirm && (() => {
        const flipTarget: CourtOrientation = courtOrientation === 'net-right' ? 'net-left' : courtOrientation === 'net-left' ? 'net-right' : courtOrientation === 'net-top' ? 'standard' : 'net-top';
        const flipLabel = courtOrientation === 'net-right' ? 'left' : courtOrientation === 'net-left' ? 'right' : courtOrientation === 'net-top' ? 'bottom' : 'top';
        return (
        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl p-5 w-full max-w-sm animate-fade-in-up">
            <h3 className="font-black text-slate-800 text-lg mb-1 text-center">Flip Net Side?</h3>
            <p className="text-sm text-slate-500 text-center mb-4">
              Switch net to the {flipLabel} side?
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setNetFlipConfirm(false)}
                className="flex-1 py-3 text-slate-500 font-bold hover:bg-slate-50 rounded-xl transition-colors"
              >Cancel</button>
              <button
                onClick={() => {
                  handleCourtOrientationChange(flipTarget);
                  setNetFlipConfirm(false);
                }}
                className="flex-1 py-3 bg-blue-600 text-white font-bold rounded-xl hover:bg-blue-700 transition-all active:scale-95"
              >Flip</button>
            </div>
          </div>
        </div>
        );
      })()}
      {/* Stat Bar Sub-Type Picker */}
      {statBarSubTypePicker?.kind === 'block' && (
        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl p-5 w-full max-w-sm animate-fade-in-up">
            <h3 className="font-black text-slate-800 text-lg mb-4 text-center">Block Type</h3>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => handleStatBarSubType('block_solo')}
                className="bg-indigo-600 hover:bg-indigo-500 active:bg-indigo-700 text-white rounded-xl py-6 flex flex-col items-center gap-2 active:scale-95 transition-all"
              >
                <Shield className="w-8 h-8" />
                <span className="text-base font-black uppercase">Solo</span>
                <span className="text-[11px] opacity-75">1.0 block</span>
              </button>
              <button
                onClick={() => handleStatBarSubType('block_assist')}
                className="bg-purple-600 hover:bg-purple-500 active:bg-purple-700 text-white rounded-xl py-6 flex flex-col items-center gap-2 active:scale-95 transition-all"
              >
                <Users className="w-8 h-8" />
                <span className="text-base font-black uppercase">Assist</span>
                <span className="text-[11px] opacity-75">0.5 each · tap each blocker</span>
              </button>
            </div>
            <button
              onClick={() => setStatBarSubTypePicker(null)}
              className="mt-3 w-full py-2.5 text-slate-500 font-bold text-sm hover:bg-slate-50 rounded-xl transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
      {showClearConfirm === 'choose' && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-sm animate-fade-in-up">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center shrink-0">
                <Trash2 className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h3 className="font-bold text-lg text-slate-900">Clear Game Data?</h3>
                <p className="text-xs text-slate-500">Choose what you'd like to clear.</p>
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <button
                type="button"
                onClick={() => setShowClearConfirm('set')}
                className="text-left w-full px-4 py-3 rounded-xl border border-slate-200 hover:border-amber-400 hover:bg-amber-50 transition-colors active:scale-[0.98]"
              >
                <div className="font-bold text-slate-800 text-sm">Clear Set {gameState.set}</div>
                <div className="text-xs text-slate-500 mt-0.5">Reset this set's score and stats only. Previous sets are preserved.</div>
              </button>
              <button
                type="button"
                onClick={() => setShowClearConfirm('match')}
                className="text-left w-full px-4 py-3 rounded-xl border border-slate-200 hover:border-red-400 hover:bg-red-50 transition-colors active:scale-[0.98]"
              >
                <div className="font-bold text-slate-800 text-sm">Clear Entire Match</div>
                <div className="text-xs text-slate-500 mt-0.5">Reset all sets, scores, and stats from the beginning.</div>
              </button>
            </div>
            <button type="button" onClick={() => setShowClearConfirm(null)} className="w-full mt-3 py-2.5 text-slate-500 font-bold hover:bg-slate-50 rounded-xl transition-colors">Cancel</button>
          </div>
        </div>
      )}
      {showClearConfirm === 'set' && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-sm animate-fade-in-up">
            <div className="flex items-center gap-3 mb-1">
              <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center shrink-0">
                <Trash2 className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <h3 className="font-bold text-lg text-slate-900">Clear Set {gameState.set}?</h3>
                <p className="text-xs text-slate-500">This set's score, stats, and rotations will be reset. Previous sets are preserved. This cannot be undone.</p>
              </div>
            </div>
            <div className="flex gap-2 mt-5">
              <button type="button" onClick={() => setShowClearConfirm('choose')} className="flex-1 py-3 text-slate-500 font-bold hover:bg-slate-50 rounded-xl transition-colors">Back</button>
              <button type="button" onClick={handleClearSet} className="flex-1 bg-amber-500 text-white font-bold py-3 rounded-xl hover:bg-amber-600 transition-all active:scale-95">Clear Set {gameState.set}</button>
            </div>
          </div>
        </div>
      )}
      {showClearConfirm === 'match' && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-sm animate-fade-in-up">
            <div className="flex items-center gap-3 mb-1">
              <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center shrink-0">
                <Trash2 className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h3 className="font-bold text-lg text-slate-900">Clear Entire Match?</h3>
                <p className="text-xs text-slate-500">All sets, scores, stats, and rotations will be reset to the beginning. This cannot be undone.</p>
              </div>
            </div>
            <div className="flex gap-2 mt-5">
              <button type="button" onClick={() => setShowClearConfirm('choose')} className="flex-1 py-3 text-slate-500 font-bold hover:bg-slate-50 rounded-xl transition-colors">Back</button>
              <button type="button" onClick={handleClearGame} className="flex-1 bg-red-600 text-white font-bold py-3 rounded-xl hover:bg-red-700 transition-all active:scale-95">Clear & Restart</button>
            </div>
          </div>
        </div>
      )}
      {/* Success banner */}
      {submitSuccessBanner && (
        <div className="fixed bottom-24 left-1/2 -translate-x-1/2 z-[90] bg-green-600 text-white text-sm font-bold px-5 py-3 rounded-full shadow-lg flex items-center gap-2 animate-fade-in-up">
          <Check className="w-4 h-4" /> Match results submitted!
        </div>
      )}
      {/* Error banner */}
      {submitErrorBanner && (
        <div className="fixed bottom-24 left-1/2 -translate-x-1/2 z-[90] bg-red-600 text-white text-sm font-bold px-5 py-3 rounded-full shadow-lg flex items-center gap-2 animate-fade-in-up">
          <AlertCircle className="w-4 h-4" /> Save failed — your game is still running. Try again.
        </div>
      )}
      {/* Offline save queued banner */}
      {offlineSaveBanner && (
        <div className="fixed bottom-24 left-1/2 -translate-x-1/2 z-[90] bg-amber-600 text-white text-sm font-bold px-5 py-3 rounded-full shadow-lg flex items-center gap-2 animate-fade-in-up">
          <WifiOff className="w-4 h-4" /> No connection — match saved locally, will sync when online.
        </div>
      )}
      {/* Offline sync success banner */}
      {syncSuccessBanner && (
        <div className="fixed bottom-24 left-1/2 -translate-x-1/2 z-[90] bg-green-600 text-white text-sm font-bold px-5 py-3 rounded-full shadow-lg flex items-center gap-2 animate-fade-in-up">
          <Check className="w-4 h-4" /> Back online — match results synced!
        </div>
      )}
      {/* Persistent pending sync indicator */}
      {pendingOfflineSave && !offlineSaveBanner && !syncSuccessBanner && (
        <div className="fixed bottom-6 right-4 z-[90] bg-amber-500 text-white text-xs font-bold px-3 py-2 rounded-full shadow-lg flex items-center gap-1.5">
          <CloudUpload className="w-3.5 h-3.5" /> Pending sync
        </div>
      )}
      {showSubModal && subContext && (
         <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
            <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-lg animate-fade-in-up">
               <div className="flex justify-between items-center mb-4"><h3 className="font-bold text-lg text-slate-900">{subContext.currentPlayerId ? 'Substitute Player' : 'Assign Player'}</h3><button onClick={() => setShowSubModal(false)}><X className="w-5 h-5 text-slate-400" /></button></div>
               {subContext.side === 'us' && subContext.currentPlayerId && (() => {
                 const remaining = appSettings.substitutionsPerSet - gameState.substitutionsUsed.us;
                 if (remaining > 3) return null;
                 const colorClass = remaining <= 0
                   ? 'bg-red-50 border-red-200 text-red-700'
                   : remaining === 1
                     ? 'bg-orange-50 border-orange-200 text-orange-700'
                     : 'bg-amber-50 border-amber-200 text-amber-700';
                 const msg = remaining <= 0
                   ? 'No subs remaining'
                   : remaining === 1
                     ? '1 sub remaining'
                     : `${remaining} subs remaining`;
                 return (
                   <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-bold mb-3 ${colorClass}`}>
                     <AlertCircle className="w-4 h-4 shrink-0" />
                     {msg}
                   </div>
                 );
               })()}
               <div className="grid grid-cols-3 gap-2 max-h-80 overflow-y-auto">
                  {(() => {
                    const allPlayers = subContext.side === 'us' ? teamPlayers : opponentRoster;
                    const currentLineup = subContext.side === 'us' ? gameState.lineup : gameState.opponentLineup;
                    // Position of the player currently in this slot (for same-position sort)
                    const currentPlayer = allPlayers.find(p => p.id === subContext.currentPlayerId);
                    const slotPosition = ('position' in (currentPlayer ?? {})) ? (currentPlayer as Player).position : '';
                    const parseNum = (p: typeof allPlayers[0]) => parseInt((('jerseyNumber' in p ? (p as Player).jerseyNumber : (p as SimplePlayer).number) ?? '').replace(/\D/g, '') || '0') || 0;
                    // Libero designees are managed exclusively through the libero sandwich system.
                    // Allowing them as a normal sub would place them directly in a lineup slot,
                    // causing them to appear on court twice (once via sandwich, once via the slot).
                    const liberoDesigs = subContext.side === 'us' ? liberoDesignations.us : liberoDesignations.them;
                    const courtPlayerIds = new Set(Object.values(currentLineup).filter(Boolean));
                    return allPlayers
                      .filter(p => p.id !== subContext.currentPlayerId) // exclude current slot's occupant
                      .filter(p => !liberoDesigs.includes(p.id))
                      .sort((a, b) => {
                        const aOnCourt = courtPlayerIds.has(a.id);
                        const bOnCourt = courtPlayerIds.has(b.id);
                        const aPos = ('position' in a) ? (a as Player).position : '';
                        const bPos = ('position' in b) ? (b as Player).position : '';
                        const aSame = slotPosition && aPos === slotPosition ? 0 : 1;
                        const bSame = slotPosition && bPos === slotPosition ? 0 : 1;
                        // bench players before on-court, same-position before different
                        const aScore = (aOnCourt ? 10 : 0) + aSame;
                        const bScore = (bOnCourt ? 10 : 0) + bSame;
                        if (aScore !== bScore) return aScore - bScore;
                        return parseNum(a) - parseNum(b);
                      })
                      .map(p => {
                        const isOnCourt = courtPlayerIds.has(p.id);
                        const isSamePos = !!(slotPosition && ('position' in p) && (p as Player).position === slotPosition);
                        return (
                          <button
                            key={p.id}
                            onClick={() => {
                              updateLineupGeneric(subContext.side, subContext.pos, p.id);
                              if (subContext.side === 'us' && subContext.currentPlayerId) {
                                setGameState(prev => ({ ...prev, substitutionsUsed: { ...prev.substitutionsUsed, us: prev.substitutionsUsed.us + 1 } }));
                              }
                              setShowSubModal(false);
                              // If either the incoming or outgoing player is in a libero sandwich,
                              // open the libero config modal after state settles so user can review.
                              // Only prompt libero config when the player going OFF was in a sandwich
                              // (their sandwich partner is now gone and needs reconfiguring).
                              // Suppress the popup when only the incoming player is in a sandwich —
                              // the existing config remains valid in that case.
                              const affectedLiberoId = subContext.currentPlayerId
                                ? Object.keys(liberoSandwiches).find(libId =>
                                    liberoSandwiches[libId]?.includes(subContext.currentPlayerId)
                                  )
                                : undefined;
                              if (affectedLiberoId) {
                                setTimeout(() => handleLiberoCardTapRef.current?.(subContext.side, affectedLiberoId), 50);
                              }
                            }}
                            className={`flex flex-col items-center justify-center p-3 border rounded-lg transition-colors relative ${
                              isOnCourt
                                ? 'border-amber-300 bg-amber-50 hover:bg-amber-100'
                                : isSamePos
                                  ? 'border-blue-300 bg-blue-50 hover:bg-blue-100'
                                  : 'border-slate-200 hover:bg-blue-50'
                            }`}
                          >
                            {isOnCourt && (
                              <span className="absolute top-1 right-1 text-[8px] font-black text-amber-600 leading-none">COURT</span>
                            )}
                            <span className="font-black text-xl text-slate-800">
                              {('jerseyNumber' in p) ? (p as Player).jerseyNumber : (p as SimplePlayer).number}
                            </span>
                            <span className="text-xs font-bold text-slate-600 truncate w-full text-center">{p.name}</span>
                            {('position' in p) && (p as Player).position && <span className="text-[10px] text-slate-400 mt-0.5">{(p as Player).position}</span>}
                          </button>
                        );
                      });
                  })()}
               </div>
               <button onClick={() => { setShowSubModal(false); setQuickCreateSide(subContext.side); setQuickCreateTargetPos(subContext.pos); setNewPlayerForm({ name: '', number: '', position: 'Unknown' }); setIsQuickCreating(true); }} className="mt-3 w-full py-2.5 bg-slate-100 border border-slate-200 text-slate-700 font-bold text-sm rounded-xl hover:bg-slate-200 transition-colors">+ New Player</button>
            </div>
         </div>
      )}

      {/* ── Rotational Subs Manager Modal ─────────────────────────────────── */}
      {showRotSubManager && (
        <div className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-black/60 p-4 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[85vh] flex flex-col animate-fade-in-up">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
              <h3 className="font-black text-slate-900 flex items-center gap-2">
                <Repeat className="w-5 h-5 text-purple-500" /> Rotational Substitutions
              </h3>
              <button onClick={() => { setShowRotSubManager(false); setEditingRotSub(null); }}><X className="w-5 h-5 text-slate-400" /></button>
            </div>
            <div className="overflow-y-auto flex-1">
              {rotationalSubs.length === 0 && !editingRotSub && (
                <p className="px-5 py-4 text-sm text-slate-400">No rotational subs configured.</p>
              )}
              {rotationalSubs.map(sub => {
                const playerOff = teamPlayers.find(p => p.id === sub.playerOffId);
                const playerOn = teamPlayers.find(p => p.id === sub.playerOnId);
                const triggerLabels: Record<string, string> = {
                  front_row: 'Front Row',
                  back_row: 'Back Row',
                  after_serving: 'After Serving',
                  rotation_number: `Rotation ${sub.triggerRotation}`,
                  serve_receive_rotation: `SR Rot ${sub.triggerRotation}`,
                };
                return (
                  <div key={sub.id} className="flex items-center gap-2 px-5 py-3 border-b border-slate-100">
                    <span className="font-semibold text-slate-700 text-sm">#{playerOff?.jerseyNumber} {playerOff?.name?.split(' ')[0]}</span>
                    <span className="text-slate-300 text-xs">→</span>
                    <span className="text-[11px] font-bold text-purple-700 bg-purple-50 border border-purple-200 px-2 py-0.5 rounded-full whitespace-nowrap">{triggerLabels[sub.triggerType]}</span>
                    <span className="text-slate-300 text-xs">→</span>
                    <span className="font-semibold text-emerald-700 text-sm">#{playerOn?.jerseyNumber} {playerOn?.name?.split(' ')[0]}</span>
                    {sub.ignoreFirstTime && <span className="text-[10px] text-slate-400 italic">skip 1st</span>}
                    <button
                      onClick={() => setRotationalSubs(prev => prev.filter(s => s.id !== sub.id))}
                      className="ml-auto p-1 text-slate-300 hover:text-red-500 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                );
              })}

              {/* Add new sub form */}
              {editingRotSub ? (
                <div className="px-5 py-4 flex flex-col gap-3 border-t border-slate-100">
                  {isSuggestedRotSub ? (
                    <div className="flex items-center gap-2 bg-indigo-50 border border-indigo-200 rounded-lg px-3 py-2 text-xs text-indigo-700 font-bold">
                      <Repeat className="w-3.5 h-3.5 shrink-0" />
                      Suggested reverse sub — edit or dismiss as needed
                    </div>
                  ) : (
                    <p className="text-xs font-bold text-slate-500 uppercase tracking-wide">New Substitution Rule</p>
                  )}
                  {/* Player OFF */}
                  <div className="flex items-center gap-3">
                    <label className="text-xs font-bold text-slate-500 w-16 shrink-0">Sub Out</label>
                    <select
                      value={editingRotSub.playerOffId || ''}
                      onChange={e => setEditingRotSub(p => ({ ...p, playerOffId: e.target.value }))}
                      className="flex-1 text-sm border border-slate-300 rounded-lg px-2 py-2 outline-none focus:ring-2 focus:ring-purple-400"
                    >
                      <option value="">— Player on court —</option>
                      {(() => {
                        if (isSuggestedRotSub) return teamPlayers;
                        const onCourt = new Set(Object.values(gameState.lineup).filter(Boolean));
                        // Also include players who will be on court via other scheduled subs
                        rotationalSubs.forEach(s => onCourt.add(s.playerOnId));
                        return teamPlayers.filter(pl => onCourt.has(pl.id));
                      })().map(p => (
                        <option key={p.id} value={p.id}>#{p.jerseyNumber} {p.name}</option>
                      ))}
                    </select>
                  </div>
                  {/* Trigger type */}
                  <div className="flex items-center gap-3">
                    <label className="text-xs font-bold text-slate-500 w-16 shrink-0">Trigger</label>
                    <select
                      value={editingRotSub.triggerType || 'front_row'}
                      onChange={e => setEditingRotSub(p => ({ ...p, triggerType: e.target.value as any, triggerRotation: undefined }))}
                      className="flex-1 text-sm border border-slate-300 rounded-lg px-2 py-2 outline-none focus:ring-2 focus:ring-purple-400"
                    >
                      <option value="front_row">A) When they reach front row</option>
                      <option value="back_row">B) When they reach the back row (before serving)</option>
                      <option value="after_serving">C) After they serve</option>
                      <option value="rotation_number">D1) At specific rotation #</option>
                      <option value="serve_receive_rotation">D2) Serve receive at rotation #</option>
                    </select>
                  </div>
                  {/* Rotation number for D1/D2 */}
                  {(editingRotSub.triggerType === 'rotation_number' || editingRotSub.triggerType === 'serve_receive_rotation') && (
                    <div className="flex items-center gap-3">
                      <label className="text-xs font-bold text-slate-500 w-16 shrink-0">Rotation #</label>
                      <select
                        value={editingRotSub.triggerRotation || 1}
                        onChange={e => setEditingRotSub(p => ({ ...p, triggerRotation: parseInt(e.target.value) }))}
                        className="text-sm border border-slate-300 rounded-lg px-2 py-2 outline-none focus:ring-2 focus:ring-purple-400"
                      >
                        {[1,2,3,4,5,6].map(n => <option key={n} value={n}>Rotation {n}</option>)}
                      </select>
                    </div>
                  )}
                  {/* Player ON */}
                  <div className="flex items-center gap-3">
                    <label className="text-xs font-bold text-slate-500 w-16 shrink-0">Sub In</label>
                    <select
                      value={editingRotSub.playerOnId || ''}
                      onChange={e => setEditingRotSub(p => ({ ...p, playerOnId: e.target.value }))}
                      className="flex-1 text-sm border border-slate-300 rounded-lg px-2 py-2 outline-none focus:ring-2 focus:ring-purple-400"
                    >
                      <option value="">— Bench player —</option>
                      {(() => {
                        if (isSuggestedRotSub) return teamPlayers;
                        const onCourt = new Set(Object.values(gameState.lineup).filter(Boolean));
                        // Also include players who will be on the bench via other scheduled subs (subbed off)
                        const willBeBenched = new Set(rotationalSubs.map(s => s.playerOffId));
                        return teamPlayers.filter(p => !onCourt.has(p.id) || willBeBenched.has(p.id));
                      })().map(p => (
                        <option key={p.id} value={p.id}>#{p.jerseyNumber} {p.name}</option>
                      ))}
                    </select>
                  </div>
                  {/* Ignore first time */}
                  <label className="flex items-center gap-2 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={editingRotSub.ignoreFirstTime || false}
                      onChange={e => setEditingRotSub(p => ({ ...p, ignoreFirstTime: e.target.checked }))}
                      className="rounded border-slate-300 text-purple-600"
                    />
                    <span className="text-sm text-slate-600">Ignore first trigger</span>
                  </label>
                  <div className="flex gap-2">
                    <button onClick={() => { setEditingRotSub(null); setIsSuggestedRotSub(false); }} className="flex-1 py-2 text-sm font-bold text-slate-600 hover:bg-slate-100 rounded-lg transition-colors">{isSuggestedRotSub ? 'Skip' : 'Cancel'}</button>
                    <button
                      onClick={() => {
                        if (!editingRotSub.playerOffId || !editingRotSub.playerOnId) return;
                        const newSub: RotationalSubstitution = {
                          id: `rs-${Date.now()}`,
                          playerOffId: editingRotSub.playerOffId,
                          playerOnId: editingRotSub.playerOnId,
                          triggerType: editingRotSub.triggerType || 'front_row',
                          triggerRotation: editingRotSub.triggerRotation,
                          ignoreFirstTime: editingRotSub.ignoreFirstTime || false,
                        };
                        setRotationalSubs(prev => [...prev, newSub]);
                        // Auto-suggest the reverse sub for position-based triggers
                        const reverseTrigger: Record<string, RotationalSubstitution['triggerType']> = {
                          front_row: 'back_row', back_row: 'front_row', after_serving: 'front_row',
                          serve_receive_rotation: 'rotation_number',
                        };
                        const rt = reverseTrigger[newSub.triggerType];
                        if (rt && !isSuggestedRotSub) {
                          setIsSuggestedRotSub(true);
                          setEditingRotSub({ playerOffId: newSub.playerOnId, playerOnId: newSub.playerOffId, triggerType: rt, triggerRotation: newSub.triggerRotation, ignoreFirstTime: false });
                        } else {
                          setIsSuggestedRotSub(false);
                          setEditingRotSub(null);
                        }
                      }}
                      disabled={!editingRotSub.playerOffId || !editingRotSub.playerOnId || (
                        (editingRotSub.triggerType === 'rotation_number' || editingRotSub.triggerType === 'serve_receive_rotation') && !editingRotSub.triggerRotation
                      )}
                      className="flex-1 py-2 text-sm font-bold bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                      Add Sub
                    </button>
                  </div>
                </div>
              ) : (
                <div className="px-5 py-3">
                  <button
                    onClick={() => setEditingRotSub({ triggerType: 'front_row', ignoreFirstTime: false })}
                    className="w-full py-2.5 border-2 border-dashed border-purple-200 text-purple-500 font-bold text-sm rounded-xl hover:bg-purple-50 transition-colors flex items-center justify-center gap-2"
                  >
                    <Plus className="w-4 h-4" /> Add Rotational Sub
                  </button>
                </div>
              )}
            </div>
            <div className="px-5 py-4 border-t border-slate-100">
              <button
                onClick={() => { setShowRotSubManager(false); setEditingRotSub(null); }}
                className="w-full py-2.5 bg-slate-100 text-slate-700 font-bold rounded-xl hover:bg-slate-200 transition-colors"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Pending Rotational Sub Confirmation Modal ──────────────────────── */}
      {pendingSubConfirms.length > 0 && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm animate-fade-in-up">
            <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2">
              <Repeat className="w-5 h-5 text-purple-500 shrink-0" />
              <h3 className="font-black text-slate-900">Sub Alert</h3>
              <span className="ml-auto text-xs font-bold text-purple-600 bg-purple-50 px-2 py-0.5 rounded-full">{pendingSubConfirms.length} pending</span>
            </div>
            {(() => {
              const remaining = appSettings.substitutionsPerSet - gameState.substitutionsUsed.us;
              if (remaining > 3) return null;
              const colorClass = remaining <= 0
                ? 'bg-red-50 border-red-200 text-red-700'
                : remaining === 1
                  ? 'bg-orange-50 border-orange-200 text-orange-700'
                  : 'bg-amber-50 border-amber-200 text-amber-700';
              const msg = remaining <= 0
                ? 'No subs remaining'
                : remaining === 1
                  ? '1 sub remaining'
                  : `${remaining} subs remaining`;
              return (
                <div className={`flex items-center gap-2 mx-5 mt-3 px-3 py-2 rounded-lg border text-sm font-bold ${colorClass}`}>
                  <AlertCircle className="w-4 h-4 shrink-0" />
                  {msg}
                </div>
              );
            })()}
            <div className="divide-y divide-slate-100">
              {pendingSubConfirms.map(sub => {
                const playerOff = teamPlayers.find(p => p.id === sub.playerOffId);
                const playerOn = teamPlayers.find(p => p.id === sub.playerOnId);
                const confirmSub = (s: RotationalSubstitution) => {
                  // Find position of playerOff in current lineup and replace with playerOn
                  const offPos = Object.entries(gameState.lineup).find(([, id]) => id === s.playerOffId)?.[0];
                  if (offPos) {
                    setGameState(prev => ({
                      ...prev,
                      lineup: { ...prev.lineup, [offPos]: s.playerOnId },
                      substitutionsUsed: { ...prev.substitutionsUsed, us: prev.substitutionsUsed.us + 1 },
                    }));
                    // Keep libero sandwich in sync: if the subbed-out player was a sandwich partner,
                    // transfer that relationship to the incoming player
                    liberoDesignations.us.forEach(liberoId => {
                      const sandwich = liberoSandwiches[liberoId] || [];
                      if (sandwich.includes(s.playerOffId)) {
                        const newSandwich = sandwich.map((pid: string) => pid === s.playerOffId ? s.playerOnId : pid);
                        setLiberoSandwiches(prev => ({ ...prev, [liberoId]: newSandwich }));
                        if (liberoServingLocks[liberoId] === s.playerOffId) {
                          setLiberoServingLocks(prev => ({ ...prev, [liberoId]: s.playerOnId }));
                        }
                      }
                    });
                  }
                  setPendingSubConfirms(prev => prev.filter(x => x.id !== s.id));
                };
                const ignoreSub = (s: RotationalSubstitution) => {
                  setPendingSubConfirms(prev => prev.filter(x => x.id !== s.id));
                };
                return (
                  <div key={sub.id} className="px-5 py-4">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="flex-1">
                        <div className="text-xs text-slate-400 mb-0.5">Subbing out</div>
                        <div className="font-black text-slate-800">#{playerOff?.jerseyNumber} {playerOff?.name}</div>
                      </div>
                      <div className="text-slate-300 text-lg font-bold">→</div>
                      <div className="flex-1 text-right">
                        <div className="text-xs text-slate-400 mb-0.5">Coming in</div>
                        <div className="font-black text-emerald-700">#{playerOn?.jerseyNumber} {playerOn?.name}</div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => ignoreSub(sub)}
                        className="flex-1 py-2 text-sm font-bold text-slate-500 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
                      >
                        Ignore
                      </button>
                      <button
                        onClick={() => confirmSub(sub)}
                        className="flex-1 py-2 text-sm font-bold text-white bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors"
                      >
                        <Check className="w-4 h-4 inline mr-1" /> Confirm
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
            {pendingSubConfirms.length > 1 && (
              <div className="px-5 pb-4">
                <button
                  onClick={() => {
                    const newLineup = { ...gameState.lineup };
                    let extraSubs = 0;
                    pendingSubConfirms.forEach(sub => {
                      const offPos = Object.entries(newLineup).find(([, id]) => id === sub.playerOffId)?.[0];
                      if (offPos) { (newLineup as any)[offPos] = sub.playerOnId; extraSubs++; }
                    });
                    setGameState(prev => ({
                      ...prev,
                      lineup: newLineup as typeof prev.lineup,
                      substitutionsUsed: { ...prev.substitutionsUsed, us: prev.substitutionsUsed.us + extraSubs },
                    }));
                    // Keep libero sandwiches in sync for all confirmed subs
                    liberoDesignations.us.forEach(liberoId => {
                      const sandwich = liberoSandwiches[liberoId] || [];
                      const newSandwich = sandwich.map(pid => {
                        const sub = pendingSubConfirms.find(s => s.playerOffId === pid);
                        return sub ? sub.playerOnId : pid;
                      });
                      if (newSandwich.some((pid, i) => pid !== sandwich[i])) {
                        setLiberoSandwiches(prev => ({ ...prev, [liberoId]: newSandwich }));
                        const lockSub = pendingSubConfirms.find(s => liberoServingLocks[liberoId] === s.playerOffId);
                        if (lockSub) setLiberoServingLocks(prev => ({ ...prev, [liberoId]: lockSub.playerOnId }));
                      }
                    });
                    setPendingSubConfirms([]);
                  }}
                  className="w-full py-2.5 text-sm font-bold text-white bg-emerald-600 hover:bg-emerald-700 rounded-xl transition-colors flex items-center justify-center gap-2"
                >
                  <Check className="w-4 h-4" /> Confirm All
                </button>
              </div>
            )}
          </div>
        </div>
      )}
      {/* ── Saved Game Conflict Warning ─────────────────────────────────── */}
      {showSavedGameWarning && upcomingGameForSwitch && (
        <div className="fixed inset-0 bg-black/60 z-[80] flex items-end sm:items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
            <div className="bg-amber-50 border-b border-amber-200 px-5 py-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
              <div>
                <h3 className="font-bold text-slate-900">Unfinished game detected</h3>
                <p className="text-sm text-slate-600 mt-0.5">
                  You have stats in progress from a previous session, but a different game is on today's schedule.
                </p>
              </div>
            </div>

            <div className="px-5 py-4 space-y-3">
              {/* Previous game summary */}
              <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm">
                <div className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1">In-progress game</div>
                <div className="font-bold text-slate-800">vs {gameState.opponent || 'Unknown'}</div>
                <div className="text-slate-500 text-xs mt-0.5">
                  {gameState.stats.length} stat{gameState.stats.length !== 1 ? 's' : ''} recorded
                  {(gameState.ourSetsWon > 0 || gameState.theirSetsWon > 0) && ` · Sets ${gameState.ourSetsWon}–${gameState.theirSetsWon}`}
                </div>
              </div>

              {/* Upcoming game */}
              <div className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm">
                <div className="text-xs font-semibold text-blue-400 uppercase tracking-wide mb-1">Today's scheduled game</div>
                <div className="font-bold text-blue-900">vs {upcomingGameForSwitch.opponent || 'Opponent'}</div>
                <div className="text-blue-600 text-xs mt-0.5">{upcomingGameForSwitch.date}{upcomingGameForSwitch.location ? ` · ${upcomingGameForSwitch.location}` : ''}</div>
              </div>
            </div>

            <div className="px-5 pb-5 space-y-2">
              <button
                onClick={handleSaveAndSwitch}
                disabled={isSubmitting}
                className="w-full py-3 rounded-xl bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white font-bold text-sm flex items-center justify-center gap-2 transition-colors"
              >
                <Save className="w-4 h-4" />
                {isSubmitting ? 'Saving…' : 'Save previous game & switch to today\'s game'}
              </button>
              <button
                onClick={handleDiscardAndSwitch}
                className="w-full py-3 rounded-xl bg-red-50 hover:bg-red-100 text-red-700 font-bold text-sm flex items-center justify-center gap-2 border border-red-200 transition-colors"
              >
                <Trash2 className="w-4 h-4" />
                Discard previous stats & switch to today's game
              </button>
              <button
                onClick={() => setShowSavedGameWarning(false)}
                className="w-full py-3 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium text-sm transition-colors"
              >
                Continue previous game (vs {gameState.opponent || 'Unknown'})
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Voice error toast */}
      {canSeeVoiceLogging && voiceError && voiceState === 'idle' && (
        <div className="fixed bottom-24 right-5 z-[70] max-w-[220px] bg-red-600 text-white text-xs px-3 py-2 rounded-xl shadow-lg flex items-center gap-2">
          <span className="flex-1">{voiceError}</span>
          <button type="button" onClick={() => clearPending()} className="shrink-0"><X className="w-3.5 h-3.5" /></button>
        </div>
      )}

      {/* ── Voice confirmation sheet ── */}
      {canSeeVoiceLogging && voiceState === 'pending' && pendingEvents.length > 0 && (() => {
        const STAT_BADGE: Record<string, string> = {
          kill: 'bg-red-100 text-red-700', attack_error: 'bg-orange-100 text-orange-700',
          attack_attempt: 'bg-slate-100 text-slate-500', ace: 'bg-green-100 text-green-700',
          serve_attempt: 'bg-slate-100 text-slate-500', serve_error: 'bg-red-100 text-red-700',
          block_solo: 'bg-purple-100 text-purple-700', block_assist: 'bg-purple-100 text-purple-700',
          block_error: 'bg-orange-100 text-orange-700', dig: 'bg-blue-100 text-blue-700',
          dig_error: 'bg-orange-100 text-orange-700', assist: 'bg-amber-100 text-amber-700',
          ball_handling_error: 'bg-orange-100 text-orange-700', reception: 'bg-teal-100 text-teal-700',
          reception_error: 'bg-red-100 text-red-700', error: 'bg-red-100 text-red-700',
        };
        const STAT_LABEL: Record<string, string> = {
          kill: 'Kill', attack_error: 'Att Err', attack_attempt: 'Attempt',
          ace: 'Ace', serve_attempt: 'Serve', serve_error: 'Svc Err',
          block_solo: 'Block', block_assist: 'Blk Ast', block_error: 'Blk Err',
          dig: 'Dig', dig_error: 'Dig Err', assist: 'Assist',
          ball_handling_error: 'BHE', reception: 'Pass', reception_error: 'Pass Err',
          error: 'Error',
        };
        const ALL_STAT_TYPES = Object.keys(STAT_LABEL) as (keyof typeof STAT_LABEL)[];
        const hasWarning = pendingEvents.some(e => e.confidence < 0.7);

        // Active pickers
        const editingEvent = voiceEditIndex !== null ? pendingEvents[voiceEditIndex] : null;
        const pickerRoster = editingEvent?.eventType === 'stat' && editingEvent.team === 'opponent'
          ? (opponentRoster as Player[])
          : teamPlayers;
        const filteredRoster = voicePlayerSearch.trim()
          ? pickerRoster.filter(p => p.name.toLowerCase().includes(voicePlayerSearch.toLowerCase()) || p.jerseyNumber?.includes(voicePlayerSearch))
          : pickerRoster;

        return (
          <>
            <div className="fixed inset-0 z-[75] bg-black/30" onClick={() => { closeVoiceEdit(); clearPending(); }} />
            <div className="fixed z-[80] bg-white rounded-2xl shadow-2xl flex flex-col max-h-[85vh] w-[calc(100%-2rem)] sm:w-[480px] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
              {/* Header */}
              <div className="flex items-center justify-between px-5 pt-4 pb-3 border-b border-slate-100 shrink-0">
                {voiceEditField ? (
                  <>
                    <button type="button" onClick={closeVoiceEdit} className="flex items-center gap-1.5 text-blue-600 text-sm font-medium">
                      ← Back
                    </button>
                    <span className="font-semibold text-slate-800 text-sm">
                      {{ player: 'Select player', statType: 'Select stat type', passQuality: 'Pass quality', serveType: 'Serve type', serveTypeReceived: 'Receive type', setLocation: 'Set location', attackLocation: 'Attack location', attackType: 'Attack type' }[voiceEditField!] ?? 'Edit'}
                    </span>
                    <div className="w-12" />
                  </>
                ) : (
                  <>
                    <div className="flex flex-col min-w-0">
                      <div className="flex items-baseline gap-2">
                        <span className="font-bold text-slate-900 text-sm">
                          {pendingEvents.length} event{pendingEvents.length !== 1 ? 's' : ''} logged
                        </span>
                        {hasWarning && <span className="text-xs text-amber-600 font-medium">⚠ review flagged</span>}
                      </div>
                      {liveTranscript && (
                        <p className="text-xs text-slate-500 italic truncate" title={liveTranscript}>Heard: "{liveTranscript}"</p>
                      )}
                    </div>
                    <button type="button" onClick={() => { closeVoiceEdit(); clearPending(); }}><X className="w-5 h-5 text-slate-400" /></button>
                  </>
                )}
              </div>

              {/* ── Player picker ── */}
              {voiceEditField === 'player' && (
                <div className="flex flex-col flex-1 min-h-0">
                  <div className="px-4 pt-3 pb-2 shrink-0">
                    <input
                      autoFocus
                      type="text"
                      placeholder="Search name or jersey #"
                      value={voicePlayerSearch}
                      onChange={e => setVoicePlayerSearch(e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
                    />
                  </div>
                  <div className="overflow-y-auto flex-1 divide-y divide-slate-100">
                    {/* Unresolved option */}
                    <button
                      type="button"
                      className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-50 active:bg-slate-100"
                      onClick={() => {
                        if (voiceEditIndex !== null) updatePendingEvent(voiceEditIndex, { resolvedPlayerId: '', resolvedPlayerName: '', confidence: 1 });
                        closeVoiceEdit();
                      }}
                    >
                      <div className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center text-slate-400 text-sm font-bold shrink-0">?</div>
                      <span className="text-sm text-slate-400 italic">Unresolved</span>
                    </button>
                    {filteredRoster.map(p => {
                      const isSelected = voiceEditIndex !== null && editingEvent?.eventType === 'stat' && editingEvent.resolvedPlayerId === p.id;
                      return (
                        <button
                          key={p.id}
                          type="button"
                          className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-50 active:bg-slate-100 ${isSelected ? 'bg-blue-50' : ''}`}
                          onClick={() => {
                            if (voiceEditIndex !== null) updatePendingEvent(voiceEditIndex, { resolvedPlayerId: p.id, resolvedPlayerName: p.name, confidence: 1 });
                            closeVoiceEdit();
                          }}
                        >
                          <div className={`w-9 h-9 rounded-full shrink-0 flex items-center justify-center text-xs font-bold bg-slate-100 text-slate-700 ${isSelected ? 'ring-2 ring-blue-400' : ''}`}>
                            {p.jerseyNumber || p.name.charAt(0)}
                          </div>
                          <div className="flex-1 text-left min-w-0">
                            <p className="text-sm font-semibold text-slate-900 truncate">{p.name}</p>
                            <p className="text-[11px] text-slate-400">{p.position}{p.jerseyNumber ? ` · #${p.jerseyNumber}` : ''}</p>
                          </div>
                          {isSelected && <span className="text-blue-500 text-xs font-bold shrink-0">✓</span>}
                        </button>
                      );
                    })}
                    {filteredRoster.length === 0 && (
                      <p className="text-sm text-slate-400 text-center py-8">No players match</p>
                    )}
                  </div>
                </div>
              )}

              {/* ── Stat type picker ── */}
              {voiceEditField === 'statType' && (
                <div className="overflow-y-auto flex-1 p-4">
                  <div className="grid grid-cols-3 gap-2">
                    {ALL_STAT_TYPES.map(st => {
                      const isSelected = voiceEditIndex !== null && editingEvent?.eventType === 'stat' && editingEvent.statType === st;
                      return (
                        <button
                          key={st}
                          type="button"
                          onClick={() => {
                            if (voiceEditIndex !== null) updatePendingEvent(voiceEditIndex, { statType: st });
                            closeVoiceEdit();
                          }}
                          className={`px-2 py-2.5 rounded-xl text-xs font-bold text-center transition-all ${isSelected ? 'ring-2 ring-blue-400 scale-95' : 'hover:scale-95 active:scale-90'} ${STAT_BADGE[st] ?? 'bg-slate-100 text-slate-500'}`}
                        >
                          {STAT_LABEL[st]}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* ── Chip field picker ── */}
              {voiceEditField && !['player', 'statType'].includes(voiceEditField) && (() => {
                const CHIP_OPTIONS: Record<string, { value: string | undefined; label: string }[]> = {
                  passQuality:       [{ value: undefined, label: 'Clear' }, { value: 'perfect', label: '3 pass — perfect' }, { value: 'good', label: '2 pass — good' }, { value: 'poor', label: '1 pass — poor' }],
                  serveType:         [{ value: undefined, label: 'Clear' }, { value: 'float', label: 'Float' }, { value: 'jump', label: 'Jump' }, { value: 'jump_float', label: 'Jump float' }, { value: 'topspin', label: 'Topspin' }],
                  serveTypeReceived: [{ value: undefined, label: 'Clear' }, { value: 'float', label: 'Float' }, { value: 'jump', label: 'Jump' }, { value: 'jump_float', label: 'Jump float' }, { value: 'topspin', label: 'Topspin' }],
                  setLocation:       [{ value: undefined, label: 'Clear' }, { value: 'outside', label: 'Outside' }, { value: 'middle', label: 'Middle' }, { value: 'right_side', label: 'Right side' }, { value: 'pipe', label: 'Pipe' }, { value: 'back_row', label: 'Back row' }],
                  attackLocation:    [{ value: undefined, label: 'Clear' }, { value: 'angle', label: 'Angle (cross-court)' }, { value: 'line', label: 'Line' }, { value: 'tool', label: 'Tool (off block)' }, { value: 'blocked', label: 'Blocked' }],
                  attackType:        [{ value: undefined, label: 'Clear' }, { value: 'swing', label: 'Swing' }, { value: 'tip', label: 'Tip' }, { value: 'roll', label: 'Roll' }],
                };
                const options = CHIP_OPTIONS[voiceEditField] ?? [];
                const currentVal = voiceEditIndex !== null && editingEvent?.eventType === 'stat' ? (editingEvent as any)[voiceEditField] : undefined;
                return (
                  <div className="overflow-y-auto flex-1 divide-y divide-slate-100">
                    {options.map((opt, oi) => {
                      const isSelected = currentVal === opt.value;
                      const isClear = opt.value === undefined;
                      return (
                        <button
                          key={oi}
                          type="button"
                          onClick={() => {
                            if (voiceEditIndex !== null) updatePendingEvent(voiceEditIndex, { [voiceEditField]: opt.value });
                            closeVoiceEdit();
                          }}
                          className={`w-full flex items-center justify-between px-5 py-3.5 hover:bg-slate-50 active:bg-slate-100 ${isSelected ? 'bg-blue-50' : ''}`}
                        >
                          <span className={`text-sm font-medium ${isClear ? 'text-slate-400 italic' : isSelected ? 'text-blue-700' : 'text-slate-800'}`}>{opt.label}</span>
                          {isSelected && <span className="text-blue-500 text-xs font-bold">✓</span>}
                        </button>
                      );
                    })}
                  </div>
                );
              })()}

              {/* ── Event rows (normal view) ── */}
              {!voiceEditField && (
                <>
                  {/* No opponent roster notice */}
                  {opponentRoster.length === 0 && pendingEvents.some(e => e.eventType === 'stat' && e.team === 'opponent') && (
                    <div className="mx-4 mt-3 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800">
                      <span className="font-semibold">No opponent roster loaded.</span> Opponent stats can't be attributed to players. Add the opponent lineup first, or discard those events below.
                    </div>
                  )}

                  <div className="overflow-y-auto flex-1 divide-y divide-slate-100">
                    {pendingEvents.map((event, i) => {
                      const isLow = event.confidence < 0.7;
                      const player = event.eventType === 'stat'
                        ? (teamPlayers.find(p => p.id === event.resolvedPlayerId)
                            ?? (opponentRoster.find(p => p.id === event.resolvedPlayerId) as Player | undefined) ?? null)
                        : null;
                      // Flag if the name doesn't match any real roster player
                      const isUnresolved = event.eventType === 'stat' && !player && !!event.resolvedPlayerName;
                      const photo   = player?.photoUrl;
                      const jersey  = player ? ('jerseyNumber' in player ? player.jerseyNumber : (player as any).number as string) : '';
                      const name    = player?.name ?? (event.eventType === 'stat' ? (event.resolvedPlayerName || event.playerRef || '?') : '');

                      const iconBg =
                        event.eventType === 'score'          ? (event.team === 'us' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700') :
                        event.eventType === 'substitution'   ? 'bg-slate-100 text-slate-600' :
                        event.eventType === 'timeout'        ? 'bg-amber-100 text-amber-700' :
                        event.eventType === 'possession_flip'? 'bg-blue-100 text-blue-700' :
                        event.eventType === 'rotation'       ? 'bg-slate-100 text-slate-600' :
                        event.eventType === 'libero_toggle'  ? 'bg-indigo-100 text-indigo-700' :
                        'bg-slate-100 text-slate-500';
                      const iconChar =
                        event.eventType === 'score'          ? (event.team === 'us' ? '✓' : '✕') :
                        event.eventType === 'substitution'   ? '⇄' :
                        event.eventType === 'timeout'        ? 'T' :
                        event.eventType === 'possession_flip'? '↔' :
                        event.eventType === 'rotation'       ? '↻' :
                        event.eventType === 'libero_toggle'  ? 'L' : '·';

                      const nonStatLabel =
                        event.eventType === 'substitution'   ? `${event.playerInRef} in · ${event.playerOutRef} out` :
                        event.eventType === 'score'          ? `Point — ${event.team === 'us' ? 'Us' : 'Opponent'}${(event.points ?? 1) > 1 ? ` ×${event.points}` : ''}` :
                        event.eventType === 'rotation'       ? `Rotate ${event.team === 'us' ? 'Us' : 'Them'} ${event.direction === 'cw' ? 'fwd' : 'back'}${(event.count ?? 1) > 1 ? ` ×${event.count}` : ''}` :
                        event.eventType === 'libero_toggle'  ? `Libero ${event.targetState === 'on_court' ? 'in' : 'out'} — ${event.liberoRef || '?'}` :
                        event.eventType === 'possession_flip'? (event.possession === 'serving' ? 'We serve' : 'We receive') :
                        event.eventType === 'timeout'        ? `Timeout — ${event.team === 'us' ? 'Us' : 'Opponent'}` : '';

                      // contactType: only 2 options — keep as inline toggle
                      const cycleCT = () => {
                        if (event.eventType !== 'stat') return;
                        const order = [undefined, 'platform', 'hands'] as const;
                        const next = order[(order.indexOf(event.contactType) + 1) % order.length];
                        updatePendingEvent(i, { contactType: next });
                      };
                      const openChip = (field: typeof voiceEditField) => { setVoiceEditIndex(i); setVoiceEditField(field); };

                      const PQ: Record<string, string> = { perfect: '3 pass', good: '2 pass', poor: '1 pass' };
                      const ST: Record<string, string> = { float: 'float', jump: 'jump', jump_float: 'jump-float', topspin: 'topspin' };
                      const CT: Record<string, string> = { platform: 'platform', hands: 'hands' };
                      const AT: Record<string, string> = { swing: 'swing', tip: 'tip', roll: 'roll' };

                      return (
                        <div key={i} className="px-4 py-4 min-h-[84px]">
                          <div className="flex items-center gap-3">
                            {/* Avatar — tap to pick player */}
                            {event.eventType === 'stat' ? (
                              <button
                                type="button"
                                onClick={() => { setVoiceEditIndex(i); setVoiceEditField('player'); setVoicePlayerSearch(''); }}
                                className={`relative w-11 h-11 rounded-full shrink-0 overflow-hidden ${isLow || isUnresolved ? 'ring-2 ring-amber-400' : 'ring-1 ring-slate-200'} active:opacity-70`}
                              >
                                {photo ? (
                                  <img src={photo} alt="" className="w-full h-full object-cover" />
                                ) : (
                                  <div className={`w-full h-full flex items-center justify-center text-sm font-bold ${event.team === 'us' ? 'bg-slate-100 text-slate-700' : 'bg-red-50 text-red-700'}`}>
                                    {jersey || name.charAt(0).toUpperCase()}
                                  </div>
                                )}
                                {(isLow || isUnresolved) && (
                                  <div className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-amber-400 rounded-full flex items-center justify-center">
                                    <span className="text-[8px] font-black text-white leading-none">!</span>
                                  </div>
                                )}
                              </button>
                            ) : (
                              <div className={`w-11 h-11 rounded-full shrink-0 flex items-center justify-center text-base font-bold ${iconBg} ${isLow ? 'ring-2 ring-amber-400' : ''}`}>
                                {iconChar}
                              </div>
                            )}

                            {/* Name / label — tap to pick player for stat events */}
                            <div className="flex-1 min-w-0">
                              {event.eventType === 'stat' ? (
                                <button
                                  type="button"
                                  onClick={() => { setVoiceEditIndex(i); setVoiceEditField('player'); setVoicePlayerSearch(''); }}
                                  className="text-left w-full active:opacity-70"
                                >
                                  <p className={`text-base font-semibold leading-tight truncate ${isUnresolved ? 'text-amber-600' : 'text-slate-900'}`}>
                                    {name}
                                    {jersey && <span className="ml-1.5 text-sm font-normal text-slate-400">#{jersey}</span>}
                                    {isUnresolved && <span className="ml-1.5 text-xs text-amber-500">not on roster</span>}
                                  </p>
                                </button>
                              ) : (
                                <p className="text-base font-medium text-slate-800 truncate">{nonStatLabel}</p>
                              )}
                            </div>

                            {/* Stat badge — tap to change stat type */}
                            {event.eventType === 'stat' && (
                              <div className="flex flex-col items-end gap-1.5 shrink-0">
                                <button
                                  type="button"
                                  onClick={() => { setVoiceEditIndex(i); setVoiceEditField('statType'); }}
                                  className={`text-xs font-bold px-2.5 py-1 rounded-full active:opacity-70 ${STAT_BADGE[event.statType] ?? 'bg-slate-100 text-slate-500'}`}
                                >
                                  {STAT_LABEL[event.statType] ?? event.statType.replace(/_/g, ' ')}
                                </button>
                                {event.suggested && (
                                  <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-violet-100 text-violet-600">suggested</span>
                                )}
                              </div>
                            )}

                            {/* Low-confidence flag for non-stat rows */}
                            {isLow && event.eventType !== 'stat' && (
                              <span className="shrink-0 text-amber-500 text-xs font-bold">⚠</span>
                            )}

                            {/* Discard */}
                            <button
                              type="button"
                              onClick={() => setPendingEvents(prev => prev.filter((_, j) => j !== i))}
                              className="shrink-0 text-slate-300 hover:text-red-400 transition-colors ml-1"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>

                          {/* Tappable metadata chips */}
                          {event.eventType === 'stat' && (() => {
                            const chips: { label: string; active: boolean; onTap: () => void }[] = [];
                            if (event.passQuality !== undefined || ['reception','reception_error'].includes(event.statType))
                              chips.push({ label: event.passQuality ? PQ[event.passQuality] : '+ pass quality', active: !!event.passQuality, onTap: () => openChip('passQuality') });
                            if (event.contactType !== undefined || ['reception','reception_error','dig','dig_error'].includes(event.statType))
                              chips.push({ label: event.contactType ? CT[event.contactType] : '+ contact', active: !!event.contactType, onTap: cycleCT });
                            if (event.serveType !== undefined || ['serve_attempt','ace','serve_error'].includes(event.statType))
                              chips.push({ label: event.serveType ? ST[event.serveType] : '+ serve type', active: !!event.serveType, onTap: () => openChip('serveType') });
                            if (event.serveTypeReceived !== undefined || ['reception','reception_error'].includes(event.statType))
                              chips.push({ label: event.serveTypeReceived ? `rcv:${ST[event.serveTypeReceived]}` : '+ rcv type', active: !!event.serveTypeReceived, onTap: () => openChip('serveTypeReceived') });
                            if (event.setLocation !== undefined || event.statType === 'assist')
                              chips.push({ label: event.setLocation ? event.setLocation.replace(/_/g, ' ') : '+ set loc', active: !!event.setLocation, onTap: () => openChip('setLocation') });
                            if (event.attackLocation !== undefined || ['kill','attack_error','attack_attempt'].includes(event.statType))
                              chips.push({ label: event.attackLocation ?? '+ atk loc', active: !!event.attackLocation, onTap: () => openChip('attackLocation') });
                            if (event.attackType !== undefined || ['kill','attack_error','attack_attempt'].includes(event.statType))
                              chips.push({ label: event.attackType ? AT[event.attackType] : '+ atk type', active: !!event.attackType, onTap: () => openChip('attackType') });
                            if (event.serveZone !== undefined)
                              chips.push({ label: `zone ${event.serveZone}${event.serveZoneInferred ? ' (inf)' : ''}`, active: true, onTap: () => {} });
                            if (chips.length === 0) return null;
                            return (
                              <div className="flex flex-wrap gap-1.5 mt-2 pl-14">
                                {chips.map((c, ci) => (
                                  <button
                                    key={ci}
                                    type="button"
                                    onClick={c.onTap}
                                    className={`text-[10px] font-medium rounded px-1.5 py-0.5 transition-colors active:scale-95 ${c.active ? 'bg-slate-200 text-slate-700 hover:bg-slate-300' : 'bg-slate-100 text-slate-400 hover:bg-slate-200 border border-dashed border-slate-300'}`}
                                  >
                                    {c.label}
                                  </button>
                                ))}
                              </div>
                            );
                          })()}
                        </div>
                      );
                    })}
                  </div>

                  {/* Footer */}
                  <div className="px-5 py-4 shrink-0 border-t border-slate-100">
                    <button
                      type="button"
                      onClick={() => confirmVoiceEvents(pendingEvents)}
                      className="w-full py-3 rounded-xl bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white font-bold text-sm transition-colors"
                    >
                      Confirm All ({pendingEvents.length})
                    </button>
                  </div>
                </>
              )}
            </div>
          </>
        );
      })()}

      {showLineupScanner && (
        <LineupSheetScanner
          players={teamPlayers}
          onApply={(positions, liberoId) => {
            setShowLineupScanner(false);
            if (newLineupScanMode) {
              // Route into new-lineup draft
              const liberoPlayer = liberoId ? teamPlayers.find(p => p.id === liberoId) : undefined;
              const libCfg: LiberoAssignment[] = liberoPlayer
                ? [{ liberoId: liberoPlayer.id, replacesPlayerIds: [positions.p3, positions.p6].filter(Boolean) as string[] }]
                : [];
              setNewLineupDraft({
                name: `Lineup ${teamLineups.length + 1}`,
                positions: { p1: '', p2: '', p3: '', p4: '', p5: '', p6: '', ...positions },
                liberoConfig: libCfg,
                liberoCount: 1,
              });
              setNewLineupFlow('manual');
              setNewLineupScanMode(false);
            } else {
              (Object.entries(positions) as [string, string][]).forEach(([pos, playerId]) => {
                if (playerId) updateLineupGeneric('us', pos, playerId);
              });
              if (liberoId) handleLiberoDesignate('us', liberoId, 0);
            }
          }}
          onClose={() => { setShowLineupScanner(false); setNewLineupScanMode(false); if (newLineupScanMode) setNewLineupFlow('chooser'); }}
        />
      )}

      {/* ── Set-End Tactical Insights Panel (Dean-only) ─────────────────────── */}
      {canSeeSetInsights && showSetInsightsPanel && (
        <div className="fixed bottom-0 left-0 right-0 z-[90] bg-slate-900 border-t border-slate-700 shadow-2xl max-h-[60vh] flex flex-col">
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-700 shrink-0">
            <div className="flex items-center gap-2">
              <Wand2 className="w-4 h-4 text-violet-400" />
              <span className="text-white font-bold text-sm">Set {gameState.set - 1} Insights</span>
              {isLoadingSetInsights && <Loader2 className="w-3.5 h-3.5 text-violet-400 animate-spin" />}
            </div>
            <button type="button" onClick={() => setShowSetInsightsPanel(false)} className="text-slate-400 hover:text-white p-1">
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="overflow-y-auto flex-1 p-4 space-y-3">
            {isLoadingSetInsights ? (
              <p className="text-slate-400 text-sm text-center py-4">Analyzing set data…</p>
            ) : setInsights.length === 0 ? (
              <p className="text-slate-400 text-sm text-center py-4">No actionable insights found for this set.</p>
            ) : (
              setInsights.map((tip, i) => (
                <div key={i} className="bg-slate-800 rounded-xl p-3 border border-slate-700">
                  <div className="flex items-start gap-2">
                    <span className="text-[10px] font-bold uppercase tracking-wider bg-violet-600/30 text-violet-300 px-2 py-0.5 rounded-full shrink-0 mt-0.5">{tip.category}</span>
                    <div className="min-w-0">
                      <p className="text-white text-sm font-semibold leading-snug">{tip.suggestion}</p>
                      <p className="text-slate-400 text-xs mt-1 leading-relaxed">{tip.rationale}</p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default GameTime;
